import '../constants/const.css';
import './css/pagePlanResult.css';

import React, { useState, useEffect, useRef, useMemo, FC } from 'react';
import { CustomDatePicker } from '../components/customDatePicker';
import 'react-datepicker/dist/react-datepicker.css';
import { ja } from 'date-fns/locale';
import { format } from 'date-fns';
import { Dayjs } from '../logic/classDayjs';
import { getCurrentFiscalYear } from '../logic/util';
import BurnGraph2 from '../components/planResult/burnGraph2';
import AshGraph from '../components/planResult/ashGraph';
import 'bootstrap/dist/css/bootstrap.min.css';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import ToggleButtonGroup from 'react-bootstrap/ToggleButtonGroup';
import ToggleButton from 'react-bootstrap/ToggleButton';
import EmptyNumberInput from '../components/planResult/emptyNumberInput';
import MarkDateGraph from '../components/planResult/markDateGraph';

import { BinaryResponse, JsonResponse, REQ_PLAN_RESULT, TextResponse, useApi } from '../logic/classApi';
import { LoginService } from '../logic/loginService';
import PastResult from '../pages/pagePastResult';
import AppPane, { MessageDialogOptions, showConfirmMessage, showErrorMessage, showMessage, showSuccessMessage } from '../components/appPane';
import { MESSAGES, PERMISSIONS, PATH_PLANRESULT } from '../constants/const';
import LineChart, { LineChartProps, LinesProp } from '../components/lineChart';
import { COLOR_CODE, LINE_SEQ } from '../constants/const';

import {
  DayContext,
  DayContextObject,
  DriveContext,
  DriveContextObject,
  DraftContext,
  DraftContextObject,
  ConditionContext,
  ConditionContextObject,
  SessionContext,
  MainContext,
  MainContextObject,
  DriveEmpty,
  DriveEmptyObject,
  DriveContextObjectIndexMap as DriveMap,
  DraftContextObjectIndexMap as DraftMap,
  MonitorObjectIndexMap as MonitorMap,
  DraftEmpty,
  DraftEmptyObject,
  MonitorContext,
  MonitorContextObject,
  MonitorMonthContext,
  MonitorMonthContextObject,
  DriveUsage,
  DriveUsageObject,
} from '../entities/planResult';
import { openNewTabComponent } from '../App';
import BurnGraphBackground from '../components/planResult/BurnGraphBackground';
import { SumTable } from '../components/planResult/sumTable';

type NumberKeys<T> = {
  [K in keyof T]: T[K] extends number ? K : never;
}[keyof T];

interface MultipleBurnSetting {
  launch_date: string;
  shutdown_date: string;
  burn_volume: number;
  burn_load: number;
}

export class MultipleBurnSettingObject {
  launch_date: string = '';
  shutdown_date: string = '';
  burn_volume: number = 0;
  burn_load: number = 0;
}

class LineData {
  title: string = '';
  labels: string[] = [] as string[];
  color: string = '';
  data: (number | null)[] = [] as (number | null)[];
  unit: string = '';
  pointRadius?: number = 3;
  plotType?: string;
}

interface ShutdownRecord {
  record_date: string;
  burn_volume: number;
}

interface DraftPattern {
  priority_id: number;
  priority_name: string;
}

interface PlanResultHistory {
  created_at: string;
  show_created_at: string;
  initial_plan_flag?: boolean;
}

type DraftContextUpdater = (
  editResource: DraftContext,
  initResource: DraftContext,
  emptyResource: DraftEmpty,
  usage: DriveUsage,
  monIndex: number,
  colIndex: number,
  furnacesIndex: number,
  previousValue?: number,
) => DraftContext;

const IS_ESCLUDED_FROM_SPEC: boolean = false;

const MONTH_CELL_WIDTH: number = 140;

const DAY_MAX_COUNT: number = 366;

const SELECT_MONTH_INDEX = 0;
const POST_REQ_DRAFT = 1;
const POST_REQ_SAVE = 2;
const POST_REQ_EXCEL_OUTPUT = 3;
const POST_REQ_RESULT_REGISTER = 4;
const POST_REQ_PAST_REFLECT = 5;
const POST_REQ_EXCEL_HISTORY_OUTPUT = 6;
const POST_REQ_UPDATE_INITIAL_PLAN = 7;

const STATUS_CODE = {
  SUCCESS: 200,
};

const PLAN_RESULT_MESSAGES = {
  SYOUKYAKURYO_IKKATU_TEIROBI_ERROR: '停炉日は焼却量を設定できません',
  SYOUKYAKURYO_MONTH_ERROR: '設定期間に別の月が含まれています',
  SYOUKYAKURYO_SHUTDOWN_ERROR: '期間の開始日を設定して下さい',
  SYOUKYAKURYO_LAUNCH_ERROR: '期間の終了日を設定して下さい',
  SYOUKYAKURYO_BURN_VOLUME_ERROR: '焼却量は0以上で設定して下さい',
  SYOUKYAKURYO_INVALID_SHUTDOWN_ERROR: '期間の終了日は開始日以降に設定してください',
};

type InputKeys = Extract<
  keyof DriveContext,
  | 'carry_volume'
  | 'combustible_waste_carry_volume'
  | 'pruning_branches_carry_volume'
  | 'small_animals_carry_volume'
  | 'combustible_bulk_waste_carry_volume'
  | 'shredded_waste_residue_carry_volume'
  | 'disaster_waste_internal_carry_volume'
  | 'disaster_waste_external_carry_volume'
  | 'other_carry_volume'
  | 'incineration_facility_bottom_ash_carry_volume'
  | 'recyclables_carry_volume'
  | 'main_ash_generate'
  | 'fly_ash_generate'
  | 'main_ash_carry_out'
  | 'fly_ash_carry_out'
  | 'dry_fly_ash_carry_out'
  | 'processed_fly_ash_carry_out'
  | 'burn_volume'
  | 'main_ash_carry_out_units'
  | 'fly_ash_carry_out_units'
  | 'trash_pit_rest'
  | 'main_ash_pit_rest'
  | 'fly_ash_pit_rest'
  | 'waste_lower_heating_value'
  | 'hot_water_supply_heating_value'
>;

const COMMON_DIGIT = 2;
const CARRY_DIGIT = 3;
const BURNVOLUME_DIGIT = 2;
const ASH_OUT_DIGIT = 2;
const TRASH_PIT_DIGIT = 2;
const ASH_PIT_DIGIT = 2;
const WASTE_DIGIT = 2;
const HOT_WATER_DIGIT = 2;

const DRIVE_INPUT_DIGITS: Record<InputKeys, number> = {
  carry_volume: CARRY_DIGIT,
  combustible_waste_carry_volume: CARRY_DIGIT,
  pruning_branches_carry_volume: CARRY_DIGIT,
  small_animals_carry_volume: CARRY_DIGIT,
  combustible_bulk_waste_carry_volume: CARRY_DIGIT,
  shredded_waste_residue_carry_volume: CARRY_DIGIT,
  disaster_waste_internal_carry_volume: CARRY_DIGIT,
  disaster_waste_external_carry_volume: CARRY_DIGIT,
  other_carry_volume: CARRY_DIGIT,
  incineration_facility_bottom_ash_carry_volume: CARRY_DIGIT,
  recyclables_carry_volume: CARRY_DIGIT,
  burn_volume: BURNVOLUME_DIGIT,
  main_ash_generate: ASH_OUT_DIGIT,
  fly_ash_generate: ASH_OUT_DIGIT,
  main_ash_carry_out: ASH_OUT_DIGIT,
  fly_ash_carry_out: ASH_OUT_DIGIT,
  dry_fly_ash_carry_out: ASH_OUT_DIGIT,
  processed_fly_ash_carry_out: ASH_OUT_DIGIT,
  trash_pit_rest: TRASH_PIT_DIGIT,
  main_ash_carry_out_units: TRASH_PIT_DIGIT,
  fly_ash_carry_out_units: TRASH_PIT_DIGIT,
  main_ash_pit_rest: TRASH_PIT_DIGIT,
  fly_ash_pit_rest: TRASH_PIT_DIGIT,
  waste_lower_heating_value: WASTE_DIGIT,
  hot_water_supply_heating_value: HOT_WATER_DIGIT,
};

const BURNLOAD_DIGIT = 0;
const ASH_BLUR_DIGIT = 2;
const CARRY_BLUR_DIGIT = 2;
const BURNVOLUME_BLUR_DIGIT = 2;
const TRASH_PIT_BLUR_DIGIT = 2;
const WASTE_BLUR_DIGIT = 2;
const HOT_WATER_BLUR_DIGIT = 2;

const DRAFT_MAX_COUNT = 3;

const PAST_RESULT_FILE_NAME: string = '過去実績登録';

const DATE_FORMAT_YYYYMMDD = 'yyyy-MM-dd';
const DATE_FORMAT_YYYYMMDD_SLASH = 'yyyy/MM/dd';
const DATE_FORMAT_MMDD_SLASH = 'MM/dd';

const months: number[] = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3];
const WEEK_DAYS = ['日', '月', '火', '水', '木', '金', '土'];
const COLOR_ARRAY: string[] = [
  COLOR_CODE.INITIAL,
  COLOR_CODE.PLAN,
  COLOR_CODE.RESULT,
  COLOR_CODE.DRAFT1,
  COLOR_CODE.DRAFT2,
  COLOR_CODE.DRAFT3,
  COLOR_CODE.DRAFT4,
  COLOR_CODE.DRAFT5,
];

const EXCLUDE_UNSAVE_KEYS: (keyof DriveContext)[] = ['burn_load', 'operating_days', 'date'];

const CARRY_KEYS: (keyof DriveUsage & keyof DriveEmpty)[] = [
  'combustible_waste_carry_volume',
  'pruning_branches_carry_volume',
  'small_animals_carry_volume',
  'combustible_bulk_waste_carry_volume',
  'shredded_waste_residue_carry_volume',
  'disaster_waste_internal_carry_volume',
  'disaster_waste_external_carry_volume',
  'other_carry_volume',
  'incineration_facility_bottom_ash_carry_volume',
  'recyclables_carry_volume',
];

const CARRY_DETAILS: number[] = [
  DriveMap.combustible_waste_carry_volume,
  DriveMap.pruning_branches_carry_volume,
  DriveMap.small_animals_carry_volume,
  DriveMap.combustible_bulk_waste_carry_volume,
  DriveMap.shredded_waste_residue_carry_volume,
  DriveMap.disaster_waste_internal_carry_volume,
  DriveMap.disaster_waste_external_carry_volume,
  DriveMap.other_carry_volume,
  DriveMap.incineration_facility_bottom_ash_carry_volume,
  DriveMap.recyclables_carry_volume,
];

const ASH_KEYS: (keyof DriveUsage & keyof DriveEmpty)[] = [
  'main_ash_generate',
  'main_ash_carry_out_units',
  'main_ash_carry_out',
  'main_ash_pit_rest',
  'fly_ash_generate',
  'fly_ash_carry_out_units',
  'fly_ash_carry_out',
  'dry_fly_ash_carry_out',
  'processed_fly_ash_carry_out',
  'fly_ash_pit_rest',
];
const ASH_DETAILS: number[] = [
  DriveMap.main_ash_generate,
  DriveMap.main_ash_carry_out_units,
  DriveMap.main_ash_carry_out,
  DriveMap.main_ash_pit_rest,
  DriveMap.fly_ash_generate,
  DriveMap.fly_ash_carry_out_units,
  DriveMap.fly_ash_carry_out,
  DriveMap.dry_fly_ash_carry_out,
  DriveMap.processed_fly_ash_carry_out,
  DriveMap.fly_ash_pit_rest,
];

const OPTIONAL_KEYS: (keyof DriveUsage & keyof DriveEmpty)[] = [
  'carry_volume',
  'combustible_waste_carry_volume',
  'pruning_branches_carry_volume',
  'small_animals_carry_volume',
  'combustible_bulk_waste_carry_volume',
  'shredded_waste_residue_carry_volume',
  'disaster_waste_internal_carry_volume',
  'disaster_waste_external_carry_volume',
  'other_carry_volume',
  'incineration_facility_bottom_ash_carry_volume',
  'recyclables_carry_volume',
  'main_ash_generate',
  'fly_ash_generate',
  'fly_ash_carry_out',
  'dry_fly_ash_carry_out',
  'processed_fly_ash_carry_out',
];

const item_shutdown_date: string = 'shutdown_date';
const item_launch_date: string = 'launch_date';

function isEqualJson(editObj: any, initObj: any) {
  return JSON.stringify(editObj) === JSON.stringify(initObj);
}

function omitProperties(obj: object, keysToOmit: string[]): any {
  const result: any = {};
  for (const key in obj) {
    if (!keysToOmit.includes(key)) {
      result[key] = (obj as any)[key];
    }
  }
  return result;
}

function getCommonKeys(from: DriveUsage, reference: DriveContext): (keyof DriveUsage & keyof DriveContext)[] {
  return Object.keys(from).filter((key): key is keyof DriveUsage & keyof DriveContext => key in reference);
}

function getUnusageProperties(usage: DriveUsage): string[] {
  const results: string[] = [] as string[];
  const keys: (keyof DriveUsage & keyof DriveContext)[] = getCommonKeys(usage, new DriveContextObject(0, 3));
  for (let i = 0; i < keys.length; i++) {
    const key: keyof DriveUsage & keyof DriveContext = keys[i];
    if (!usage[key]) {
      results.push(key);
    }
  }
  return results;
}

function getCalendarYear(year: number, monNum: number): number {
  if (monNum <= 3) {
    year = year + 1;
  }
  return year;
}

function getFiscalYearDayCounts(year: number): number[] {
  return months.map((month) => {
    const calendarYear = getCalendarYear(year, month);
    return new Date(calendarYear, month, 0).getDate();
  });
}

function formatTwoDigits(num: number): string {
  return num.toString().padStart(2, '0');
}

function calcDecimalSum(value1: number, value2: number) {
  const digitVal1: number = String(value1).indexOf('.') > 0 ? String(value1).length - String(value1).indexOf('.') - 1 : 0;
  const digitVal2: number = String(value2).indexOf('.') > 0 ? String(value2).length - String(value2).indexOf('.') - 1 : 0;
  const digit: number = Math.max(digitVal1, digitVal2);
  const largeVal1: number = parseInt((value1.toFixed(digit) + '').replace('.', ''));
  const largeVal2: number = parseInt((value2.toFixed(digit) + '').replace('.', ''));
  const sumLarge: number = largeVal1 + largeVal2;
  const ret: number = sumLarge / 10 ** digit;
  return ret;
}

function addComma(value: number): string {
  const strValue: string = String(value);
  const disit: number = strValue.indexOf('.');
  const formatter = new Intl.NumberFormat('ja-JP');
  if (disit <= 0) {
    return formatter.format(Number(strValue));
  } else {
    let intValue: string = strValue.substring(0, disit);
    let pointValue: string = strValue.substring(disit, strValue.length);
    return formatter.format(Number(intValue)) + pointValue;
  }
}

function deleteComma(value: string): number {
  return Number(value.replaceAll(',', ''));
}

function calcMathRound(value: number, digit: number) {
  if (digit < 0) {
    return value;
  }
  const val1: number = value * 10 ** digit;
  const val2: number = Math.round(val1);
  const ret: number = val2 / 10 ** digit;
  return ret;
}

function calcMathFloor(value: number, digit: number) {
  if (digit < 0) {
    return value;
  }
  const val1: number = value * 10 ** digit;
  const val2: number = Math.floor(val1);
  const ret: number = val2 / 10 ** digit;
  return ret;
}

function sumArray(numbers: number[]): number {
  let sumNum: number = 0;
  for (let i = 0; i < numbers.length; i++) {
    sumNum = calcDecimalSum(sumNum, numbers[i]);
  }
  return sumNum;
}

function createDay(year: number, dayNums: number[], dayIndex: number): string {
  let monIndex: number = 0;
  let dateIndex: number = 0;

  let addMonthNums: number = 0;
  for (let i = 0; i < dayNums.length; i++) {
    if (addMonthNums + dayNums[i] > dayIndex) {
      monIndex = i;
      dateIndex = dayIndex - addMonthNums;
      break;
    }
    addMonthNums = addMonthNums + dayNums[i];
  }
  year = getCalendarYear(year, months[monIndex]);
  const strDate: string = String(year)
    .concat('-')
    .concat(formatTwoDigits(months[monIndex]))
    .concat('-')
    .concat(formatTwoDigits(dateIndex + 1));
  return strDate;
}

function createDateInYear(year: number, dayArray: number[]): string[] {
  const strDateArray: string[] = [] as string[];
  let dayIndex: number = 0;
  for (let i = 0; i < dayArray.length; i++) {
    for (let j = 0; j < dayArray[i]; j++) {
      strDateArray.push(createDay(year, dayArray, dayIndex));
      dayIndex = dayIndex + 1;
    }
  }
  return strDateArray;
}

function getInputableMinDate(numYear: number, displayDays: number[], draftDays: DriveEmpty[], systemDayIndex: number): string {
  const yearDays: string[] = createDateInYear(numYear, displayDays);
  for (let i = 0; i < yearDays.length; i++) {
    const draftDay = draftDays[i];
    if (!draftDay) continue;
    // データ有り かつ システム日付以降
    if (!draftDays[i].burn_volume.some((value) => value === true) && i >= systemDayIndex) return yearDays[i];
  }
  return getMaxDate(numYear);
}

function getMinDate(numYear: number): string {
  return String(numYear).concat('-04-01');
}

function getMaxDate(numYear: number): string {
  return String(getCalendarYear(numYear, 3)).concat('-03-31');
}

function getStrDate(numYear: number, numMonth: number, numDate: number): string {
  return String(getCalendarYear(numYear, numMonth)).concat('-').concat(formatTwoDigits(numMonth)).concat('-').concat(formatTwoDigits(numDate));
}

function getStrDateCalendar(numYear: number, numMonth: number, numDate: number): string {
  return String(numYear).concat('-').concat(formatTwoDigits(numMonth)).concat('-').concat(formatTwoDigits(numDate));
}

// 立下日・立上日を
function excludeOutOfShutdownDays(shutdownDates: DayContext[], yearNum: number, startStr: string, endStr: string): DayContext[] {
  // ディープコピー
  const letDates: DayContext[] = shutdownDates.map((date) => ({
    ...date,
    shutdown_date: date.shutdown_date,
    launch_date: date.launch_date,
    not_change_flag: date.not_change_flag,
  }));

  const sortedDates: DayContext[] = letDates.sort((a, b) => {
    const dateA = new Date(a.shutdown_date).getTime();
    const dateB = new Date(b.shutdown_date).getTime();
    return dateA - dateB; // 昇順
  });

  const shutArray: DayContext[] = [] as DayContext[];
  const startDate = new Date(startStr);
  const endDate = new Date(endStr);
  const startTime = startDate.getTime();
  const endTime = endDate.getTime();
  for (let i = 0; i < sortedDates.length; i++) {
    let shutdownTime = new Date(sortedDates[i].shutdown_date).getTime();
    let launchTime = new Date(sortedDates[i].launch_date).getTime();
    if ((startTime <= shutdownTime && shutdownTime <= endTime) || (startTime <= launchTime && launchTime <= endTime)) {
      if (shutdownTime <= startTime) {
        sortedDates[i].shutdown_date = startStr;
      } else if (launchTime >= endTime) {
        sortedDates[i].launch_date = endStr;
      }
      shutArray.push(sortedDates[i]);
    }
  }
  return sortedDates;
}

function applyGraphInfo(data: LineData[], gridBottomColors: string[], isSelect: boolean[], titleYLeft?: string, titleYRight?: string): LineChartProps {
  if (data.length === 0) {
    return {} as LineChartProps;
  }
  const lines: LinesProp[] = [];
  for (let i = 0; i < data.length; i++) {
    const line: LinesProp = {
      title: data[i].title,
      data: data[i].data,
      unit: `[${data[i].unit}]`,
      style: {
        borderColor: data[i].color,
        backgroundColor: data[i].color,
        pointRadius: data[i].pointRadius,
        type: data[i].plotType,
        thickness: 2,
      },
      state: {
        hidden: !isSelect[i],
      },
    };
    lines.push(line);
  }
  const labelsBottom: string[] = data[0].labels;
  const labelsTop: string[] = Array(labelsBottom.length > 0 ? labelsBottom.length - 1 : 0).fill('');

  const gridTopColors: string[] = [];
  for (let i = 0; i < labelsBottom.length; i++) {
    gridTopColors.push('white');
  }
  const prop: LineChartProps = {
    canvas: {
      labelsTop: [...labelsTop, ''],
      labelsBottom: [...labelsBottom, ''],
      style: 'chart-canvas',
      height: '320px',
      width: '1180px',
      titleYLeft: titleYLeft,
      titleYRight: titleYRight,
      titleYLeftUnit: data[0].unit,
    },
    lines: lines,
    grid: { topColors: gridTopColors, bottomColors: gridBottomColors, thickness: 0.3 },
  };
  return prop;
}

function getArrayElementState<T>(array: T[], setArray: React.Dispatch<React.SetStateAction<T[]>>, index: number): [T, React.Dispatch<React.SetStateAction<T>>] {
  return [
    array[index],
    (newValue: React.SetStateAction<T>) => {
      setArray((prev) => {
        const updated = [...prev];
        const prevItem = prev[index];
        updated[index] = typeof newValue === 'function' ? (newValue as (prev: T) => T)(prevItem) : newValue;
        return updated;
      });
    },
  ];
}

function calcMonitorItem(context: DriveContext, usage: DriveUsage, key: keyof MonitorContext): number {
  const keys = Object.keys(new MonitorContextObject(0, 3)) as (keyof MonitorContext)[];
  if (key === keys[MonitorMap.total_carry_volume]) {
    if (
      usage.combustible_waste_carry_volume ||
      usage.pruning_branches_carry_volume ||
      usage.small_animals_carry_volume ||
      usage.combustible_bulk_waste_carry_volume ||
      usage.shredded_waste_residue_carry_volume ||
      usage.disaster_waste_internal_carry_volume ||
      usage.disaster_waste_external_carry_volume ||
      usage.other_carry_volume ||
      usage.incineration_facility_bottom_ash_carry_volume ||
      usage.recyclables_carry_volume
    ) {
      return (
        context.combustible_waste_carry_volume +
        context.pruning_branches_carry_volume +
        context.small_animals_carry_volume +
        context.combustible_bulk_waste_carry_volume +
        context.shredded_waste_residue_carry_volume +
        context.disaster_waste_internal_carry_volume +
        context.disaster_waste_external_carry_volume +
        context.other_carry_volume +
        context.incineration_facility_bottom_ash_carry_volume +
        context.recyclables_carry_volume
      );
    } else {
      return context.carry_volume;
    }
  } else if (key === keys[MonitorMap.main_ash_carry_out_sum]) {
    return context.main_ash_carry_out;
  } else if (key === keys[MonitorMap.fly_ash_carry_out_sum]) {
    if (usage.dry_fly_ash_carry_out || usage.processed_fly_ash_carry_out) {
      return context.dry_fly_ash_carry_out + context.processed_fly_ash_carry_out;
    } else {
      return context.fly_ash_carry_out;
    }
  } else if (key === keys[MonitorMap.electric_use_sum]) {
    if (
      usage.plant_power_high_voltage_main_energy ||
      usage.building_power_high_voltage_main_energy ||
      usage.lighting_power_high_voltage_main_energy ||
      usage.security_power_high_voltage_main_energy ||
      usage.recycle_emergency_low_voltage_power_energy
    ) {
      return (
        context.plant_power_high_voltage_main_energy +
        context.building_power_high_voltage_main_energy +
        context.lighting_power_high_voltage_main_energy +
        context.security_power_high_voltage_main_energy +
        context.recycle_emergency_low_voltage_power_energy
      );
    } else {
      return context.electric_use;
    }
  }
  return 0;
}

function getDateIndex(targetDate: string, year: number, days: number[]): number {
  for (let dayIndex = 0; dayIndex < sumArray(days); dayIndex++) {
    if (targetDate === createDay(year, days, dayIndex)) {
      return dayIndex;
    }
  }
  return -1;
}

interface PageProps {
  isUnsavedState: [boolean, React.Dispatch<React.SetStateAction<boolean>>];
}

const Page: FC<PageProps> = (props) => {
  // 画面操作
  const [isReload, setIsReload] = useState<boolean>(true);
  const [isUnsaved, setIsUnsaved] = props.isUnsavedState;
  const [selectMonthIndex, setSelectMonthIndex] = useState<number>(SELECT_MONTH_INDEX);
  const [apiYear, setApiYear] = useState<number>(getCurrentFiscalYear(new Date()));
  const [displayYear, setDisplayYear] = useState<number>(new Date().getFullYear());
  const [pastYear, setPastYear] = useState<number>(new Date().getFullYear());
  const [autoDraftBaseDate, setAutoDraftBaseDate] = useState<string>(getMinDate(getCurrentFiscalYear(new Date())));
  const [trashAcceptableAmount, setTrashAcceptableAmount] = useState({ date: '', value: 0, percent: 0 });
  const [displayDays, setDisplayDays] = useState<number[]>(getFiscalYearDayCounts(getCurrentFiscalYear(new Date())));
  const [showInitialPlanVisible, setShowInitialPlanVisible] = useState<boolean>(false);
  const [showPlanVisible, setShowPlanVisible] = useState<boolean>(true);
  const [showResultVisible, setShowResultVisible] = useState<boolean>(true);
  const [showPowerVisible, setShowPowerVisible] = useState<boolean>(false);
  const [showAshVisible, setShowAshVisible] = useState<boolean>(false);
  const [showOperationDaysVisible, setShowOperationDaysVisible] = useState<boolean>(false);
  const initialPlanShutdownVisibleState = useState<boolean[]>(Array(LoginService.getLoginInfo().furnacesCount).fill(false));
  const [initialPlanShutdownVisible, setInitialPlanShutdownVisible] = initialPlanShutdownVisibleState;
  const planShutdownVisibleState = useState<boolean[]>(Array(LoginService.getLoginInfo().furnacesCount).fill(false));
  const [planShutdownVisible, setPlanShutdownVisible] = planShutdownVisibleState;
  const draftShutdownVisibleState = useState<boolean[][]>(Array.from({ length: DRAFT_MAX_COUNT }, () => Array(LoginService.getLoginInfo().furnacesCount).fill(false)));
  const [draftShutdownVisible, setDraftShutdownVisible] = draftShutdownVisibleState;
  const initialPlanShutdownInputVisibleState = useState<boolean[]>(Array(LoginService.getLoginInfo().furnacesCount).fill(false));
  const [initialPlanShutdownInputVisible, setInitialPlanShutdownInputVisible] = initialPlanShutdownInputVisibleState;
  const planShutdownInputVisibleState = useState<boolean[]>(Array(LoginService.getLoginInfo().furnacesCount).fill(false));
  const [planShutdownInputVisible, setPlanShutdownInputVisible] = planShutdownInputVisibleState;
  const draftShutdownInputVisibleState = useState<boolean[][]>(Array.from({ length: DRAFT_MAX_COUNT }, () => Array(LoginService.getLoginInfo().furnacesCount).fill(false)));
  const [draftShutdownInputVisible, setDraftShutdownInputVisible] = draftShutdownInputVisibleState;
  const [pastYearList, setPastYearList] = useState<string[]>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [draftPattern, setDraftPattern] = useState<DraftPattern[]>([]);
  const [excelOutputDraftPattern, setExcelOutputDraftPattern] = useState<DraftPattern[]>([]);
  const tableStr: string[] = useMemo(() => ['期初計画', '計画', '実績', ...draftPattern.map((pattern) => pattern.priority_name)], [draftPattern]);
  const [selectedPriorityId, setSelectedPriorityId] = useState<number>(0);
  const [planResultHistory, setPlanResultHistory] = useState<PlanResultHistory[]>([]);
  const [selectedCreatedAt, setSelectedselectedCreatedAt] = useState<string>('');
  const [selectedInitialPlanTargetCreatedAt, setSelectedInitialPlanTargetCreatedAt] = useState<string>('');

  const [role] = useState<number>(LoginService.getLoginInfo().role);

  function isMonthDisplay() {
    return selectMonthIndex === SELECT_MONTH_INDEX;
  }

  function daySum(numIndex: number): number {
    if (numIndex === SELECT_MONTH_INDEX || displayDays.length === 0) {
      return 0;
    }
    return sumArray(displayDays.slice(0, numIndex));
  }

  function concatResultPlanMonthDraft(resultMonthContexts: DriveContext[], planMonthContexts: DriveContext[], resultDayContexts: DriveContext[]): DriveContext[] {
    let retArray: DriveContext[] = [];
    for (let i = 0; i < planMonthContexts.length; i++) {
      if (resultMonthContexts[i].date !== '' && resultDayContexts[daySum(i + 1) - 1].date !== '') {
        retArray.push(resultMonthContexts[i]);
      } else {
        retArray.push(planMonthContexts[i]);
      }
    }
    return retArray;
  }

  function concatResultPlanDayDraft(resultContexts: DriveContext[], planContexts: DriveContext[]): DriveContext[] {
    let retArray: DriveContext[] = [];
    for (let i = 0; i < planContexts.length; i++) {
      if (resultContexts[i].date !== '') {
        retArray.push(resultContexts[i]);
      } else {
        retArray.push(planContexts[i]);
      }
    }
    return retArray;
  }

  function getMonitorList(contexts: DriveContext[], usage: DriveUsage): MonitorContext[] {
    const keys = Object.keys(new MonitorContextObject(0, 3)) as (keyof MonitorContext)[];
    const objectList: MonitorContext[] = Array.from({ length: DAY_MAX_COUNT }, () => new MonitorContextObject(0, 3));
    for (let i = 0; i < contexts.length; i++) {
      for (let j = 0; j < keys.length; j++) {
        const key: keyof MonitorContext = keys[j];
        objectList[i][key] = calcMonitorItem(contexts[i], usage, key);
      }
    }
    return objectList;
  }

  function getMonitorSum(contexts: DriveContext[], usage: DriveUsage): MonitorContext {
    const keys = Object.keys(new MonitorContextObject(0, 3)) as (keyof MonitorContext)[];
    const objectSum: MonitorContext = new MonitorContextObject(0, 3);
    for (let i = 0; i < contexts.length; i++) {
      for (let j = 0; j < keys.length; j++) {
        const key: keyof MonitorContext = keys[j];
        objectSum[key] += calcMonitorItem(contexts[i], usage, key);
      }
    }
    return objectSum;
  }

  function getMonitorMonthList(contexts: DriveContext[], furnacesCount: number): MonitorMonthContext[] {
    const objectList: MonitorMonthContext[] = Array.from({ length: displayDays.length }, () => new MonitorMonthContextObject(0, furnacesCount));
    for (let monthIndex = 0; monthIndex < displayDays.length; monthIndex++) {
      const obj: MonitorMonthContext = new MonitorMonthContextObject(0, furnacesCount);
      const furnaceBurns: number[][] = [] as number[][];
      const furnaceWorkCounts: number[] = [] as number[];
      for (let furnacesIndex = 0; furnacesIndex < furnacesCount; furnacesIndex++) {
        const burnVolumes: number[] = selectItemFurnaceArray(contexts.slice(daySum(monthIndex), daySum(monthIndex + 1)), 'burn_volume', furnacesIndex);
        furnaceBurns.push(burnVolumes);
        furnaceWorkCounts.push(burnVolumes.filter((value) => value > 0).length);
      }
      let sumBurn: number = 0;
      let sumOperationDays: number = 0;
      for (let furnacesIndex = 0; furnacesIndex < furnacesCount; furnacesIndex++) {
        const furnaceOperationDays: number = furnaceBurns[furnacesIndex].filter((value) => value > 0).length;
        const furnaceSumBurn: number = sumArray(furnaceBurns[furnacesIndex]);
        obj.operating_days[furnacesIndex] = furnaceOperationDays;
        obj.burn_load[furnacesIndex] = calcBurnload(furnaceSumBurn, furnaceOperationDays);
        obj.burn_load_include_shutdown[furnacesIndex] = calcBurnload(furnaceSumBurn, displayDays[monthIndex]);
        sumOperationDays += furnaceOperationDays;
        sumBurn += furnaceSumBurn;
      }
      const furnaceRuns: number[] = Array(furnacesCount + 1).fill(0);
      for (let i = 0; i < displayDays[monthIndex]; i++) {
        let count: number = 0;
        for (let furnacesIndex = 0; furnacesIndex < furnacesCount; furnacesIndex++) {
          if (furnaceBurns[furnacesIndex][i] > 0) {
            count += 1;
          }
        }
        furnaceRuns[count] += 1;
      }
      obj.furnace_operating_days = furnaceRuns;
      obj.burn_load_sum = calcBurnload(sumBurn, sumOperationDays);
      obj.burn_load_include_shutdown_sum = calcBurnload(sumBurn, displayDays[monthIndex] * furnacesCount);
      objectList[monthIndex] = obj;
    }
    return objectList;
  }

  function getMonitorMonthSum(contexts: DriveContext[], furnacesCount: number): MonitorMonthContext {
    const obj: MonitorMonthContext = new MonitorMonthContextObject(0, furnacesCount);
    const furnaceBurns: number[][] = [] as number[][];
    const furnaceWorkCounts: number[] = [] as number[];
    for (let furnacesIndex = 0; furnacesIndex < furnacesCount; furnacesIndex++) {
      const burnVolumes: number[] = selectItemFurnaceArray(contexts, 'burn_volume', furnacesIndex);
      furnaceBurns.push(burnVolumes);
      furnaceWorkCounts.push(burnVolumes.filter((value) => value > 0).length);
    }
    let sumBurn: number = 0;
    let sumOperationDays: number = 0;
    for (let furnacesIndex = 0; furnacesIndex < furnacesCount; furnacesIndex++) {
      const furnaceOperationDays: number = furnaceBurns[furnacesIndex].filter((value) => value > 0).length;
      const furnaceSumBurn: number = sumArray(furnaceBurns[furnacesIndex]);
      obj.operating_days[furnacesIndex] = furnaceOperationDays;
      obj.burn_load[furnacesIndex] = calcBurnload(furnaceSumBurn, furnaceOperationDays);
      obj.burn_load_include_shutdown[furnacesIndex] = calcBurnload(furnaceSumBurn, sumArray(displayDays));
      sumOperationDays += furnaceOperationDays;
      sumBurn += furnaceSumBurn;
    }
    const furnaceRuns: number[] = Array(furnacesCount + 1).fill(0);
    for (let dayIndex = 0; dayIndex < contexts.length; dayIndex++) {
      let count: number = 0;
      for (let furnacesIndex = 0; furnacesIndex < furnacesCount; furnacesIndex++) {
        if (furnaceBurns[furnacesIndex][dayIndex] > 0) {
          count += 1;
        }
      }
      furnaceRuns[count] += 1;
    }
    obj.furnace_operating_days = furnaceRuns;
    obj.burn_load_sum = calcBurnload(sumBurn, sumOperationDays);
    obj.burn_load_include_shutdown_sum = calcBurnload(sumBurn, sumArray(displayDays) * furnacesCount);
    return obj;
  }

  function getMonitorValues(contexts: MonitorContext[], key: keyof MonitorContext): number[] {
    const values: number[] = contexts.map((contexts) => contexts[key]);
    return values;
  }

  function concatPranResultMonthUnitDay(resultDraft: DraftContext, planDraft: DraftContext, dayCounts: number[]): DriveContext[] {
    const planResultDraft: DriveContext[] = [] as DriveContext[];
    for (let monIndex = 0; monIndex < dayCounts.length; monIndex++) {
      for (let dayIndex = 0; dayIndex < dayCounts[monIndex]; dayIndex++) {
        if (resultDraft.month[monIndex].date !== '') {
          planResultDraft.push(resultDraft.day[daySum(monIndex) + dayIndex]);
        } else {
          planResultDraft.push(planDraft.day[daySum(monIndex) + dayIndex]);
        }
      }
    }
    return planResultDraft;
  }
  const dateFormatSlash = (dateStr: string) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    const weekDay = WEEK_DAYS[date.getDay()];
    return `${year}/${month}/${day}(${weekDay})`;
  };

  const lastDayOfYear = useMemo(() => {
    return daySum(displayDays.length);
  }, [displayDays]);

  const lastDayOfLastMonth = useMemo(() => {
    return daySum(selectMonthIndex - 1);
  }, [selectMonthIndex]);

  const displayDayCountOfMonth = useMemo(() => {
    if (isMonthDisplay()) {
      return 0;
    } else {
      return displayDays[selectMonthIndex - 1];
    }
  }, [selectMonthIndex]);

  const systemDateIndex = useMemo(() => {
    const today = new Date();

    const todayFiscalYear = getCurrentFiscalYear(today);

    // 今日が表示年度より前 → その年度の最初
    if (todayFiscalYear < displayYear) {
      return 0;
    }

    // 今日が表示年度より後 → その年度の最後
    if (todayFiscalYear > displayYear) {
      return sumArray(displayDays) - 1;
    }

    // 同じ年度の場合のみ今日を使う
    const systemDate = getStrDate(today.getFullYear(), today.getMonth() + 1, today.getDate());

    return getDateIndex(systemDate, displayYear, displayDays);
  }, [displayYear, displayDays]);

  // サーバ操作
  const [existAutoDraft, setExistAutoDraft] = useState<boolean>(false);
  const [existInitialPlanDraft, setExistInitialPlanDraft] = useState<boolean>(false);
  const [existPlanDraft, setExistPlanDraft] = useState<boolean>(false);
  const [existResultDraft, setExistResultDraft] = useState<boolean>(false);
  const existSomeDraft: boolean = existAutoDraft || existPlanDraft || existResultDraft;
  const [isSelectDraft, setIsSelectDraft] = useState<boolean[]>([] as boolean[]);
  const planEmptyCheckState = useState<DraftEmpty>(new DraftEmptyObject(false, LoginService.getLoginInfo().furnacesCount));
  const [planEmptyCheck, setPlanEmptyCheck] = planEmptyCheckState;
  const planWarningValsState = useState<boolean[][]>(
    Array(LoginService.getLoginInfo().furnacesCount)
      .fill(null)
      .map(() => Array(366).fill(false)),
  );
  const [planWarningVals, setPlanWarningVals] = planWarningValsState;
  const [planDisableCheck, setPlanDisableCheck] = useState<DraftEmpty>(new DraftEmptyObject(false, LoginService.getLoginInfo().furnacesCount));
  const draftWarningValsState = useState<boolean[][][]>(
    Array.from({ length: DRAFT_MAX_COUNT }, () =>
      Array(LoginService.getLoginInfo().furnacesCount)
        .fill(null)
        .map(() => Array(366).fill(false)),
    ),
  );
  const [draftWarningVals, setDraftWarningVals] = draftWarningValsState;
  const resultEmptyCheckState = useState<DraftEmpty>(new DraftEmptyObject(false, LoginService.getLoginInfo().furnacesCount));
  const [resultEmptyCheck, setResultEmptyCheck] = resultEmptyCheckState;
  const [resultDisableCheck, setResultDisableCheck] = useState<DraftEmpty>(new DraftEmptyObject(false, LoginService.getLoginInfo().furnacesCount));
  const draftEmptyCheckState = useState<DraftEmpty[]>(Array.from({ length: DRAFT_MAX_COUNT }, () => new DraftEmptyObject(false, LoginService.getLoginInfo().furnacesCount)));
  const [draftEmptyCheck, setDraftEmptyCheck] = draftEmptyCheckState;
  const [draftDisableCheck, setDraftDisableCheck] = useState<DraftEmpty[]>(
    Array.from({ length: DRAFT_MAX_COUNT }, () => new DraftEmptyObject(false, LoginService.getLoginInfo().furnacesCount)),
  );
  const [jsonMainResource, setMainResource] = useState<MainContext>(new MainContextObject());
  const [conditionResource, setConditionResource] = useState<ConditionContext>(new ConditionContextObject());
  const [planDayUsage, setPlanUsage] = useState<DriveUsage>(new DriveUsageObject(false, LoginService.getLoginInfo().furnacesCount));
  const [planMonthUsage, setPlanMonthUsage] = useState<DriveUsage>(new DriveUsageObject(false, LoginService.getLoginInfo().furnacesCount));
  const planUsageDisplay: DriveUsage = isMonthDisplay() ? planMonthUsage : planDayUsage;
  const planUsageCalc: DriveUsage = Object.keys(planDayUsage).reduce((acc, key) => {
    const k = key as keyof DriveUsage;
    acc[k] = planDayUsage[k] || planMonthUsage[k];
    return acc;
  }, {} as DriveUsage); // 任意項目の有無によって計算方式が変わる

  const initialPlanEditResourceState = useState<DraftContext>(new DraftContextObject(0, LoginService.getLoginInfo().furnacesCount));
  const initialPlanEmptyCheckState = useState<DraftEmpty>(new DraftEmptyObject(false, LoginService.getLoginInfo().furnacesCount));
  const [initialPlanEmptyCheck, setInitialPlanEmptyCheck] = initialPlanEmptyCheckState;
  const [initialPlanInitResource, setInitialPlanInitResource] = useState<DraftContext>(new DraftContextObject(0, LoginService.getLoginInfo().furnacesCount));
  const [initialPlanEditResource, setInitialPlanEditResource] = initialPlanEditResourceState;
  const initialPlanMonitorResource = useMemo<MonitorContext[]>(() => {
    return getMonitorList(isMonthDisplay() ? initialPlanEditResource.month : initialPlanEditResource.day, planUsageCalc);
  }, [initialPlanEditResource, selectMonthIndex]);
  const initialPlanMonitorMonthResource = useMemo<MonitorMonthContext[]>(() => {
    return getMonitorMonthList(initialPlanEditResource.day, LoginService.getLoginInfo().furnacesCount);
  }, [initialPlanEditResource]);
  const initialPlanMonitorMonthSumResource = useMemo<MonitorMonthContext>(() => {
    return getMonitorMonthSum(initialPlanEditResource.day, LoginService.getLoginInfo().furnacesCount);
  }, [initialPlanEditResource]);
  const initialPlanWarningValsState = useState<boolean[][]>(
    Array(LoginService.getLoginInfo().furnacesCount)
      .fill(null)
      .map(() => Array(366).fill(false)),
  );
  const [initialPlanWarningVals, setInitialPlanWarningVals] = initialPlanWarningValsState;

  const [planInitResource, setPlanInitResource] = useState<DraftContext>(new DraftContextObject(0, LoginService.getLoginInfo().furnacesCount));
  const planEditResourceState = useState<DraftContext>(new DraftContextObject(0, LoginService.getLoginInfo().furnacesCount));
  const [planEditResource, setPlanEditResource] = planEditResourceState;
  const planMonitorResource = useMemo<MonitorContext[]>(() => {
    return getMonitorList(isMonthDisplay() ? planEditResource.month : planEditResource.day, planUsageCalc);
  }, [planEditResource, selectMonthIndex]);
  const planMonitorSumResource = useMemo<MonitorContext>(() => {
    return getMonitorSum(isMonthDisplay() ? planEditResource.month : planEditResource.day.slice(lastDayOfLastMonth, daySum(selectMonthIndex)), planUsageCalc);
  }, [planEditResource, selectMonthIndex]);
  const planMonitorMonthResource = useMemo<MonitorMonthContext[]>(() => {
    return getMonitorMonthList(planEditResource.day, LoginService.getLoginInfo().furnacesCount);
  }, [planEditResource]);
  const planMonitorMonthSumResource = useMemo<MonitorMonthContext>(() => {
    return getMonitorMonthSum(planEditResource.day, LoginService.getLoginInfo().furnacesCount);
  }, [planEditResource]);
  const [resultDayUsage, setResultUsage] = useState<DriveUsage>(new DriveUsageObject(true, LoginService.getLoginInfo().furnacesCount));
  const [resultMonthUsage, setResultMonthUsage] = useState<DriveUsage>(new DriveUsageObject(true, LoginService.getLoginInfo().furnacesCount));
  const resultUsageDisplay: DriveUsage = isMonthDisplay() ? resultMonthUsage : resultDayUsage;
  const resultUsageCalc: DriveUsage = Object.keys(resultDayUsage).reduce((acc, key) => {
    const k = key as keyof DriveUsage;
    acc[k] = resultDayUsage[k] || resultMonthUsage[k];
    return acc;
  }, {} as DriveUsage); // 任意項目の有無によって計算方式が変わる
  const [resultInitResource, setResultInitResource] = useState<DraftContext>(new DraftContextObject(0, LoginService.getLoginInfo().furnacesCount));
  const resultEditResourceState = useState<DraftContext>(new DraftContextObject(0, LoginService.getLoginInfo().furnacesCount));
  const [resultEditResource, setResultEditResource] = resultEditResourceState;
  const resultMonitorResource = useMemo<MonitorContext[]>(() => {
    return getMonitorList(isMonthDisplay() ? resultEditResource.month : resultEditResource.day, resultUsageCalc);
  }, [resultEditResource, selectMonthIndex]);
  const resultMonitorSumResource = useMemo<MonitorContext>(() => {
    return getMonitorSum(isMonthDisplay() ? resultEditResource.month : resultEditResource.day.slice(lastDayOfLastMonth, daySum(selectMonthIndex)), resultUsageCalc);
  }, [resultEditResource, selectMonthIndex]);
  const resultMonitorMonthResource = useMemo<MonitorMonthContext[]>(() => {
    return getMonitorMonthList(resultEditResource.day, LoginService.getLoginInfo().furnacesCount);
  }, [resultEditResource]);
  const resultMonitorMonthSumResource = useMemo<MonitorMonthContext>(() => {
    return getMonitorMonthSum(resultEditResource.day, LoginService.getLoginInfo().furnacesCount);
  }, [resultEditResource]);
  const planResultUsageDisplay: DriveUsage = Object.keys(resultUsageDisplay).reduce((acc, key) => {
    const k = key as keyof DriveUsage;
    acc[k] = resultUsageDisplay[k] || planUsageDisplay[k];
    return acc;
  }, {} as DriveUsage);
  const initialPlanUsageDisplay: DriveUsage = planUsageDisplay;
  const planResultMonitorResource = useMemo<MonitorContext[]>(() => {
    if (isMonthDisplay()) {
      const ret: MonitorContext[] = [];
      for (let monthIndex = 0; monthIndex < resultEditResource.month.length; monthIndex++) {
        if (resultEditResource.month[monthIndex].date !== '' && resultEditResource.day[daySum(monthIndex + 1) - 1].date !== '') {
          ret.push(resultMonitorResource[monthIndex]);
        } else {
          ret.push(planMonitorResource[monthIndex]);
        }
      }
      return ret;
    } else {
      const ret: MonitorContext[] = [];
      for (let dayIndex = 0; dayIndex < resultEditResource.day.length; dayIndex++) {
        if (resultEditResource.day[dayIndex].date !== '') {
          ret.push(resultMonitorResource[dayIndex]);
        } else {
          ret.push(planMonitorResource[dayIndex]);
        }
      }
      return ret;
    }
  }, [planMonitorResource, resultMonitorResource, selectMonthIndex]);
  const planResultMonitorMonthResource = useMemo<MonitorMonthContext[]>(() => {
    return getMonitorMonthList(concatPranResultMonthUnitDay(resultEditResource, planEditResource, displayDays), LoginService.getLoginInfo().furnacesCount);
  }, [planEditResource, resultEditResource]);
  const planResultMonitorMonthSumResource = useMemo<MonitorMonthContext>(() => {
    return getMonitorMonthSum(concatPranResultMonthUnitDay(resultEditResource, planEditResource, displayDays), LoginService.getLoginInfo().furnacesCount);
  }, [planEditResource, resultEditResource]);
  const planResultMonitorSumResource = useMemo<MonitorContext>(() => {
    const ret: MonitorContext = new MonitorContextObject(0, 3);
    for (const key of Object.keys(ret) as (keyof MonitorContext)[]) {
      ret[key] = planResultMonitorResource.reduce((sum, d) => sum + (d[key] ?? 0), 0) as any;
    }
    return ret;
  }, [planResultMonitorResource, selectMonthIndex]);
  const [draftDayUsage, setDraftUsage] = useState<DriveUsage>(new DriveUsageObject(true, LoginService.getLoginInfo().furnacesCount));
  const [draftMonthUsage, setDraftMonthUsage] = useState<DriveUsage>(new DriveUsageObject(true, LoginService.getLoginInfo().furnacesCount));
  const draftUsageDisplay: DriveUsage = isMonthDisplay() ? draftMonthUsage : draftDayUsage;
  const draftUsageCalc: DriveUsage = Object.keys(draftDayUsage).reduce((acc, key) => {
    const k = key as keyof DriveUsage;
    acc[k] = draftDayUsage[k] || draftMonthUsage[k];
    return acc;
  }, {} as DriveUsage);
  const [draftInitResource, setDraftInitResource] = useState<DraftContext[]>([] as DraftContextObject[]);
  const draftEditResourceState = useState<DraftContext[]>([] as DraftContextObject[]);
  const [draftEditResource, setDraftEditResource] = draftEditResourceState;
  const draftMonitorResource = useMemo<MonitorContext[][]>(() => {
    return draftEditResource.map((resource) => getMonitorList(isMonthDisplay() ? resource.month : resource.day, draftUsageCalc));
  }, [draftEditResource, selectMonthIndex]);
  const draftMonitorSumResource = useMemo<MonitorContext[]>(() => {
    return draftEditResource.map((resource) => getMonitorSum(resource.day.slice(lastDayOfLastMonth, daySum(selectMonthIndex)), draftUsageCalc));
  }, [draftEditResource, selectMonthIndex]);
  const draftMonitorMonthResource = useMemo<MonitorMonthContext[][]>(() => {
    return draftEditResource.map((resource) => getMonitorMonthList(resource.day, LoginService.getLoginInfo().furnacesCount));
  }, [draftEditResource]);
  const draftMonitorMonthSumResource = useMemo<MonitorMonthContext[]>(() => {
    return draftEditResource.map((resource) => getMonitorMonthSum(resource.day, LoginService.getLoginInfo().furnacesCount));
  }, [draftEditResource]);
  const [sessionResource, setSessionResource] = useState<SessionContext>({
    furnacesCount: LoginService.getLoginInfo().furnacesCount,
    branchId: LoginService.getLoginInfo().branchId,
    selectYear: new Date().getFullYear(),
  });
  const planResultUnionPitRest = useMemo(() => {
    if (existResultDraft && existPlanDraft) {
      const vals: (number | null)[] = [] as (number | null)[];
      let planResultDiff: number = 0;
      if (isMonthDisplay()) {
        for (let dayIndex = 0; dayIndex < sumArray(displayDays); dayIndex++) {
          if (resultEditResource.day[dayIndex].date !== '') {
            if (dayIndex + 1 < sumArray(displayDays) && resultEditResource.day[dayIndex + 1].date === '') {
              vals.push(resultEditResource.day[dayIndex].trash_pit_rest);
              planResultDiff = resultEditResource.day[dayIndex].trash_pit_rest - planEditResource.day[dayIndex].trash_pit_rest;
            } else {
              vals.push(null);
            }
          } else {
            vals.push(planEditResource.day[dayIndex].trash_pit_rest + planResultDiff);
          }
        }
      } else {
        for (let colIndex = 0; colIndex < displayDays[selectMonthIndex + 1]; colIndex++) {
          if (resultEditResource.day[lastDayOfLastMonth + colIndex].date !== '') {
            if (colIndex + 1 < sumArray(displayDays) && resultEditResource.day[lastDayOfLastMonth + colIndex + 1].date === '') {
              vals.push(resultEditResource.day[lastDayOfLastMonth + colIndex].trash_pit_rest);
              planResultDiff = resultEditResource.day[lastDayOfLastMonth + colIndex].trash_pit_rest - planEditResource.day[lastDayOfLastMonth + colIndex].trash_pit_rest;
            } else {
              vals.push(null);
            }
          } else {
            vals.push(planEditResource.day[lastDayOfLastMonth + colIndex].trash_pit_rest + planResultDiff);
          }
        }
      }
      return vals;
    } else {
      const vals = [...new Array(isMonthDisplay() ? sumArray(displayDays) : displayDays[selectMonthIndex + 1]).fill(null)];
      return vals;
    }
  }, [planEditResource, resultEditResource, selectMonthIndex]);

  const [updateEditRecord, setUpdateEditRecord] = useState({ userName: '', time: '' });

  const planInputShutdownState = useState<DayContext[]>(Array.from({ length: sessionResource.furnacesCount }, () => new DayContextObject()));
  const [planInputShutdown, setPlanInputShutdown] = planInputShutdownState;

  const initialPlanInputShutdownState = useState<DayContext[]>(Array.from({ length: sessionResource.furnacesCount }, () => new DayContextObject()));
  const [initialPlanInputShutdown, setInitialPlanInputShutdown] = initialPlanInputShutdownState;

  const draftInputShutdownState = useState<DayContext[][]>(
    Array.from({ length: DRAFT_MAX_COUNT }, () => Array.from({ length: sessionResource.furnacesCount }, () => new DayContextObject())),
  );
  const [draftInputShutdown, setDraftInputShutdown] = draftInputShutdownState;

  const [planMultipleBurn, setPlanMultipleBurn] = useState<MultipleBurnSetting[]>(Array.from({ length: sessionResource.furnacesCount }, () => new MultipleBurnSettingObject()));

  const newWindowRef = useRef<Window | null>(null);

  const { showProgress, getRequest, postRequestJson, postRequestBinary } = useApi();

  const { permissions } = LoginService.getLoginInfo();

  const [preBetweenDate, setPreBetweenDate] = useState<string>(getMinDate(displayYear));
  const [nextBetweenDate, setNextBetweenDate] = useState<string>(getMaxDate(displayYear));

  const initialPlanShutdownRecordState = useState<ShutdownRecord[][][]>(Array.from({ length: sessionResource.furnacesCount }, () => []));
  const [initialPlanShutdownRecord, setInitialPlanShutdownRecord] = initialPlanShutdownRecordState;

  const planShutdownRecordState = useState<ShutdownRecord[][][]>(Array.from({ length: sessionResource.furnacesCount }, () => []));
  const [planShutdownRecord, setPlanShutdownRecord] = planShutdownRecordState;
  const draftShutdownRecordState = useState<ShutdownRecord[][][][]>(Array.from({ length: DRAFT_MAX_COUNT }, () => Array.from({ length: sessionResource.furnacesCount }, () => [])));
  const [draftShutdownRecord, setDraftShutdownRecord] = draftShutdownRecordState;

  const draftLength = useMemo(() => {
    return draftInitResource.length;
  }, [draftInitResource]);

  const displayTrashAcceptableAmountDate = useMemo<string>(() => {
    if (!(trashAcceptableAmount.date !== '')) {
      return '';
    }
    const dateNow = new Date();
    const dateNowStr: string = `${dateNow.getFullYear()}-${formatTwoDigits(dateNow.getMonth() + 1)}-${formatTwoDigits(dateNow.getDate())}`;
    const apiNow = new Date(trashAcceptableAmount.date);
    const apiNowStr: string = `${apiNow.getFullYear()}-${formatTwoDigits(apiNow.getMonth() + 1)}-${formatTwoDigits(apiNow.getDate())}`;
    if (dateNowStr === apiNowStr) {
      return String(apiNow.getHours());
    } else {
      return '';
    }
  }, [trashAcceptableAmount]);

  const [initialPlanHistory, setInitialPlanHistory] = useState<DayContext[][][]>(Array.from({ length: sessionResource.furnacesCount }, () => []));
  const [isInitialPlanSetting, setIsInitialPlanSetting] = useState<boolean>(false);
  const [planHistory, setPlanHistory] = useState<DayContext[][][]>(Array.from({ length: sessionResource.furnacesCount }, () => []));
  const [draftHistory, setDraftHistory] = useState<DayContext[][][][]>(
    Array.from({ length: DRAFT_MAX_COUNT }, () => Array.from({ length: sessionResource.furnacesCount }, () => [])),
  );

  const dataLoadedAtRef = useRef<Date>(new Date());

  useEffect(() => {
    LoginService.setScreenInfo(PATH_PLANRESULT);
  }, []);

  useEffect(() => {
    if (isReload) {
      setIsReload(false);
      (async () => {
        try {
          const requestUrl = `${REQ_PLAN_RESULT}?branchId=${String(sessionResource.branchId)}&selectDate=${getMinDate(apiYear)}&furnaces=${String(sessionResource.furnacesCount)}`;
          const { status, payload: _json } = await getRequest<JsonResponse>(requestUrl);
          //TODO: 下記、適切な実装なら不要な対応 サーバレスポンスヘッダにapplication/jsonを追加すること
          const json = typeof _json === 'string' ? JSON.parse(_json) : _json;

          console.log(status);
          if (status === STATUS_CODE.SUCCESS) {
            const apiDisplayYear: number = new Date(json.activeYear).getFullYear();
            const dayCounts: number[] = [];
            for (let i = 0; i < months.length; i++) {
              const dateStr: string = String(getCalendarYear(apiDisplayYear, months[i])).concat('-'.concat(formatTwoDigits(months[i]).concat('-').concat(formatTwoDigits(1))));
              dayCounts.push(Dayjs.getAllDaysInMonth(dateStr).length);
            }
            const dayAllSum: number = sumArray(dayCounts);
            const furnacesCount: number = sessionResource.furnacesCount;
            const dateArray: string[] = [] as string[];
            for (let i = 0; i < dayCounts.length; i++) {
              for (let j = 0; j < dayCounts[i]; j++) {
                const dateStr: string = String(getCalendarYear(apiDisplayYear, months[i])).concat(
                  '-'.concat(
                    formatTwoDigits(months[i])
                      .concat('-')
                      .concat(formatTwoDigits(j + 1)),
                  ),
                );
                dateArray.push(dateStr);
              }
            }
            // 日付
            setDisplayYear(apiDisplayYear);
            setApiYear(new Date(json.activeYear).getFullYear());
            setAutoDraftBaseDate(json.autoDraftBaseDate);
            setPlanResultHistory(json.planResultHistory);
            setDisplayDays(dayCounts);
            // OriginData
            if (true) {
              let resource: ConditionContext = new ConditionContextObject();
              if ('originData' in json) {
                resource = getConditionResource(resource, json.originData);
              }
              // 初期設定よりも立案条件が優先
              if ('draftConditionData' in json) {
                resource = getConditionResource(resource, json.draftConditionData);
              }
              const burnCapacityArray: number[] = [] as number[];
              for (let i = 0; i < json.burnLoadCapacity.length; i++) {
                burnCapacityArray.push(Number(json.burnLoadCapacity[i]));
              }
              resource.burnloadCapacity = burnCapacityArray;

              const electricCreateArray: number[][] = [] as number[][];
              for (let dateIndex = 0; dateIndex < dayAllSum; dateIndex++) {
                const miniElectricCreateArray: number[] = [] as number[];
                const originElectric = json.originElectric[dateIndex];
                if (!originElectric || !Array.isArray(originElectric.electricCreate)) {
                  continue;
                }
                for (let furnaceIndex = 0; furnaceIndex < furnacesCount; furnaceIndex++) {
                  miniElectricCreateArray.push(originElectric.electricCreate[furnaceIndex]);
                }
                electricCreateArray.push(miniElectricCreateArray);
              }
              resource.electricCreate = electricCreateArray;
              const electricUseArray: number[][] = [] as number[][];
              const recycleCostPower: number[] = Array.isArray(json.originData?.recycle_cost_power)
                ? json.originData.recycle_cost_power.map((value: number) => Number(value))
                : [0, 0];
              const otherCostPower: number[] = Array.isArray(json.originData?.other_cost_power) ? json.originData.other_cost_power.map((value: number) => Number(value)) : [0, 0];
              for (let monthIndex = 0; monthIndex < dayCounts.length; monthIndex++) {
                for (let dayIndex = 0; dayIndex < dayCounts[monthIndex]; dayIndex++) {
                  const miniElectricUseArray: number[] = [] as number[];
                  const targetDate = new Date(getCalendarYear(apiDisplayYear, months[monthIndex]), months[monthIndex] - 1, dayIndex + 1, 9);
                  const isWeekday: boolean = targetDate.getDay() >= 1 && targetDate.getDay() <= 5;
                  const fixedElectricUse: number = Number(recycleCostPower[isWeekday ? 0 : 1] ?? 0) + Number(otherCostPower[isWeekday ? 0 : 1] ?? 0);
                  for (let furnaceIndex = 0; furnaceIndex <= furnacesCount; furnaceIndex++) {
                    miniElectricUseArray.push(Number(json.costPowers?.[furnaceIndex]?.[monthIndex] ?? 0) + fixedElectricUse);
                  }
                  electricUseArray.push(miniElectricUseArray);
                }
              }
              resource.electricUse = electricUseArray;
              setConditionResource(resource);
            }
            // Main
            const mainResource: MainContext = new MainContextObject();
            mainResource.active_year = String(new Date(json.activeYear).getFullYear());
            for (let i = 0; i < json.yearItems.length; i++) {
              mainResource.year_list.push(json.yearItems[i]);
            }
            setMainResource(mainResource);
            // YearList
            if (json.pastYearItem.length > 0) {
              const pastList: string[] = [] as string[];
              for (let i = 0; i < json.pastYearItem.length; i++) {
                pastList.push(json.pastYearItem[i]);
              }
              setPastYearList(pastList);
              setPastYear(Number(pastList[0]));
            } else {
              setPastYearList([]);
              setPastYear(0);
            }

            setUpdateEditRecord({ userName: json.userName, time: json.createdAt });
            setTrashAcceptableAmount({ date: json.trashAcceptableAmountDate, value: json.trashAcceptableAmount, percent: json.trashAcceptableAmountPercent });

            const planDayOptional: DriveUsage = new DriveUsageObject(false, furnacesCount);
            const resultDayOptional: DriveUsage = new DriveUsageObject(false, furnacesCount);
            const planMonthOptional: DriveUsage = new DriveUsageObject(false, furnacesCount);
            const resultMonthOptional: DriveUsage = new DriveUsageObject(false, furnacesCount);
            if (json.branchOptional) {
              for (let i = 0; i < json.branchOptional.length; i++) {
                const obj = json.branchOptional[i];
                const keyName = obj.optionalName as keyof DriveUsage;
                if (keyName in planDayOptional) {
                  planDayOptional[keyName] = obj.monthPlanSystemFlag;
                  resultDayOptional[keyName] = obj.monthResultSystemFlag;
                  planMonthOptional[keyName] = obj.yearPlanSystemFlag;
                  resultMonthOptional[keyName] = obj.yearResultSystemFlag;
                }
              }
            }
            setPlanUsage(planDayOptional);
            setResultUsage(resultDayOptional);
            setDraftUsage(planDayOptional);
            setPlanMonthUsage(planMonthOptional);
            setResultMonthUsage(resultMonthOptional);
            setDraftMonthUsage(planMonthOptional);
            const isExistInitialPlan: boolean = json.initialPlanDay && json.initialPlanDay.length > 0;
            let initialPlanResoruce: DraftContext = new DraftContextObject(0, furnacesCount);
            if (isExistInitialPlan) {
              initialPlanResoruce = getDraftContext(json.initialPlanDay, json.initialPlanMonth, json.initialPlanShutDown, dateArray, dayAllSum, furnacesCount, COMMON_DIGIT);
            } else {
              initialPlanResoruce = getDraftContext([], [], [], dateArray, dayAllSum, furnacesCount, COMMON_DIGIT);
            }
            setInitialPlanInitResource(initialPlanResoruce);
            ResetResource(furnacesCount, initialPlanResoruce, setInitialPlanEditResource, setInitialPlanEmptyCheck, planDayOptional, planMonthOptional);
            setExistInitialPlanDraft(json.initialPlanDay && json.initialPlanDay.length > 0);
            setShowInitialPlanVisible(false);
            const isExistPlan: boolean = json.planDay.length > 0;
            let planResoruce: DraftContext = new DraftContextObject(0, furnacesCount);
            if (isExistPlan) {
              planResoruce = getDraftContext(json.planDay, json.planMonth, json.planShutDown, dateArray, dayAllSum, furnacesCount, COMMON_DIGIT);
            } else {
              planResoruce = getDraftContext([], [], [], dateArray, dayAllSum, furnacesCount, COMMON_DIGIT);
            }
            setPlanInitResource(planResoruce);
            setExistPlanDraft(isExistPlan);
            ResetResource(furnacesCount, planResoruce, setPlanEditResource, setPlanEmptyCheck, planDayOptional, planMonthOptional);
            setPlanDisableCheck(() => {
              const updated = new DraftEmptyObject(false, furnacesCount);
              updated.day = getDisableDayCells(
                Array.from({ length: dayAllSum }, (_, index) => new DriveEmptyObject(false, furnacesCount)),
                furnacesCount,
                apiDisplayYear,
                dayCounts,
              );
              updated.month = getDisableMonthCells(
                Array.from({ length: dayCounts.length }, (_, index) => new DriveEmptyObject(false, furnacesCount)),
                furnacesCount,
                apiDisplayYear,
              );
              return updated;
            });
            //前に戻す履歴を削除
            setInitialPlanShutdownRecord(Array.from({ length: furnacesCount }, () => []));
            setInitialPlanHistory(Array.from({ length: furnacesCount }, () => []));
            setPlanShutdownRecord(Array.from({ length: furnacesCount }, () => []));
            setPlanHistory(Array.from({ length: furnacesCount }, () => []));
            // 焼却量強調フラグクリア
            setPlanWarningVals(
              Array(LoginService.getLoginInfo().furnacesCount)
                .fill(null)
                .map(() => Array(366).fill(false)),
            );
            //実績
            const isExistResult: boolean = json.resultDay.length > 0;
            let resultResoruce: DraftContext = new DraftContextObject(0, furnacesCount);
            if (isExistResult) {
              resultResoruce = getDraftContext(json.resultDay, json.resultMonth, json.resultShutDown, dateArray, dayAllSum, furnacesCount, COMMON_DIGIT);
            } else {
              resultResoruce = getDraftContext([], [], [], dateArray, dayAllSum, furnacesCount, COMMON_DIGIT);
            }
            setResultInitResource(resultResoruce);
            setExistResultDraft(isExistResult);
            ResetResource(furnacesCount, resultResoruce, setResultEditResource, setResultEmptyCheck, resultDayOptional, resultMonthOptional);
            setResultDisableCheck(() => {
              const updated = new DraftEmptyObject(false, furnacesCount);
              updated.day = getEmptyCells(resultResoruce.day, furnacesCount, resultDayOptional);
              updated.month = getEmptyCells(resultResoruce.month, furnacesCount, resultMonthOptional);
              return updated;
            });

            //立案
            const draftCount: number = json.autoDraft ? Number(json.autoDraft.length) : 0;
            const isExistDraft: boolean = draftCount > 0;
            const draftArray: DraftContext[] = [] as DraftContext[];
            for (let i = 0; i < draftCount; i++) {
              const resourceDraft: DraftContext = getDraftContext(
                json.autoDraft[i].draftDay,
                json.autoDraft[i].draftMonth,
                json.autoDraft[i].draftShutDown,
                dateArray,
                dayAllSum,
                furnacesCount,
                COMMON_DIGIT,
              );
              draftArray.push(resourceDraft);
            }
            setDraftInitResource(draftArray);
            setDraftEditResource(draftArray);
            for (let i = 0; i < draftArray.length; i++) {
              const [draftElement, setDraftElement] = getArrayElementState(draftArray, setDraftEditResource, i);
              const [draftEmpty, setDraftEmpty] = getArrayElementState(draftEmptyCheck, setDraftEmptyCheck, i);
              ResetResource(sessionResource.furnacesCount, draftArray[i], setDraftElement, setDraftEmpty, planDayOptional, planMonthOptional);
            }
            setExistAutoDraft(isExistDraft);
            setIsSelectDraft([...new Array(draftCount).fill(true)]);
            // //前に戻す履歴を削除
            setDraftShutdownRecord(Array.from({ length: draftCount }, () => Array.from({ length: furnacesCount }, () => [])));
            setDraftHistory(Array.from({ length: draftCount }, () => Array.from({ length: furnacesCount }, () => [])));
            //焼却量フラグをクリア
            setDraftWarningVals(
              Array(draftCount)
                .fill(null)
                .map(() =>
                  Array(furnacesCount)
                    .fill(null)
                    .map(() => Array(366).fill(false)),
                ),
            );
            //操作不可フラグを更新
            for (let i = 0; i < draftArray.length; i++) {
              const [draftDisable, setDraftDisable] = getArrayElementState(draftDisableCheck, setDraftDisableCheck, i);
              setDraftDisable(() => {
                const updated = new DraftEmptyObject(false, furnacesCount);
                updated.day = getDisableDayCells(getEmptyCells(draftArray[i].day, furnacesCount, planDayOptional), furnacesCount, apiDisplayYear, dayCounts);
                updated.month = getDisableMonthCells(getEmptyCells(draftArray[i].month, furnacesCount, planMonthOptional), furnacesCount, apiDisplayYear);
                return updated;
              });
            }
            // Excel出力項目を追加
            const fixedDraftPattern = [];
            if (isExistPlan || isExistResult) {
              fixedDraftPattern.push({ priority_id: -1, priority_name: '計画実績（計画見直し）' });
            }
            if (isExistInitialPlan) {
              fixedDraftPattern.push({ priority_id: -3, priority_name: '計画実績（期初計画）' });
            }
            if (isExistDraft) {
              fixedDraftPattern.push({ priority_id: -2, priority_name: '全ての案' });
            }
            setExcelOutputDraftPattern([...fixedDraftPattern, ...json.draftPattern]);
            setDraftPattern([...json.draftPattern]);
            dataLoadedAtRef.current = new Date(json.current ?? Date.now);
          }
        } catch (e) {
          console.error(e);
        }
      })();
    }
  }, [isReload]);

  useEffect(() => {
    if (excelOutputDraftPattern.length > 0) {
      setSelectedPriorityId(excelOutputDraftPattern[0].priority_id);
    }
    if (planResultHistory.length > 0) {
      setSelectedselectedCreatedAt(planResultHistory[planResultHistory.length - 1].created_at);
      setSelectedInitialPlanTargetCreatedAt(planResultHistory[planResultHistory.length - 1].created_at);
    }
  }, [excelOutputDraftPattern, planResultHistory]);

  useEffect(() => {
    return () => {
      if (newWindowRef.current && !newWindowRef.current.closed) {
        newWindowRef.current.close();
      }
    };
  }, []);

  const isSavedPlan = useMemo(() => {
    return isEqualJson(JSON.stringify(planEditResource), JSON.stringify(planInitResource));
  }, [planEditResource]);

  const isSavedResult = useMemo(() => {
    return isEqualJson(JSON.stringify(resultEditResource), JSON.stringify(resultInitResource));
  }, [resultEditResource]);

  const isSavedDraft = useMemo(() => {
    return isEqualJson(JSON.stringify(draftEditResource), JSON.stringify(draftInitResource));
  }, [draftEditResource]);

  useEffect(() => {
    let isSaved: boolean = false;
    if (isSavedPlan && isSavedResult && isSavedDraft) {
      isSaved = true;
    }
    setIsUnsaved(!isSaved);
  }, [isSavedPlan, isSavedResult, isSavedDraft]);

  const graphProps = useMemo(() => {
    const labels: string[] = [] as string[];
    const gridBottomColors: string[] = [];
    if (isMonthDisplay()) {
      for (let monthIndex = 0; monthIndex < displayDays.length; monthIndex++) {
        labels.push(`${String(months[monthIndex])}月`);
        gridBottomColors.push('gray');
      }
    } else {
      for (let dayIndex = 0; dayIndex < displayDayCountOfMonth; dayIndex++) {
        labels.push(`${String(dayIndex + 1)}日`);
      }
    }

    // 発電電力量, 売電電力量, 買電電力量, 主灰発生量, 飛灰発生量
    const linePropeties: any = {};
    const items: (keyof DriveContext)[] = ['electric_create', 'electric_sale', 'electric_buy', 'main_ash_generate', 'fly_ash_generate'] as (keyof DriveContext)[];
    const itemsUnit: string[] = ['MWh', 'MWh', 'MWh', 't', 't'];
    const usagekeys: (keyof DriveUsage)[] = ['electric_create', 'electric_sale', 'electric_buy', 'main_ash_generate', 'fly_ash_generate'] as (keyof DriveUsage)[];
    for (let i = 0; i < items.length; i++) {
      const lineDatas: LineData[] = [] as LineData[];
      const lineVisibles: boolean[] = [] as boolean[];
      for (let seq = 0; seq <= LINE_SEQ.RESULT + draftLength; seq++) {
        let resource: DraftContext = new DraftContextObject(0, sessionResource.furnacesCount);
        if (seq === LINE_SEQ.INITIAL && existInitialPlanDraft && initialPlanUsageDisplay[usagekeys[i]]) {
          resource = structuredClone(initialPlanEditResource);
          lineVisibles.push(showInitialPlanVisible);
        } else if (seq === LINE_SEQ.PLAN && existPlanDraft && planUsageDisplay[usagekeys[i]]) {
          resource = structuredClone(planEditResource);
          lineVisibles.push(showPlanVisible);
        } else if (seq === LINE_SEQ.RESULT && existResultDraft && resultUsageDisplay[usagekeys[i]]) {
          resource = structuredClone(resultEditResource);
          lineVisibles.push(showResultVisible);
        } else if (seq >= LINE_SEQ.DRAFT1 && existAutoDraft && draftUsageDisplay[usagekeys[i]]) {
          resource = structuredClone(draftEditResource[seq - (LINE_SEQ.RESULT + 1)]);
          lineVisibles.push(isSelectDraft[seq - (LINE_SEQ.RESULT + 1)]);
        } else {
          continue;
        }
        let dataNum: number[] = itemDraftVolumes(resource.month, resource.day, items[i]);
        if (items[i] === 'electric_create' || items[i] === 'electric_sale' || items[i] === 'electric_buy') {
          dataNum = convertMWhArray(dataNum);
        }
        const data: LineData = {
          labels: labels,
          data: dataNum,
          color: COLOR_ARRAY[seq],
          title: tableStr[seq],
          unit: itemsUnit[i],
          plotType: 'rect',
        };
        lineDatas.push(data);
      }
      linePropeties[items[i]] = applyGraphInfo(lineDatas, gridBottomColors, lineVisibles);
    }
    return linePropeties;
  }, [
    initialPlanEditResource,
    planEditResource,
    resultEditResource,
    draftEditResource,
    selectMonthIndex,
    showInitialPlanVisible,
    showPlanVisible,
    showResultVisible,
    isSelectDraft,
  ]);

  const pitGraphProps = useMemo(() => {
    const labels: string[] = [] as string[];
    const gridBottomColors: string[] = [];
    if (isMonthDisplay()) {
      gridBottomColors.push(...Array(30).fill('white'));
      for (let monthIndex = 0; monthIndex < displayDays.length; monthIndex++) {
        for (let dayIndex = 0; dayIndex < displayDays[monthIndex]; dayIndex++) {
          if (dayIndex === 14) {
            labels.push(`${String(months[monthIndex])}月`);
          } else {
            labels.push('');
          }
          if (dayIndex === 0 || (monthIndex === displayDays.length - 1 && dayIndex === displayDays[monthIndex] - 1)) {
            gridBottomColors.push('gray');
          } else {
            gridBottomColors.push('white');
          }
        }
      }
    } else {
      for (let dayIndex = 0; dayIndex < displayDayCountOfMonth; dayIndex++) {
        labels.push(`${String(dayIndex + 1)}日`);
      }
    }

    // 主灰ピット残量, 飛灰ピット残量, ごみピット残量
    const linePropeties: any = {};
    const items: (keyof DriveContext)[] = ['main_ash_pit_rest', 'fly_ash_pit_rest', 'trash_pit_rest'] as (keyof DriveContext)[];
    const itemsUnit: string[] = ['t', 't', 't'];
    const usagekeys: (keyof DriveUsage)[] = ['main_ash_pit_rest', 'fly_ash_pit_rest', ''] as (keyof DriveUsage)[];
    for (let i = 0; i < items.length; i++) {
      const lineDatas: LineData[] = [] as LineData[];
      const lineVisibles: boolean[] = [] as boolean[];

      let unionData: LineData | null = null;
      let unionVisible = false;

      if (existPlanDraft && existResultDraft && !existAutoDraft && items[i] === 'trash_pit_rest') {
        unionData = {
          labels: labels,
          data: isMonthDisplay() ? planResultUnionPitRest : planResultUnionPitRest,
          color: ' #BE3E1F',
          title: `実績 + 計画`,
          unit: itemsUnit[i],
          pointRadius: isMonthDisplay() ? 0 : undefined, // ポイントが日ごとに出ると多すぎるため、0指定
          plotType: 'rect',
        };
        unionVisible = showPlanVisible && showResultVisible;
      }
      for (let seq = 0; seq <= LINE_SEQ.RESULT + draftLength; seq++) {
        let pitResource: DraftContext = new DraftContextObject(0, sessionResource.furnacesCount);
        if (seq === LINE_SEQ.INITIAL && existInitialPlanDraft && (items[i] === 'trash_pit_rest' || initialPlanUsageDisplay[usagekeys[i]])) {
          pitResource = structuredClone(initialPlanEditResource);
          lineVisibles.push(showInitialPlanVisible);
        } else if (seq === LINE_SEQ.PLAN && existPlanDraft && (items[i] === 'trash_pit_rest' || planUsageDisplay[usagekeys[i]])) {
          pitResource = structuredClone(planEditResource);
          lineVisibles.push(showPlanVisible);
        } else if (seq === LINE_SEQ.RESULT && existResultDraft && (items[i] === 'trash_pit_rest' || resultUsageDisplay[usagekeys[i]])) {
          pitResource = structuredClone(resultEditResource);
          lineVisibles.push(showResultVisible);
        } else if (seq >= LINE_SEQ.DRAFT1 && existAutoDraft && (items[i] === 'trash_pit_rest' || draftUsageDisplay[usagekeys[i]])) {
          pitResource = structuredClone(draftEditResource[seq - (LINE_SEQ.RESULT + 1)]);
          lineVisibles.push(isSelectDraft[seq - (LINE_SEQ.RESULT + 1)]);
        } else {
          continue;
        }
        let dataNum: (number | null)[] = isMonthDisplay() ? selectItemArray(pitResource.day, items[i]) : itemDraftVolumes(pitResource.month, pitResource.day, items[i]);
        const data: LineData = {
          labels: labels,
          data: dataNum,
          color: COLOR_ARRAY[seq],
          title: tableStr[seq],
          unit: itemsUnit[i],
          pointRadius: isMonthDisplay() ? 0 : undefined, // ポイントが日ごとに出ると多すぎるため、0指定
          plotType: 'rect',
        };
        lineDatas.push(data);
      }

      if (unionData) {
        // 期初計画の次、なければ先頭
        const insertIndex = existInitialPlanDraft ? 1 : 0;

        lineDatas.splice(insertIndex, 0, unionData);
        lineVisibles.splice(insertIndex, 0, unionVisible);
      }

      if (items[i] === 'main_ash_pit_rest' && lineDatas.length > 0) {
        const dataNum: number = isMonthDisplay() ? lastDayOfYear : displayDays[selectMonthIndex - 1];
        const data: LineData = {
          labels: labels,
          data: Array(dataNum).fill(conditionResource.ashPitCapacity * 1),
          color: 'red',
          title: `上限`,
          unit: itemsUnit[i],
          pointRadius: isMonthDisplay() ? 0 : undefined, // ポイントが日ごとに出ると多すぎるため、0指定
          plotType: 'rect',
        };
        lineDatas.push(data);
        lineVisibles.push(true);
      }
      if (items[i] === 'trash_pit_rest' && lineDatas.length > 0) {
        const PIT_TITLES: string[] = [`トラブル時`, `上限`, `下限`];
        const PIT_VALUES: number[] = [conditionResource.pitLimit, conditionResource.pitMax, conditionResource.pitMin];
        const PIT_COLORS: string[] = ['#000000', 'red', '#F4E511'];
        const dataNum: number = isMonthDisplay() ? lastDayOfYear : displayDays[selectMonthIndex - 1];
        for (let i = 0; i < PIT_TITLES.length; i++) {
          const data: LineData = {
            labels: labels,
            data: Array(dataNum).fill(PIT_VALUES[i] * conditionResource.trashDensity),
            color: PIT_COLORS[i],
            title: PIT_TITLES[i],
            unit: itemsUnit[i],
            pointRadius: isMonthDisplay() ? 0 : undefined, // ポイントが日ごとに出ると多すぎるため、0指定
            plotType: 'rect',
          };
          lineDatas.push(data);
          lineVisibles.push(true);
        }
      }
      linePropeties[items[i]] = applyGraphInfo(lineDatas, gridBottomColors, lineVisibles);
    }
    return linePropeties;
  }, [
    initialPlanEditResource,
    planEditResource,
    resultEditResource,
    draftEditResource,
    selectMonthIndex,
    showInitialPlanVisible,
    showPlanVisible,
    showResultVisible,
    isSelectDraft,
  ]);

  useEffect(() => {
    // 焼却量一括変更 設定値更新
    const preDate: string = isMonthDisplay() ? getMinDate(displayYear) : String(createDay(displayYear, displayDays, lastDayOfLastMonth));
    const nextDate: string = isMonthDisplay()
      ? getMaxDate(displayYear)
      : displayDays.length === selectMonthIndex
        ? getMaxDate(displayYear)
        : String(createDay(displayYear, displayDays, daySum(selectMonthIndex) - 1));
    setPreBetweenDate(preDate);
    setNextBetweenDate(nextDate);

    // jsonPlanPreResourceはAPI実行を検知するトリガーに
  }, [selectMonthIndex, planInitResource]);

  // 判定

  const isPlanVisible = () => {
    return existPlanDraft && showPlanVisible;
  };

  const isResultVisible = () => {
    return existResultDraft && showResultVisible;
  };

  const handleApiYearChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setApiYear(Number(event.target.value));
  };

  const handlePastYearChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setPastYear(Number(event.target.value));
  };

  const handleGetApiClick = async () => {
    let isTransition: boolean = true;
    if (isUnsaved) {
      isTransition = await showConfirmMessage(MESSAGES.CONFIRM_UNSAVED);
    }
    if (isTransition) {
      setIsReload(true);
    } else {
      setApiYear(displayYear);
    }
  };

  const handleGetPastClick = async () => {
    showSuccessMessage('過去実績の反映が完了しました。', {
      showOnResolved: new Promise<MessageDialogOptions | void>((resolve) => {
        (async () => {
          const requestUrl = `${REQ_PLAN_RESULT}?branchId=${String(sessionResource.branchId)}&selectDate=${getMinDate(pastYear).concat(' 00:00:00')}&furnaces=${String(
            sessionResource.furnacesCount,
          )}&putDate=${displayYear}&requestParam=${POST_REQ_PAST_REFLECT}`;
          let { payload: message } = await postRequestJson<TextResponse>(requestUrl, '');
          console.log(message);
          if (message) {
            const dialogOptions = { message: message, type: 'info' } as MessageDialogOptions;
            resolve(dialogOptions);
          }
        })();
      }),
    })
      .then(() => {
        setIsReload(true);
      })
      .catch(() => {
        showErrorMessage(MESSAGES.ERROR_SERVER);
      });
  };

  // 画面の表示切替
  const handleMonthClick = (monthIndex: number) => {
    setSelectMonthIndex(monthIndex);
  };

  const handleAnClick = (index: number) => {
    const temp: boolean[] = [...isSelectDraft]; // 新たに生成した配列で上書きすることでReactが検知する
    temp[index] = !isSelectDraft[index];
    setIsSelectDraft(temp);
  };

  const handleAnDecideClick = async () => {
    let draftNumber: number = 0;
    let draftCount: number = 0;
    for (let j = 0; j < draftLength; j++) {
      if (isSelectDraft[j]) {
        draftCount = draftCount + 1;
        draftNumber = j + 1;
      }
    }
    if (draftCount === 1) {
      const confirmResult = await showConfirmMessage('案を確定してもよろしいでしょうか');
      if (confirmResult) {
        let initialPlanSetting = false;
        if (!existInitialPlanDraft) {
          initialPlanSetting = await showConfirmMessage('案を期初計画としますか');
        }
        showSuccessMessage(MESSAGES.SUCCESS_FIX_DRAFT, {
          showOnResolved: new Promise<MessageDialogOptions | void>((resolve, reject) => {
            (async () => {
              try {
                const pushObject = {
                  initialPlanSetting: initialPlanSetting,
                  lastReadAt: dataLoadedAtRef.current,
                };
                const requestUrl = `${REQ_PLAN_RESULT}?branchId=${String(sessionResource.branchId)}&selectDate=${getMinDate(displayYear).concat(' 00:00:00')}&furnaces=${String(
                  sessionResource.furnacesCount,
                )}&draftNumber=${String(draftNumber)}&requestParam=${POST_REQ_DRAFT}`;
                const { payload: message } = await postRequestJson<JsonResponse>(requestUrl, pushObject);
                console.log(message);
                if (message) {
                  const dialogOptions = { message: MESSAGES.SUCCESS_FIX_DRAFT, type: 'success' } as MessageDialogOptions;
                  resolve(dialogOptions);
                }
              } catch (e) {
                reject(e);
              }
            })();
          }),
        })
          .then(() => {
            setIsReload(true);
          })
          .catch(() => {
            // reject側で処理されるため、こちらには分岐しないが念のため残す
            showErrorMessage(MESSAGES.ERROR_SERVER);
          });
      }
    } else {
      showMessage({ message: '立案計画を1つ選択してください。', type: 'error' });
    }
  };

  function openAllErrorFurnaces() {
    const [flags, setFlags] = planShutdownVisibleState;
    let updatedFlags = [...flags];

    for (let furnacesIndex = 0; furnacesIndex < sessionResource.furnacesCount; furnacesIndex++) {
      let hasError = false;
      for (let dataIndex = 0; dataIndex < planEditResource.shutdown[furnacesIndex].length; dataIndex++) {
        if (
          getErrMassage(
            planEditResource.shutdown[furnacesIndex][dataIndex],
            planEditResource.shutdown[furnacesIndex],
            planEditResource.shutdown[furnacesIndex].length === dataIndex + 1,
          ) !== '' ||
          getErrMassageCommonPeriodNotOverlap(planEditResource, furnacesIndex, dataIndex) !== ''
        ) {
          hasError = true;
          break;
        }
      }
      if (hasError) {
        updatedFlags[furnacesIndex] = true;
      }
    }
    setFlags(updatedFlags);

    let updatedDraftFlags = draftShutdownVisible.map((row) => [...row]);

    for (let draftIndex = 0; draftIndex < draftLength; draftIndex++) {
      for (let furnacesIndex = 0; furnacesIndex < sessionResource.furnacesCount; furnacesIndex++) {
        let hasError = false;
        for (let dataIndex = 0; dataIndex < draftEditResource[draftIndex].shutdown[furnacesIndex].length; dataIndex++) {
          if (
            getErrMassage(
              draftEditResource[draftIndex].shutdown[furnacesIndex][dataIndex],
              draftEditResource[draftIndex].shutdown[furnacesIndex],
              draftEditResource[draftIndex].shutdown[furnacesIndex].length === dataIndex + 1,
            ) !== '' ||
            getErrMassageCommonPeriodNotOverlap(draftEditResource[draftIndex], furnacesIndex, dataIndex) !== ''
          ) {
            hasError = true;
            break;
          }
        }
        if (hasError) {
          updatedDraftFlags[draftIndex][furnacesIndex] = true;
        }
      }
    }
    setDraftShutdownVisible(updatedDraftFlags);
  }

  const handleSaveClick = async () => {
    // 開閉状態を更新する
    openAllErrorFurnaces();

    for (let draftIndex = 0; draftIndex < draftLength; draftIndex++) {
      if (await isInvalidCheck(draftInitResource[draftIndex], draftEditResource[draftIndex])) {
        return;
      }
    }
    if (!(await isInvalidCheck(planInitResource, planEditResource))) {
      const confirmResult = await showConfirmMessage('入力内容を保存します。よろしいですか。');
      if (confirmResult) {
        const warningCheck: boolean = isWarningBurnVolumeCheck(planEditResource) || draftEditResource.some(isWarningBurnVolumeCheck);
        let comfirmWarning: boolean = true;
        if (warningCheck) {
          comfirmWarning = await showConfirmMessage('定格処理量を超える焼却量が設定されています。保存を実行してもよろしいでしょうか。');
        }
        if (!existInitialPlanDraft && !isInitialPlanSetting) {
          comfirmWarning = await showConfirmMessage('期初計画が決定していませんが、変更してよいですか');
        }
        if (comfirmWarning) {
          executeSave();
        }
      }
    }
  };

  const handleResetClick = async () => {
    const confirmResult = await showConfirmMessage(MESSAGES.CONFIRM_RESET_INPUT);
    if (confirmResult) {
      ResetResource(sessionResource.furnacesCount, initialPlanInitResource, setInitialPlanEditResource, setInitialPlanEmptyCheck, planDayUsage, planMonthUsage);
      ResetResource(sessionResource.furnacesCount, planInitResource, setPlanEditResource, setPlanEmptyCheck, planDayUsage, planMonthUsage);
      ResetResource(sessionResource.furnacesCount, resultInitResource, setResultEditResource, setResultEmptyCheck, resultDayUsage, resultMonthUsage);
      for (let i = 0; i < draftLength; i++) {
        const [draftElement, setDraftElement] = getArrayElementState(draftInitResource, setDraftEditResource, i);
        const [draftEmpty, setDraftEmpty] = getArrayElementState(draftEmptyCheck, setDraftEmptyCheck, i);
        ResetResource(sessionResource.furnacesCount, draftInitResource[i], setDraftElement, setDraftEmpty, draftDayUsage, draftMonthUsage);
      }
      //日付入力欄をクリア
      const tempInputShutdown: DayContext[] = structuredClone(planInputShutdown);
      const tempVisible: boolean[] = structuredClone(planShutdownInputVisible);
      for (let furnacesIndex = 0; furnacesIndex < sessionResource.furnacesCount; furnacesIndex++) {
        tempInputShutdown[furnacesIndex].shutdown_date = '';
        tempInputShutdown[furnacesIndex].launch_date = '';
        tempVisible[furnacesIndex] = false;
      }
      setPlanInputShutdown(tempInputShutdown);
      setPlanShutdownInputVisible(tempVisible);

      //前に戻す履歴を削除
      setInitialPlanShutdownRecord(Array.from({ length: sessionResource.furnacesCount }, () => []));
      setInitialPlanHistory(Array.from({ length: sessionResource.furnacesCount }, () => []));
      setPlanShutdownRecord(Array.from({ length: sessionResource.furnacesCount }, () => []));
      setPlanHistory(Array.from({ length: sessionResource.furnacesCount }, () => []));

      //定格処理量フラグをオフ
      setPlanWarningVals(
        Array(LoginService.getLoginInfo().furnacesCount)
          .fill(null)
          .map(() => Array(366).fill(false)),
      );
      if (existAutoDraft) {
        //前に戻す履歴を削除
        setDraftShutdownRecord(Array.from({ length: draftLength }, () => Array.from({ length: sessionResource.furnacesCount }, () => [])));
        setDraftHistory(Array.from({ length: draftLength }, () => Array.from({ length: sessionResource.furnacesCount }, () => [])));

        //焼却量フラグをクリア
        setDraftWarningVals(
          Array(draftLength)
            .fill(null)
            .map(() =>
              Array(LoginService.getLoginInfo().furnacesCount)
                .fill(null)
                .map(() => Array(366).fill(false)),
            ),
        );

        //日付入力欄をクリア
        const tempDraftInputShutdown: DayContext[][] = structuredClone(draftInputShutdown);
        const tempDraftVisible: boolean[][] = structuredClone(draftShutdownInputVisible);
        for (let draftIndex = 0; draftIndex < draftLength; draftIndex++) {
          for (let furnacesIndex = 0; furnacesIndex < sessionResource.furnacesCount; furnacesIndex++) {
            tempDraftInputShutdown[draftIndex][furnacesIndex].shutdown_date = '';
            tempDraftInputShutdown[draftIndex][furnacesIndex].launch_date = '';
            tempDraftVisible[draftIndex][furnacesIndex] = false;
          }
        }
        setDraftInputShutdown(tempDraftInputShutdown);
        setDraftShutdownInputVisible(tempDraftVisible);
      }
      showSuccessMessage(MESSAGES.SUCCESS_RESET);
    }
  };

  const handleShowInitialPlanClick = () => {
    setShowInitialPlanVisible(!showInitialPlanVisible);
  };

  const handleShowPlanClick = () => {
    setShowPlanVisible(!showPlanVisible);
  };

  const handleShowResultClick = () => {
    setShowResultVisible(!showResultVisible);
  };
  const handleShowPowerClick = () => {
    setShowPowerVisible(!showPowerVisible);
  };
  const handleShowAshClick = () => {
    setShowAshVisible(!showAshVisible);
  };

  const handleShowOperationDaysClick = () => {
    setShowOperationDaysVisible(!showOperationDaysVisible);
  };

  const handleShutdownClick = ([flags, setFlags]: [boolean[], React.Dispatch<React.SetStateAction<boolean[]>>], furnacesIndex: number) => {
    const updated = [...flags];
    updated[furnacesIndex] = !updated[furnacesIndex];
    setFlags(updated);
  };

  const updateCarryDependent = (
    editResource: DraftContext,
    initResource: DraftContext,
    emptyResource: DraftEmpty,
    usage: DriveUsage,
    monIndex: number,
    colIndex: number,
    furnacesIndex: number,
  ): DraftContext => {
    const tempContext = structuredClone(editResource);
    const firstDateIndex: number = colIndex;
    const firstDay: DriveContext = structuredClone(initResource.day[firstDateIndex]);
    // 前日分が編集不可ならば、当日分の初期データを使用して前日のピット残量を求める
    // 再立案した日は当日分の初期データを使用する ただし、再立案した日より前の搬入量・焼却量が編集されてピット残量が再計算された場合は除く
    // 上記の条件以外は、前日分の編集データを使用して前日のピット残量を求める
    const firstPrePitValue: number =
      firstDateIndex === 0 ||
      firstDateIndex === systemDateIndex ||
      (firstDateIndex === getDateIndex(autoDraftBaseDate, displayYear, displayDays) &&
        firstDateIndex !== 0 &&
        initResource.day[firstDateIndex - 1].trash_pit_rest === editResource.day[firstDateIndex - 1].trash_pit_rest)
        ? firstDay.trash_pit_rest - firstDay.carry_volume + sumArray(firstDay.burn_volume)
        : structuredClone(editResource.day[firstDateIndex - 1]).trash_pit_rest;
    if (isMonthDisplay()) {
      tempContext.day = [...calcCarryRate(tempContext.day, colIndex, tempContext.month[colIndex].carry_volume)];
      let carrySum: number = 0;
      for (let i = 0; i < displayDays[colIndex]; i++) {
        carrySum = calcDecimalSum(carrySum, tempContext.day[daySum(colIndex) + i].carry_volume);
      }
      tempContext.month[colIndex].carry_volume = calcMathRound(carrySum, CARRY_DIGIT);
    }
    tempContext.day = calcPit(tempContext.day, usage, firstPrePitValue, firstDateIndex);
    return tempContext;
  };

  const updateWarningVals = (
    [warningVals, setWarningVals]: [boolean[][], React.Dispatch<React.SetStateAction<boolean[][]>>],
    monIndex: number,
    colIndex: number,
    furnacesIndex: number,
  ): void => {
    const tempWarning: boolean[][] = warningVals;
    tempWarning[furnacesIndex][colIndex + daySum(monIndex - 1)] = false;
    setWarningVals(tempWarning);
  };

  const updateBurnDependent = (
    editResource: DraftContext,
    initResource: DraftContext,
    emptyResource: DraftEmpty,
    usage: DriveUsage,
    monIndex: number,
    colIndex: number,
    furnacesIndex: number,
    previousValue?: number,
    oldBurnArray?: number[],
  ): DraftContext => {
    const temp: DraftContext = structuredClone(editResource);
    const firstDateIndex: number = daySum(monIndex - 1) + colIndex;
    const firstDay: DriveContext = structuredClone(initResource.day[firstDateIndex]);
    const previousBurnArray: number[] = structuredClone(oldBurnArray ?? editResource.day[firstDateIndex].burn_volume);
    if (oldBurnArray === undefined && previousValue !== undefined && previousBurnArray[furnacesIndex] !== undefined) {
      previousBurnArray[furnacesIndex] = previousValue;
    }
    // 前日分が編集不可ならば、当日分の初期データを使用して前日のピット残量を求める
    // 再立案した日は当日分の初期データを使用する ただし、再立案した日より前の搬入量・焼却量が編集されてピット残量が再計算された場合は除く
    // 上記の条件以外は、前日分の編集データを使用して前日のピット残量を求める
    const prePitValue: number =
      firstDateIndex === 0 ||
      firstDateIndex === systemDateIndex ||
      (firstDateIndex === getDateIndex(autoDraftBaseDate, displayYear, displayDays) &&
        firstDateIndex !== 0 &&
        initResource.day[firstDateIndex - 1].trash_pit_rest === editResource.day[firstDateIndex - 1].trash_pit_rest)
        ? firstDay.trash_pit_rest - firstDay.carry_volume + sumArray(firstDay.burn_volume)
        : structuredClone(editResource.day[firstDateIndex - 1]).trash_pit_rest;
    temp.day = calcPit(temp.day, usage, prePitValue, firstDateIndex);
    temp.day = calcAsh(
      temp.day,
      initResource.day,
      emptyResource.day,
      usage,
      'main_ash_generate',
      'main_ash_carry_out_sum',
      'main_ash_carry_out_units',
      'main_ash_pit_rest',
      conditionResource.mainAshRate,
      conditionResource.truckMainAshCarryOut,
      firstDateIndex,
    );
    temp.day = calcAsh(
      temp.day,
      initResource.day,
      emptyResource.day,
      usage,
      'fly_ash_generate',
      'fly_ash_carry_out_sum',
      'fly_ash_carry_out_units',
      'fly_ash_pit_rest',
      conditionResource.flyAshRate,
      conditionResource.truckFlyAshCarryOut,
      firstDateIndex,
    );
    temp.day = calcElectric(temp.day, planUsageCalc, previousBurnArray, colIndex + daySum(monIndex - 1));
    const cureentTempDays: DriveContext[] = temp.day.slice(daySum(monIndex - 1), daySum(monIndex));
    temp.month[monIndex - 1].burn_volume[furnacesIndex] = calcItemBurnSum(cureentTempDays, furnacesIndex);
    temp.month[monIndex - 1].main_ash_generate = calcItemSum(cureentTempDays, 'main_ash_generate');
    temp.month[monIndex - 1].fly_ash_generate = calcItemSum(cureentTempDays, 'fly_ash_generate');
    temp.month[monIndex - 1].main_ash_carry_out_units = calcItemSum(cureentTempDays, 'main_ash_carry_out_units');
    temp.month[monIndex - 1].fly_ash_carry_out_units = calcItemSum(cureentTempDays, 'fly_ash_carry_out_units');
    temp.month[monIndex - 1].electric_sale = calcItemSum(cureentTempDays, 'electric_sale');
    temp.month[monIndex - 1].electric_buy = calcItemSum(cureentTempDays, 'electric_buy');
    temp.month[monIndex - 1].electric_create = calcItemSum(cureentTempDays, 'electric_create');
    return temp;
  };

  // ピット残のみ更新
  const updatePitRest = (
    editResource: DraftContext,
    initResource: DraftContext,
    emptyResource: DraftEmpty,
    usage: DriveUsage,
    monIndex: number,
    colIndex: number,
    furnacesIndex: number,
  ): DraftContext => {
    const temp: DraftContext = structuredClone(editResource);
    // 更新セルの翌日を計算開始日とする
    const firstDateIndex: number = daySum(monIndex - 1) + colIndex + 1;
    const firstDay: DriveContext = structuredClone(initResource.day[firstDateIndex]);
    const prePitValue: number =
      firstDateIndex === 0 ||
      firstDateIndex === systemDateIndex ||
      (firstDateIndex === getDateIndex(autoDraftBaseDate, displayYear, displayDays) &&
        firstDateIndex !== 0 &&
        initResource.day[firstDateIndex - 1].trash_pit_rest === editResource.day[firstDateIndex - 1].trash_pit_rest)
        ? firstDay.trash_pit_rest - firstDay.carry_volume + sumArray(firstDay.burn_volume)
        : structuredClone(editResource.day[firstDateIndex - 1]).trash_pit_rest;
    temp.day = calcPit(temp.day, usage, prePitValue, firstDateIndex);
    return temp;
  };

  // 主灰ピット残のみ更新
  const updateMainAshRest = (
    editResource: DraftContext,
    initResource: DraftContext,
    emptyResource: DraftEmpty,
    usage: DriveUsage,
    monIndex: number,
    colIndex: number,
    furnacesIndex: number,
  ): DraftContext => {
    const temp: DraftContext = structuredClone(editResource);
    // 更新セルの翌日を計算開始日とする
    const firstDateIndex: number = daySum(monIndex - 1) + colIndex + 1;
    temp.day = calcAsh(
      temp.day,
      initResource.day,
      emptyResource.day,
      usage,
      'main_ash_generate',
      'main_ash_carry_out_sum',
      'main_ash_carry_out_units',
      'main_ash_pit_rest',
      conditionResource.mainAshRate,
      conditionResource.truckMainAshCarryOut,
      firstDateIndex,
    );
    return temp;
  };

  // 主灰搬出量変更時のみ使用
  // 当日を基準に主灰発生量、主灰搬出量、主灰ピット残量を更新する
  // 主灰搬出台数の基準値を複数持つ施設の対応のため、搬出台数が自動計算されないために導入
  const updateMainAshCarryOut = (
    editResource: DraftContext,
    initResource: DraftContext,
    emptyResource: DraftEmpty,
    usage: DriveUsage,
    monIndex: number,
    colIndex: number,
    furnacesIndex: number,
  ): DraftContext => {
    const temp: DraftContext = structuredClone(editResource);
    const firstDateIndex: number = daySum(monIndex - 1) + colIndex;
    temp.day = calcAsh(
      temp.day,
      initResource.day,
      emptyResource.day,
      usage,
      'main_ash_generate',
      'main_ash_carry_out_sum',
      'main_ash_carry_out_units',
      'main_ash_pit_rest',
      conditionResource.mainAshRate,
      conditionResource.truckMainAshCarryOut,
      firstDateIndex,
      false,
    );
    return temp;
  };

  // 飛灰ピット残のみ更新
  const updateFlyAshRest = (
    editResource: DraftContext,
    initResource: DraftContext,
    emptyResource: DraftEmpty,
    usage: DriveUsage,
    monIndex: number,
    colIndex: number,
    furnacesIndex: number,
  ): DraftContext => {
    const temp: DraftContext = structuredClone(editResource);
    // 更新セルの翌日を計算開始日とする
    const firstDateIndex: number = daySum(monIndex - 1) + colIndex + 1;
    temp.day = calcAsh(
      temp.day,
      initResource.day,
      emptyResource.day,
      usage,
      'fly_ash_generate',
      'fly_ash_carry_out_sum',
      'fly_ash_carry_out_units',
      'fly_ash_pit_rest',
      conditionResource.flyAshRate,
      conditionResource.truckFlyAshCarryOut,
      firstDateIndex,
    );
    return temp;
  };

  // 飛灰搬出量変更時のみ使用
  // 当日を基準に飛灰発生量、飛灰搬出量、飛灰ピット残量を更新する
  // 飛灰搬出台数の基準値を複数持つ施設の対応のため、搬出台数が自動計算されないために導入
  const updateFlyAshCarryOut = (
    editResource: DraftContext,
    initResource: DraftContext,
    emptyResource: DraftEmpty,
    usage: DriveUsage,
    monIndex: number,
    colIndex: number,
    furnacesIndex: number,
  ): DraftContext => {
    const temp: DraftContext = structuredClone(editResource);
    const firstDateIndex: number = daySum(monIndex - 1) + colIndex;
    temp.day = calcAsh(
      temp.day,
      initResource.day,
      emptyResource.day,
      usage,
      'fly_ash_generate',
      'fly_ash_carry_out_sum',
      'fly_ash_carry_out_units',
      'fly_ash_pit_rest',
      conditionResource.flyAshRate,
      conditionResource.truckFlyAshCarryOut,
      firstDateIndex,
      false,
    );
    return temp;
  };

  const updateAshFuncMap: Record<string, any> = {
    main_ash_pit_rest: updateMainAshRest,
    main_ash_carry_out: updateMainAshCarryOut,
    fly_ash_pit_rest: updateFlyAshRest,
    fly_ash_carry_out: updateFlyAshCarryOut,
  };

  function validBurnChange(before: number, after: number) {
    return before !== 0 && after !== 0;
  }

  const updateAshDependent = (
    editResource: DraftContext,
    initResource: DraftContext,
    emptyResource: DraftEmpty,
    usage: DriveUsage,
    monIndex: number,
    colIndex: number,
    furnacesIndex: number,
  ): DraftContext => {
    const firstDateIndex: number = daySum(monIndex - 1) + colIndex;
    const temp: DraftContext = structuredClone(editResource);
    temp.day = calcAsh(
      temp.day,
      initResource.day,
      emptyResource.day,
      usage,
      'main_ash_generate',
      'main_ash_carry_out_sum',
      'main_ash_carry_out_units',
      'main_ash_pit_rest',
      conditionResource.mainAshRate,
      conditionResource.truckMainAshCarryOut,
      firstDateIndex,
    );
    temp.day = calcAsh(
      temp.day,
      initResource.day,
      emptyResource.day,
      usage,
      'fly_ash_generate',
      'fly_ash_carry_out_sum',
      'fly_ash_carry_out_units',
      'fly_ash_pit_rest',
      conditionResource.flyAshRate,
      conditionResource.truckFlyAshCarryOut,
      firstDateIndex,
    );
    const cureentTempDays: DriveContext[] = temp.day.slice(daySum(monIndex - 1), daySum(monIndex));
    temp.month[monIndex - 1].main_ash_generate = calcItemSum(cureentTempDays, 'main_ash_generate');
    temp.month[monIndex - 1].fly_ash_generate = calcItemSum(cureentTempDays, 'fly_ash_generate');
    temp.month[monIndex - 1].main_ash_carry_out_units = calcItemSum(cureentTempDays, 'main_ash_carry_out_units');
    temp.month[monIndex - 1].fly_ash_carry_out_units = calcItemSum(cureentTempDays, 'fly_ash_carry_out_units');
    return temp;
  };

  const handleInputAddDateClick = ([visibles, setVisibles]: [boolean[], React.Dispatch<React.SetStateAction<boolean[]>>], furnacesIndex: number) => {
    const updated: boolean[] = structuredClone(visibles);
    updated[furnacesIndex] = true;
    setVisibles(updated);
  };

  const handleInputDeleteDateClick = (
    [inputShutdowns, setInputShutdowns]: [DayContext[], React.Dispatch<React.SetStateAction<DayContext[]>>],
    [visibles, setVisibles]: [boolean[], React.Dispatch<React.SetStateAction<boolean[]>>],
    furnacesIndex: number,
  ) => {
    // 入力欄をクリア
    let tempInputShutdown: DayContext[] = structuredClone(inputShutdowns);
    tempInputShutdown[furnacesIndex].shutdown_date = '';
    tempInputShutdown[furnacesIndex].launch_date = '';
    setInputShutdowns(tempInputShutdown);
    let tempVisible: boolean[] = structuredClone(visibles);
    tempVisible[furnacesIndex] = false;
    setVisibles(tempVisible);
  };

  const handleInputUndoDateClick = (
    [resource, setResource]: [DraftContext, React.Dispatch<React.SetStateAction<DraftContext>>],
    [shutdownRecords, setShutdownRecords]: [ShutdownRecord[][][], React.Dispatch<React.SetStateAction<ShutdownRecord[][][]>>],
    warningValsState: [boolean[][], React.Dispatch<React.SetStateAction<boolean[][]>>],
    initResource: DraftContext,
    emptyResource: DraftEmpty,
    usage: DriveUsage,
    furnacesIndex: number,
    draftIndex?: number,
    initialFlag?: boolean,
  ) => {
    if (draftIndex !== undefined) {
      if (shutdownRecords[draftIndex]?.[furnacesIndex]?.length === 0 || draftHistory[draftIndex]?.[furnacesIndex]?.length === 0) {
        return;
      }

      const readShutdown = structuredClone(draftHistory[draftIndex][furnacesIndex][draftHistory[draftIndex][furnacesIndex].length - 1]);
      const readRecords = structuredClone(shutdownRecords[furnacesIndex][shutdownRecords[furnacesIndex].length - 1]);

      setDraftHistory((prev) => {
        const next = structuredClone(prev);
        next[draftIndex][furnacesIndex].pop();
        return next;
      });

      setShutdownRecords((prev) => {
        const next = structuredClone(prev);
        next[furnacesIndex].pop();
        return next;
      });

      setDraftEditResource((prevResources) => {
        const updatedResources = structuredClone(prevResources);
        const updated = updatedResources[draftIndex];

        updated.shutdown[furnacesIndex] = readShutdown;

        let recordIndex = 0;
        for (let dayIndex = 0; dayIndex < updated.day.length; dayIndex++) {
          const targetDate = createDay(displayYear, displayDays, dayIndex);
          if (readRecords[recordIndex]?.record_date === targetDate) {
            updated.day[dayIndex].burn_volume[furnacesIndex] = readRecords[recordIndex].burn_volume;
            recordIndex++;
            if (recordIndex >= readRecords.length) break;
          }
        }

        updatedResources[draftIndex] = calcBurnStopShutdown(warningValsState, updated, initResource, emptyResource, usage, furnacesIndex);

        return updatedResources;
      });
    } else if (initialFlag) {
      if (shutdownRecords[furnacesIndex].length === 0) return;

      if (!initialPlanHistory[furnacesIndex] || initialPlanHistory[furnacesIndex].length === 0) return;

      const readShutdown = initialPlanHistory[furnacesIndex][initialPlanHistory[furnacesIndex].length - 1];
      const readRecords = structuredClone(shutdownRecords[furnacesIndex][shutdownRecords[furnacesIndex].length - 1]);

      setInitialPlanHistory((prev) => {
        const next = structuredClone(prev);
        next[furnacesIndex].pop();
        return next;
      });

      setShutdownRecords((prev) => {
        const next = structuredClone(prev);
        next[furnacesIndex].pop();
        return next;
      });

      const updated = structuredClone(resource);
      updated.shutdown[furnacesIndex] = readShutdown;

      let recordIndex = 0;
      for (let dayIndex = 0; dayIndex < updated.day.length; dayIndex++) {
        const targetDate = createDay(displayYear, displayDays, dayIndex);
        if (readRecords[recordIndex]?.record_date === targetDate) {
          updated.day[dayIndex].burn_volume[furnacesIndex] = readRecords[recordIndex].burn_volume;
          recordIndex++;
          if (recordIndex >= readRecords.length) break;
        }
      }

      const finalResource = calcBurnStopShutdown(warningValsState, updated, initResource, emptyResource, usage, furnacesIndex);
      setResource(finalResource);
    } else {
      if (shutdownRecords[furnacesIndex].length === 0) return;

      if (!planHistory[furnacesIndex] || planHistory[furnacesIndex].length === 0) return;

      const readShutdown = planHistory[furnacesIndex][planHistory[furnacesIndex].length - 1];
      const readRecords = structuredClone(shutdownRecords[furnacesIndex][shutdownRecords[furnacesIndex].length - 1]);

      setPlanHistory((prev) => {
        const next = structuredClone(prev);
        next[furnacesIndex].pop();
        return next;
      });

      setShutdownRecords((prev) => {
        const next = structuredClone(prev);
        next[furnacesIndex].pop();
        return next;
      });

      const updated = structuredClone(resource);
      updated.shutdown[furnacesIndex] = readShutdown;

      let recordIndex = 0;
      for (let dayIndex = 0; dayIndex < updated.day.length; dayIndex++) {
        const targetDate = createDay(displayYear, displayDays, dayIndex);
        if (readRecords[recordIndex]?.record_date === targetDate) {
          updated.day[dayIndex].burn_volume[furnacesIndex] = readRecords[recordIndex].burn_volume;
          recordIndex++;
          if (recordIndex >= readRecords.length) break;
        }
      }

      const finalResource = calcBurnStopShutdown(warningValsState, updated, initResource, emptyResource, usage, furnacesIndex);
      setResource(finalResource);
    }
  };

  const handleDeleteDateClick = (
    [resource, setResource]: [DraftContext, React.Dispatch<React.SetStateAction<DraftContext>>],
    [shutdownRecords, setShutdownRecords]: [ShutdownRecord[][][], React.Dispatch<React.SetStateAction<ShutdownRecord[][][]>>],
    warningValsState: [boolean[][], React.Dispatch<React.SetStateAction<boolean[][]>>],
    initResource: DraftContext,
    emptyResource: DraftEmpty,
    usage: DriveUsage,
    furnacesIndex: number,
    index: number,
    draftIndex?: number,
    initialFlag?: boolean,
  ) => {
    let temp: DraftContext = structuredClone(resource);
    if (temp.shutdown[furnacesIndex][index].not_change_flag) {
      showErrorMessage('共通停止期間を含むため、削除できません。');
      return;
    }
    const deletedShutdownDateIndex = getDateIndex(temp.shutdown[furnacesIndex][index].shutdown_date, displayYear, displayDays);
    if (systemDateIndex > deletedShutdownDateIndex) {
      showErrorMessage('当日より前の期間を含むため、削除できません。');
      return;
    }
    // 立上下日 変更日
    const records: ShutdownRecord[] = [];
    const modifyDays: boolean[] = calcStopArray([temp.shutdown[furnacesIndex][index]]);
    for (let dayIndex = 0; dayIndex < resource.day.length; dayIndex++) {
      if (modifyDays[dayIndex]) {
        const obj: ShutdownRecord = { record_date: createDay(displayYear, displayDays, dayIndex), burn_volume: resource.day[dayIndex].burn_volume[furnacesIndex] };
        records.push(obj);
      }
    }
    // 履歴を挿入
    let tempRecordInsert: ShutdownRecord[][][] = structuredClone(shutdownRecords);
    tempRecordInsert[furnacesIndex].push(records);
    setShutdownRecords(tempRecordInsert);

    // 立上下日と焼却量を変更
    // const deletedLaunchDateIndex = getDateIndex(temp.shutdown[furnacesIndex][index].launch_date, displayYear, displayDays);
    // for (let i = deletedShutdownDateIndex; i <= deletedLaunchDateIndex; i++) {
    //   // 削除された停炉期間と判定できるように0埋めする（後続処理にて、運転時処理量に置き換える）
    //   temp.day[i].burn_volume[furnacesIndex] = 0;
    // }
    temp.shutdown[furnacesIndex].splice(index, 1);
    temp = calcBurnStopShutdown(warningValsState, temp, initResource, emptyResource, usage, furnacesIndex);
    saveShutdownHistory(resource, furnacesIndex, draftIndex, initialFlag);
    setResource(temp);
  };

  const handleInputDateChange = (
    [resource, setResource]: [DraftContext, React.Dispatch<React.SetStateAction<DraftContext>>],
    [shutdownRecords, setShutdownRecords]: [ShutdownRecord[][][], React.Dispatch<React.SetStateAction<ShutdownRecord[][][]>>],
    [inputShutdowns, setInputShutdowns]: [DayContext[], React.Dispatch<React.SetStateAction<DayContext[]>>],
    [visibles, setVisibles]: [boolean[], React.Dispatch<React.SetStateAction<boolean[]>>],
    warningValsState: [boolean[][], React.Dispatch<React.SetStateAction<boolean[][]>>],
    initResource: DraftContext,
    emptyResource: DraftEmpty,
    usage: DriveUsage,
    kind: string,
    value: Date | null,
    furnacesIndex: number,
    draftIndex?: number,
    initialFlag?: boolean,
  ) => {
    const tempShutdown: DayContext[] = structuredClone(inputShutdowns);
    if (kind === item_shutdown_date) {
      tempShutdown[furnacesIndex].shutdown_date = value ? format(value, DATE_FORMAT_YYYYMMDD) : '';
    } else {
      tempShutdown[furnacesIndex].launch_date = value ? format(value, DATE_FORMAT_YYYYMMDD) : '';
    }
    let endInputFlag: boolean = false;
    if (tempShutdown[furnacesIndex].shutdown_date !== '' && resource.shutdown[furnacesIndex].length > 0) {
      const inputDate: Date = new Date(tempShutdown[furnacesIndex].shutdown_date);
      const endDate: Date = new Date(resource.shutdown[furnacesIndex][resource.shutdown[furnacesIndex].length - 1].shutdown_date);
      if (inputDate.getTime() > endDate.getTime()) {
        endInputFlag = true;
      }
    }
    if (getErrMassage(tempShutdown[furnacesIndex], [...resource.shutdown[furnacesIndex], tempShutdown[furnacesIndex]], endInputFlag) === '') {
      const preResource: DraftContext = structuredClone(resource);
      // キューに挿入
      const day: DayContext = new DayContextObject();
      day.shutdown_date = tempShutdown[furnacesIndex].shutdown_date;
      day.launch_date = tempShutdown[furnacesIndex].launch_date;
      day.not_change_flag = false;
      let tempInsert: DraftContext = structuredClone(resource);
      tempInsert.shutdown[furnacesIndex].push(day);
      tempInsert.shutdown[furnacesIndex] = sortShutdown(tempInsert.shutdown[furnacesIndex]);
      tempInsert = calcBurnStopShutdown(warningValsState, tempInsert, initResource, emptyResource, usage, furnacesIndex);
      // 履歴を保持
      const nextShutdown: DayContext[] = [...tempInsert.shutdown[furnacesIndex]];
      // 計画値更新
      setResource(tempInsert);
      // 立上下日(前)
      const preDays: boolean[] = calcStopArray(preResource.shutdown[furnacesIndex]);
      // 立上下日(後)
      const nextDays: boolean[] = calcStopArray(nextShutdown);
      // 比較処理
      const records: ShutdownRecord[] = [];
      for (let dayIndex = 0; dayIndex < preResource.day.length; dayIndex++) {
        if (preDays[dayIndex] !== nextDays[dayIndex]) {
          const obj: ShutdownRecord = { record_date: createDay(displayYear, displayDays, dayIndex), burn_volume: preResource.day[dayIndex].burn_volume[furnacesIndex] };
          records.push(obj);
        }
      }
      // 履歴を挿入
      const tempRecordInsert = structuredClone(shutdownRecords);
      tempRecordInsert[furnacesIndex].push(records);
      setShutdownRecords(tempRecordInsert);

      // 入力欄をクリア
      tempShutdown[furnacesIndex].shutdown_date = '';
      tempShutdown[furnacesIndex].launch_date = '';
      const tempVisible: boolean[] = structuredClone(visibles);
      tempVisible[furnacesIndex] = false;
      setVisibles(tempVisible);
      saveShutdownHistory(resource, furnacesIndex, draftIndex, initialFlag);
    }
    setInputShutdowns(tempShutdown);
  };

  const handleInputBurnClick = (furnacesIndex: number) => {
    if (planMultipleBurn[furnacesIndex].shutdown_date === '') {
      showErrorMessage(PLAN_RESULT_MESSAGES.SYOUKYAKURYO_SHUTDOWN_ERROR);
      return;
    }
    if (planMultipleBurn[furnacesIndex].launch_date === '') {
      showErrorMessage(PLAN_RESULT_MESSAGES.SYOUKYAKURYO_LAUNCH_ERROR);
      return;
    }
    if (selectMonthIndex !== 0) {
      const targetMonth = (selectMonthIndex + 3) % 12 || 12;

      const shutdownMonth = new Date(planMultipleBurn[furnacesIndex].shutdown_date).getMonth() + 1;
      const launchMonth = new Date(planMultipleBurn[furnacesIndex].launch_date).getMonth() + 1;
      if (shutdownMonth !== targetMonth || launchMonth !== targetMonth) {
        showErrorMessage(PLAN_RESULT_MESSAGES.SYOUKYAKURYO_MONTH_ERROR);
        return;
      }
    }
    if (new Date(planMultipleBurn[furnacesIndex].shutdown_date) >= new Date(planMultipleBurn[furnacesIndex].launch_date)) {
      showErrorMessage(PLAN_RESULT_MESSAGES.SYOUKYAKURYO_INVALID_SHUTDOWN_ERROR);
      return;
    }

    const value: number = planMultipleBurn[furnacesIndex].burn_volume;
    if (value !== 0) {
      const dayCount: number = sumArray(displayDays);
      const yearNum: number = displayYear;
      let dayStartIndex: number = 0;
      let dayEndIndex: number = 0;
      for (let dayIndex = 0; dayIndex < dayCount; dayIndex++) {
        if (planMultipleBurn[furnacesIndex].shutdown_date === createDay(yearNum, displayDays, dayIndex)) {
          dayStartIndex = dayIndex;
        }
        if (planMultipleBurn[furnacesIndex].launch_date === createDay(yearNum, displayDays, dayIndex)) {
          dayEndIndex = dayIndex;
        }
      }
      //期間内に停炉日が存在しないか
      const stopArray: boolean[] = calcStopArray(planEditResource.shutdown[furnacesIndex]);
      for (let dayIndex = dayStartIndex; dayIndex < dayEndIndex + 1; dayIndex++) {
        if (stopArray[dayIndex]) {
          showErrorMessage(`${PLAN_RESULT_MESSAGES.SYOUKYAKURYO_IKKATU_TEIROBI_ERROR} ${createDay(yearNum, displayDays, dayIndex)}`);
          return;
        }
      }
      // 焼却量セット
      let tempPlans: DraftContext = { ...planEditResource };
      for (let dayIndex = dayStartIndex; dayIndex < dayEndIndex + 1; dayIndex++) {
        // 焼却量セット
        const monIndex = calcMonthIndex(dayIndex);
        const colIndex = calcDayIndex(dayIndex);
        const previousBurnArray: number[] = structuredClone(tempPlans.day[colIndex + daySum(monIndex)].burn_volume);
        tempPlans.day[colIndex + daySum(monIndex)].burn_volume[furnacesIndex] = value;
        tempPlans = updateBurnDependent(tempPlans, planInitResource, planEmptyCheck, planUsageCalc, monIndex + 1, colIndex, furnacesIndex, undefined, previousBurnArray);
      }
      // 焼却量セット
      setPlanEditResource(tempPlans);
    } else {
      showErrorMessage(PLAN_RESULT_MESSAGES.SYOUKYAKURYO_BURN_VOLUME_ERROR);
      return;
    }

    showSuccessMessage(
      MESSAGES.SUCCESS_BURN_VOLUME(
        dateFormatSlash(String(planMultipleBurn[furnacesIndex].shutdown_date)),
        dateFormatSlash(String(planMultipleBurn[furnacesIndex].launch_date)),
        String(value),
      ),
    );
  };

  const handleDeleteBurnClick = (furnacesIndex: number) => {
    // 入力フォームの日付,焼却負荷率,焼却量をクリア
    let tempBurns: MultipleBurnSetting[] = { ...planMultipleBurn };
    tempBurns[furnacesIndex] = new MultipleBurnSettingObject();
    tempBurns[furnacesIndex].shutdown_date = '';
    tempBurns[furnacesIndex].launch_date = '';
    setPlanMultipleBurn(tempBurns);
  };

  function saveShutdownHistory(resource: DraftContext, furnacesIndex: number, draftIndex?: number, initialFlag?: boolean) {
    if (draftIndex !== undefined) {
      setDraftHistory((prev) => {
        const next = structuredClone(prev);
        next[draftIndex] = next[draftIndex] || [];
        next[draftIndex][furnacesIndex] = next[draftIndex][furnacesIndex] || [];
        next[draftIndex][furnacesIndex].push(structuredClone(resource.shutdown[furnacesIndex]));
        return next;
      });
    } else if (initialFlag) {
      setInitialPlanHistory((prev) => {
        const next = structuredClone(prev);
        next[furnacesIndex] = next[furnacesIndex] || [];
        next[furnacesIndex].push(structuredClone(resource.shutdown[furnacesIndex]));
        return next;
      });
    } else {
      setPlanHistory((prev) => {
        const next = structuredClone(prev);
        next[furnacesIndex] = next[furnacesIndex] || [];
        next[furnacesIndex].push(structuredClone(resource.shutdown[furnacesIndex]));
        return next;
      });
    }
  }

  const handleDateChange = (
    [resource, setResource]: [DraftContext, React.Dispatch<React.SetStateAction<DraftContext>>],
    [shutdownRecords, setRecords]: [ShutdownRecord[][][], React.Dispatch<React.SetStateAction<ShutdownRecord[][][]>>],
    warningValsState: [boolean[][], React.Dispatch<React.SetStateAction<boolean[][]>>],
    initResource: DraftContext,
    emptyResource: DraftEmpty,
    usage: DriveUsage,
    kind: string,
    value: Date | null,
    furnacesIndex: number,
    dataIndex: number,
    draftIndex?: number,
    initialFlag?: boolean,
  ) => {
    showProgress(async () => {
      saveShutdownHistory(resource, furnacesIndex, draftIndex, initialFlag);
      const preResource: DraftContext = structuredClone(resource);
      let temp: DraftContext = structuredClone(resource);
      if (kind === item_shutdown_date) {
        temp.shutdown[furnacesIndex][dataIndex].shutdown_date = value ? format(value, DATE_FORMAT_YYYYMMDD) : '';
      } else {
        temp.shutdown[furnacesIndex][dataIndex].launch_date = value ? format(value, DATE_FORMAT_YYYYMMDD) : '';
      }
      let isSort: boolean = true;
      for (let i = 0; i < temp.shutdown[furnacesIndex].length; i++) {
        if (getErrMassage(temp.shutdown[furnacesIndex][dataIndex], [...temp.shutdown[furnacesIndex]], true) !== '') {
          isSort = false;
        }
      }
      if (isSort) {
        temp.shutdown[furnacesIndex] = sortShutdown(temp.shutdown[furnacesIndex]);
        temp = calcBurnStopShutdown(warningValsState, temp, initResource, emptyResource, usage, furnacesIndex);
      }
      // 履歴を保持
      const nextShutdown: DayContext[] = structuredClone(temp.shutdown[furnacesIndex]);
      setResource(temp);
      // 立上下日(前)
      const preDays: boolean[] = calcStopArray(preResource.shutdown[furnacesIndex]);
      // 立上下日(後)
      const nextDays: boolean[] = calcStopArray(nextShutdown);
      // 比較処理
      const records: ShutdownRecord[] = [];
      for (let dayIndex = 0; dayIndex < preResource.day.length; dayIndex++) {
        if (preDays[dayIndex] !== nextDays[dayIndex]) {
          const obj: ShutdownRecord = { record_date: createDay(displayYear, displayDays, dayIndex), burn_volume: preResource.day[dayIndex].burn_volume[furnacesIndex] };
          records.push(obj);
        }
      }
      // 履歴を挿入
      const tempRecordInsert: ShutdownRecord[][][] = shutdownRecords;
      tempRecordInsert[furnacesIndex].push(records);
      setRecords(tempRecordInsert);
    });
  };

  const handleResultRegistChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      setSelectedFile(event.target.files[0]);
    }
  };

  const handleBurnMultipleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const targetValue = Number(event.target.value);
    const min = Number(event.target.min);
    const max = Number(event.target.max);
    // NaNを含め最小値、最大値丸め込み
    const clamped = Number.isNaN(targetValue) ? min : Math.min(max, Math.max(min, targetValue));
    const val = Math.floor(clamped); // 小数を切り捨てて整数のみ許容
    event.target.value = String(val);

    const furnacesIndex: number = event.target.dataset.furnaces ? Number(event.target.dataset.furnaces) : 0;
    let temp = { ...planMultipleBurn };
    temp[furnacesIndex].burn_load = val;
    temp[furnacesIndex].burn_volume = temp[furnacesIndex].burn_load === 0 ? 0 : Math.floor((val * conditionResource.rateCapacity) / 100);
    setPlanMultipleBurn(temp);
  };

  const handleBurnMultipleDateChange = (kind: string, value: string, furnacesIndex: number) => {
    let temp = { ...planMultipleBurn };
    if (kind === item_shutdown_date) {
      temp[furnacesIndex].shutdown_date = value;
    } else {
      temp[furnacesIndex].launch_date = value;
    }
    setPlanMultipleBurn(temp);
  };

  const handleBurnVolumeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const targetValue = Number(event.target.value);
    if (event.target.value.includes('.')) return;
    event.target.value = String(targetValue);
    const furnacesIndex: number = event.target.dataset.furnaces ? Number(event.target.dataset.furnaces) : 0;
    let temp = { ...planMultipleBurn };
    temp[furnacesIndex].burn_volume = targetValue;
    setPlanMultipleBurn(temp);
  };

  const handleHistoryRegist = async () => {
    // ファイル未選択エラー
    if (!selectedFile) {
      await showErrorMessage(MESSAGES.ERROR_NOTFOUND_FILE);
      return;
    } else if (selectedFile.name.indexOf(PAST_RESULT_FILE_NAME) < 0) {
      await showErrorMessage(`ファイル名に「${PAST_RESULT_FILE_NAME}」が含まれていません。`);
      return;
    }

    // 続行確認
    const result = await showConfirmMessage(MESSAGES.CONFIRM_POST_HISTORY);

    if (!result) {
      return;
    }

    // 成功すればメッセージ表示・ページリロード
    // 失敗すればエラーメッセージを表示する
    showSuccessMessage(MESSAGES.SUCCESS_POST_HISTORY, {
      showOnResolved: new Promise<MessageDialogOptions | void>((resolve) => {
        const reader = new FileReader();

        reader.onload = async (e) => {
          if (e.target && e.target.result) {
            const arrayBuffer = e.target.result as ArrayBuffer;
            const uint8Array = new Uint8Array(arrayBuffer);
            const requestUrl = `${REQ_PLAN_RESULT}?branchId=${String(sessionResource.branchId)}&furnaces=${String(
              sessionResource.furnacesCount,
            )}&requestParam=${POST_REQ_RESULT_REGISTER}`;
            const { payload: json } = await postRequestBinary<JsonResponse>(requestUrl, uint8Array);
            resolve();
          }
        };

        reader.readAsArrayBuffer(selectedFile); // ファイルの読み込みを開始
      }),
    })
      .then(() => {
        // 成功メッセージダイアログを閉じたらリロード
        setIsReload(true);
      })
      .catch(() => {
        // 過去実績登録失敗
        showErrorMessage(MESSAGES.ERROR_SERVER);
      });
  };

  const handleHistoryDisplay = () => {
    openNewTabComponent(
      <AppPane>
        <PastResult />
      </AppPane>,
      newWindowRef,
    );
  };

  const handleDraftPatternChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedPriorityId(Number(event.target.value));
    console.log(Number(event.target.value));
  };

  const handleExcelOutput = async () => {
    const result = await showConfirmMessage(MESSAGES.CONFIRM_OUTPUT_EXCEL);

    if (!result) {
      return;
    }

    // 「全ての案」が選択されている場合（-2）
    if (selectedPriorityId === -2) {
      const draftNumbers = [1, 2, 3];
      for (const draftNumber of draftNumbers) {
        const draft = excelOutputDraftPattern.find((dp) => dp.priority_id === draftNumber);
        if (!draft) continue;

        try {
          const requestUrl = `${REQ_PLAN_RESULT}?branchId=${String(sessionResource.branchId)}&selectDate=${getMinDate(displayYear)}&furnaces=${String(
            sessionResource.furnacesCount,
          )}&draftNumber=${draftNumber}&requestParam=${POST_REQ_EXCEL_OUTPUT}`;
          const uint8Array = new Uint8Array();
          const { payload: blob } = await postRequestBinary<BinaryResponse>(requestUrl, uint8Array);
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          const strYear: string = String(new Date().getFullYear());
          const strMonth: string = formatTwoDigits(new Date().getMonth() + 1);
          const strDate: string = formatTwoDigits(new Date().getDate());
          a.download = strYear.concat(strMonth).concat(strDate).concat(`_年間・月間運転計画_${draft.priority_name}.xlsx`);
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        } catch (e) {
          console.error(e);
        }
      }
    } else {
      try {
        // 期初計画の出力フラグ
        // selectedPriorityId:-3 ⇒期初計画の出力 -1 ⇒現行の計画出力
        const initialExportFlag = selectedPriorityId === -3 ? 1 : 0;
        const requestUrl = `${REQ_PLAN_RESULT}?branchId=${String(sessionResource.branchId)}&selectDate=${getMinDate(displayYear)}&furnaces=${String(
          sessionResource.furnacesCount,
        )}&draftNumber=${selectedPriorityId}&requestParam=${POST_REQ_EXCEL_OUTPUT}&initialExportFlag=${initialExportFlag}`;
        const draft = draftPattern.find((dp) => dp.priority_id === selectedPriorityId);
        const uint8Array = new Uint8Array();
        const { payload: blob } = await postRequestBinary<BinaryResponse>(requestUrl, uint8Array);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const strYear: string = String(new Date().getFullYear());
        const strMonth: string = formatTwoDigits(new Date().getMonth() + 1);
        const strDate: string = formatTwoDigits(new Date().getDate());
        if (draft && draft.priority_id > 0) {
          a.download = strYear.concat(strMonth).concat(strDate).concat(`_年間・月間運転計画_${draft.priority_name}.xlsx`);
        } else {
          a.download = strYear.concat(strMonth).concat(strDate).concat('_年間・月間運転計画.xlsx');
        }
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } catch (e) {
        console.error(e);
      }
    }
  };

  const handlePlanResultHistoryChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedselectedCreatedAt(event.target.value);
  };

  const handleHistoryExcelOutput = async () => {
    const result = await showConfirmMessage(MESSAGES.CONFIRM_OUTPUT_EXCEL);

    if (!result) {
      return;
    }

    try {
      const requestUrl = `${REQ_PLAN_RESULT}?branchId=${String(sessionResource.branchId)}&selectDate=${getMinDate(displayYear)}&furnaces=${String(
        sessionResource.furnacesCount,
      )}&createdAt=${selectedCreatedAt}&requestParam=${POST_REQ_EXCEL_HISTORY_OUTPUT}`;
      const uint8Array = new Uint8Array();
      const { payload: blob } = await postRequestBinary<BinaryResponse>(requestUrl, uint8Array);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const date = selectedCreatedAt.slice(0, 19).replace('T', ' ');
      a.download = date.concat('_年間・月間運転計画.xlsx');
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (e) {
      console.error(e);
    }
  };

  const handleInitialPlanTargetChange = async (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedInitialPlanTargetCreatedAt(event.target.value);
  };

  const handleInitialPlanSet = async () => {
    const result = await showConfirmMessage(MESSAGES.CONFIRM_SET_INITIAL_PLAN_SETTING);
    if (!result) {
      return;
    }
    executeInitialPlanSet();
  };

  const handleInitialPlanCancel = async () => {
    const result = await showConfirmMessage(MESSAGES.CONFIRM_CANCEL_INITIAL_PLAN_SETTING);
    if (!result) {
      return;
    }
    executeCancelInitialPlan();
  };

  const handleDraftBlurChange = (
    event: React.FocusEvent<HTMLInputElement>,
    draftIndex: number,
    driveIndex: number,
    monthIndex: number,
    colIndex: number,
    value: number,
  ): DraftContext[] => {
    const resources: DraftContext[] = structuredClone(draftEditResource);
    for (let i = 0; i < resources.length; i++) {
      resources[i].day[colIndex].carry_volume = value;
    }
    return resources;
  };

  const handleCommonBlurChange = <T extends { [key: string]: any }, EmptyContext extends { [key: string]: any }>(
    event: React.FocusEvent<HTMLInputElement>,
    valueState: [T, React.Dispatch<React.SetStateAction<T>>],
    emptyState: [EmptyContext, React.Dispatch<React.SetStateAction<EmptyContext>>],
    initResource: DraftContext,
    usage: DriveUsage,
    draftIndex: number,
    driveIndex: number,
    monthIndex: number,
    colIndex?: number,
    rowIndex?: number,
    updater?: DraftContextUpdater,
    previousValue?: number,
  ) => {
    const updaterPreviousValue: number | undefined = draftIndex === DraftMap.day && driveIndex === DriveMap.burn_volume ? previousValue : undefined;
    if (draftIndex === DraftMap.day) {
      const [valueResource, setValueResource] = valueState;
      const [emptyResource, setEmptyResource] = emptyState;
      const draftKey = Object.keys(valueResource)[draftIndex];
      const driveKey = Object.keys(valueResource[draftKey][0])[driveIndex];
      const daysResource = valueResource[Object.keys(valueResource)[DraftMap.day]];
      const daysNarrowResource = daysResource.slice(lastDayOfLastMonth, daySum(monthIndex));
      let valueSum: number = 0;
      for (let i = 0; i < daysNarrowResource.length; i++) {
        if (typeof rowIndex === 'number') {
          valueSum = valueSum + Number(daysNarrowResource[i][driveKey][rowIndex]);
        } else {
          valueSum = valueSum + Number(daysNarrowResource[i][driveKey]);
        }
      }
      const dayKey = Object.keys(valueResource)[DraftMap.day];
      const monthKey = Object.keys(valueResource)[DraftMap.month];
      const shutdownKey = Object.keys(valueResource)[DraftMap.shutdown];
      const monthsResource = [...valueResource[monthKey]];
      if (typeof rowIndex === 'number') {
        monthsResource[monthIndex - 1][driveKey][rowIndex] = valueSum;
        emptyResource[monthKey][monthIndex - 1][driveKey][rowIndex] = false;
      } else {
        monthsResource[monthIndex - 1][driveKey] = valueSum;
        emptyResource[monthKey][monthIndex - 1][driveKey] = false;
      }
      setEmptyResource(emptyResource);
      let context = new DraftContextObject(0, sessionResource.furnacesCount);
      context.day = [...valueResource[dayKey]];
      context.month = [...monthsResource];
      context.shutdown = [...valueResource[Object.keys(valueResource)[DraftMap.shutdown]]];
      let emptyContext: DraftEmpty = { day: emptyResource.day, month: emptyResource.month };
      if (updater) {
        if (typeof colIndex === 'number' && typeof rowIndex === 'number') {
          context = updater(context, initResource, emptyContext, usage, monthIndex, colIndex, rowIndex, updaterPreviousValue);
        } else if (typeof colIndex === 'number') {
          context = updater(context, initResource, emptyContext, usage, monthIndex, colIndex, 0, updaterPreviousValue);
        }
      }
      setValueResource({ ...valueResource, [monthKey]: context.month, [dayKey]: context.day, [shutdownKey]: context.shutdown });
    } else {
      const [valueResource, setValueResource] = valueState;
      let context = new DraftContextObject(0, sessionResource.furnacesCount);
      context.day = [...valueResource[Object.keys(valueResource)[DraftMap.day]]];
      context.month = [...valueResource[Object.keys(valueResource)[DraftMap.month]]];
      context.shutdown = [...valueResource[Object.keys(valueResource)[DraftMap.shutdown]]];
      const [emptyResource, setEmptyResource] = emptyState;
      let emptyContext: DraftEmpty = { day: emptyResource.day, month: emptyResource.month };
      if (updater) {
        if (typeof colIndex === 'number' && typeof rowIndex === 'number') {
          context = updater(context, initResource, emptyContext, usage, monthIndex, colIndex, rowIndex, updaterPreviousValue);
        } else if (typeof colIndex === 'number') {
          context = updater(context, initResource, emptyContext, usage, monthIndex, colIndex, 0, updaterPreviousValue);
        }
      }
      setValueResource({
        ...valueResource,
        [Object.keys(valueResource)[DraftMap.month]]: context.month,
        [Object.keys(valueResource)[DraftMap.day]]: context.day,
        [Object.keys(valueResource)[DraftMap.shutdown]]: context.shutdown,
      });
    }
  };

  function getConditionResource(resource: ConditionContext, originData: any): ConditionContext {
    resource.pitLimit = 'pit_limit' in originData ? Number(originData.pit_limit) : resource.pitLimit;
    resource.pitMin = 'pit_min' in originData ? Number(originData.pit_min) : resource.pitMin;
    resource.pitMax = 'pit_max' in originData ? Number(originData.pit_max) : resource.pitMax;
    resource.ashPitCapacity = 'ash_pit_capacity' in originData ? Number(originData.ash_pit_capacity) : resource.ashPitCapacity;
    resource.rateCapacity = 'rate_capacity' in originData ? Number(originData.rate_capacity) : resource.rateCapacity;
    resource.trashDensity = 'trash_density' in originData ? Number(originData.trash_density) : resource.trashDensity;
    resource.mainAshRate = 'ash_rate' in originData ? Number(originData.ash_rate) : resource.mainAshRate;
    resource.flyAshRate = 'fly_ash_rate' in originData ? Number(originData.fly_ash_rate) : resource.flyAshRate;
    resource.truckMainAshCarryOut = 'truck_main_ash_carry_out' in originData ? Number(originData.truck_main_ash_carry_out) : resource.truckMainAshCarryOut;
    resource.truckFlyAshCarryOut = 'truck_fly_ash_carry_out' in originData ? Number(originData.truck_fly_ash_carry_out) : resource.truckFlyAshCarryOut;

    if ('shutdown_launch_hour' in originData) {
      const shutdownLaunchHour: number[] = [] as number[];
      for (let i = 0; i < originData.shutdown_launch_hour.length; i++) {
        shutdownLaunchHour.push(Number(originData.shutdown_launch_hour[i]));
      }
      resource.shutdownLaunchHour = shutdownLaunchHour;
    }
    if ('shutdown_burn_hours' in originData) {
      const shutdownBurnHours: number[] = [] as number[];
      for (let i = 0; i < originData.shutdown_burn_hours.length; i++) {
        shutdownBurnHours.push(Number(originData.shutdown_burn_hours[i]));
      }
      resource.shutdownBurnHours = shutdownBurnHours;
    }
    if ('shutdown_electric_create_hours' in originData) {
      const shutdownElectricCreateHours: number[] = [] as number[];
      for (let i = 0; i < originData.shutdown_electric_create_hours.length; i++) {
        shutdownElectricCreateHours.push(Number(originData.shutdown_electric_create_hours[i]));
      }
      resource.shutdownElectricCreateHours = shutdownElectricCreateHours;
    }

    if ('weekly_carry_calculation' in originData) {
      const carryCalculationArray: number[] = [] as number[];
      for (let i = 0; i < originData.weekly_carry_calculation.length; i++) {
        carryCalculationArray.push(Number(originData.weekly_carry_calculation[i]));
      }
      resource.weeklyCarryCalculation = carryCalculationArray;
    }
    return resource;
  }

  const getDriveContext = (driveResource: any, furnacesCount: number, useDigit: number): DriveContext => {
    const resourceDrive: DriveContext = new DriveContextObject(0, furnacesCount);
    if (driveResource && driveResource.hasOwnProperty('planDate')) {
      resourceDrive.date = driveResource.planDate.split(' ')[0];
    } else if (driveResource && driveResource.hasOwnProperty('resultDate')) {
      resourceDrive.date = driveResource.resultDate.split(' ')[0];
    } else if (driveResource && driveResource.hasOwnProperty('draftDate')) {
      resourceDrive.date = driveResource.draftDate.split(' ')[0];
    }
    resourceDrive.carry_volume = calcMathRound(Number(driveResource.carryVolume ?? 0), useDigit);
    for (let i = 0; i < furnacesCount; i++) {
      resourceDrive.burn_volume[i] = calcMathRound(Number(driveResource.burnVolume[i]), useDigit);
      if (driveResource.wasteLowerHeatingValue) {
        resourceDrive.waste_lower_heating_value[i] = calcMathRound(Number(driveResource.wasteLowerHeatingValue[i] ?? 0), useDigit);
      }
    }
    resourceDrive.electric_create = calcMathRound(Number(driveResource.electricCreate ?? 0), useDigit);
    resourceDrive.electric_use = calcMathRound(Number(driveResource.electricUse ?? 0), useDigit);
    resourceDrive.plant_power_high_voltage_main_energy = calcMathRound(Number(driveResource.plantPowerHighVoltageMainEnergy ?? 0), useDigit);
    resourceDrive.building_power_high_voltage_main_energy = calcMathRound(Number(driveResource.buildingPowerHighVoltageMainEnergy ?? 0), useDigit);
    resourceDrive.lighting_power_high_voltage_main_energy = calcMathRound(Number(driveResource.lightingPowerHighVoltageMainEnergy ?? 0), useDigit);
    resourceDrive.security_power_high_voltage_main_energy = calcMathRound(Number(driveResource.securityPowerHighVoltageMainEnergy ?? 0), useDigit);
    resourceDrive.recycle_emergency_low_voltage_power_energy = calcMathRound(Number(driveResource.recycleEmergencyLowVoltagePowerEnergy ?? 0), useDigit);
    resourceDrive.electric_sale = calcMathRound(Number(driveResource.electricSale ?? 0), useDigit);
    resourceDrive.electric_buy = calcMathRound(Number(driveResource.electricBuy ?? 0), useDigit);
    resourceDrive.hot_water_supply_heating_value = calcMathRound(Number(driveResource.hotWaterSupplyHeatingValue ?? 0), useDigit);
    resourceDrive.main_ash_generate = calcMathRound(Number(driveResource.mainAshGenerate ?? 0), useDigit);
    resourceDrive.fly_ash_generate = calcMathRound(Number(driveResource.flyAshGenerate ?? 0), useDigit);
    resourceDrive.main_ash_carry_out = calcMathRound(Number(driveResource.mainAshCarryOut ?? 0), useDigit);
    resourceDrive.fly_ash_carry_out = calcMathRound(Number(driveResource.flyAshCarryOut ?? 0), useDigit);
    resourceDrive.main_ash_carry_out_units = calcMathRound(Number(driveResource.mainAshCarryOutUnits ?? 0), useDigit);
    resourceDrive.fly_ash_carry_out_units = calcMathRound(Number(driveResource.flyAshCarryOutUnits ?? 0), useDigit);
    resourceDrive.trash_pit_rest = calcMathRound(Number(driveResource.trashPitRest), useDigit);
    resourceDrive.main_ash_pit_rest = calcMathRound(Number(driveResource.mainAshPitRest ?? 0), useDigit);
    resourceDrive.fly_ash_pit_rest = calcMathRound(Number(driveResource.flyAshPitRest ?? 0), useDigit);
    resourceDrive.origin_electric_create = calcMathRound(Number(driveResource.originElectricCreate ?? 0), useDigit);
    resourceDrive.combustible_waste_carry_volume = calcMathRound(Number(driveResource.combustibleWasteCarryVolume ?? 0), useDigit);
    resourceDrive.pruning_branches_carry_volume = calcMathRound(Number(driveResource.pruningBranchesCarryVolume ?? 0), useDigit);
    resourceDrive.small_animals_carry_volume = calcMathRound(Number(driveResource.smallAnimalsCarryVolume ?? 0), useDigit);
    resourceDrive.combustible_bulk_waste_carry_volume = calcMathRound(Number(driveResource.combustibleBulkWasteCarryVolume ?? 0), useDigit);
    resourceDrive.shredded_waste_residue_carry_volume = calcMathRound(Number(driveResource.shreddedWasteResidueCarryVolume ?? 0), useDigit);
    resourceDrive.disaster_waste_internal_carry_volume = calcMathRound(Number(driveResource.disasterWasteInternalCarryVolume ?? 0), useDigit);
    resourceDrive.disaster_waste_external_carry_volume = calcMathRound(Number(driveResource.disasterWasteExternalCarryVolume ?? 0), useDigit);
    resourceDrive.other_carry_volume = calcMathRound(Number(driveResource.otherCarryVolume ?? 0), useDigit);
    resourceDrive.incineration_facility_bottom_ash_carry_volume = calcMathRound(Number(driveResource.incinerationFacilityBottomAshCarryVolume ?? 0), useDigit);
    resourceDrive.recyclables_carry_volume = calcMathRound(Number(driveResource.recyclablesCarryVolume ?? 0), useDigit);
    resourceDrive.dry_fly_ash_carry_out = calcMathRound(Number(driveResource.dryFlyAshCarryOut ?? 0), useDigit);
    resourceDrive.processed_fly_ash_carry_out = calcMathRound(Number(driveResource.processedFlyAshCarryOut ?? 0), useDigit);
    return resourceDrive;
  };

  const getDriveMonthContext = (driveResource: any, furnacesCount: number, useDigit: number): DriveContext => {
    const resourceDrive: DriveContext = new DriveContextObject(0, furnacesCount);
    if (driveResource && driveResource.hasOwnProperty('planMonthDate')) {
      resourceDrive.date = driveResource.planMonthDate.split(' ')[0];
    } else if (driveResource && driveResource.hasOwnProperty('resultMonthDate')) {
      resourceDrive.date = driveResource.resultMonthDate.split(' ')[0];
    } else if (driveResource && driveResource.hasOwnProperty('draftMonthDate')) {
      resourceDrive.date = driveResource.draftMonthDate.split(' ')[0];
    }
    resourceDrive.carry_volume = calcMathRound(Number(driveResource.carryVolume ?? 0), useDigit);
    for (let i = 0; i < furnacesCount; i++) {
      resourceDrive.burn_volume[i] = calcMathRound(Number(driveResource.burnVolume[i]), useDigit);
      if (driveResource.wasteLowerHeatingValue) {
        resourceDrive.waste_lower_heating_value[i] = calcMathRound(Number(driveResource.wasteLowerHeatingValue[i] ?? 0), useDigit);
      }
    }
    resourceDrive.electric_create = calcMathRound(Number(driveResource.electricCreate ?? 0), useDigit);
    resourceDrive.electric_use = calcMathRound(Number(driveResource.electricUse ?? 0), useDigit);
    resourceDrive.plant_power_high_voltage_main_energy = calcMathRound(Number(driveResource.plantPowerHighVoltageMainEnergy ?? 0), useDigit);
    resourceDrive.building_power_high_voltage_main_energy = calcMathRound(Number(driveResource.buildingPowerHighVoltageMainEnergy ?? 0), useDigit);
    resourceDrive.lighting_power_high_voltage_main_energy = calcMathRound(Number(driveResource.lightingPowerHighVoltageMainEnergy ?? 0), useDigit);
    resourceDrive.security_power_high_voltage_main_energy = calcMathRound(Number(driveResource.securityPowerHighVoltageMainEnergy ?? 0), useDigit);
    resourceDrive.recycle_emergency_low_voltage_power_energy = calcMathRound(Number(driveResource.recycleEmergencyLowVoltagePowerEnergy ?? 0), useDigit);
    resourceDrive.electric_sale = calcMathRound(Number(driveResource.electricSale ?? 0), useDigit);
    resourceDrive.electric_buy = calcMathRound(Number(driveResource.electricBuy ?? 0), useDigit);
    resourceDrive.hot_water_supply_heating_value = calcMathRound(Number(driveResource.hotWaterSupplyHeatingValue ?? 0), useDigit);
    resourceDrive.main_ash_generate = calcMathRound(Number(driveResource.mainAshGenerate ?? 0), useDigit);
    resourceDrive.fly_ash_generate = calcMathRound(Number(driveResource.flyAshGenerate ?? 0), useDigit);
    resourceDrive.main_ash_carry_out = calcMathRound(Number(driveResource.mainAshCarryOut ?? 0), useDigit);
    resourceDrive.fly_ash_carry_out = calcMathRound(Number(driveResource.flyAshCarryOut ?? 0), useDigit);
    resourceDrive.main_ash_carry_out_units = calcMathRound(Number(driveResource.mainAshCarryOutUnits ?? 0), useDigit);
    resourceDrive.fly_ash_carry_out_units = calcMathRound(Number(driveResource.flyAshCarryOutUnits ?? 0), useDigit);
    resourceDrive.trash_pit_rest = calcMathRound(Number(driveResource.trashPitRest), useDigit);
    resourceDrive.origin_electric_create = calcMathRound(Number(driveResource.originElectricCreate ?? 0), useDigit);
    resourceDrive.combustible_waste_carry_volume = calcMathRound(Number(driveResource.combustibleWasteCarryVolume ?? 0), useDigit);
    resourceDrive.pruning_branches_carry_volume = calcMathRound(Number(driveResource.pruningBranchesCarryVolume ?? 0), useDigit);
    resourceDrive.small_animals_carry_volume = calcMathRound(Number(driveResource.smallAnimalsCarryVolume ?? 0), useDigit);
    resourceDrive.combustible_bulk_waste_carry_volume = calcMathRound(Number(driveResource.combustibleBulkWasteCarryVolume ?? 0), useDigit);
    resourceDrive.shredded_waste_residue_carry_volume = calcMathRound(Number(driveResource.shreddedWasteResidueCarryVolume ?? 0), useDigit);
    resourceDrive.disaster_waste_internal_carry_volume = calcMathRound(Number(driveResource.disasterWasteInternalCarryVolume ?? 0), useDigit);
    resourceDrive.disaster_waste_external_carry_volume = calcMathRound(Number(driveResource.disasterWasteExternalCarryVolume ?? 0), useDigit);
    resourceDrive.other_carry_volume = calcMathRound(Number(driveResource.otherCarryVolume ?? 0), useDigit);
    resourceDrive.incineration_facility_bottom_ash_carry_volume = calcMathRound(Number(driveResource.incinerationFacilityBottomAshCarryVolume ?? 0), useDigit);
    resourceDrive.recyclables_carry_volume = calcMathRound(Number(driveResource.recyclablesCarryVolume ?? 0), useDigit);
    resourceDrive.dry_fly_ash_carry_out = calcMathRound(Number(driveResource.dryFlyAshCarryOut ?? 0), useDigit);
    resourceDrive.processed_fly_ash_carry_out = calcMathRound(Number(driveResource.processedFlyAshCarryOut ?? 0), useDigit);
    return resourceDrive;
  };

  const getDayContext = (shutdownArray: any): DayContext => {
    const resourceDrive: DayContext = new DayContextObject();
    resourceDrive.shutdown_date = shutdownArray.shutdownDate.split(' ')[0];
    resourceDrive.launch_date = shutdownArray.launchDate.split(' ')[0];
    resourceDrive.not_change_flag = shutdownArray.notChangeFlag;
    return resourceDrive;
  };

  function getDraftContext(planDay: any, planMonth: any, planShutDown: any, dateArray: string[], dayAllSum: number, furnacesCount: number, useDigit: number): DraftContext {
    const resourceDraft: DraftContext = new DraftContextObject(0, furnacesCount);
    const dayArray: DriveContext[] = [] as DriveContext[];
    let dateIndex: number = 0;
    for (let i = 0; i < dayAllSum; i++) {
      let date: string = '';
      if (planDay.length <= dateIndex) {
        date = '';
      } else if (planDay[dateIndex] && planDay[dateIndex].hasOwnProperty('planDate')) {
        date = planDay[dateIndex].planDate.split(' ')[0];
      } else if (planDay[dateIndex] && planDay[dateIndex].hasOwnProperty('resultDate')) {
        date = planDay[dateIndex].resultDate.split(' ')[0];
      } else if (planDay[dateIndex] && planDay[dateIndex].hasOwnProperty('draftDate')) {
        date = planDay[dateIndex].draftDate.split(' ')[0];
      }
      if (dateArray[i] === date) {
        dayArray.push(getDriveContext(planDay[dateIndex], furnacesCount, useDigit));
        dateIndex = dateIndex + 1;
      } else {
        dayArray.push(new DriveContextObject(0, furnacesCount));
      }
    }
    resourceDraft.day = dayArray;
    const monthArray: DriveContext[] = [] as DriveContext[];
    let monthIndex: number = 0;
    for (let i = 0; i < months.length; i++) {
      let date: string = '';
      if (planMonth.length <= monthIndex) {
        date = '';
      } else if (planMonth[monthIndex] && planMonth[monthIndex].hasOwnProperty('planMonthDate')) {
        date = planMonth[monthIndex].planMonthDate.split(' ')[0];
      } else if (planMonth[monthIndex] && planMonth[monthIndex].hasOwnProperty('resultMonthDate')) {
        date = planMonth[monthIndex].resultMonthDate.split(' ')[0];
      } else if (planMonth[monthIndex] && planMonth[monthIndex].hasOwnProperty('draftMonthDate')) {
        date = planMonth[monthIndex].draftMonthDate.split(' ')[0];
      }
      const monthStr: string = formatTwoDigits(Number(date.split('-')[1])).trim();
      if (formatTwoDigits(months[i]).trim() === monthStr.trim()) {
        monthArray.push(getDriveMonthContext(planMonth[monthIndex], furnacesCount, useDigit));
        monthIndex = monthIndex + 1;
      } else {
        monthArray.push(new DriveContextObject(0, furnacesCount));
      }
    }
    resourceDraft.month = monthArray;
    const shutdownArray: DayContext[][] = [] as DayContext[][];
    if (planShutDown.length === 0) {
      for (let furnacesIndex = 0; furnacesIndex < furnacesCount; furnacesIndex++) {
        const furnacesDriveArray: DayContext[] = [] as DayContext[];
        shutdownArray.push(furnacesDriveArray);
      }
    } else {
      for (let furnacesIndex = 0; furnacesIndex < Math.min(planShutDown.length, furnacesCount); furnacesIndex++) {
        const furnacesDriveArray: DayContext[] = [] as DayContext[];
        for (let i = 0; i < planShutDown[furnacesIndex].length; i++) {
          furnacesDriveArray.push(getDayContext(planShutDown[furnacesIndex][i]));
        }
        shutdownArray.push(furnacesDriveArray);
      }
    }
    resourceDraft.shutdown = shutdownArray;
    return resourceDraft;
  }

  function getEmptyCells(driveContext: DriveContext[], furnacesCount: number, usage: DriveUsage): DriveEmpty[] {
    const emptyCells: DriveEmpty[] = [];
    for (let i = 0; i < driveContext.length; i++) {
      const obj: DriveEmpty = new DriveEmptyObject(driveContext[i].date === '', furnacesCount);
      for (let j = 0; j < OPTIONAL_KEYS.length; j++) {
        if (!usage[OPTIONAL_KEYS[j]]) {
          (obj as any)[OPTIONAL_KEYS[j]] = true;
        }
      }
      if (!usage['waste_lower_heating_value']) {
        for (let furnacesIndex = 0; furnacesIndex < furnacesCount; furnacesIndex++) {
          obj.waste_lower_heating_value[furnacesIndex] = true;
        }
      }
      emptyCells.push(obj);
    }
    return emptyCells;
  }

  const fiscalYearOf = (d: Date) => (d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1);

  function getDisableDayCells(driveContext: DriveEmpty[], furnacesCount: number, year: number, days: number[]): DriveEmpty[] {
    const today: Date = new Date();
    const todayFiscalYear = fiscalYearOf(today);
    if (year !== todayFiscalYear) return driveContext;

    const systemDate: string = getStrDateCalendar(today.getFullYear(), today.getMonth() + 1, today.getDate());
    const systemDayIndex = getDateIndex(systemDate, year, days);
    for (let i = 0; i < driveContext.length; i++) {
      if (i < systemDayIndex) {
        driveContext[i] = new DriveEmptyObject(true, furnacesCount);
      }
    }
    return driveContext;
  }

  function getDisableMonthCells(driveContext: DriveEmpty[], furnacesCount: number, year: number): DriveEmpty[] {
    const today: Date = new Date();
    const todayFiscalYear = fiscalYearOf(today);
    if (year !== todayFiscalYear) return driveContext;
    const systemMonthIndex = (today.getMonth() - 3 + 12) % 12;
    for (let i = 0; i < driveContext.length; i++) {
      if (i < systemMonthIndex) {
        driveContext[i] = new DriveEmptyObject(true, furnacesCount);
      }
    }
    return driveContext;
  }

  function ResetResource(
    furnacesCount: number,
    initResource: DraftContext,
    SetEditResource: React.Dispatch<React.SetStateAction<DraftContext>>,
    SetEmptyResource: React.Dispatch<React.SetStateAction<DraftEmpty>>,
    dayUsage: DriveUsage,
    monthUsage: DriveUsage,
  ): void {
    SetEditResource({ ...structuredClone(initResource) });
    SetEmptyResource(() => {
      const updated = new DraftEmptyObject(false, furnacesCount);
      updated.day = getEmptyCells(initResource.day, furnacesCount, dayUsage);
      updated.month = getEmptyCells(initResource.month, furnacesCount, monthUsage);
      return updated;
    });
  }

  //計算
  function calcItemSum(planData: DriveContext[], key: keyof DriveContext): number {
    let val: number[] = selectItemArray(planData, key);
    let sumVal: number = sumArray(val);
    return sumVal;
  }

  function calcItemBurnSum(planData: DriveContext[], furnacesIndex: number): number {
    let val: number[] = selectItemFurnaceArray(planData, 'burn_volume', furnacesIndex);
    let sumVal: number = sumArray(val);
    return sumVal;
  }

  function calcBurnFurnacesSum(planData: DriveContext): number {
    let sumVal: number = sumArray(planData.burn_volume.slice(0, sessionResource.furnacesCount + 1));
    return sumVal;
  }

  function calcBurnload(burnVolume: number, operationDays: number) {
    const rateCapacity: number = conditionResource.rateCapacity;
    if (rateCapacity === 0 || operationDays === 0) {
      return 0;
    }
    const burnload: number = (burnVolume * 100) / (operationDays * rateCapacity);
    return burnload;
  }

  function calcPit(planData: DriveContext[], usage: DriveUsage, prePitValue: number, firstDayIndex: number): DriveContext[] {
    let prePitRest: number = prePitValue;
    for (let i = firstDayIndex; i < planData.length; i++) {
      planData[i].trash_pit_rest = calcMathRound(prePitRest + getMonitorList(planData, usage)[i].total_carry_volume - calcBurnFurnacesSum(planData[i]), TRASH_PIT_DIGIT);
      prePitRest = planData[i].trash_pit_rest;
    }
    return planData;
  }

  function calcAsh(
    editResource: DriveContext[],
    initResource: DriveContext[],
    emptyResource: DriveEmpty[],
    usage: DriveUsage,
    keyGenerate: NumberKeys<DriveContext>,
    keyCarryOutSum: NumberKeys<MonitorContext>,
    keyUnit: NumberKeys<DriveContext>,
    keyRest: NumberKeys<DriveContext>,
    ashRate: number,
    truckAshCarryVolume: number,
    firstDayIndex: number,
    ashUnitUpdateFlg: boolean = true,
  ): DriveContext[] {
    const firstDay: DriveContext = structuredClone(initResource[firstDayIndex]);
    // 前日分が編集不可ならば、当日分の初期データを使用して前日のピット残量を求める
    // 再立案した日は当日分の初期データを使用する ただし、再立案した日より前の搬入量・焼却量が編集されてピット残量が再計算された場合は除く
    // 上記の条件以外は、前日分の編集データを使用して前日のピット残量を求める
    const initPrePitRest: number =
      firstDayIndex === 0 ||
      firstDayIndex === systemDateIndex ||
      (firstDayIndex === getDateIndex(autoDraftBaseDate, displayYear, displayDays) &&
        firstDayIndex !== 0 &&
        initResource[firstDayIndex - 1][keyRest] === editResource[firstDayIndex - 1][keyRest])
        ? firstDay[keyRest] - firstDay[keyGenerate] + calcMonitorItem(firstDay, usage, keyCarryOutSum)
        : structuredClone(editResource[firstDayIndex - 1])[keyRest];
    let prePitRest: number = initPrePitRest;
    for (let i = firstDayIndex; i < editResource.length; i++) {
      if (!emptyResource[i].burn_volume.some((value) => value === true)) {
        const ashGenerate: number = calcMathRound(calcBurnFurnacesSum(editResource[i]) * ashRate, ASH_PIT_DIGIT);
        const ashCarryOutSum: number = calcMathRound(calcMonitorItem(editResource[i], usage, keyCarryOutSum), ASH_PIT_DIGIT);
        const nextPitRest: number = Math.max(0, prePitRest + ashGenerate - ashCarryOutSum);
        editResource[i][keyGenerate] = ashGenerate;
        editResource[i][keyRest] = nextPitRest;
        if (ashUnitUpdateFlg) {
          const ashUnit: number = Math.ceil(ashCarryOutSum / truckAshCarryVolume);
          editResource[i][keyUnit] = ashUnit;
        }
        prePitRest = nextPitRest;
      }
    }
    return editResource;
  }

  function calcElectric(planData: DriveContext[], usage: DriveUsage, oldBurnArray: number[], dateIndex: number): DriveContext[] {
    const currentBurnArray: number[] = structuredClone(planData[dateIndex].burn_volume);
    const oldLaunchCount: number = oldBurnArray.filter((burnVolume) => burnVolume > 0).length;
    const currentLaunchCount: number = currentBurnArray.filter((burnVolume) => burnVolume > 0).length;
    let electricCreate: number = 0;
    if (currentLaunchCount === 0) {
      electricCreate = 0;
    } else if (oldLaunchCount === currentLaunchCount) {
      const oldSumBurn: number = sumArray(oldBurnArray);
      const sumBurn: number = sumArray(currentBurnArray);
      // 計画電力量が0の場合は、基準電力量を元に稼働炉数に合わせた電力量に再設定する
      if (planData[dateIndex].electric_create === 0) {
        electricCreate = conditionResource.electricCreate[dateIndex][currentLaunchCount - 1] * (sumBurn / (conditionResource.rateCapacity * currentLaunchCount));
      } else {
        electricCreate = planData[dateIndex].electric_create * (sumBurn / oldSumBurn);
      }
    } else {
      // 稼働炉数が変わった場合、基準電力量を元に稼働炉数に合わせた電力量に再設定する
      const sumBurn: number = sumArray(currentBurnArray);
      electricCreate = conditionResource.electricCreate[dateIndex][currentLaunchCount - 1] * (sumBurn / (conditionResource.rateCapacity * currentLaunchCount));
    }
    const useBuy: number = conditionResource.electricUse[dateIndex][currentLaunchCount] - electricCreate;
    planData[dateIndex].electric_create = electricCreate;
    planData[dateIndex].electric_buy = useBuy >= 0 ? Math.abs(useBuy) : 0;
    planData[dateIndex].electric_sale = useBuy < 0 ? Math.abs(useBuy) : 0;
    return planData;
  }

  function calcCarryRate(dayDatas: DriveContext[], colIndex: number, value: number): DriveContext[] {
    const retDays: DriveContext[] = [...dayDatas];
    let denominator: number = 0;
    for (let i = 0; i < displayDays[colIndex]; i++) {
      let targetDate = new Date(getYear(colIndex), months[colIndex] - 1, 1 + i, 9);
      denominator = calcDecimalSum(denominator, conditionResource.weeklyCarryCalculation[targetDate.getDay()]);
    }
    let addSum: number = 0;
    const offsetStart: number = daySum(colIndex);
    for (let i = 0; i < displayDays[colIndex]; i++) {
      let targetDate = new Date(getYear(colIndex), months[colIndex] - 1, 1 + i, 9);
      if (denominator !== 0) {
        retDays[i + offsetStart].carry_volume = calcMathRound(
          (value * conditionResource.weeklyCarryCalculation[targetDate.getDay()]) / denominator,
          DRIVE_INPUT_DIGITS['carry_volume'],
        );
      } else {
        retDays[i + offsetStart].carry_volume = 0;
      }
      addSum += retDays[i + offsetStart].carry_volume;
    }
    // 月末の値は月入力=月末までの入力+月末の値となるように調整する
    // 月末が搬入量0の曜日ならば、月末に近く搬入量が0でない日に調整する
    for (let i = displayDays[colIndex] - 1; i > 0; i--) {
      const targetDate = new Date(getYear(colIndex), months[colIndex] - 1, 1 + i, 9);
      if (conditionResource.weeklyCarryCalculation[targetDate.getDay()] !== 0) {
        retDays[i + offsetStart].carry_volume = calcMathRound(retDays[i + offsetStart].carry_volume + (value - addSum), DRIVE_INPUT_DIGITS['carry_volume']);
        break;
      }
    }
    return retDays;
  }

  function convertMWhArray(valueArray: number[]): number[] {
    const retArray: number[] = [] as number[];
    for (let i = 0; i < valueArray.length; i++) {
      retArray.push(calcMWh(valueArray[i]));
    }
    return retArray;
  }

  function calcMWh(value: number): number {
    const ret: number = Math.round((value / 1000) * 100) / 100;
    return ret;
  }

  function calcStopArray(planDays: DayContext[]): boolean[] {
    const yearNum: number = displayYear;
    const yearDays: string[] = createDateInYear(yearNum, displayDays);
    const retDays: DayContext[] = [...planDays];
    const inPeriodShutdownDays: DayContext[] = excludeOutOfShutdownDays(retDays, yearNum, getMinDate(yearNum), getMaxDate(yearNum));
    let isStopFlag: boolean = false;
    const stopDays: boolean[] = [] as boolean[];
    let stopCount: number = 0;
    // // 停止期間はいつか
    for (let i = 0; i < yearDays.length; i++) {
      if (stopCount < inPeriodShutdownDays.length) {
        if (yearDays[i] === inPeriodShutdownDays[stopCount].shutdown_date) {
          isStopFlag = true;
        } else if (yearDays[i] === inPeriodShutdownDays[stopCount].launch_date) {
          isStopFlag = false;
          stopCount = stopCount + 1;
        }
        stopDays.push(isStopFlag);
      } else {
        stopDays.push(false);
      }
    }
    return stopDays;
  }

  function sortShutdown(day: DayContext[]): DayContext[] {
    let tempInsert = [...day];
    tempInsert = tempInsert.sort((a, b) => {
      const dateA = new Date(a.shutdown_date);
      const dateB = new Date(b.shutdown_date);
      return dateA.getTime() - dateB.getTime(); // 昇順
    });
    return tempInsert;
  }

  function calcBurnStopShutdown(
    [warningVals, setWarningVals]: [boolean[][], React.Dispatch<React.SetStateAction<boolean[][]>>],
    planData: DraftContext,
    initResource: DraftContext,
    emptyResource: DraftEmpty,
    usage: DriveUsage,
    furnaceIndex: number,
  ): DraftContext {
    // 停止期間はいつか
    const stopArray: boolean[] = calcStopArray(planData.shutdown[furnaceIndex]);
    const tempWarnings: boolean[][] = structuredClone(warningVals);
    const totalDays: number = Math.min(daySum(displayDays.length), planData.day.length, emptyResource.day.length, stopArray.length);
    for (let monIndex = 0; monIndex < displayDays.length; monIndex++) {
      for (let colIndex = 0; colIndex < displayDays[monIndex]; colIndex++) {
        const dayIndex: number = daySum(monIndex) + colIndex;
        if (dayIndex >= totalDays) {
          break;
        }
        if (emptyResource.day[dayIndex].burn_volume[furnaceIndex]) {
          // 焼却量が未設定の日は焼却量を上書きしない
          continue;
        }
        if (stopArray[dayIndex]) {
          if (tempWarnings[furnaceIndex][dayIndex]) {
            tempWarnings[furnaceIndex][dayIndex] = false;
          }
          if (planData.day[dayIndex].burn_volume[furnaceIndex] !== 0) {
            const previousBurnArray: number[] = structuredClone(planData.day[colIndex + daySum(monIndex)].burn_volume);
            planData.day[colIndex + daySum(monIndex)].burn_volume[furnaceIndex] = 0;
            planData = updateBurnDependent(planData, initResource, emptyResource, usage, monIndex + 1, colIndex, furnaceIndex, undefined, previousBurnArray);
          }
        } else if (planData.day[dayIndex].burn_volume[furnaceIndex] === 0) {
          let launchCount: number = 0;
          for (let iFurnace = 0; iFurnace < sessionResource.furnacesCount; iFurnace++) {
            if (iFurnace === furnaceIndex || planData.day[dayIndex].burn_volume[iFurnace] > 0) {
              launchCount = launchCount + 1;
            }
          }
          for (let iFurnace = 0; iFurnace < sessionResource.furnacesCount; iFurnace++) {
            if (iFurnace === furnaceIndex || planData.day[dayIndex].burn_volume[iFurnace] >= conditionResource.burnloadCapacity[launchCount - 1]) {
              tempWarnings[iFurnace][dayIndex] = true;
            }
          }
        }
      }
    }
    setWarningVals(tempWarnings);

    let warningIndex: number = 0;
    for (let monIndex = 0; monIndex < displayDays.length; monIndex++) {
      for (let colIndex = 0; colIndex < displayDays[monIndex]; colIndex++) {
        if (warningIndex >= totalDays) {
          break;
        }
        let launchCount: number = 0;
        for (let iFurnace = 0; iFurnace < sessionResource.furnacesCount; iFurnace++) {
          if (iFurnace === furnaceIndex && tempWarnings[iFurnace][warningIndex]) {
            launchCount = launchCount + 1; // 後続処理で運転処理量が設定されるため
          } else if (planData.day[warningIndex].burn_volume[iFurnace] > 0) {
            launchCount = launchCount + 1;
          }
        }
        for (let iFurnace = 0; iFurnace < sessionResource.furnacesCount; iFurnace++) {
          if (tempWarnings[iFurnace][warningIndex]) {
            const previousBurnArray: number[] = structuredClone(planData.day[colIndex + daySum(monIndex)].burn_volume);
            planData.day[colIndex + daySum(monIndex)].burn_volume[iFurnace] = conditionResource.burnloadCapacity[launchCount - 1];
            planData = updateBurnDependent(planData, initResource, emptyResource, usage, monIndex + 1, colIndex, iFurnace, undefined, previousBurnArray);
          }
        }
        warningIndex = warningIndex + 1;
      }
    }
    return planData;
  }

  function getYear(monIndex: number): number {
    // [0] = 4月
    const numYear: number = getCalendarYear(displayYear, months[monIndex]);
    return numYear;
  }

  const getMonthData = (dayDatas: DriveContext[]): DriveContext[] => {
    return dayDatas.slice(lastDayOfLastMonth, daySum(selectMonthIndex));
  };

  function calcMonthIndex(dayIndex: number): number {
    for (let i = 0; i < displayDays.length; i++) {
      if (dayIndex < daySum(i + 1)) {
        return i;
      }
    }
    return 0;
  }

  function calcDayIndex(dayIndex: number): number {
    for (let i = 0; i < displayDays.length; i++) {
      if (dayIndex < daySum(i + 1)) {
        if (i === 0) {
          return dayIndex;
        } else {
          return dayIndex - daySum(i);
        }
      }
    }
    return 0;
  }

  const shutDownSums = (itemStr: string, lShutdown: DayContext[]): string[] => {
    if (!lShutdown) {
      return [''];
    }
    const startTime: number = new Date(getMinDate(displayYear)).getTime();
    const endTime: number = new Date(getMaxDate(displayYear)).getTime();
    let retArray: string[] = [];
    lShutdown.forEach((day) => {
      if (itemStr === item_shutdown_date) {
        const shutdownTime: number = new Date(day.shutdown_date).getTime();
        if (startTime <= shutdownTime && shutdownTime <= endTime) {
          retArray.push(day.shutdown_date);
        }
      } else {
        const launchTime: number = new Date(day.launch_date).getTime();
        if (startTime <= launchTime && launchTime <= endTime) {
          retArray.push(day.launch_date);
        }
      }
    });
    const sortedDates = retArray.sort((a, b) => {
      const dateA = new Date(a);
      const dateB = new Date(b);
      return dateA.getTime() - dateB.getTime(); // 昇順
    });
    return sortedDates;
  };

  function itemDraftVolumes(monthContexts: DriveContext[], dayContexts: DriveContext[], itemKey: keyof DriveContext): number[] {
    let retArray: number[] = [];
    if (isMonthDisplay()) {
      retArray = selectItemArray(monthContexts, itemKey);
    } else {
      retArray = selectItemArray(dayContexts.slice(lastDayOfLastMonth, daySum(selectMonthIndex)), itemKey);
    }
    return retArray;
  }

  function selectItemArray(planData: DriveContext[], key: keyof DriveContext): number[] {
    if (planData.length === 0) {
      return [0]; // dataが読み込み前
    }
    let val: number[] = [] as number[];
    for (let i = 0; i < planData.length; i++) {
      const row = planData[i];
      if (!row) {
        continue;
      }
      val.push(typeof row[key] === 'number' ? (row[key] as number) : 0);
    }
    return val;
  }

  const burnDraftVolumes = (monthContexts: DriveContext[], dayContexts: DriveContext[], key: keyof DriveContext, furnacesIndex: number): number[] => {
    let retArray: number[] = [];
    if (isMonthDisplay()) {
      retArray = selectItemFurnaceArray(monthContexts, key, furnacesIndex);
    } else {
      retArray = selectItemFurnaceArray(dayContexts.slice(lastDayOfLastMonth, daySum(selectMonthIndex)), key, furnacesIndex);
    }
    return retArray;
  };

  const burnDraftDaysVolumes = (dayContexts: DriveContext[], key: keyof DriveContext, furnacesIndex: number): number[] => {
    let retArray: number[] = [];
    if (isMonthDisplay()) {
      retArray = selectItemFurnaceArray(dayContexts, key, furnacesIndex);
    } else {
      retArray = selectItemFurnaceArray(dayContexts.slice(lastDayOfLastMonth, daySum(selectMonthIndex)), key, furnacesIndex);
    }
    return retArray;
  };

  function selectItemFurnaceArray(planData: DriveContext[], key: keyof DriveContext, furnacesIndex: number): number[] {
    if (planData.length === 0) {
      return [0]; // dataが読み込み前
    }
    let val: number[] = [] as number[];
    for (let i = 0; i < planData.length; i++) {
      const row = planData[i];
      if (!row) {
        continue;
      }
      const values = row[key];
      if (Array.isArray(values) && values.every((item) => typeof item === 'number')) {
        val.push(Number(values[furnacesIndex]));
      }
    }
    return val;
  }

  function isShutdownWithout(targetPeriods: DayContext, otherPeriods: DayContext[]): boolean {
    let isMyselfSkip: boolean = false;
    for (let i = 0; i < otherPeriods.length; i++) {
      if (targetPeriods.launch_date === otherPeriods[i].launch_date && targetPeriods.shutdown_date === otherPeriods[i].shutdown_date && !isMyselfSkip) {
        isMyselfSkip = true; // 配列要素との1回目の一致は自分自身なのでスルー
      } else if (isDuplicationShutdown(targetPeriods, otherPeriods[i])) {
        return true;
      }
    }
    return false; // 重複なし
  }

  function isDuplicationShutdown(targetPeriods: DayContext, otherPeriod: DayContext): boolean {
    const targetStart: number = new Date(targetPeriods.shutdown_date).getTime();
    const targetEnd: number = new Date(targetPeriods.launch_date).getTime();
    const otherStart: number = new Date(otherPeriod.shutdown_date).getTime();
    const otherEnd: number = new Date(otherPeriod.launch_date).getTime();
    if (
      (targetStart <= otherStart && otherStart <= targetEnd) || //立ち下げ期間に他の立ち下げ日を含む
      (targetStart <= otherEnd && otherEnd <= targetEnd) || //立ち下げ期間に他の立ち上げ日を含む
      (otherStart <= targetStart && targetStart <= otherEnd) || //他の立ち下げ期間に立ち下げ日を含む
      (otherStart <= targetEnd && targetEnd <= otherEnd) //他の立ち下げ期間に立ち上げ日を含む
    ) {
      return true; // 重複あり
    }
    return false; // 重複なし
  }

  async function executeSave() {
    const planPushData: any[] = [];
    let planPushShutdown: DayContext[][] = [] as DayContext[][];
    if (existPlanDraft && !isSavedPlan) {
      const rawData: DriveContext[] = structuredClone(planEditResource.day);
      const encludeKeys: string[] = getUnusageProperties(planUsageCalc);
      for (let i = 0; i < rawData.length; i++) {
        if (!isEqualJson(rawData[i], planInitResource.day[i])) {
          const dateStr: string = createDay(displayYear, displayDays, i);
          const obj = omitProperties(rawData[i], [...encludeKeys, ...EXCLUDE_UNSAVE_KEYS]);
          obj['plan_date'] = dateStr;
          planPushData.push(obj);
        }
      }
      planPushShutdown = planEditResource.shutdown;
    }

    const resultPushData: any[] = [];
    if (existResultDraft && !isSavedResult) {
      const rawData: DriveContext[] = structuredClone(resultEditResource.day);
      const encludeKeys: string[] = getUnusageProperties(resultUsageCalc);
      for (let i = 0; i < rawData.length; i++) {
        if (!isEqualJson(rawData[i], resultInitResource.day[i])) {
          const dateStr: string = createDay(displayYear, displayDays, i);
          const obj = omitProperties(rawData[i], [...encludeKeys, ...EXCLUDE_UNSAVE_KEYS]);
          obj['result_date'] = dateStr;
          resultPushData.push(obj);
        }
      }
    }

    const draftPushDatas: any[][] = Array.from({ length: draftLength }, () => []);
    const draftPushShutdown: DayContext[][][] = Array.from({ length: draftLength }, () => []);
    if (existAutoDraft && !isSavedDraft) {
      for (let draftIndex = 0; draftIndex < draftLength; draftIndex++) {
        const draftArrayData: any[] = [];
        const rawData: DriveContext[] = structuredClone(draftEditResource[draftIndex].day);
        const encludeKeys: string[] = getUnusageProperties(draftUsageCalc);
        for (let i = 0; i < rawData.length; i++) {
          if (!isEqualJson(rawData[i], draftInitResource[draftIndex].day[i])) {
            const dateStr: string = createDay(displayYear, displayDays, i);
            const obj = omitProperties(rawData[i], [...encludeKeys, ...EXCLUDE_UNSAVE_KEYS]);
            obj['draft_date'] = dateStr;
            draftArrayData[i] = obj;
          }
        }
        draftPushDatas[draftIndex] = draftArrayData;
        draftPushShutdown[draftIndex] = draftEditResource[draftIndex].shutdown;
      }
    }

    const pushObject = {
      ...(planPushData.length > 0 && { planDayList: planPushData }),
      ...(planPushShutdown.length > 0 && { planShutdownList: planPushShutdown }),
      ...(resultPushData.length > 0 && { resultDayList: resultPushData }),
      ...(draftPushDatas.length > 0 && { autoDraftDayList: draftPushDatas }),
      ...(draftPushShutdown.length > 0 && { autoDraftShutdownList: draftPushShutdown }),
      initialPlanSetting: isInitialPlanSetting,
      lastReadAt: dataLoadedAtRef.current,
    };

    showSuccessMessage(MESSAGES.SUCCESS_SAVE, {
      showOnResolved: new Promise<MessageDialogOptions | void>((resolve, reject) => {
        (async () => {
          try {
            const requestUrl = `${REQ_PLAN_RESULT}?branchId=${String(sessionResource.branchId)}&selectDate=${getMinDate(displayYear)}&furnaces=${String(
              sessionResource.furnacesCount,
            )}&requestParam=${POST_REQ_SAVE}`;
            const { payload: json } = await postRequestJson<JsonResponse>(requestUrl, pushObject);
            const dialogOptions = { message: MESSAGES.SUCCESS_SAVE, type: 'success' } as MessageDialogOptions;
            resolve(dialogOptions);
          } catch (e) {
            reject(e);
          }
        })();
      }),
    })
      .then(() => {
        setIsReload(true);
      })
      .catch(() => {
        // reject側で処理されるため、こちらには分岐しないが念のため残す
        showErrorMessage(MESSAGES.ERROR_SERVER);
      });
  }

  async function executeInitialPlanSet() {
    try {
      const pushObject = {
        initialPlanSetting: true,
        createdAt: selectedInitialPlanTargetCreatedAt,
      };

      const params = {
        branchId: String(sessionResource.branchId),
        selectDate: getMinDate(displayYear),
        requestParam: POST_REQ_UPDATE_INITIAL_PLAN,
      };

      const query = Object.entries(params)
        .map(([key, value]) => `${key}=${value}`)
        .join('&');
      const requestUrl = `${REQ_PLAN_RESULT}?${query}`;
      const { payload: json } = await postRequestJson<JsonResponse>(requestUrl, pushObject);
      await showSuccessMessage(MESSAGES.SUCCESS_SET_INITIAL_PLAN_SETTING);
      setIsReload(true);
    } catch (e) {
      return showErrorMessage(MESSAGES.ERROR_SERVER);
    }
  }

  async function executeCancelInitialPlan() {
    try {
      const pushObject = {
        initialPlanSetting: false,
        createdAt: undefined,
      };

      const params = {
        branchId: String(sessionResource.branchId),
        selectDate: getMinDate(displayYear),
        requestParam: POST_REQ_UPDATE_INITIAL_PLAN,
      };

      const query = Object.entries(params)
        .map(([key, value]) => `${key}=${value}`)
        .join('&');
      const requestUrl = `${REQ_PLAN_RESULT}?${query}`;
      const { payload: json } = await postRequestJson<JsonResponse>(requestUrl, pushObject);
      await showSuccessMessage(MESSAGES.SUCCESS_CANCEL_INITIAL_PLAN_SETTING);
      setIsReload(true);
    } catch (e) {
      return showErrorMessage(MESSAGES.ERROR_SERVER);
    }
  }

  async function isInvalidCheck(initResource: DraftContext, editResource: DraftContext): Promise<boolean> {
    for (let furnacesIndex = 0; furnacesIndex < sessionResource.furnacesCount; furnacesIndex++) {
      for (let dataIndex = 0; dataIndex < editResource.shutdown[furnacesIndex].length; dataIndex++) {
        if (
          getErrMassage(editResource.shutdown[furnacesIndex][dataIndex], editResource.shutdown[furnacesIndex], editResource.shutdown[furnacesIndex].length === dataIndex + 1) !==
            '' ||
          getErrMassageCommonPeriodNotOverlap(editResource, furnacesIndex, dataIndex)
        ) {
          showErrorMessage('立下日・立上日の設定が不正です。');
          return true;
        }
      }
    }
    return false;
  }

  function isWarningBurnVolumeCheck(editResource: DraftContext): boolean {
    for (let dateIndex = 0; dateIndex < lastDayOfYear; dateIndex++) {
      for (let furnacesIndex = 0; furnacesIndex < sessionResource.furnacesCount; furnacesIndex++) {
        // 実績など編集不可セルはスキップ
        if (planDisableCheck.day[dateIndex]['burn_volume'][furnacesIndex]) continue;

        if (editResource.day[dateIndex].burn_volume[furnacesIndex] > conditionResource.rateCapacity) {
          return true;
        }
      }
    }
    return false;
  }

  function invalidateDateSequence(prePeriods: DayContext) {
    // 立下日・立上日が設定されていなければチェックを行わない
    if (prePeriods.shutdown_date === '' || prePeriods.launch_date === '') {
      return false;
    }
    const shutdownTime: number = new Date(prePeriods.shutdown_date).getTime();
    const launchTime: number = new Date(prePeriods.launch_date).getTime();
    return shutdownTime >= launchTime;
  }

  function invalidateBetweenDate(prePeriods: DayContext) {
    const shutdownTime: number = new Date(prePeriods.shutdown_date).getTime();
    const launchTime: number = new Date(prePeriods.launch_date).getTime();
    const minTime: number = new Date(getMinDate(displayYear)).getTime();
    const maxTime: number = new Date(getMaxDate(displayYear)).getTime();
    if (shutdownTime < minTime && maxTime < launchTime) {
      return true;
    } else {
      return false;
    }
  }

  function getErrMassage(targetPeriods: DayContext, otherPeriods: DayContext[], isLastData?: boolean) {
    if (targetPeriods.shutdown_date === '') {
      return '立下日を設定してください。';
    } else if (targetPeriods.launch_date === '' && !isLastData) {
      return '立上日を設定してください。';
    } else if (invalidateDateSequence(targetPeriods)) {
      return '立上日は立下日よりも後に設定してください。';
    } else if (isShutdownWithout(targetPeriods, otherPeriods)) {
      return '立下日と立上日の期間が他の設定期間と被っています。';
    } else if (invalidateBetweenDate(targetPeriods)) {
      return '立下日・立上日がどちらも表示期間から外れています。';
    } else {
      return '';
    }
  }

  function getErrMassageCommonPeriodNotOverlap(editResource: DraftContext, furnacesIndex: number, dataIndex: number) {
    // 共通停止期間をフラグとして取得する
    if (!editResource.shutdown[furnacesIndex][dataIndex].not_change_flag) {
      // 共通停止期間の入力フォームで無ければエラーメッセージを表示しない
      return '';
    } else if (new Date(editResource.shutdown[furnacesIndex][dataIndex].launch_date).getTime() > new Date(createDay(displayYear, displayDays, lastDayOfYear - 1)).getTime()) {
      // 共通停止期間が年度跨ぎしていたら、今年度はチェックしないしない
      return '';
    }

    const stopDaysArray: boolean[][] = [...Array(lastDayOfYear)].map(() => [...Array(sessionResource.furnacesCount)].map(() => false));
    for (let fIndex = 0; fIndex < sessionResource.furnacesCount; fIndex++) {
      for (let dIndex = 0; dIndex < editResource.shutdown[fIndex].length; dIndex++) {
        if (editResource.shutdown[fIndex][dIndex].not_change_flag && !(fIndex === furnacesIndex && dIndex !== dataIndex)) {
          // 共通停止期間のフラグ化
          const yearDays: string[] = createDateInYear(displayYear, displayDays);
          let stopFlag: boolean = false;
          for (let dateIndex = 0; dateIndex < yearDays.length; dateIndex++) {
            if (
              editResource.shutdown[fIndex][dIndex].shutdown_date === yearDays[dateIndex] ||
              (dateIndex === 0 && new Date(editResource.shutdown[fIndex][dIndex].shutdown_date).getTime() < new Date(yearDays[dateIndex]).getTime())
            ) {
              stopFlag = true;
            } else if (editResource.shutdown[fIndex][dIndex].launch_date === yearDays[dateIndex]) {
              stopFlag = false;
            }
            if (stopFlag) {
              stopDaysArray[dateIndex][fIndex] = true;
            }
          }
        }
      }
    }
    for (let dateIndex = 0; dateIndex < lastDayOfYear; dateIndex++) {
      let commonStop: boolean = true;
      for (let fIndex = 0; fIndex < sessionResource.furnacesCount; fIndex++) {
        if (!stopDaysArray[dateIndex][fIndex]) {
          commonStop = false;
        }
      }
      if (commonStop) {
        return '';
      }
    }
    return '他の炉と共通停止期間が被っていません。';
  }

  function inShutdownPeriod(dayResource: DayContext, kind: string): boolean {
    let strStartDate: string = '';
    let strEndDate: string = '';
    if (isMonthDisplay()) {
      strStartDate = getMinDate(displayYear);
      strEndDate = getMaxDate(displayYear);
    } else {
      strStartDate = getStrDate(displayYear, months[selectMonthIndex - 1], 1);
      strEndDate = getStrDate(displayYear, months[selectMonthIndex - 1], displayDayCountOfMonth);
    }
    const minTime: number = new Date(strStartDate).getTime();
    const maxTime: number = new Date(strEndDate).getTime();
    if (kind === item_shutdown_date) {
      const shutdownTime: number = new Date(dayResource.shutdown_date).getTime();
      if (minTime <= shutdownTime && shutdownTime <= maxTime) {
        return true;
      }
      return false;
    } else if (kind === item_launch_date) {
      const launchTime: number = new Date(dayResource.launch_date).getTime();
      if (minTime <= launchTime && launchTime <= maxTime) {
        return true;
      }
      return false;
    }
    return false;
  }

  function isDayFull(emptyDay: DriveEmpty, usage: DriveUsage): boolean {
    return isCarryDayFull(emptyDay, usage) && isBurnDayFull(emptyDay);
  }

  // 日の搬入量が全て設定されているか
  function isCarryDayFull(emptyDay: DriveEmpty, usage: DriveUsage): boolean {
    if (
      usage.combustible_waste_carry_volume ||
      usage.pruning_branches_carry_volume ||
      usage.small_animals_carry_volume ||
      usage.combustible_bulk_waste_carry_volume ||
      usage.shredded_waste_residue_carry_volume ||
      usage.disaster_waste_internal_carry_volume ||
      usage.disaster_waste_external_carry_volume ||
      usage.other_carry_volume ||
      usage.incineration_facility_bottom_ash_carry_volume ||
      usage.recyclables_carry_volume
    ) {
      for (let keyIndex = 0; keyIndex < CARRY_KEYS.length; keyIndex++) {
        if (usage[CARRY_KEYS[keyIndex]] && emptyDay[CARRY_KEYS[keyIndex]]) {
          return false;
        }
      }
      return true;
    } else {
      return !emptyDay.carry_volume;
    }
  }

  // 日の焼却量が全て設定されているか
  function isBurnDayFull(emptyDay: DriveEmpty): boolean {
    for (let furnacesIndex = 0; furnacesIndex < emptyDay.burn_volume.length; furnacesIndex++) {
      if (emptyDay.burn_volume[furnacesIndex]) {
        return false;
      }
    }
    return true;
  }

  // 月の搬入量が全て設定されているか
  function isCarryMonthFull(emptyMonths: DriveEmpty[], emptyDays: DriveEmpty[], targetMonthIndex: number): boolean {
    if (emptyMonths[targetMonthIndex].carry_volume) {
      return false;
    }
    const targetCarrys: DriveEmpty[] = emptyDays.slice(daySum(targetMonthIndex), daySum(targetMonthIndex + 1));
    for (let dayIndex = 0; dayIndex < targetCarrys.length; dayIndex++) {
      if (targetCarrys[dayIndex].carry_volume) {
        return false;
      }
    }
    return true;
  }

  function existDayFull(driveDay: DriveContext) {
    if (driveDay.date === '') {
      return false;
    } else {
      return true;
    }
  }

  function existMonthFull(driveDays: DriveContext[], targetMonthIndex: number) {
    const targetCarrys: DriveContext[] = driveDays.slice(daySum(targetMonthIndex), daySum(targetMonthIndex + 1));
    for (let dayIndex = 0; dayIndex < targetCarrys.length; dayIndex++) {
      if (targetCarrys[dayIndex].date === '') {
        return false;
      }
    }
    return true;
  }

  return (
    <div className="screen-planresult">
      <br></br>

      <div className="p-relative">
        <div className="header-planresult"></div>
        <select className="yearselect-planresult" value={apiYear} onChange={handleApiYearChange}>
          {jsonMainResource.year_list.map((option, index) => (
            <option key={index} value={option}>
              {option + '年度'}
            </option>
          ))}
        </select>
        <Button className="acquisitionbutton-planresult" variant="outline-light" size="sm" onClick={handleGetApiClick}>
          取得
        </Button>
        <Button className="acquisitionbutton-planresult_display_history" variant="outline-light" size="sm" onClick={handleHistoryDisplay} disabled={pastYearList.length === 0}>
          過去表示
        </Button>

        <div
          className="last-update-editor-planresult"
          style={{ display: updateEditRecord.userName ? 'inline-block' : 'none' }}
        >{`最終更新者：${updateEditRecord.userName} (${updateEditRecord.time})`}</div>
      </div>
      {permissions & (PERMISSIONS.PLAN_RESULT_MAIN_EDIT | PERMISSIONS.PLAN_RESULT_SUB_EDIT | PERMISSIONS.PLAN_RESULT_OTHER_EDIT) ? (
        <div className="p-relative" style={{ display: !existPlanDraft ? 'block' : 'none' }}>
          <div className="header-planresult"></div>
          <select className="yearselect-planresult" value={pastYear} onChange={handlePastYearChange} disabled={pastYearList.length === 0}>
            {pastYearList.map((option, index) => (
              <option key={index} value={option}>
                {option + '年度'}
              </option>
            ))}
          </select>
          <Button className="acquisitionbutton-planresult" variant="outline-light" size="sm" onClick={handleGetPastClick} disabled={pastYearList.length === 0}>
            過去実績流用
          </Button>
        </div>
      ) : (
        ''
      )}

      <div className="indent-planresult">
        {permissions & (PERMISSIONS.PLAN_RESULT_MAIN_EDIT | PERMISSIONS.PLAN_RESULT_SUB_EDIT | PERMISSIONS.PLAN_RESULT_OTHER_EDIT) ? (
          <div className="row-planresult">
            {Array.from({ length: draftPattern.length }).map((_, colIndex) => (
              <>
                <ToggleButton
                  className={'button-1-plan' + colIndex}
                  variant="outline-secondary"
                  id={'toggle-check' + colIndex}
                  value={colIndex}
                  type="checkbox"
                  checked={isSelectDraft[colIndex]}
                  onClick={() => handleAnClick(colIndex)}
                  style={{ display: existAutoDraft ? 'button' : 'none' }}
                >
                  {draftPattern[colIndex].priority_name}
                </ToggleButton>
              </>
            ))}
            <Button className="button-1-planresult" variant="secondary" style={{ display: existAutoDraft ? 'inline-block' : 'none' }} onClick={() => handleAnDecideClick()}>
              案確定
            </Button>
          </div>
        ) : (
          ''
        )}
        <div className="row-planresult">
          {existInitialPlanDraft && (
            <ToggleButtonGroup type="checkbox" defaultValue={showInitialPlanVisible ? [2] : []}>
              <ToggleButton variant="outline-success" size="sm" id={'initial-plan-check'} value={2} checked={showInitialPlanVisible} onClick={() => handleShowInitialPlanClick()}>
                期初計画
              </ToggleButton>
            </ToggleButtonGroup>
          )}
          {existPlanDraft || existResultDraft ? (
            <ToggleButtonGroup className="column-planresult" type="checkbox" defaultValue={[1, 2]}>
              {existPlanDraft ? (
                <ToggleButton variant="outline-success" size="sm" id={'plan-check'} value={1} checked={showPlanVisible} onClick={() => handleShowPlanClick()}>
                  計画
                </ToggleButton>
              ) : (
                ''
              )}
              {existResultDraft ? (
                <ToggleButton variant="outline-success" size="sm" id={'planAchievements-check'} value={2} checked={showResultVisible} onClick={() => handleShowResultClick()}>
                  実績
                </ToggleButton>
              ) : (
                ''
              )}
            </ToggleButtonGroup>
          ) : (
            ''
          )}
          <ToggleButtonGroup type="checkbox" defaultValue={[1]}>
            <ToggleButton variant="outline-success" size="sm" id={'power-check'} value={2} checked={showResultVisible} onClick={() => handleShowPowerClick()}>
              電力量
            </ToggleButton>
          </ToggleButtonGroup>
          <ToggleButtonGroup type="checkbox" defaultValue={[1]}>
            <ToggleButton variant="outline-success" size="sm" id={'ash-check'} value={2} checked={showResultVisible} onClick={() => handleShowAshClick()}>
              主灰・飛灰
            </ToggleButton>
          </ToggleButtonGroup>
          <ToggleButtonGroup className="column-planresult" type="checkbox" defaultValue={[1]}>
            <ToggleButton
              style={{ display: planResultUsageDisplay['furnace_operating_days'] ? 'flex' : 'none' }}
              variant="outline-success"
              size="sm"
              id={'operation-days-check'}
              value={2}
              checked={showResultVisible}
              onClick={() => handleShowOperationDaysClick()}
            >
              炉別運転日数
            </ToggleButton>
          </ToggleButtonGroup>
        </div>
        <div className="row-planresult" style={{ alignItems: 'flex-end' }}>
          <ToggleButtonGroup className="column-planresult" type="radio" defaultValue={0} name="calender">
            {Array.from({ length: months.length + 1 }).map((_, colIndex) => (
              <ToggleButton
                variant="outline-success"
                size="sm"
                onClick={() => handleMonthClick(colIndex)}
                id={'calender' + colIndex}
                value={colIndex}
                style={{ alignContent: 'center', height: '30px' }}
              >
                {colIndex === 0 ? '年間' : months[colIndex - 1] + '月'}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          <select
            style={{ marginLeft: '30px', height: '30px', display: excelOutputDraftPattern.length > 0 ? 'inline' : 'none' }}
            value={selectedPriorityId}
            onChange={handleDraftPatternChange}
          >
            {excelOutputDraftPattern.map((option) => (
              <option key={option.priority_id} value={option.priority_id}>
                {option.priority_name}
              </option>
            ))}
          </select>
          <Button
            className="button-1-planresult"
            variant="outline-success"
            size="sm"
            onClick={handleExcelOutput}
            style={{ display: excelOutputDraftPattern.length > 0 ? 'inline-block' : 'none' }}
          >
            計画・実績表出力
          </Button>
          <div style={{ flex: 1 }} />
          {permissions & (PERMISSIONS.PLAN_RESULT_MAIN_EDIT | PERMISSIONS.PLAN_RESULT_SUB_EDIT | PERMISSIONS.PLAN_RESULT_OTHER_EDIT) ? (
            <>
              {!existInitialPlanDraft && !existAutoDraft && (
                <ToggleButtonGroup type="checkbox" defaultValue={isInitialPlanSetting ? [1] : []}>
                  <ToggleButton
                    variant="outline-success"
                    size="sm"
                    id="initial-plan-setting-check"
                    value={1}
                    checked={isInitialPlanSetting}
                    onClick={() => setIsInitialPlanSetting((prev) => !prev)}
                    className="enterbutton-planresult"
                    style={{ marginRight: '8px' }}
                  >
                    {`期初計画設定 ${isInitialPlanSetting ? 'ON' : 'OFF'}`}
                  </ToggleButton>
                </ToggleButtonGroup>
              )}
              <Button style={{ fontSize: '22px' }} variant="warning" size="sm" className="enterbutton-planresult" onClick={() => handleSaveClick()}>
                保存
              </Button>
            </>
          ) : (
            ''
          )}

          <Button style={{ fontSize: '22px' }} variant="danger" size="sm" className="enterbutton-planresult" onClick={() => handleResetClick()}>
            編集前に戻す
          </Button>
        </div>
      </div>

      <br></br>

      <div className="indent-planresult" style={{ display: isPlanVisible() ? 'inline-block' : 'none' }}>
        <div className="row-planresult">
          <div className="itemname-planresult-subtitle">
            焼却量一括変更
          </div>
        </div>
        {Array.from({ length: sessionResource.furnacesCount }).map((_, furnacesIndex) => (
          <div className="row-planresult" style={{ display: 'flex' }}>
            <div className="itemname-planresult">
              <div className="itemname-back-planresult"></div>
              {`${furnacesIndex + 1}号炉焼却量`}
            </div>
            <CustomDatePicker
              className="dateinput-planresult"
              minDate={
                getDateIndex(preBetweenDate, displayYear, displayDays) >
                getDateIndex(getInputableMinDate(displayYear, displayDays, planEmptyCheck.day, systemDateIndex), displayYear, displayDays)
                  ? new Date(preBetweenDate)
                  : new Date(getInputableMinDate(displayYear, displayDays, planEmptyCheck.day, systemDateIndex))
              }
              maxDate={new Date(nextBetweenDate)}
              selected={planMultipleBurn[furnacesIndex].shutdown_date ? new Date(planMultipleBurn[furnacesIndex].shutdown_date) : null}
              onChange={(date: Date | null) => {
                handleBurnMultipleDateChange(item_shutdown_date, date ? format(date, DATE_FORMAT_YYYYMMDD) : '', furnacesIndex);
              }}
              dateFormat={`${DATE_FORMAT_YYYYMMDD_SLASH}(${WEEK_DAYS[new Date(planMultipleBurn[furnacesIndex].shutdown_date).getDay()]})`}
              portalId="datepicker-portal"
              locale={ja}
              placeholderText="年/月/日(曜日)"
            />
            <div style={{ marginLeft: '10px', marginRight: '-10px' }}>～</div>
            <CustomDatePicker
              className="dateinput-planresult"
              minDate={
                getDateIndex(preBetweenDate, displayYear, displayDays) >
                getDateIndex(getInputableMinDate(displayYear, displayDays, planEmptyCheck.day, systemDateIndex), displayYear, displayDays)
                  ? new Date(preBetweenDate)
                  : new Date(getInputableMinDate(displayYear, displayDays, planEmptyCheck.day, systemDateIndex))
              }
              maxDate={new Date(nextBetweenDate)}
              selected={planMultipleBurn[furnacesIndex].launch_date ? new Date(planMultipleBurn[furnacesIndex].launch_date) : null}
              onChange={(date: Date | null) => {
                handleBurnMultipleDateChange(item_launch_date, date ? format(date, DATE_FORMAT_YYYYMMDD) : '', furnacesIndex);
              }}
              dateFormat={`${DATE_FORMAT_YYYYMMDD_SLASH}(${WEEK_DAYS[new Date(planMultipleBurn[furnacesIndex].launch_date).getDay()]})`}
              portalId="datepicker-portal"
              locale={ja}
              placeholderText="年/月/日(曜日)"
            />
            <div className="itemname-planresult-burn-load">焼却負荷率：</div>
            <input
              style={{ height: '25px', width: '50px' }}
              type="number"
              data-kind="burn_load"
              data-furnaces={furnacesIndex}
              min={0}
              max={100}
              value={planMultipleBurn[furnacesIndex].burn_load}
              onChange={handleBurnMultipleChange}
            ></input>
            <div style={{ padding: '2px' }}>%</div>
            <div className="itemname-planresult-burn-volume">焼却量：</div>
            <input
              style={{ height: '25px', width: '60px' }}
              type="number"
              data-kind="burn_volume"
              data-furnaces={furnacesIndex}
              value={planMultipleBurn[furnacesIndex].burn_volume}
              onChange={handleBurnVolumeChange}
            ></input>
            <div style={{ padding: '1px', marginLeft: '1px' }}>t</div>
            <Button
              className="button-2-planresult"
              variant="outline-secondary"
              size="sm"
              value={'適用'}
              style={{ display: 'inline-block' }}
              onClick={() => handleInputBurnClick(furnacesIndex)}
            >
              適用
            </Button>
            <Button
              className="button-2-planresult"
              variant="outline-secondary"
              size="sm"
              value={'クリア'}
              onClick={() => handleDeleteBurnClick(furnacesIndex)}
              style={{ display: 'inline-block' }}
            >
              クリア
            </Button>
          </div>
        ))}
      </div>
      <div className="indent-planresult" style={{ overflowY: 'hidden' }}>
        <div className="row-planresult" style={{ display: 'inline' }}>
          <div
            className="table-planresult"
            style={{ display: isPlanVisible() || isResultVisible() || isSelectDraft.some((value) => value) ? 'flex' : 'none', marginBottom: '-20px' }}
          >
            <table className="left-table">
              <thead>
                <tr className="table-carryrow">
                  <td className="table-carrydoublecell-18" colSpan={2}></td>
                </tr>
              </thead>
              <tbody style={{ display: showInitialPlanVisible ? 'table-row-group' : 'none' }}>
                <tr className="table-carryrow">
                  <td className="table-carrydoublecell-14-blue" colSpan={2}>
                    {'期初計画'}
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: initialPlanUsageDisplay['carry_volume'] ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    搬入量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: initialPlanUsageDisplay['combustible_waste_carry_volume'] ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    可燃ごみ搬入量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: initialPlanUsageDisplay['pruning_branches_carry_volume'] ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    剪定枝等搬入量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: initialPlanUsageDisplay['small_animals_carry_volume'] ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    小動物搬入量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: initialPlanUsageDisplay['combustible_bulk_waste_carry_volume'] ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    可燃粗大ごみ搬入量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: initialPlanUsageDisplay['shredded_waste_residue_carry_volume'] ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    破砕ごみ残渣等搬入量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: initialPlanUsageDisplay['disaster_waste_internal_carry_volume'] ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    災害ごみ組合内搬入量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: initialPlanUsageDisplay['disaster_waste_external_carry_volume'] ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    災害ごみ組合外搬入量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: initialPlanUsageDisplay['other_carry_volume'] ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    その他搬入量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: initialPlanUsageDisplay['incineration_facility_bottom_ash_carry_volume'] ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    焼却施設底ごみ搬入量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: initialPlanUsageDisplay['recyclables_carry_volume'] ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    資源物焼却処分搬入量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: initialPlanUsageDisplay['total_carry_volume'] ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    搬入量合計[t]
                  </td>
                </tr>
                {Array.from({ length: sessionResource.furnacesCount }).map((_, furnacesIndex) => (
                  <>
                    <tr className="table-carryrow">
                      <td
                        className="table-carrydoublecell-14-item-head"
                        rowSpan={
                          isMonthDisplay()
                            ? 2 +
                              Number(initialPlanUsageDisplay['burn_load']) +
                              Number(initialPlanUsageDisplay['burn_load_include_shutdown']) +
                              Number(initialPlanUsageDisplay['waste_lower_heating_value'])
                            : 1 + Number(initialPlanUsageDisplay['waste_lower_heating_value'])
                        }
                      >
                        {String(furnacesIndex + 1).concat('号炉')}
                      </td>
                      <td className="table-carrycell-14">焼却量[t]</td>
                    </tr>
                    <tr className="table-carryrow" style={{ display: initialPlanUsageDisplay['burn_load'] && isMonthDisplay() ? 'table-row' : 'none' }}>
                      <td className="table-carrycell-14">焼却負荷率[%]</td>
                    </tr>
                    <tr className="table-carryrow" style={{ display: initialPlanUsageDisplay['burn_load_include_shutdown'] && isMonthDisplay() ? 'table-row' : 'none' }}>
                      <td className="table-carrycell-14">焼却負荷率[%]</td>
                    </tr>
                    <tr className="table-carryrow" style={{ display: isMonthDisplay() ? 'table-row' : 'none' }}>
                      <td className="table-carrycell-14">運転日数[日]</td>
                    </tr>
                    <tr className="table-carryrow" style={{ display: initialPlanUsageDisplay['waste_lower_heating_value'] ? 'table-row' : 'none' }}>
                      <td className="table-carrydoublecell-14" colSpan={2}>
                        ごみ低位発熱量
                      </td>
                    </tr>
                  </>
                ))}
                <tr className="table-carryrow">
                  <td
                    className="table-carrydoublecell-14-item-head"
                    rowSpan={isMonthDisplay() ? 1 + Number(initialPlanUsageDisplay['burn_load']) + Number(initialPlanUsageDisplay['burn_load_include_shutdown']) : 1}
                  >
                    全炉
                  </td>
                  <td className="table-carrycell-14"> 焼却量[t]</td>
                </tr>
                <tr className="table-carryrow" style={{ display: initialPlanUsageDisplay['burn_load'] && isMonthDisplay() ? 'table-row' : 'none' }}>
                  <td className="table-carrycell-14">焼却負荷率[%]</td>
                </tr>
                <tr className="table-carryrow" style={{ display: initialPlanUsageDisplay['burn_load_include_shutdown'] && isMonthDisplay() ? 'table-row' : 'none' }}>
                  <td className="table-carrycell-14">焼却負荷率[%]</td>
                </tr>
                <tr className="table-carryrow" style={{ display: isMonthDisplay() ? 'none' : 'table-row' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    ピット残量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: initialPlanUsageDisplay['main_ash_generate'] && showAshVisible ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    主灰発生量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: initialPlanUsageDisplay['main_ash_carry_out_units'] && showAshVisible ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    主灰搬出台数[台]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: initialPlanUsageDisplay['main_ash_carry_out'] && showAshVisible ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    主灰搬出量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: showAshVisible && !isMonthDisplay() ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    主灰ピット残量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: initialPlanUsageDisplay['fly_ash_generate'] && showAshVisible ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    飛灰発生量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: initialPlanUsageDisplay['fly_ash_carry_out_units'] && showAshVisible ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    飛灰搬出台数[台]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: initialPlanUsageDisplay['fly_ash_carry_out'] && showAshVisible ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    飛灰搬出量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: initialPlanUsageDisplay['dry_fly_ash_carry_out'] && showAshVisible ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    飛灰処理物搬出量 乾灰[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: initialPlanUsageDisplay['processed_fly_ash_carry_out'] && showAshVisible ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    飛灰処理物搬出量 処理灰[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: showAshVisible && !isMonthDisplay() ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    飛灰ピット残量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: initialPlanUsageDisplay['hot_water_supply_heating_value'] ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    温水供給設備供給熱量
                  </td>
                </tr>
                <tr
                  className="table-carryrow"
                  style={{ display: initialPlanUsageDisplay['furnace_operating_days'] && isMonthDisplay() && showOperationDaysVisible ? 'table-row' : 'none' }}
                >
                  <td className="table-carrydoublecell-14-item-head" rowSpan={sessionResource.furnacesCount + 1}>
                    炉別
                    <br />
                    運転日数
                  </td>
                  <td className="table-carrydoublecell-14">全炉停止(日)</td>
                </tr>
                {Array.from({ length: sessionResource.furnacesCount }).map((_, furnacesIndex) => (
                  <tr
                    className="table-carryrow"
                    style={{ display: initialPlanUsageDisplay['furnace_operating_days'] && isMonthDisplay() && showOperationDaysVisible ? 'table-row' : 'none' }}
                  >
                    <td className="table-carrydoublecell-14">{`${furnacesIndex + 1}炉運転日数`}</td>
                  </tr>
                ))}
              </tbody>
              <tbody style={{ display: existAutoDraft && isResultVisible() && isPlanVisible() ? 'table-row-group' : 'none' }}>
                <tr className="table-carryrow">
                  <td className="table-carrydoublecell-14-blue" colSpan={2}>
                    {'計画/実績'}
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: planResultUsageDisplay['carry_volume'] ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    搬入量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: planResultUsageDisplay['combustible_waste_carry_volume'] ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    可燃ごみ搬入量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: planResultUsageDisplay['pruning_branches_carry_volume'] ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    剪定枝等搬入量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: planResultUsageDisplay['small_animals_carry_volume'] ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    小動物搬入量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: planResultUsageDisplay['combustible_bulk_waste_carry_volume'] ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    可燃粗大ごみ搬入量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: planResultUsageDisplay['shredded_waste_residue_carry_volume'] ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    破砕ごみ残渣等搬入量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: planResultUsageDisplay['disaster_waste_internal_carry_volume'] ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    災害ごみ組合内搬入量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: planResultUsageDisplay['disaster_waste_external_carry_volume'] ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    災害ごみ組合外搬入量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: planResultUsageDisplay['other_carry_volume'] ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    その他搬入量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: planResultUsageDisplay['incineration_facility_bottom_ash_carry_volume'] ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    焼却施設底ごみ搬入量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: planResultUsageDisplay['recyclables_carry_volume'] ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    資源物焼却処分搬入量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: planResultUsageDisplay['total_carry_volume'] ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    搬入量合計[t]
                  </td>
                </tr>
                {Array.from({ length: sessionResource.furnacesCount }).map((_, furnacesIndex) => (
                  <>
                    <tr className="table-carryrow">
                      <td
                        className="table-carrydoublecell-14-item-head"
                        rowSpan={
                          isMonthDisplay()
                            ? 2 +
                              Number(planResultUsageDisplay['burn_load']) +
                              Number(planResultUsageDisplay['burn_load_include_shutdown']) +
                              Number(planResultUsageDisplay['waste_lower_heating_value'])
                            : 1 + Number(planResultUsageDisplay['waste_lower_heating_value'])
                        }
                      >
                        {String(furnacesIndex + 1).concat('号炉')}
                      </td>
                      <td className="table-carrycell-14">焼却量[t]</td>
                    </tr>
                    <tr className="table-carryrow" style={{ display: planResultUsageDisplay['burn_load'] && isMonthDisplay() ? 'table-row' : 'none' }}>
                      <td className="table-carrycell-14">焼却負荷率[%]</td>
                    </tr>
                    <tr className="table-carryrow" style={{ display: planResultUsageDisplay['burn_load_include_shutdown'] && isMonthDisplay() ? 'table-row' : 'none' }}>
                      <td className="table-carrycell-14">焼却負荷率[%]</td>
                    </tr>
                    <tr className="table-carryrow" style={{ display: isMonthDisplay() ? 'table-row' : 'none' }}>
                      <td className="table-carrycell-14">運転日数[日]</td>
                    </tr>
                    <tr className="table-carryrow" style={{ display: planResultUsageDisplay['waste_lower_heating_value'] ? 'table-row' : 'none' }}>
                      <td className="table-carrydoublecell-14" colSpan={2}>
                        ごみ低位発熱量
                      </td>
                    </tr>
                  </>
                ))}
                <tr className="table-carryrow">
                  <td
                    className="table-carrydoublecell-14-item-head"
                    rowSpan={isMonthDisplay() ? 1 + Number(planResultUsageDisplay['burn_load']) + Number(planResultUsageDisplay['burn_load_include_shutdown']) : 1}
                  >
                    全炉
                  </td>
                  <td className="table-carrycell-14"> 焼却量[t]</td>
                </tr>
                <tr className="table-carryrow" style={{ display: planResultUsageDisplay['burn_load'] && isMonthDisplay() ? 'table-row' : 'none' }}>
                  <td className="table-carrycell-14">焼却負荷率[%]</td>
                </tr>
                <tr className="table-carryrow" style={{ display: planResultUsageDisplay['burn_load_include_shutdown'] && isMonthDisplay() ? 'table-row' : 'none' }}>
                  <td className="table-carrycell-14">焼却負荷率[%]</td>
                </tr>
                <tr className="table-carryrow" style={{ display: isMonthDisplay() ? 'none' : 'table-row' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    ピット残量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: planResultUsageDisplay['main_ash_generate'] && showAshVisible ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    主灰発生量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: planResultUsageDisplay['main_ash_carry_out_units'] && showAshVisible ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    主灰搬出台数[台]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: planResultUsageDisplay['main_ash_carry_out'] && showAshVisible ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    主灰搬出量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: showAshVisible && !isMonthDisplay() ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    主灰ピット残量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: planResultUsageDisplay['fly_ash_generate'] && showAshVisible ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    飛灰発生量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: planResultUsageDisplay['fly_ash_carry_out_units'] && showAshVisible ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    飛灰搬出台数[台]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: planResultUsageDisplay['fly_ash_carry_out'] && showAshVisible ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    飛灰搬出量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: planResultUsageDisplay['dry_fly_ash_carry_out'] && showAshVisible ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    飛灰処理物搬出量 乾灰[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: planResultUsageDisplay['processed_fly_ash_carry_out'] && showAshVisible ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    飛灰処理物搬出量 処理灰[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: showAshVisible && !isMonthDisplay() ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    飛灰ピット残量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: planResultUsageDisplay['hot_water_supply_heating_value'] ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    温水供給設備供給熱量
                  </td>
                </tr>
                <tr
                  className="table-carryrow"
                  style={{ display: planResultUsageDisplay['furnace_operating_days'] && isMonthDisplay() && showOperationDaysVisible ? 'table-row' : 'none' }}
                >
                  <td className="table-carrydoublecell-14-item-head" rowSpan={sessionResource.furnacesCount + 1}>
                    炉別
                    <br />
                    運転日数
                  </td>
                  <td className="table-carrydoublecell-14">全炉停止(日)</td>
                </tr>
                {Array.from({ length: sessionResource.furnacesCount }).map((_, furnacesIndex) => (
                  <tr
                    className="table-carryrow"
                    style={{ display: planResultUsageDisplay['furnace_operating_days'] && isMonthDisplay() && showOperationDaysVisible ? 'table-row' : 'none' }}
                  >
                    <td className="table-carrydoublecell-14">{`${furnacesIndex + 1}炉運転日数`}</td>
                  </tr>
                ))}
              </tbody>
              <tbody style={{ display: !(existAutoDraft && isResultVisible()) && isPlanVisible() ? 'table-row-group' : 'none' }}>
                <tr className="table-carryrow">
                  <td className="table-carrydoublecell-14-blue" colSpan={2}>
                    {'計画/入力'}
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: planUsageDisplay['carry_volume'] ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    搬入量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: planUsageDisplay['combustible_waste_carry_volume'] ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    可燃ごみ搬入量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: planUsageDisplay['pruning_branches_carry_volume'] ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    剪定枝等搬入量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: planUsageDisplay['small_animals_carry_volume'] ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    小動物搬入量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: planUsageDisplay['combustible_bulk_waste_carry_volume'] ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    可燃粗大ごみ搬入量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: planUsageDisplay['shredded_waste_residue_carry_volume'] ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    破砕ごみ残渣等搬入量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: planUsageDisplay['disaster_waste_internal_carry_volume'] ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    災害ごみ組合内搬入量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: planUsageDisplay['disaster_waste_external_carry_volume'] ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    災害ごみ組合外搬入量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: planUsageDisplay['other_carry_volume'] ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    その他搬入量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: planUsageDisplay['incineration_facility_bottom_ash_carry_volume'] ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    焼却施設底ごみ搬入量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: planUsageDisplay['recyclables_carry_volume'] ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    資源物焼却処分搬入量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: planUsageDisplay['total_carry_volume'] ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    搬入量合計[t]
                  </td>
                </tr>
                {Array.from({ length: sessionResource.furnacesCount }).map((_, furnacesIndex) => (
                  <>
                    <tr className="table-carryrow">
                      <td
                        className="table-carrydoublecell-14-item-head"
                        rowSpan={
                          isMonthDisplay()
                            ? 2 +
                              Number(planUsageDisplay['burn_load']) +
                              Number(planUsageDisplay['burn_load_include_shutdown']) +
                              Number(planUsageDisplay['waste_lower_heating_value'])
                            : 1 + Number(planUsageDisplay['waste_lower_heating_value'])
                        }
                      >
                        {String(furnacesIndex + 1).concat('号炉')}
                      </td>
                      <td className="table-carrycell-14">焼却量[t]</td>
                    </tr>
                    <tr className="table-carryrow" style={{ display: planUsageDisplay['burn_load'] && isMonthDisplay() ? 'table-row' : 'none' }}>
                      <td className="table-carrycell-14">焼却負荷率[%]</td>
                    </tr>
                    <tr className="table-carryrow" style={{ display: planUsageDisplay['burn_load_include_shutdown'] && isMonthDisplay() ? 'table-row' : 'none' }}>
                      <td className="table-carrycell-14">焼却負荷率[%]</td>
                    </tr>
                    <tr className="table-carryrow" style={{ display: isMonthDisplay() ? 'table-row' : 'none' }}>
                      <td className="table-carrycell-14">運転日数[日]</td>
                    </tr>
                    <tr className="table-carryrow" style={{ display: planUsageDisplay['waste_lower_heating_value'] ? 'table-row' : 'none' }}>
                      <td className="table-carrydoublecell-14" colSpan={2}>
                        ごみ低位発熱量
                      </td>
                    </tr>
                  </>
                ))}
                <tr className="table-carryrow">
                  <td
                    className="table-carrydoublecell-14-item-head"
                    rowSpan={isMonthDisplay() ? 1 + Number(planUsageDisplay['burn_load']) + Number(planUsageDisplay['burn_load_include_shutdown']) : 1}
                  >
                    全炉
                  </td>
                  <td className="table-carrycell-14"> 焼却量[t]</td>
                </tr>
                <tr className="table-carryrow" style={{ display: planUsageDisplay['burn_load'] && isMonthDisplay() ? 'table-row' : 'none' }}>
                  <td className="table-carrycell-14">焼却負荷率[%]</td>
                </tr>
                <tr className="table-carryrow" style={{ display: planUsageDisplay['burn_load_include_shutdown'] && isMonthDisplay() ? 'table-row' : 'none' }}>
                  <td className="table-carrycell-14">焼却負荷率[%]</td>
                </tr>
                <tr className="table-carryrow" style={{ display: isMonthDisplay() ? 'none' : 'table-row' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    ピット残量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: planUsageDisplay['main_ash_generate'] && showAshVisible ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    主灰発生量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: planUsageDisplay['main_ash_carry_out_units'] && showAshVisible ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    主灰搬出台数[台]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: planUsageDisplay['main_ash_carry_out'] && showAshVisible ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    主灰搬出量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: showAshVisible && !isMonthDisplay() ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    主灰ピット残量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: planUsageDisplay['fly_ash_generate'] && showAshVisible ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    飛灰発生量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: planUsageDisplay['fly_ash_carry_out_units'] && showAshVisible ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    飛灰搬出台数[台]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: planUsageDisplay['fly_ash_carry_out'] && showAshVisible ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    飛灰搬出量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: planUsageDisplay['dry_fly_ash_carry_out'] && showAshVisible ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    飛灰処理物搬出量 乾灰[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: planUsageDisplay['processed_fly_ash_carry_out'] && showAshVisible ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    飛灰処理物搬出量 処理灰[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: showAshVisible && !isMonthDisplay() ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    飛灰ピット残量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: planUsageDisplay['hot_water_supply_heating_value'] ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    温水供給設備供給熱量
                  </td>
                </tr>
                <tr
                  className="table-carryrow"
                  style={{ display: planUsageDisplay['furnace_operating_days'] && isMonthDisplay() && showOperationDaysVisible ? 'table-row' : 'none' }}
                >
                  <td className="table-carrydoublecell-14-item-head" rowSpan={sessionResource.furnacesCount + 1}>
                    炉別
                    <br />
                    運転日数
                  </td>
                  <td className="table-carrydoublecell-14">全炉停止(日)</td>
                </tr>
                {Array.from({ length: sessionResource.furnacesCount }).map((_, furnacesIndex) => (
                  <tr
                    className="table-carryrow"
                    style={{ display: planUsageDisplay['furnace_operating_days'] && isMonthDisplay() && showOperationDaysVisible ? 'table-row' : 'none' }}
                  >
                    <td className="table-carrydoublecell-14">{`${furnacesIndex + 1}炉運転日数`}</td>
                  </tr>
                ))}
              </tbody>
              <tbody style={{ display: !(existAutoDraft && isPlanVisible()) && isResultVisible() ? 'table-row-group' : 'none' }}>
                <tr className="table-carryrow">
                  <td className="table-carrydoublecell-14-blue" colSpan={2}>
                    {'実績'}
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: resultUsageDisplay['carry_volume'] ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    搬入量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: resultUsageDisplay['combustible_waste_carry_volume'] ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    可燃ごみ搬入量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: resultUsageDisplay['pruning_branches_carry_volume'] ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    剪定枝等搬入量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: resultUsageDisplay['small_animals_carry_volume'] ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    小動物搬入量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: resultUsageDisplay['combustible_bulk_waste_carry_volume'] ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    可燃粗大ごみ搬入量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: resultUsageDisplay['shredded_waste_residue_carry_volume'] ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    破砕ごみ残渣等搬入量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: resultUsageDisplay['disaster_waste_internal_carry_volume'] ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    災害ごみ組合内搬入量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: resultUsageDisplay['disaster_waste_external_carry_volume'] ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    災害ごみ組合外搬入量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: resultUsageDisplay['other_carry_volume'] ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    その他搬入量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: resultUsageDisplay['incineration_facility_bottom_ash_carry_volume'] ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    焼却施設底ごみ搬入量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: resultUsageDisplay['recyclables_carry_volume'] ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    資源物焼却処分搬入量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: resultUsageDisplay['total_carry_volume'] ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    搬入量合計[t]
                  </td>
                </tr>
                {Array.from({ length: sessionResource.furnacesCount }).map((_, furnacesIndex) => (
                  <>
                    <tr className="table-carryrow">
                      <td
                        className="table-carrydoublecell-14-item-head"
                        rowSpan={
                          isMonthDisplay()
                            ? 2 +
                              Number(resultUsageDisplay['burn_load']) +
                              Number(resultUsageDisplay['burn_load_include_shutdown']) +
                              Number(resultUsageDisplay['waste_lower_heating_value'])
                            : 1 + Number(resultUsageDisplay['waste_lower_heating_value'])
                        }
                      >
                        {String(furnacesIndex + 1).concat('号炉')}
                      </td>
                      <td className="table-carrycell-14">焼却量[t]</td>
                    </tr>
                    <tr className="table-carryrow" style={{ display: resultUsageDisplay['burn_load'] && isMonthDisplay() ? 'table-row' : 'none' }}>
                      <td className="table-carrycell-14">焼却負荷率[%]</td>
                    </tr>
                    <tr className="table-carryrow" style={{ display: resultUsageDisplay['burn_load_include_shutdown'] && isMonthDisplay() ? 'table-row' : 'none' }}>
                      <td className="table-carrycell-14">焼却負荷率[%]</td>
                    </tr>
                    <tr className="table-carryrow" style={{ display: isMonthDisplay() ? 'table-row' : 'none' }}>
                      <td className="table-carrycell-14">運転日数[日]</td>
                    </tr>
                    <tr className="table-carryrow" style={{ display: resultUsageDisplay['waste_lower_heating_value'] ? 'table-row' : 'none' }}>
                      <td className="table-carrydoublecell-14" colSpan={2}>
                        ごみ低位発熱量
                      </td>
                    </tr>
                  </>
                ))}
                <tr className="table-carryrow">
                  <td
                    className="table-carrydoublecell-14-item-head"
                    rowSpan={isMonthDisplay() ? 1 + Number(resultUsageDisplay['burn_load']) + Number(resultUsageDisplay['burn_load_include_shutdown']) : 1}
                  >
                    全炉
                  </td>
                  <td className="table-carrycell-14"> 焼却量[t]</td>
                </tr>
                <tr className="table-carryrow" style={{ display: resultUsageDisplay['burn_load'] && isMonthDisplay() ? 'table-row' : 'none' }}>
                  <td className="table-carrycell-14">焼却負荷率[%]</td>
                </tr>
                <tr className="table-carryrow" style={{ display: resultUsageDisplay['burn_load_include_shutdown'] && isMonthDisplay() ? 'table-row' : 'none' }}>
                  <td className="table-carrycell-14">焼却負荷率[%]</td>
                </tr>
                <tr className="table-carryrow" style={{ display: isMonthDisplay() ? 'none' : 'table-row' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    ピット残量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: resultUsageDisplay['main_ash_generate'] && showAshVisible ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    主灰発生量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: resultUsageDisplay['main_ash_carry_out_units'] && showAshVisible ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    主灰搬出台数[台]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: resultUsageDisplay['main_ash_carry_out'] && showAshVisible ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    主灰搬出量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: showAshVisible && !isMonthDisplay() ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    主灰ピット残量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: resultUsageDisplay['fly_ash_generate'] && showAshVisible ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    飛灰発生量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: resultUsageDisplay['fly_ash_carry_out_units'] && showAshVisible ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    飛灰搬出台数[台]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: resultUsageDisplay['fly_ash_carry_out'] && showAshVisible ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    飛灰搬出量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: resultUsageDisplay['dry_fly_ash_carry_out'] && showAshVisible ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    飛灰処理物搬出量 乾灰[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: resultUsageDisplay['processed_fly_ash_carry_out'] && showAshVisible ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    飛灰処理物搬出量 処理灰[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: showAshVisible && !isMonthDisplay() ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    飛灰ピット残量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: resultUsageDisplay['hot_water_supply_heating_value'] ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    温水供給設備供給熱量
                  </td>
                </tr>
                <tr
                  className="table-carryrow"
                  style={{ display: resultUsageDisplay['furnace_operating_days'] && isMonthDisplay() && showOperationDaysVisible ? 'table-row' : 'none' }}
                >
                  <td className="table-carrydoublecell-14-item-head" rowSpan={sessionResource.furnacesCount + 1}>
                    炉別
                    <br />
                    運転日数
                  </td>
                  <td className="table-carrydoublecell-14">全炉停止(日)</td>
                </tr>
                {Array.from({ length: sessionResource.furnacesCount }).map((_, furnacesIndex) => (
                  <tr
                    className="table-carryrow"
                    style={{ display: resultUsageDisplay['furnace_operating_days'] && isMonthDisplay() && showOperationDaysVisible ? 'table-row' : 'none' }}
                  >
                    <td className="table-carrydoublecell-14">{`${furnacesIndex + 1}炉運転日数`}</td>
                  </tr>
                ))}
              </tbody>
              <tbody>
                <tr className="table-carryrow" style={{ display: isSelectDraft.some((value) => value) ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14-blue" colSpan={2}>
                    {'計画立案'}
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: isSelectDraft.some((value) => value) && draftUsageDisplay['carry_volume'] ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    搬入量[t]
                  </td>
                </tr>
                <tr
                  className="table-carryrow"
                  style={{ display: isSelectDraft.some((value) => value) && draftUsageDisplay['combustible_waste_carry_volume'] ? 'table-row' : 'none' }}
                >
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    可燃ごみ搬入量[t]
                  </td>
                </tr>
                <tr
                  className="table-carryrow"
                  style={{ display: isSelectDraft.some((value) => value) && draftUsageDisplay['pruning_branches_carry_volume'] ? 'table-row' : 'none' }}
                >
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    剪定枝等搬入量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: isSelectDraft.some((value) => value) && draftUsageDisplay['small_animals_carry_volume'] ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    小動物搬入量[t]
                  </td>
                </tr>
                <tr
                  className="table-carryrow"
                  style={{ display: isSelectDraft.some((value) => value) && draftUsageDisplay['combustible_bulk_waste_carry_volume'] ? 'table-row' : 'none' }}
                >
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    可燃粗大ごみ搬入量[t]
                  </td>
                </tr>
                <tr
                  className="table-carryrow"
                  style={{ display: isSelectDraft.some((value) => value) && draftUsageDisplay['shredded_waste_residue_carry_volume'] ? 'table-row' : 'none' }}
                >
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    破砕ごみ残渣等搬入量[t]
                  </td>
                </tr>
                <tr
                  className="table-carryrow"
                  style={{ display: isSelectDraft.some((value) => value) && draftUsageDisplay['disaster_waste_internal_carry_volume'] ? 'table-row' : 'none' }}
                >
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    災害ごみ組合内搬入量[t]
                  </td>
                </tr>
                <tr
                  className="table-carryrow"
                  style={{ display: isSelectDraft.some((value) => value) && draftUsageDisplay['disaster_waste_external_carry_volume'] ? 'table-row' : 'none' }}
                >
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    災害ごみ組合外搬入量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: isSelectDraft.some((value) => value) && draftUsageDisplay['other_carry_volume'] ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    その他搬入量[t]
                  </td>
                </tr>
                <tr
                  className="table-carryrow"
                  style={{ display: isSelectDraft.some((value) => value) && draftUsageDisplay['incineration_facility_bottom_ash_carry_volume'] ? 'table-row' : 'none' }}
                >
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    焼却施設底ごみ搬入量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: isSelectDraft.some((value) => value) && draftUsageDisplay['recyclables_carry_volume'] ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    資源物焼却処分搬入量[t]
                  </td>
                </tr>
                <tr className="table-carryrow" style={{ display: isSelectDraft.some((value) => value) && draftUsageDisplay['total_carry_volume'] ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14" colSpan={2}>
                    搬入量合計[t]
                  </td>
                </tr>
                {Array.from({ length: draftPattern.length }).map((_, draftIndex) => (
                  <>
                    <tr className="table-carryrow" style={{ display: isSelectDraft[draftIndex] ? 'table-row' : 'none' }}>
                      <td className="table-carrydoublecell-14-blue" colSpan={2}>
                        {draftPattern[draftIndex].priority_name}
                      </td>
                    </tr>
                    {Array.from({ length: sessionResource.furnacesCount }).map((_, furnacesIndex) => (
                      <>
                        <tr className="table-carryrow" style={{ display: isSelectDraft[draftIndex] ? 'table-row' : 'none' }}>
                          <td
                            className="table-carrydoublecell-14-item-head"
                            rowSpan={
                              isSelectDraft[draftIndex] && isMonthDisplay()
                                ? 2 +
                                  Number(draftUsageDisplay['burn_load']) +
                                  Number(draftUsageDisplay['burn_load_include_shutdown']) +
                                  Number(draftUsageDisplay['waste_lower_heating_value'])
                                : 1 + Number(draftUsageDisplay['waste_lower_heating_value'])
                            }
                          >
                            {String(furnacesIndex + 1).concat('号炉')}
                          </td>
                          <td className="table-carrycell-14"> 焼却量[t]</td>
                        </tr>
                        <tr className="table-carryrow" style={{ display: draftUsageDisplay['burn_load'] && isSelectDraft[draftIndex] && isMonthDisplay() ? 'table-row' : 'none' }}>
                          <td className="table-carrycell-14"> 焼却負荷率[%]</td>
                        </tr>
                        <tr
                          className="table-carryrow"
                          style={{ display: draftUsageDisplay['burn_load_include_shutdown'] && isSelectDraft[draftIndex] && isMonthDisplay() ? 'table-row' : 'none' }}
                        >
                          <td className="table-carrycell-14"> 焼却負荷率[%]</td>
                        </tr>
                        <tr className="table-carryrow" style={{ display: isSelectDraft[draftIndex] && isMonthDisplay() ? 'table-row' : 'none' }}>
                          <td className="table-carrycell-14">運転日数[日]</td>
                        </tr>
                        <tr className="table-carryrow" style={{ display: isSelectDraft[draftIndex] && draftUsageDisplay['waste_lower_heating_value'] ? 'table-row' : 'none' }}>
                          <td className="table-carrydoublecell-14" colSpan={2}>
                            ごみ低位発熱量
                          </td>
                        </tr>
                      </>
                    ))}
                    <tr className="table-carryrow" style={{ display: isSelectDraft[draftIndex] ? 'table-row' : 'none' }}>
                      <td
                        className="table-carrydoublecell-14-item-head"
                        rowSpan={isMonthDisplay() ? 1 + Number(draftUsageDisplay['burn_load']) + Number(draftUsageDisplay['burn_load_include_shutdown']) : 1}
                      >
                        全炉
                      </td>
                      <td className="table-carrycell-14"> 焼却量[t]</td>
                    </tr>
                    <tr className="table-carryrow" style={{ display: draftUsageDisplay['burn_load'] && isSelectDraft[draftIndex] && isMonthDisplay() ? 'table-row' : 'none' }}>
                      <td className="table-carrycell-14"> 焼却負荷率[%]</td>
                    </tr>
                    <tr
                      className="table-carryrow"
                      style={{ display: draftUsageDisplay['burn_load_include_shutdown'] && isSelectDraft[draftIndex] && isMonthDisplay() ? 'table-row' : 'none' }}
                    >
                      <td className="table-carrycell-14"> 焼却負荷率[%]</td>
                    </tr>
                    <tr className="table-carryrow" style={{ display: isSelectDraft[draftIndex] && !isMonthDisplay() ? 'table-row' : 'none' }}>
                      <td className="table-carrydoublecell-14" colSpan={2}>
                        ピット残量[t]
                      </td>
                    </tr>
                    <tr
                      className="table-carryrow"
                      style={{ display: isSelectDraft[draftIndex] && draftUsageDisplay['main_ash_generate'] && showAshVisible ? 'table-row' : 'none' }}
                    >
                      <td className="table-carrydoublecell-14" colSpan={2}>
                        主灰発生量[t]
                      </td>
                    </tr>
                    <tr
                      className="table-carryrow"
                      style={{ display: isSelectDraft[draftIndex] && draftUsageDisplay['main_ash_carry_out_units'] && showAshVisible ? 'table-row' : 'none' }}
                    >
                      <td className="table-carrydoublecell-14" colSpan={2}>
                        主灰搬出台数[台]
                      </td>
                    </tr>
                    <tr
                      className="table-carryrow"
                      style={{ display: isSelectDraft[draftIndex] && draftUsageDisplay['main_ash_carry_out'] && showAshVisible ? 'table-row' : 'none' }}
                    >
                      <td className="table-carrydoublecell-14" colSpan={2}>
                        主灰搬出量[t]
                      </td>
                    </tr>
                    <tr className="table-carryrow" style={{ display: isSelectDraft[draftIndex] && showAshVisible && !isMonthDisplay() ? 'table-row' : 'none' }}>
                      <td className="table-carrydoublecell-14" colSpan={2}>
                        主灰ピット残量[t]
                      </td>
                    </tr>
                    <tr className="table-carryrow" style={{ display: isSelectDraft[draftIndex] && draftUsageDisplay['fly_ash_generate'] && showAshVisible ? 'table-row' : 'none' }}>
                      <td className="table-carrydoublecell-14" colSpan={2}>
                        飛灰発生量[t]
                      </td>
                    </tr>
                    <tr
                      className="table-carryrow"
                      style={{ display: isSelectDraft[draftIndex] && draftUsageDisplay['fly_ash_carry_out_units'] && showAshVisible ? 'table-row' : 'none' }}
                    >
                      <td className="table-carrydoublecell-14" colSpan={2}>
                        飛灰搬出台数[台]
                      </td>
                    </tr>
                    <tr
                      className="table-carryrow"
                      style={{ display: isSelectDraft[draftIndex] && draftUsageDisplay['fly_ash_carry_out'] && showAshVisible ? 'table-row' : 'none' }}
                    >
                      <td className="table-carrydoublecell-14" colSpan={2}>
                        飛灰搬出量[t]
                      </td>
                    </tr>
                    <tr
                      className="table-carryrow"
                      style={{ display: isSelectDraft[draftIndex] && draftUsageDisplay['dry_fly_ash_carry_out'] && showAshVisible ? 'table-row' : 'none' }}
                    >
                      <td className="table-carrydoublecell-14" colSpan={2}>
                        飛灰処理物搬出量 乾灰[t]
                      </td>
                    </tr>
                    <tr
                      className="table-carryrow"
                      style={{ display: isSelectDraft[draftIndex] && draftUsageDisplay['processed_fly_ash_carry_out'] && showAshVisible ? 'table-row' : 'none' }}
                    >
                      <td className="table-carrydoublecell-14" colSpan={2}>
                        飛灰処理物搬出量 処理灰[t]
                      </td>
                    </tr>
                    <tr className="table-carryrow" style={{ display: isSelectDraft[draftIndex] && showAshVisible && !isMonthDisplay() ? 'table-row' : 'none' }}>
                      <td className="table-carrydoublecell-14" colSpan={2}>
                        飛灰ピット残量[t]
                      </td>
                    </tr>
                    <tr className="table-carryrow" style={{ display: isSelectDraft[draftIndex] && draftUsageDisplay['hot_water_supply_heating_value'] ? 'table-row' : 'none' }}>
                      <td className="table-carrydoublecell-14" colSpan={2}>
                        温水供給設備供給熱量
                      </td>
                    </tr>
                    <tr
                      className="table-carryrow"
                      style={{
                        display: draftUsageDisplay['furnace_operating_days'] && isSelectDraft[draftIndex] && isMonthDisplay() && showOperationDaysVisible ? 'table-row' : 'none',
                      }}
                    >
                      <td className="table-carrydoublecell-14-item-head" rowSpan={sessionResource.furnacesCount + 1}>
                        炉別
                        <br />
                        運転日数
                      </td>
                      <td className="table-carrydoublecell-14">全炉停止(日)</td>
                    </tr>
                    {Array.from({ length: sessionResource.furnacesCount }).map((_, furnacesIndex) => (
                      <tr
                        className="table-carryrow"
                        style={{
                          display: draftUsageDisplay['furnace_operating_days'] && isSelectDraft[draftIndex] && isMonthDisplay() && showOperationDaysVisible ? 'table-row' : 'none',
                        }}
                      >
                        <td className="table-carrydoublecell-14">{`${furnacesIndex + 1}炉運転日数`}</td>
                      </tr>
                    ))}
                  </>
                ))}
              </tbody>
            </table>
            <table
              style={{
                borderCollapse: 'collapse',
                zIndex: 1,
                position: 'relative',
              }}
            >
              {/* 年・月のテーブルヘッダ表示 */}
              <thead className="table-sticky-header">
                <tr className="table-carryrow">
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td className="table-carrycell-blue">
                      {isMonthDisplay()
                        ? months[colIndex] + '月'
                        : `${colIndex + 1}日(${WEEK_DAYS[new Date(createDay(displayYear, displayDays, lastDayOfLastMonth + colIndex)).getDay()]})`}
                    </td>
                  ))}
                </tr>
              </thead>

              {/* ##### 期初計画情報 テーブル表示・入力管理 #### */}
              <tbody style={{ display: showInitialPlanVisible ? 'table-row-group' : 'none' }}>
                <tr className="table-carryrow" style={{ zIndex: 2 }}>
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td className="table-carrycell-initial-plan" style={{ width: isMonthDisplay() ? MONTH_CELL_WIDTH : '' }}>
                      {'期初計画'}
                    </td>
                  ))}
                </tr>
                {/* 搬入量[t] */}
                <tr className="table-carryrow" style={{ display: initialPlanUsageDisplay['carry_volume'] ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td key={colIndex} className={'table-carrycell'} style={{ pointerEvents: 'none' }}>
                      <EmptyNumberInput
                        className="table-inputcell"
                        colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                        data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                        data-key={[DriveMap.carry_volume]}
                        formValueState={initialPlanEditResourceState}
                        emptyValueState={initialPlanEmptyCheckState}
                        mathCalc={(value: number) => calcMathRound(value, CARRY_BLUR_DIGIT)}
                        significantDigits={DRIVE_INPUT_DIGITS['carry_volume']}
                      />
                    </td>
                  ))}
                </tr>
                {/* 可燃ごみ搬入量[t] */}
                <tr className="table-carryrow" style={{ display: initialPlanUsageDisplay['combustible_waste_carry_volume'] ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td className="table-carrycell" style={{ pointerEvents: 'none' }}>
                      <EmptyNumberInput
                        className="table-inputcell"
                        colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                        data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                        data-key={[DriveMap.combustible_waste_carry_volume]}
                        formValueState={initialPlanEditResourceState}
                        emptyValueState={initialPlanEmptyCheckState}
                        mathCalc={(value: number) => calcMathRound(value, CARRY_BLUR_DIGIT)}
                        significantDigits={DRIVE_INPUT_DIGITS['combustible_waste_carry_volume']}
                      />
                    </td>
                  ))}
                </tr>
                {/* 剪定枝等搬入量[t] */}
                <tr className="table-carryrow" style={{ display: initialPlanUsageDisplay['pruning_branches_carry_volume'] ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td className="table-carrycell" style={{ pointerEvents: 'none' }}>
                      <EmptyNumberInput
                        className="table-inputcell"
                        colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                        data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                        data-key={[DriveMap.pruning_branches_carry_volume]}
                        formValueState={initialPlanEditResourceState}
                        emptyValueState={initialPlanEmptyCheckState}
                        mathCalc={(value: number) => calcMathRound(value, CARRY_BLUR_DIGIT)}
                        significantDigits={DRIVE_INPUT_DIGITS['pruning_branches_carry_volume']}
                      />
                    </td>
                  ))}
                </tr>
                {/* 小動物搬入量[t] */}
                <tr className="table-carryrow" style={{ display: initialPlanUsageDisplay['small_animals_carry_volume'] ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td className="table-carrycell" style={{ pointerEvents: 'none' }}>
                      <EmptyNumberInput
                        className="table-inputcell"
                        colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                        data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                        data-key={[DriveMap.small_animals_carry_volume]}
                        formValueState={initialPlanEditResourceState}
                        emptyValueState={initialPlanEmptyCheckState}
                        mathCalc={(value: number) => calcMathRound(value, CARRY_BLUR_DIGIT)}
                        significantDigits={DRIVE_INPUT_DIGITS['small_animals_carry_volume']}
                      />
                    </td>
                  ))}
                </tr>
                {/* 可燃粗大ごみ搬入量[t] */}
                <tr className="table-carryrow" style={{ display: initialPlanUsageDisplay['combustible_bulk_waste_carry_volume'] ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td className="table-carrycell" style={{ pointerEvents: 'none' }}>
                      <EmptyNumberInput
                        className="table-inputcell"
                        colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                        data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                        data-key={[DriveMap.combustible_bulk_waste_carry_volume]}
                        formValueState={initialPlanEditResourceState}
                        emptyValueState={initialPlanEmptyCheckState}
                        mathCalc={(value: number) => calcMathRound(value, CARRY_BLUR_DIGIT)}
                        significantDigits={DRIVE_INPUT_DIGITS['combustible_bulk_waste_carry_volume']}
                      />
                    </td>
                  ))}
                </tr>
                {/* 破砕ごみ残渣等搬入量[t] */}
                <tr className="table-carryrow" style={{ display: initialPlanUsageDisplay['shredded_waste_residue_carry_volume'] ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td className="table-carrycell" style={{ pointerEvents: 'none' }}>
                      <EmptyNumberInput
                        className="table-inputcell"
                        colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                        data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                        data-key={[DriveMap.shredded_waste_residue_carry_volume]}
                        formValueState={initialPlanEditResourceState}
                        emptyValueState={initialPlanEmptyCheckState}
                        mathCalc={(value: number) => calcMathRound(value, CARRY_BLUR_DIGIT)}
                        significantDigits={DRIVE_INPUT_DIGITS['shredded_waste_residue_carry_volume']}
                      />
                    </td>
                  ))}
                </tr>
                {/* 災害ごみ組合内搬入量[t] */}
                <tr className="table-carryrow" style={{ display: initialPlanUsageDisplay['disaster_waste_internal_carry_volume'] ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td className="table-carrycell" style={{ pointerEvents: 'none' }}>
                      <EmptyNumberInput
                        className="table-inputcell"
                        colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                        data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                        data-key={[DriveMap.disaster_waste_internal_carry_volume]}
                        formValueState={initialPlanEditResourceState}
                        emptyValueState={initialPlanEmptyCheckState}
                        mathCalc={(value: number) => calcMathRound(value, CARRY_BLUR_DIGIT)}
                        significantDigits={DRIVE_INPUT_DIGITS['disaster_waste_internal_carry_volume']}
                      />
                    </td>
                  ))}
                </tr>
                {/* 災害ごみ組合外搬入量[t] */}
                <tr className="table-carryrow" style={{ display: initialPlanUsageDisplay['disaster_waste_external_carry_volume'] ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td className="table-carrycell" style={{ pointerEvents: 'none' }}>
                      <EmptyNumberInput
                        className="table-inputcell"
                        colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                        data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                        data-key={[DriveMap.disaster_waste_external_carry_volume]}
                        formValueState={initialPlanEditResourceState}
                        emptyValueState={initialPlanEmptyCheckState}
                        mathCalc={(value: number) => calcMathRound(value, CARRY_BLUR_DIGIT)}
                        significantDigits={DRIVE_INPUT_DIGITS['disaster_waste_external_carry_volume']}
                      />
                    </td>
                  ))}
                </tr>
                {/* その他搬入量[t] */}
                <tr className="table-carryrow" style={{ display: initialPlanUsageDisplay['other_carry_volume'] ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td className="table-carrycell" style={{ pointerEvents: 'none' }}>
                      <EmptyNumberInput
                        className="table-inputcell"
                        colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                        data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                        data-key={[DriveMap.other_carry_volume]}
                        formValueState={initialPlanEditResourceState}
                        emptyValueState={initialPlanEmptyCheckState}
                        mathCalc={(value: number) => calcMathRound(value, CARRY_BLUR_DIGIT)}
                        significantDigits={DRIVE_INPUT_DIGITS['other_carry_volume']}
                      />
                    </td>
                  ))}
                </tr>
                {/* 焼却施設底ごみ搬入量[t] */}
                <tr className="table-carryrow" style={{ display: initialPlanUsageDisplay['incineration_facility_bottom_ash_carry_volume'] ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td className="table-carrycell" style={{ pointerEvents: 'none' }}>
                      <EmptyNumberInput
                        className="table-inputcell"
                        colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                        data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                        data-key={[DriveMap.incineration_facility_bottom_ash_carry_volume]}
                        formValueState={initialPlanEditResourceState}
                        emptyValueState={initialPlanEmptyCheckState}
                        mathCalc={(value: number) => calcMathRound(value, CARRY_BLUR_DIGIT)}
                        significantDigits={DRIVE_INPUT_DIGITS['incineration_facility_bottom_ash_carry_volume']}
                      />
                    </td>
                  ))}
                </tr>
                {/* 資源物焼却処分搬入量[t] */}
                <tr className="table-carryrow" style={{ display: initialPlanUsageDisplay['recyclables_carry_volume'] ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td className="table-carrycell" style={{ pointerEvents: 'none' }}>
                      <EmptyNumberInput
                        className="table-inputcell"
                        colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                        data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                        data-key={[DriveMap.recyclables_carry_volume]}
                        formValueState={initialPlanEditResourceState}
                        emptyValueState={initialPlanEmptyCheckState}
                        mathCalc={(value: number) => calcMathRound(value, CARRY_BLUR_DIGIT)}
                        significantDigits={DRIVE_INPUT_DIGITS['recyclables_carry_volume']}
                      />
                    </td>
                  ))}
                </tr>
                {/* 搬入量合計[t] */}
                <tr className="table-carryrow" style={{ display: initialPlanUsageDisplay['total_carry_volume'] ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td className="table-carrycell">
                      <div className="table-inputcell m-auto">
                        {!CARRY_KEYS.every((key) => (isMonthDisplay() ? planEmptyCheck.month[colIndex][key] : planEmptyCheck.day[lastDayOfLastMonth + colIndex][key]))
                          ? addComma(calcMathRound(initialPlanMonitorResource[isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex].total_carry_volume, CARRY_BLUR_DIGIT))
                          : ''}
                      </div>
                    </td>
                  ))}
                </tr>

                {/* 各炉の状態 */}
                {Array.from({ length: sessionResource.furnacesCount }).map((_, furnacesIndex) => (
                  <>
                    {/* 焼却量[t] */}
                    <tr className="table-carryrow">
                      {Array.from({
                        length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                      }).map((_, colIndex) => (
                        <td
                          className="table-carrycell"
                          style={{
                            pointerEvents: 'none',
                            background: !isMonthDisplay() && planWarningVals[furnacesIndex][lastDayOfLastMonth + colIndex] ? 'yellow' : undefined,
                          }}
                        >
                          <EmptyNumberInput
                            className={`table-inputcell${
                              !isMonthDisplay() && planEditResource.day[lastDayOfLastMonth + colIndex].burn_volume[furnacesIndex] > conditionResource.rateCapacity
                                ? ' color-red'
                                : ''
                            }`}
                            colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                            rowIndex={furnacesIndex}
                            data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                            data-key={[DriveMap.burn_volume]}
                            formValueState={initialPlanEditResourceState}
                            emptyValueState={initialPlanEmptyCheckState}
                            mathCalc={(value: number) => calcMathRound(value, BURNVOLUME_BLUR_DIGIT)}
                            validateChange={validBurnChange}
                            significantDigits={DRIVE_INPUT_DIGITS['burn_volume']}
                          />
                        </td>
                      ))}
                    </tr>
                    {/* 焼却負荷率(停炉日を含まない)[%] */}
                    <tr className="table-carryrow" style={{ display: initialPlanUsageDisplay['burn_load'] && isMonthDisplay() ? 'table-row' : 'none' }}>
                      {Array.from({
                        length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                      }).map((_, colIndex) => (
                        <td className="table-carrycell">
                          {isMonthDisplay()
                            ? !planEmptyCheck.month[colIndex].burn_volume[furnacesIndex]
                              ? addComma(calcMathFloor(initialPlanMonitorMonthResource[colIndex].burn_load[furnacesIndex], BURNLOAD_DIGIT))
                              : ''
                            : ''}
                        </td>
                      ))}
                    </tr>
                    {/* 焼却負荷率(停炉日を含む)[%] */}
                    <tr className="table-carryrow" style={{ display: initialPlanUsageDisplay['burn_load_include_shutdown'] && isMonthDisplay() ? 'table-row' : 'none' }}>
                      {Array.from({
                        length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                      }).map((_, colIndex) => (
                        <td className="table-carrycell">
                          {isMonthDisplay()
                            ? !planEmptyCheck.month[colIndex].burn_volume[furnacesIndex]
                              ? addComma(calcMathFloor(initialPlanMonitorMonthResource[colIndex].burn_load_include_shutdown[furnacesIndex], BURNLOAD_DIGIT))
                              : ''
                            : ''}
                        </td>
                      ))}
                    </tr>
                    {/* 運転日数[日] */}
                    <tr className="table-carryrow" style={{ display: isMonthDisplay() ? 'table-row' : 'none' }}>
                      {Array.from({
                        length: isMonthDisplay() ? displayDays.length : 0,
                      }).map((_, colIndex) => (
                        <td className="table-carrycell">
                          {!planEmptyCheck.month[colIndex].burn_volume[furnacesIndex] ? initialPlanMonitorMonthResource[colIndex].operating_days[furnacesIndex] : ''}
                        </td>
                      ))}
                    </tr>
                    {/* ごみ低位発熱量 */}
                    <tr className="table-carryrow" style={{ display: initialPlanUsageDisplay['waste_lower_heating_value'] ? 'table-row' : 'none' }}>
                      {Array.from({
                        length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                      }).map((_, colIndex) => (
                        <td className="table-carrycell" style={{ pointerEvents: 'none' }}>
                          <EmptyNumberInput
                            className="table-inputcell"
                            colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                            rowIndex={furnacesIndex}
                            data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                            data-key={[DriveMap.waste_lower_heating_value]}
                            formValueState={initialPlanEditResourceState}
                            emptyValueState={initialPlanEmptyCheckState}
                            mathCalc={(value: number) => calcMathRound(value, WASTE_BLUR_DIGIT)}
                            significantDigits={DRIVE_INPUT_DIGITS['waste_lower_heating_value']}
                          />
                        </td>
                      ))}
                    </tr>
                  </>
                ))}
                {/* 全炉 */}
                {/* 焼却量[t] */}
                <tr className="table-carryrow">
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) =>
                    isMonthDisplay() ? (
                      <td className="table-carrycell">
                        {!planEmptyCheck.month[colIndex].burn_volume.some((value) => value === true)
                          ? addComma(
                              calcMathRound(
                                Array.from({ length: sessionResource.furnacesCount }, (_, furnacesIndex) => planEditResource.month[colIndex].burn_volume[furnacesIndex]).reduce(
                                  (sum, value) => sum + value,
                                  0,
                                ),
                                BURNVOLUME_BLUR_DIGIT,
                              ),
                            )
                          : ''}
                      </td>
                    ) : (
                      <td className="table-carrycell">
                        {!planEmptyCheck.day[lastDayOfLastMonth + colIndex].burn_volume.some((value) => value === true)
                          ? addComma(
                              calcMathRound(
                                Array.from(
                                  { length: sessionResource.furnacesCount },
                                  (_, furnacesIndex) => planEditResource.day[lastDayOfLastMonth + colIndex].burn_volume[furnacesIndex],
                                ).reduce((sum, value) => sum + value, 0),
                                BURNVOLUME_BLUR_DIGIT,
                              ),
                            )
                          : ''}
                      </td>
                    ),
                  )}
                </tr>
                {/* 焼却負荷率(停炉日を含まない)[%] */}
                <tr className="table-carryrow" style={{ display: initialPlanUsageDisplay['burn_load'] && isMonthDisplay() ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: displayDays.length,
                  }).map((_, colIndex) => (
                    <td className="table-carrycell">
                      {!planEmptyCheck.month[colIndex].burn_volume.some((value) => value === true)
                        ? addComma(calcMathFloor(initialPlanMonitorMonthResource[colIndex].burn_load_sum, BURNLOAD_DIGIT))
                        : ''}
                    </td>
                  ))}
                </tr>
                {/* 焼却負荷率(停炉日を含む)[%] */}
                <tr className="table-carryrow" style={{ display: initialPlanUsageDisplay['burn_load_include_shutdown'] && isMonthDisplay() ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: displayDays.length,
                  }).map((_, colIndex) => (
                    <td className="table-carrycell">
                      {!planEmptyCheck.month[colIndex].burn_volume.some((value) => value === true)
                        ? addComma(calcMathFloor(initialPlanMonitorMonthResource[colIndex].burn_load_include_shutdown_sum, BURNLOAD_DIGIT))
                        : ''}
                    </td>
                  ))}
                </tr>
                {/* ピット残量[t] */}
                <tr className="table-carryrow" style={{ display: isMonthDisplay() ? 'none' : 'table-row' }}>
                  {' '}
                  {Array.from({ length: displayDayCountOfMonth }).map((_, colIndex) =>
                    existDayFull(planEditResource.day[lastDayOfLastMonth + colIndex]) ? (
                      !planEmptyCheck.day[lastDayOfLastMonth + colIndex].trash_pit_rest === true ? (
                        <td className="table-carrycell" style={{ pointerEvents: 'none' }}>
                          {' '}
                          <EmptyNumberInput
                            className="table-inputcell"
                            colIndex={lastDayOfLastMonth + colIndex}
                            data-sign={[DraftMap.day]}
                            data-key={[DriveMap.trash_pit_rest]}
                            formValueState={initialPlanEditResourceState}
                            emptyValueState={initialPlanEmptyCheckState}
                            mathCalc={(value: number) => calcMathRound(value, TRASH_PIT_BLUR_DIGIT)}
                            significantDigits={DRIVE_INPUT_DIGITS['trash_pit_rest']}
                          />{' '}
                        </td>
                      ) : (
                        <td className="table-carrycell" />
                      )
                    ) : (
                      <td className="table-carrycell">
                        {' '}
                        {isDayFull(planEmptyCheck.day[lastDayOfLastMonth + colIndex], planDayUsage)
                          ? addComma(calcMathRound(planEditResource.day[lastDayOfLastMonth + colIndex].main_ash_pit_rest, TRASH_PIT_BLUR_DIGIT))
                          : ''}{' '}
                      </td>
                    ),
                  )}{' '}
                </tr>
                {/* 主灰発生量[t] */}
                <tr className="table-carryrow" style={{ display: initialPlanUsageDisplay['main_ash_generate'] && showAshVisible ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td className="table-carrycell" style={{ pointerEvents: 'none' }}>
                      <EmptyNumberInput
                        className="table-inputcell"
                        colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                        data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                        data-key={[DriveMap.main_ash_generate]}
                        formValueState={initialPlanEditResourceState}
                        emptyValueState={initialPlanEmptyCheckState}
                        mathCalc={(value: number) => calcMathRound(value, ASH_BLUR_DIGIT)}
                        significantDigits={DRIVE_INPUT_DIGITS['main_ash_generate']}
                      />
                    </td>
                  ))}
                </tr>
                {/* 主灰搬出台数[t] */}
                <tr className="table-carryrow" style={{ display: initialPlanUsageDisplay['main_ash_carry_out_units'] && showAshVisible ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td className="table-carrycell" style={{ pointerEvents: 'none' }}>
                      <EmptyNumberInput
                        className="table-inputcell"
                        colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                        data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                        data-key={[DriveMap.main_ash_carry_out_units]}
                        formValueState={initialPlanEditResourceState}
                        emptyValueState={initialPlanEmptyCheckState}
                        mathCalc={(value: number) => calcMathRound(value, ASH_BLUR_DIGIT)}
                        significantDigits={DRIVE_INPUT_DIGITS['main_ash_carry_out_units']}
                      />
                    </td>
                  ))}
                </tr>
                {/* 主灰搬出量[t] */}
                <tr className="table-carryrow" style={{ display: initialPlanUsageDisplay['main_ash_carry_out'] && showAshVisible ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td className="table-carrycell" style={{ pointerEvents: 'none' }}>
                      <EmptyNumberInput
                        className="table-inputcell"
                        colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                        data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                        data-key={[DriveMap.main_ash_carry_out]}
                        formValueState={initialPlanEditResourceState}
                        emptyValueState={initialPlanEmptyCheckState}
                        mathCalc={(value: number) => calcMathRound(value, ASH_BLUR_DIGIT)}
                        significantDigits={DRIVE_INPUT_DIGITS['main_ash_carry_out']}
                      />
                    </td>
                  ))}
                </tr>
                {/* 主灰ピット残量[t] */}
                <tr className="table-carryrow" style={{ display: showAshVisible && !isMonthDisplay() ? 'table-row' : 'none' }}>
                  {' '}
                  {Array.from({ length: displayDayCountOfMonth }).map((_, colIndex) =>
                    existDayFull(planEditResource.day[lastDayOfLastMonth + colIndex]) ? (
                      !planEmptyCheck.day[lastDayOfLastMonth + colIndex].main_ash_generate === true ? (
                        <td className="table-carrycell" style={{ pointerEvents: 'none' }}>
                          {' '}
                          <EmptyNumberInput
                            className="table-inputcell"
                            colIndex={lastDayOfLastMonth + colIndex}
                            data-sign={[DraftMap.day]}
                            data-key={[DriveMap.main_ash_pit_rest]}
                            formValueState={initialPlanEditResourceState}
                            emptyValueState={initialPlanEmptyCheckState}
                            mathCalc={(value: number) => calcMathRound(value, TRASH_PIT_BLUR_DIGIT)}
                            significantDigits={DRIVE_INPUT_DIGITS['main_ash_pit_rest']}
                          />{' '}
                        </td>
                      ) : (
                        <td className="table-carrycell" />
                      )
                    ) : (
                      <td className="table-carrycell">
                        {' '}
                        {isDayFull(planEmptyCheck.day[lastDayOfLastMonth + colIndex], planDayUsage)
                          ? addComma(calcMathRound(planEditResource.day[lastDayOfLastMonth + colIndex].main_ash_pit_rest, TRASH_PIT_BLUR_DIGIT))
                          : ''}{' '}
                      </td>
                    ),
                  )}{' '}
                </tr>
                {/* 飛灰発生量[t] */}
                <tr className="table-carryrow" style={{ display: initialPlanUsageDisplay['fly_ash_generate'] && showAshVisible ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td className="table-carrycell" style={{ pointerEvents: 'none' }}>
                      <EmptyNumberInput
                        className="table-inputcell"
                        colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                        data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                        data-key={[DriveMap.fly_ash_generate]}
                        formValueState={initialPlanEditResourceState}
                        emptyValueState={initialPlanEmptyCheckState}
                        mathCalc={(value: number) => calcMathRound(value, ASH_BLUR_DIGIT)}
                        significantDigits={DRIVE_INPUT_DIGITS['fly_ash_generate']}
                      />
                    </td>
                  ))}
                </tr>
                {/* 飛灰搬出台数[台] */}
                <tr className="table-carryrow" style={{ display: initialPlanUsageDisplay['fly_ash_carry_out_units'] && showAshVisible ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td className="table-carrycell" style={{ pointerEvents: 'none' }}>
                      <EmptyNumberInput
                        className="table-inputcell"
                        colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                        data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                        data-key={[DriveMap.fly_ash_carry_out_units]}
                        formValueState={initialPlanEditResourceState}
                        emptyValueState={initialPlanEmptyCheckState}
                        mathCalc={(value: number) => calcMathRound(value, ASH_BLUR_DIGIT)}
                        significantDigits={DRIVE_INPUT_DIGITS['fly_ash_carry_out_units']}
                      />
                    </td>
                  ))}
                </tr>
                {/* 飛灰搬出量[t] */}
                <tr className="table-carryrow" style={{ display: initialPlanUsageDisplay['fly_ash_carry_out'] && showAshVisible ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td className="table-carrycell" style={{ pointerEvents: 'none' }}>
                      <EmptyNumberInput
                        className="table-inputcell"
                        colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                        data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                        data-key={[DriveMap.fly_ash_carry_out]}
                        formValueState={initialPlanEditResourceState}
                        emptyValueState={initialPlanEmptyCheckState}
                        mathCalc={(value: number) => calcMathRound(value, ASH_BLUR_DIGIT)}
                        significantDigits={DRIVE_INPUT_DIGITS['fly_ash_carry_out']}
                      />
                    </td>
                  ))}
                </tr>
                {/* 飛灰処理物搬出量 乾灰[t] */}
                <tr className="table-carryrow" style={{ display: initialPlanUsageDisplay['dry_fly_ash_carry_out'] && showAshVisible ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td className="table-carrycell" style={{ pointerEvents: 'none' }}>
                      <EmptyNumberInput
                        className="table-inputcell"
                        colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                        data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                        data-key={[DriveMap.dry_fly_ash_carry_out]}
                        formValueState={initialPlanEditResourceState}
                        emptyValueState={initialPlanEmptyCheckState}
                        mathCalc={(value: number) => calcMathRound(value, ASH_BLUR_DIGIT)}
                        significantDigits={DRIVE_INPUT_DIGITS['dry_fly_ash_carry_out']}
                      />
                    </td>
                  ))}
                </tr>
                {/* 飛灰処理物搬出量 処理灰[t] */}
                <tr className="table-carryrow" style={{ display: initialPlanUsageDisplay['processed_fly_ash_carry_out'] && showAshVisible ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td className="table-carrycell" style={{ pointerEvents: 'none' }}>
                      <EmptyNumberInput
                        className="table-inputcell"
                        colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                        data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                        data-key={[DriveMap.processed_fly_ash_carry_out]}
                        formValueState={initialPlanEditResourceState}
                        emptyValueState={initialPlanEmptyCheckState}
                        mathCalc={(value: number) => calcMathRound(value, ASH_BLUR_DIGIT)}
                        significantDigits={DRIVE_INPUT_DIGITS['processed_fly_ash_carry_out']}
                      />
                    </td>
                  ))}
                </tr>
                {/* 飛灰ピット残量[t] */}
                <tr className="table-carryrow" style={{ display: showAshVisible && !isMonthDisplay() ? 'table-row' : 'none' }}>
                  {' '}
                  {Array.from({ length: displayDayCountOfMonth }).map((_, colIndex) =>
                    existDayFull(planEditResource.day[lastDayOfLastMonth + colIndex]) ? (
                      !planEmptyCheck.day[lastDayOfLastMonth + colIndex].fly_ash_pit_rest === true ? (
                        <td className="table-carrycell" style={{ pointerEvents: 'none' }}>
                          {' '}
                          <EmptyNumberInput
                            className="table-inputcell"
                            colIndex={lastDayOfLastMonth + colIndex}
                            data-sign={[DraftMap.day]}
                            data-key={[DriveMap.fly_ash_pit_rest]}
                            formValueState={initialPlanEditResourceState}
                            emptyValueState={initialPlanEmptyCheckState}
                            mathCalc={(value: number) => calcMathRound(value, TRASH_PIT_BLUR_DIGIT)}
                            significantDigits={DRIVE_INPUT_DIGITS['fly_ash_pit_rest']}
                          />{' '}
                        </td>
                      ) : (
                        <td className="table-carrycell" />
                      )
                    ) : (
                      <td className="table-carrycell">
                        {' '}
                        {isDayFull(planEmptyCheck.day[lastDayOfLastMonth + colIndex], planDayUsage)
                          ? addComma(calcMathRound(planEditResource.day[lastDayOfLastMonth + colIndex].fly_ash_pit_rest, TRASH_PIT_BLUR_DIGIT))
                          : ''}{' '}
                      </td>
                    ),
                  )}{' '}
                </tr>
                {/* 温水供給設備供給熱量 */}
                <tr className="table-carryrow" style={{ display: initialPlanUsageDisplay['hot_water_supply_heating_value'] ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td className="table-carrycell" style={{ pointerEvents: 'none' }}>
                      <EmptyNumberInput
                        className="table-inputcell"
                        colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                        data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                        data-key={[DriveMap.hot_water_supply_heating_value]}
                        formValueState={initialPlanEditResourceState}
                        emptyValueState={initialPlanEmptyCheckState}
                        mathCalc={(value: number) => calcMathRound(value, HOT_WATER_BLUR_DIGIT)}
                        significantDigits={DRIVE_INPUT_DIGITS['hot_water_supply_heating_value']}
                      />
                    </td>
                  ))}
                </tr>
                {/* 炉別運転日数 */}
                {Array.from({ length: sessionResource.furnacesCount + 1 }).map((_, runIndex) => (
                  <tr
                    className="table-carryrow"
                    style={{ display: isMonthDisplay() && showOperationDaysVisible && initialPlanUsageDisplay['furnace_operating_days'] ? 'table-row' : 'none' }}
                  >
                    {Array.from({
                      length: displayDays.length,
                    }).map((_, colIndex) => (
                      <td className="table-carrycell">
                        {!planEmptyCheck.month[colIndex].burn_volume.some((value) => value === true)
                          ? initialPlanMonitorMonthResource[colIndex].furnace_operating_days[runIndex]
                          : ''}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>

              {/* ##### 計画＋実績情報 テーブル表示・入力管理 #### */}
              <tbody style={{ display: existAutoDraft && isResultVisible() && isPlanVisible() ? 'table-row-group' : 'none' }}>
                <tr className="table-carryrow" style={{ zIndex: 2 }}>
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td
                      className="table-carrycell"
                      style={{
                        background: isMonthDisplay()
                          ? existMonthFull(resultEditResource.day, colIndex)
                            ? 'var(--color-triad-1)'
                            : 'var(--color-triad-2)'
                          : existDayFull(resultEditResource.day[lastDayOfLastMonth + colIndex])
                            ? 'var(--color-triad-1)'
                            : 'var(--color-triad-2)',
                        width: isMonthDisplay() ? MONTH_CELL_WIDTH : '',
                      }}
                    >
                      {isMonthDisplay()
                        ? existMonthFull(resultEditResource.day, colIndex)
                          ? '実績'
                          : '計画'
                        : existDayFull(resultEditResource.day[lastDayOfLastMonth + colIndex])
                          ? '実績'
                          : '計画'}
                    </td>
                  ))}
                </tr>
                {/* 搬入量[t] */}
                <tr className="table-carryrow" style={{ display: planResultUsageDisplay['carry_volume'] ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) =>
                    isMonthDisplay() ? (
                      existMonthFull(resultEditResource.day, colIndex) ? (
                        //月 実績値
                        <td
                          className={isMonthDisplay() || !resultUsageDisplay['carry_volume'] ? 'table-carrycell' : 'table-carrycell-green'}
                          style={{ pointerEvents: isMonthDisplay() || !resultUsageDisplay['carry_volume'] ? 'none' : 'auto' }}
                        >
                          <EmptyNumberInput
                            className="table-inputcell"
                            colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                            data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                            data-key={[DriveMap.carry_volume]}
                            formValueState={resultEditResourceState}
                            emptyValueState={resultEmptyCheckState}
                            onBlur={(event, previousValue) => {
                              handleCommonBlurChange(
                                event,
                                resultEditResourceState,
                                resultEmptyCheckState,
                                resultInitResource,
                                resultUsageCalc,
                                isMonthDisplay() ? DraftMap.month : DraftMap.day,
                                DriveMap.carry_volume,
                                selectMonthIndex,
                                isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex,
                                undefined,
                                undefined,
                              );
                            }}
                            mathCalc={(value: number) => calcMathRound(value, CARRY_BLUR_DIGIT)}
                            significantDigits={DRIVE_INPUT_DIGITS['carry_volume']}
                          />
                        </td>
                      ) : (
                        <td
                          className={!planUsageDisplay['carry_volume'] || planDisableCheck.month[colIndex]['carry_volume'] ? 'table-carrycell' : 'table-carrycell-green'}
                          style={{ pointerEvents: !planUsageDisplay['carry_volume'] || planDisableCheck.month[colIndex]['carry_volume'] ? 'none' : 'auto' }}
                        >
                          <EmptyNumberInput
                            className="table-inputcell"
                            colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                            data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                            data-key={[DriveMap.carry_volume]}
                            formValueState={planEditResourceState}
                            emptyValueState={planEmptyCheckState}
                            onBlur={(event, previousValue) => {
                              handleCommonBlurChange(
                                event,
                                planEditResourceState,
                                planEmptyCheckState,
                                planInitResource,
                                planUsageCalc,
                                isMonthDisplay() ? DraftMap.month : DraftMap.day,
                                DriveMap.carry_volume,
                                selectMonthIndex,
                                isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex,
                                undefined,
                                updateCarryDependent,
                              );
                            }}
                            mathCalc={(value: number) => calcMathRound(value, CARRY_BLUR_DIGIT)}
                            significantDigits={DRIVE_INPUT_DIGITS['carry_volume']}
                          />
                        </td>
                      )
                    ) : existDayFull(resultEditResource.day[lastDayOfLastMonth + colIndex]) ? (
                      //日 実績値
                      <td
                        className={
                          !resultUsageDisplay['carry_volume'] || resultDisableCheck.day[lastDayOfLastMonth + colIndex]['carry_volume'] ? 'table-carrycell' : 'table-carrycell-green'
                        }
                        style={{ pointerEvents: !resultUsageDisplay['carry_volume'] || resultDisableCheck.day[lastDayOfLastMonth + colIndex]['carry_volume'] ? 'none' : 'auto' }}
                      >
                        <EmptyNumberInput
                          className="table-inputcell"
                          colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                          data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                          data-key={[DriveMap.carry_volume]}
                          formValueState={resultEditResourceState}
                          emptyValueState={resultEmptyCheckState}
                          onBlur={(event) => {
                            handleCommonBlurChange(
                              event,
                              resultEditResourceState,
                              resultEmptyCheckState,
                              resultInitResource,
                              resultUsageCalc,
                              isMonthDisplay() ? DraftMap.month : DraftMap.day,
                              DriveMap.carry_volume,
                              selectMonthIndex,
                              isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex,
                              undefined,
                              undefined,
                            );
                          }}
                          mathCalc={(value: number) => calcMathRound(value, CARRY_BLUR_DIGIT)}
                          significantDigits={DRIVE_INPUT_DIGITS['carry_volume']}
                        />
                      </td>
                    ) : (
                      <td
                        className={
                          !planUsageDisplay['carry_volume'] || planDisableCheck.day[lastDayOfLastMonth + colIndex]['carry_volume'] ? 'table-carrycell' : 'table-carrycell-green'
                        }
                        style={{
                          pointerEvents: !planUsageDisplay['carry_volume'] || planDisableCheck.day[lastDayOfLastMonth + colIndex]['carry_volume'] ? 'none' : 'auto',
                        }}
                      >
                        <EmptyNumberInput
                          className="table-inputcell"
                          colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                          data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                          data-key={[DriveMap.carry_volume]}
                          formValueState={planEditResourceState}
                          emptyValueState={planEmptyCheckState}
                          onBlur={(event) => {
                            handleCommonBlurChange(
                              event,
                              planEditResourceState,
                              planEmptyCheckState,
                              planInitResource,
                              planUsageCalc,
                              isMonthDisplay() ? DraftMap.month : DraftMap.day,
                              DriveMap.carry_volume,
                              selectMonthIndex,
                              isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex,
                              undefined,
                              updateCarryDependent,
                            );
                          }}
                          mathCalc={(value: number) => calcMathRound(value, CARRY_BLUR_DIGIT)}
                          significantDigits={DRIVE_INPUT_DIGITS['carry_volume']}
                        />
                      </td>
                    ),
                  )}
                </tr>
                {/* 可燃ごみ搬入量[t]～資源物焼却処分搬入量[t] */}
                {Array.from({
                  length: CARRY_DETAILS.length,
                }).map((_, mapIndex) => (
                  <tr
                    className="table-carryrow"
                    style={{
                      display: planResultUsageDisplay[CARRY_KEYS[mapIndex] as keyof DriveUsage] ? 'table-row' : 'none',
                    }}
                  >
                    {Array.from({
                      length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                    }).map((_, colIndex) =>
                      isMonthDisplay() ? (
                        existMonthFull(resultEditResource.day, colIndex) ? (
                          //月 実績値
                          <td className="table-carrycell" style={{ pointerEvents: 'none' }}>
                            <EmptyNumberInput
                              className="table-inputcell"
                              colIndex={colIndex}
                              data-sign={[DraftMap.month]}
                              data-key={[CARRY_DETAILS[mapIndex]]}
                              formValueState={resultEditResourceState}
                              emptyValueState={resultEmptyCheckState}
                              significantDigits={DRIVE_INPUT_DIGITS[CARRY_KEYS[mapIndex] as InputKeys]}
                            />
                          </td>
                        ) : (
                          <>
                            <td className="table-carrycell" style={{ pointerEvents: 'none' }}>
                              <EmptyNumberInput
                                className="table-inputcell"
                                colIndex={colIndex}
                                data-sign={[DraftMap.month]}
                                data-key={[CARRY_DETAILS[mapIndex]]}
                                formValueState={planEditResourceState}
                                emptyValueState={planEmptyCheckState}
                                significantDigits={DRIVE_INPUT_DIGITS[CARRY_KEYS[mapIndex] as InputKeys]}
                              />
                            </td>
                          </>
                        )
                      ) : existDayFull(resultEditResource.day[lastDayOfLastMonth + colIndex]) ? (
                        //日 実績値
                        <td
                          className={
                            !resultUsageDisplay[CARRY_KEYS[mapIndex] as keyof DriveUsage] || resultDisableCheck.day[lastDayOfLastMonth + colIndex][CARRY_KEYS[mapIndex]]
                              ? 'table-carrycell'
                              : 'table-carrycell-green'
                          }
                          style={{
                            pointerEvents:
                              !resultUsageDisplay[CARRY_KEYS[mapIndex] as keyof DriveUsage] || resultDisableCheck.day[lastDayOfLastMonth + colIndex][CARRY_KEYS[mapIndex]]
                                ? 'none'
                                : 'auto',
                          }}
                        >
                          <EmptyNumberInput
                            className="table-inputcell"
                            colIndex={lastDayOfLastMonth + colIndex}
                            data-sign={[DraftMap.day]}
                            data-key={[CARRY_DETAILS[mapIndex]]}
                            formValueState={resultEditResourceState}
                            emptyValueState={resultEmptyCheckState}
                            onBlur={(event) =>
                              handleCommonBlurChange(
                                event,
                                resultEditResourceState,
                                resultEmptyCheckState,
                                resultInitResource,
                                resultUsageCalc,
                                DraftMap.day,
                                CARRY_DETAILS[mapIndex],
                                selectMonthIndex,
                                isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex,
                                undefined,
                                undefined,
                              )
                            }
                            mathCalc={(value: number) => calcMathRound(value, CARRY_BLUR_DIGIT)}
                            significantDigits={DRIVE_INPUT_DIGITS[CARRY_KEYS[mapIndex] as InputKeys]}
                          />
                        </td>
                      ) : (
                        <td
                          className={
                            !planUsageDisplay[CARRY_KEYS[mapIndex] as keyof DriveUsage] || planDisableCheck.day[lastDayOfLastMonth + colIndex][CARRY_KEYS[mapIndex]]
                              ? 'table-carrycell'
                              : 'table-carrycell-green'
                          }
                          style={{
                            pointerEvents:
                              !planUsageDisplay[CARRY_KEYS[mapIndex] as keyof DriveUsage] || planDisableCheck.day[lastDayOfLastMonth + colIndex][CARRY_KEYS[mapIndex]]
                                ? 'none'
                                : 'auto',
                          }}
                        >
                          <EmptyNumberInput
                            className="table-inputcell"
                            colIndex={lastDayOfLastMonth + colIndex}
                            data-sign={[DraftMap.day]}
                            data-key={[CARRY_DETAILS[mapIndex]]}
                            formValueState={planEditResourceState}
                            emptyValueState={planEmptyCheckState}
                            onBlur={(event) =>
                              handleCommonBlurChange(
                                event,
                                planEditResourceState,
                                planEmptyCheckState,
                                planInitResource,
                                planUsageCalc,
                                DraftMap.day,
                                CARRY_DETAILS[mapIndex],
                                selectMonthIndex,
                                isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex,
                                undefined,
                                updateCarryDependent,
                              )
                            }
                            mathCalc={(value: number) => calcMathRound(value, CARRY_BLUR_DIGIT)}
                            significantDigits={DRIVE_INPUT_DIGITS[CARRY_KEYS[mapIndex] as InputKeys]}
                          />
                        </td>
                      ),
                    )}
                  </tr>
                ))}
                {/* 搬入量合計[t] */}
                <tr className="table-carryrow" style={{ display: planResultUsageDisplay['total_carry_volume'] ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td className="table-carrycell">
                      <div className="table-inputcell m-auto">
                        {!CARRY_KEYS.every((key) =>
                          isMonthDisplay()
                            ? existMonthFull(resultEditResource.day, colIndex)
                              ? resultEmptyCheck.month[colIndex][key]
                              : planEmptyCheck.month[colIndex][key]
                            : resultInitResource.day[lastDayOfLastMonth + colIndex].date !== ''
                              ? resultEmptyCheck.day[lastDayOfLastMonth + colIndex][key]
                              : planEmptyCheck.day[lastDayOfLastMonth + colIndex][key],
                        )
                          ? addComma(calcMathRound(planResultMonitorResource[isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex].total_carry_volume, CARRY_BLUR_DIGIT))
                          : ''}
                      </div>
                    </td>
                  ))}
                </tr>
                {/* 各炉の状態 */}
                {Array.from({ length: sessionResource.furnacesCount }).map((_, furnacesIndex) => (
                  <>
                    {/* 焼却量[t] */}
                    <tr className="table-carryrow">
                      {Array.from({
                        length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                      }).map((_, colIndex) =>
                        isMonthDisplay() ? (
                          <td className="table-carrycell">
                            <div className="table-inputcell m-auto">
                              {existMonthFull(resultEditResource.day, colIndex)
                                ? //月 実績値
                                  !resultEmptyCheck.month[colIndex].burn_volume[furnacesIndex]
                                  ? addComma(calcMathRound(resultEditResource.month[colIndex].burn_volume[furnacesIndex], BURNVOLUME_BLUR_DIGIT))
                                  : ''
                                : !planEmptyCheck.month[colIndex].burn_volume[furnacesIndex]
                                  ? //月 計画値
                                    addComma(calcMathRound(planEditResource.month[colIndex].burn_volume[furnacesIndex], BURNVOLUME_BLUR_DIGIT))
                                  : ''}
                            </div>
                          </td>
                        ) : existDayFull(resultEditResource.day[lastDayOfLastMonth + colIndex]) ? (
                          <td className="table-carrycell">
                            <div className="table-inputcell m-auto">
                              {!resultEmptyCheck.day[lastDayOfLastMonth + colIndex].burn_volume[furnacesIndex]
                                ? addComma(calcMathRound(resultEditResource.day[lastDayOfLastMonth + colIndex].burn_volume[furnacesIndex], BURNVOLUME_BLUR_DIGIT))
                                : ''}
                            </div>
                          </td>
                        ) : (
                          <td
                            className={planDisableCheck.day[lastDayOfLastMonth + colIndex]['burn_volume'][furnacesIndex] ? 'table-carrycell' : 'table-carrycell-green'}
                            style={{
                              pointerEvents: planDisableCheck.day[lastDayOfLastMonth + colIndex]['burn_volume'][furnacesIndex] ? 'none' : 'auto',
                              background: planWarningVals[furnacesIndex][lastDayOfLastMonth + colIndex] ? 'yellow' : undefined,
                            }}
                          >
                            <EmptyNumberInput
                              className={`table-inputcell${
                                planEditResource.day[lastDayOfLastMonth + colIndex].burn_volume[furnacesIndex] > conditionResource.rateCapacity ? ' color-red' : ''
                              }`}
                              colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                              rowIndex={furnacesIndex}
                              data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                              data-key={[DriveMap.burn_volume]}
                              formValueState={planEditResourceState}
                              emptyValueState={planEmptyCheckState}
                              onBlur={(event) => {
                                handleCommonBlurChange(
                                  event,
                                  planEditResourceState,
                                  planEmptyCheckState,
                                  planInitResource,
                                  planUsageCalc,
                                  isMonthDisplay() ? DraftMap.month : DraftMap.day,
                                  DriveMap.burn_volume,
                                  selectMonthIndex,
                                  colIndex,
                                  furnacesIndex,
                                  updateBurnDependent,
                                );
                                updateWarningVals(planWarningValsState, selectMonthIndex, isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex, furnacesIndex);
                              }}
                              mathCalc={(value: number) => calcMathRound(value, BURNVOLUME_BLUR_DIGIT)}
                              validateChange={validBurnChange}
                              significantDigits={DRIVE_INPUT_DIGITS['burn_volume']}
                            />
                          </td>
                        ),
                      )}
                    </tr>
                    {/* 焼却負荷率(停炉日を含まない)[%] */}
                    <tr
                      className="table-carryrow"
                      style={{
                        display: planResultUsageDisplay['burn_load'] && isMonthDisplay() ? 'table-row' : 'none',
                      }}
                    >
                      {Array.from({
                        length: isMonthDisplay() ? displayDays.length : 0,
                      }).map((_, colIndex) => (
                        <td className="table-carrycell">
                          {(
                            existMonthFull(resultEditResource.day, colIndex)
                              ? !resultEmptyCheck.month[colIndex].burn_volume[furnacesIndex]
                              : !planEmptyCheck.month[colIndex].burn_volume[furnacesIndex]
                          )
                            ? addComma(calcMathFloor(planResultMonitorMonthResource[colIndex].burn_load[furnacesIndex], BURNLOAD_DIGIT))
                            : ''}
                        </td>
                      ))}
                    </tr>
                    {/* 焼却負荷率(停炉日を含む)[%] */}
                    <tr
                      className="table-carryrow"
                      style={{
                        display: planResultUsageDisplay['burn_load_include_shutdown'] && isMonthDisplay() ? 'table-row' : 'none',
                      }}
                    >
                      {Array.from({
                        length: isMonthDisplay() ? displayDays.length : 0,
                      }).map((_, colIndex) => (
                        <td className="table-carrycell">
                          {(
                            existMonthFull(resultEditResource.day, colIndex)
                              ? !resultEmptyCheck.month[colIndex].burn_volume[furnacesIndex]
                              : !planEmptyCheck.month[colIndex].burn_volume[furnacesIndex]
                          )
                            ? addComma(calcMathFloor(planResultMonitorMonthResource[colIndex].burn_load_include_shutdown[furnacesIndex], BURNLOAD_DIGIT))
                            : ''}
                        </td>
                      ))}
                    </tr>
                    {/* 運転日数[日] */}
                    <tr
                      className="table-carryrow"
                      style={{
                        display: isMonthDisplay() ? 'table-row' : 'none',
                      }}
                    >
                      {Array.from({
                        length: isMonthDisplay() ? displayDays.length : 0,
                      }).map((_, colIndex) => (
                        <td className="table-carrycell">
                          {(
                            existMonthFull(resultEditResource.day, colIndex)
                              ? !resultEmptyCheck.month[colIndex].burn_volume[furnacesIndex]
                              : !planEmptyCheck.month[colIndex].burn_volume[furnacesIndex]
                          )
                            ? planResultMonitorMonthResource[colIndex].operating_days[furnacesIndex]
                            : ''}
                        </td>
                      ))}
                    </tr>
                    {/* ごみ低位発熱量 */}
                    <tr
                      className="table-carryrow"
                      style={{
                        display: planResultUsageDisplay['waste_lower_heating_value'] ? 'table-row' : 'none',
                      }}
                    >
                      {Array.from({
                        length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                      }).map((_, colIndex) =>
                        isMonthDisplay() ? (
                          existMonthFull(resultEditResource.day, colIndex) ? (
                            //月 実績値
                            <td className="table-carrycell" style={{ pointerEvents: 'none' }}>
                              <EmptyNumberInput
                                className="table-inputcell"
                                colIndex={colIndex}
                                rowIndex={furnacesIndex}
                                data-sign={[DraftMap.month]}
                                data-key={[DriveMap.waste_lower_heating_value]}
                                formValueState={resultEditResourceState}
                                emptyValueState={resultEmptyCheckState}
                                significantDigits={DRIVE_INPUT_DIGITS['waste_lower_heating_value']}
                              />
                            </td>
                          ) : (
                            <>
                              <td className="table-carrycell" style={{ pointerEvents: 'none' }}>
                                <EmptyNumberInput
                                  className="table-inputcell"
                                  colIndex={colIndex}
                                  rowIndex={furnacesIndex}
                                  data-sign={[DraftMap.month]}
                                  data-key={[DriveMap.waste_lower_heating_value]}
                                  formValueState={planEditResourceState}
                                  emptyValueState={planEmptyCheckState}
                                  significantDigits={DRIVE_INPUT_DIGITS['waste_lower_heating_value']}
                                />
                              </td>
                            </>
                          )
                        ) : existDayFull(resultEditResource.day[lastDayOfLastMonth + colIndex]) ? (
                          //日 実績値
                          <td
                            className={
                              !resultUsageDisplay['waste_lower_heating_value'] || resultDisableCheck.day[lastDayOfLastMonth + colIndex]['waste_lower_heating_value'][furnacesIndex]
                                ? 'table-carrycell'
                                : 'table-carrycell-green'
                            }
                            style={{
                              pointerEvents:
                                !resultUsageDisplay['waste_lower_heating_value'] ||
                                resultDisableCheck.day[lastDayOfLastMonth + colIndex]['waste_lower_heating_value'][furnacesIndex]
                                  ? 'none'
                                  : 'auto',
                            }}
                          >
                            <EmptyNumberInput
                              className="table-inputcell"
                              colIndex={lastDayOfLastMonth + colIndex}
                              rowIndex={furnacesIndex}
                              data-sign={[DraftMap.day]}
                              data-key={[DriveMap.waste_lower_heating_value]}
                              formValueState={resultEditResourceState}
                              emptyValueState={resultEmptyCheckState}
                              onBlur={(event) =>
                                handleCommonBlurChange(
                                  event,
                                  resultEditResourceState,
                                  resultEmptyCheckState,
                                  resultInitResource,
                                  resultUsageCalc,
                                  isMonthDisplay() ? DraftMap.month : DraftMap.day,
                                  DriveMap.waste_lower_heating_value,
                                  selectMonthIndex,
                                  colIndex,
                                  furnacesIndex,
                                )
                              }
                              mathCalc={(value: number) => calcMathRound(value, WASTE_BLUR_DIGIT)}
                              significantDigits={DRIVE_INPUT_DIGITS['waste_lower_heating_value']}
                            />
                          </td>
                        ) : (
                          <>
                            <td
                              className={
                                !planUsageDisplay['waste_lower_heating_value'] || planDisableCheck.day[lastDayOfLastMonth + colIndex]['waste_lower_heating_value'][furnacesIndex]
                                  ? 'table-carrycell'
                                  : 'table-carrycell-green'
                              }
                              style={{
                                pointerEvents:
                                  !planUsageDisplay['waste_lower_heating_value'] || planDisableCheck.day[lastDayOfLastMonth + colIndex]['waste_lower_heating_value'][furnacesIndex]
                                    ? 'none'
                                    : 'auto',
                              }}
                            >
                              <EmptyNumberInput
                                className="table-inputcell"
                                colIndex={lastDayOfLastMonth + colIndex}
                                rowIndex={furnacesIndex}
                                data-sign={[DraftMap.day]}
                                data-key={[DriveMap.waste_lower_heating_value]}
                                formValueState={planEditResourceState}
                                emptyValueState={planEmptyCheckState}
                                onBlur={(event) =>
                                  handleCommonBlurChange(
                                    event,
                                    planEditResourceState,
                                    planEmptyCheckState,
                                    planInitResource,
                                    planUsageCalc,
                                    isMonthDisplay() ? DraftMap.month : DraftMap.day,
                                    DriveMap.waste_lower_heating_value,
                                    selectMonthIndex,
                                    colIndex,
                                    furnacesIndex,
                                  )
                                }
                                mathCalc={(value: number) => calcMathRound(value, WASTE_BLUR_DIGIT)}
                                significantDigits={DRIVE_INPUT_DIGITS['waste_lower_heating_value']}
                              />
                            </td>
                          </>
                        ),
                      )}
                    </tr>
                  </>
                ))}
                {/* 全炉 */}
                {/* 焼却量[t] */}
                <tr className="table-carryrow">
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) =>
                    isMonthDisplay() ? (
                      <td className="table-carrycell">
                        <div className="table-inputcell m-auto">
                          {existMonthFull(resultEditResource.day, colIndex)
                            ? //月 実績値
                              !resultEmptyCheck.month[colIndex].burn_volume.some((value) => value === true)
                              ? addComma(
                                  calcMathRound(
                                    Array.from(
                                      { length: sessionResource.furnacesCount },
                                      (_, furnacesIndex) => resultEditResource.month[colIndex].burn_volume[furnacesIndex],
                                    ).reduce((sum, value) => sum + value, 0),
                                    BURNVOLUME_BLUR_DIGIT,
                                  ),
                                )
                              : ''
                            : !planEmptyCheck.month[colIndex].burn_volume.some((value) => value === true)
                              ? //月 計画値
                                addComma(
                                  calcMathRound(
                                    Array.from({ length: sessionResource.furnacesCount }, (_, furnacesIndex) => planEditResource.month[colIndex].burn_volume[furnacesIndex]).reduce(
                                      (sum, value) => sum + value,
                                      0,
                                    ),
                                    BURNVOLUME_BLUR_DIGIT,
                                  ),
                                )
                              : ''}
                        </div>
                      </td>
                    ) : existDayFull(resultEditResource.day[lastDayOfLastMonth + colIndex]) ? (
                      <td className="table-carrycell">
                        <div className="table-inputcell">
                          {!resultEmptyCheck.day[lastDayOfLastMonth + colIndex].burn_volume.some((value) => value === true)
                            ? addComma(
                                calcMathRound(
                                  Array.from(
                                    { length: sessionResource.furnacesCount },
                                    (_, furnacesIndex) => resultEditResource.day[lastDayOfLastMonth + colIndex].burn_volume[furnacesIndex],
                                  ).reduce((sum, value) => sum + value, 0),
                                  BURNVOLUME_BLUR_DIGIT,
                                ),
                              )
                            : ''}
                        </div>
                      </td>
                    ) : (
                      <>
                        <td
                          className={'table-carrycell'}
                          style={{
                            display: !planEmptyCheck.day[lastDayOfLastMonth + colIndex].burn_volume.some((value) => value === true) ? 'table-cell' : 'none',
                          }}
                        >
                          <div className="table-inputcell">
                            {addComma(
                              calcMathRound(
                                Array.from(
                                  { length: sessionResource.furnacesCount },
                                  (_, furnacesIndex) => planEditResource.day[lastDayOfLastMonth + colIndex].burn_volume[furnacesIndex],
                                ).reduce((sum, value) => sum + value, 0),
                                BURNVOLUME_BLUR_DIGIT,
                              ),
                            )}
                          </div>
                        </td>
                        <td
                          className="table-carrycell-green"
                          style={{
                            display: !planEmptyCheck.day[lastDayOfLastMonth + colIndex].burn_volume.some((value) => value === true) ? 'none' : 'table-cell',
                          }}
                        >
                          <div className="table-inputcell"></div>
                        </td>
                      </>
                    ),
                  )}
                </tr>
                {/* 焼却負荷率(停炉日を含まない)[%] */}
                <tr
                  className="table-carryrow"
                  style={{
                    display: planResultUsageDisplay['burn_load'] && isMonthDisplay() ? 'table-row' : 'none',
                  }}
                >
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : 0,
                  }).map((_, colIndex) => (
                    <td className="table-carrycell">
                      {(
                        existMonthFull(resultEditResource.day, colIndex)
                          ? !resultEmptyCheck.month[colIndex].burn_volume.some((value) => value === true)
                          : !planEmptyCheck.month[colIndex].burn_volume.some((value) => value === true)
                      )
                        ? addComma(calcMathFloor(planResultMonitorMonthResource[colIndex].burn_load_sum, BURNLOAD_DIGIT))
                        : ''}
                    </td>
                  ))}
                </tr>
                {/* 焼却負荷率(停炉日を含む)[%] */}
                <tr
                  className="table-carryrow"
                  style={{
                    display: planResultUsageDisplay['burn_load_include_shutdown'] && isMonthDisplay() ? 'table-row' : 'none',
                  }}
                >
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : 0,
                  }).map((_, colIndex) => (
                    <td className="table-carrycell">
                      {(
                        existMonthFull(resultEditResource.day, colIndex)
                          ? !resultEmptyCheck.month[colIndex].burn_volume.some((value) => value === true)
                          : !planEmptyCheck.month[colIndex].burn_volume.some((value) => value === true)
                      )
                        ? addComma(calcMathFloor(planResultMonitorMonthResource[colIndex].burn_load_include_shutdown_sum, BURNLOAD_DIGIT))
                        : ''}
                    </td>
                  ))}
                </tr>
                {/* ピット残量[t] */}
                <tr
                  className="table-carryrow"
                  style={{
                    display: isMonthDisplay() ? 'none' : 'table-row',
                  }}
                >
                  {Array.from({
                    length: displayDayCountOfMonth,
                  }).map((_, colIndex) =>
                    existDayFull(resultEditResource.day[lastDayOfLastMonth + colIndex]) ? (
                      !resultEmptyCheck.day[lastDayOfLastMonth + colIndex].burn_volume.some((value) => value === true) ? (
                        <td
                          className={resultDisableCheck.day[lastDayOfLastMonth + colIndex]['trash_pit_rest'] ? 'table-carrycell' : 'table-carrycell-green'}
                          style={{ pointerEvents: resultDisableCheck.day[lastDayOfLastMonth + colIndex]['trash_pit_rest'] ? 'none' : 'auto' }}
                        >
                          <EmptyNumberInput
                            className="table-inputcell"
                            colIndex={lastDayOfLastMonth + colIndex}
                            data-sign={[DraftMap.day]}
                            data-key={[DriveMap.trash_pit_rest]}
                            formValueState={resultEditResourceState}
                            emptyValueState={resultEmptyCheckState}
                            mathCalc={(value: number) => calcMathRound(value, TRASH_PIT_BLUR_DIGIT)}
                            significantDigits={DRIVE_INPUT_DIGITS['trash_pit_rest']}
                          />
                        </td>
                      ) : (
                        <td className="table-carrycell" />
                      )
                    ) : !planEmptyCheck.day[lastDayOfLastMonth + colIndex].trash_pit_rest === true ? (
                      <td
                        className={(isMonthDisplay() ? true : planDisableCheck.day[lastDayOfLastMonth + colIndex]['trash_pit_rest']) ? 'table-carrycell' : 'table-carrycell-green'}
                        style={{
                          pointerEvents: (isMonthDisplay() ? true : planDisableCheck.day[lastDayOfLastMonth + colIndex]['trash_pit_rest']) ? 'none' : 'auto',
                        }}
                      >
                        {' '}
                        <EmptyNumberInput
                          className="table-inputcell"
                          colIndex={lastDayOfLastMonth + colIndex}
                          data-sign={[DraftMap.day]}
                          data-key={[DriveMap.trash_pit_rest]}
                          formValueState={planEditResourceState}
                          emptyValueState={planEmptyCheckState}
                          onBlur={(event) =>
                            handleCommonBlurChange(
                              event,
                              planEditResourceState,
                              planEmptyCheckState,
                              planInitResource,
                              planUsageCalc,
                              isMonthDisplay() ? DraftMap.month : DraftMap.day,
                              DriveMap.trash_pit_rest,
                              selectMonthIndex,
                              colIndex,
                              undefined,
                              updatePitRest,
                            )
                          }
                          mathCalc={(value: number) => calcMathRound(value, TRASH_PIT_BLUR_DIGIT)}
                          significantDigits={DRIVE_INPUT_DIGITS['trash_pit_rest']}
                        />{' '}
                      </td>
                    ) : (
                      <td className="table-carrycell" />
                    ),
                  )}
                </tr>
                {/* 主灰発生量[t]～飛灰処理物搬出量 処理灰[t] */}
                {Array.from({
                  length: ASH_DETAILS.length,
                }).map((_, mapIndex) => (
                  <tr
                    className="table-carryrow"
                    style={{
                      display:
                        planResultUsageDisplay[ASH_KEYS[mapIndex] as keyof DriveUsage] &&
                        showAshVisible &&
                        !(isMonthDisplay() && ['main_ash_pit_rest', 'fly_ash_pit_rest'].includes(ASH_KEYS[mapIndex]))
                          ? 'table-row'
                          : 'none',
                    }}
                  >
                    {Array.from({
                      length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                    }).map((_, colIndex) =>
                      isMonthDisplay() ? (
                        existMonthFull(resultEditResource.day, colIndex) ? (
                          //月 実績値
                          <td className="table-carrycell" style={{ pointerEvents: 'none' }}>
                            <EmptyNumberInput
                              className="table-inputcell"
                              colIndex={colIndex}
                              data-sign={[DraftMap.month]}
                              data-key={[ASH_DETAILS[mapIndex]]}
                              formValueState={resultEditResourceState}
                              emptyValueState={resultEmptyCheckState}
                              significantDigits={DRIVE_INPUT_DIGITS[ASH_KEYS[mapIndex] as InputKeys]}
                            />
                          </td>
                        ) : (
                          //月 計画値
                          <>
                            <td className="table-carrycell" style={{ pointerEvents: 'none' }}>
                              <EmptyNumberInput
                                className="table-inputcell"
                                colIndex={colIndex}
                                data-sign={[DraftMap.month]}
                                data-key={[ASH_DETAILS[mapIndex]]}
                                formValueState={planEditResourceState}
                                emptyValueState={planEmptyCheckState}
                                significantDigits={DRIVE_INPUT_DIGITS[ASH_KEYS[mapIndex] as InputKeys]}
                              />
                            </td>
                          </>
                        )
                      ) : existDayFull(resultEditResource.day[lastDayOfLastMonth + colIndex]) ? (
                        //日 実績値
                        <td
                          className={
                            !resultUsageDisplay[ASH_KEYS[mapIndex] as keyof DriveUsage] || resultDisableCheck.day[lastDayOfLastMonth + colIndex][ASH_KEYS[mapIndex]]
                              ? 'table-carrycell'
                              : 'table-carrycell-green'
                          }
                          style={{
                            pointerEvents:
                              !resultUsageDisplay[ASH_KEYS[mapIndex] as keyof DriveUsage] || resultDisableCheck.day[lastDayOfLastMonth + colIndex][ASH_KEYS[mapIndex]]
                                ? 'none'
                                : 'auto',
                          }}
                        >
                          <EmptyNumberInput
                            className="table-inputcell"
                            colIndex={lastDayOfLastMonth + colIndex}
                            data-sign={[DraftMap.day]}
                            data-key={[ASH_DETAILS[mapIndex]]}
                            formValueState={resultEditResourceState}
                            emptyValueState={resultEmptyCheckState}
                            onBlur={(event) =>
                              handleCommonBlurChange(
                                event,
                                resultEditResourceState,
                                resultEmptyCheckState,
                                resultInitResource,
                                resultUsageCalc,
                                isMonthDisplay() ? DraftMap.month : DraftMap.day,
                                ASH_DETAILS[mapIndex],
                                selectMonthIndex,
                                colIndex,
                                undefined,
                              )
                            }
                            mathCalc={(value: number) => calcMathRound(value, ASH_BLUR_DIGIT)}
                            significantDigits={DRIVE_INPUT_DIGITS[ASH_KEYS[mapIndex] as InputKeys]}
                          />
                        </td>
                      ) : (
                        //日 計画値
                        <>
                          <td
                            className={
                              !planUsageDisplay[ASH_KEYS[mapIndex] as keyof DriveUsage] || planDisableCheck.day[lastDayOfLastMonth + colIndex][ASH_KEYS[mapIndex]]
                                ? 'table-carrycell'
                                : 'table-carrycell-green'
                            }
                            style={{
                              pointerEvents:
                                !planUsageDisplay[ASH_KEYS[mapIndex] as keyof DriveUsage] || planDisableCheck.day[lastDayOfLastMonth + colIndex][ASH_KEYS[mapIndex]]
                                  ? 'none'
                                  : 'auto',
                            }}
                          >
                            <EmptyNumberInput
                              className="table-inputcell"
                              colIndex={lastDayOfLastMonth + colIndex}
                              data-sign={[DraftMap.day]}
                              data-key={[ASH_DETAILS[mapIndex]]}
                              formValueState={planEditResourceState}
                              emptyValueState={planEmptyCheckState}
                              onBlur={(event) =>
                                handleCommonBlurChange(
                                  event,
                                  planEditResourceState,
                                  planEmptyCheckState,
                                  planInitResource,
                                  planUsageCalc,
                                  isMonthDisplay() ? DraftMap.month : DraftMap.day,
                                  ASH_DETAILS[mapIndex],
                                  selectMonthIndex,
                                  colIndex,
                                  undefined,
                                  updateAshFuncMap[ASH_KEYS[mapIndex]],
                                )
                              }
                              mathCalc={(value: number) => calcMathRound(value, ASH_BLUR_DIGIT)}
                              significantDigits={DRIVE_INPUT_DIGITS[ASH_KEYS[mapIndex] as InputKeys]}
                            />
                          </td>
                        </>
                      ),
                    )}
                  </tr>
                ))}
                {/* 温水供給設備供給熱量 */}
                <tr
                  className="table-carryrow"
                  style={{
                    display: planResultUsageDisplay['hot_water_supply_heating_value'] ? 'table-row' : 'none',
                  }}
                >
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) =>
                    isMonthDisplay() ? (
                      existMonthFull(resultEditResource.day, colIndex) ? (
                        //月 実績値
                        <td className="table-carrycell" style={{ pointerEvents: 'none' }}>
                          <EmptyNumberInput
                            className="table-inputcell"
                            colIndex={colIndex}
                            data-sign={[DraftMap.month]}
                            data-key={[DriveMap.hot_water_supply_heating_value]}
                            formValueState={resultEditResourceState}
                            emptyValueState={resultEmptyCheckState}
                            significantDigits={DRIVE_INPUT_DIGITS['hot_water_supply_heating_value']}
                          />
                        </td>
                      ) : (
                        <>
                          <td className="table-carrycell" style={{ pointerEvents: 'none' }}>
                            <EmptyNumberInput
                              className="table-inputcell"
                              colIndex={colIndex}
                              data-sign={[DraftMap.month]}
                              data-key={[DriveMap.hot_water_supply_heating_value]}
                              formValueState={planEditResourceState}
                              emptyValueState={planEmptyCheckState}
                              significantDigits={DRIVE_INPUT_DIGITS['hot_water_supply_heating_value']}
                            />
                          </td>
                        </>
                      )
                    ) : existDayFull(resultEditResource.day[lastDayOfLastMonth + colIndex]) ? (
                      //日 実績値
                      <td
                        className={
                          !resultUsageDisplay['hot_water_supply_heating_value'] || resultDisableCheck.day[lastDayOfLastMonth + colIndex]['hot_water_supply_heating_value']
                            ? 'table-carrycell'
                            : 'table-carrycell-green'
                        }
                        style={{
                          pointerEvents:
                            !resultUsageDisplay['hot_water_supply_heating_value'] || resultDisableCheck.day[lastDayOfLastMonth + colIndex]['hot_water_supply_heating_value']
                              ? 'none'
                              : 'auto',
                        }}
                      >
                        <EmptyNumberInput
                          className="table-inputcell"
                          colIndex={lastDayOfLastMonth + colIndex}
                          data-sign={[DraftMap.day]}
                          data-key={[DriveMap.hot_water_supply_heating_value]}
                          formValueState={resultEditResourceState}
                          emptyValueState={resultEmptyCheckState}
                          onBlur={(event) =>
                            handleCommonBlurChange(
                              event,
                              resultEditResourceState,
                              resultEmptyCheckState,
                              resultInitResource,
                              resultUsageCalc,
                              isMonthDisplay() ? DraftMap.month : DraftMap.day,
                              DriveMap.hot_water_supply_heating_value,
                              selectMonthIndex,
                              colIndex,
                            )
                          }
                          mathCalc={(value: number) => calcMathRound(value, HOT_WATER_BLUR_DIGIT)}
                          significantDigits={DRIVE_INPUT_DIGITS['hot_water_supply_heating_value']}
                        />
                      </td>
                    ) : (
                      <>
                        <td
                          className={
                            !planUsageDisplay['hot_water_supply_heating_value'] || planDisableCheck.day[lastDayOfLastMonth + colIndex]['hot_water_supply_heating_value']
                              ? 'table-carrycell'
                              : 'table-carrycell-green'
                          }
                          style={{
                            pointerEvents:
                              !planUsageDisplay['hot_water_supply_heating_value'] || planDisableCheck.day[lastDayOfLastMonth + colIndex]['hot_water_supply_heating_value']
                                ? 'none'
                                : 'auto',
                          }}
                        >
                          <EmptyNumberInput
                            className="table-inputcell"
                            colIndex={lastDayOfLastMonth + colIndex}
                            data-sign={[DraftMap.day]}
                            data-key={[DriveMap.hot_water_supply_heating_value]}
                            formValueState={planEditResourceState}
                            emptyValueState={planEmptyCheckState}
                            onBlur={(event) =>
                              handleCommonBlurChange(
                                event,
                                planEditResourceState,
                                planEmptyCheckState,
                                planInitResource,
                                planUsageCalc,
                                isMonthDisplay() ? DraftMap.month : DraftMap.day,
                                DriveMap.hot_water_supply_heating_value,
                                selectMonthIndex,
                                colIndex,
                              )
                            }
                            mathCalc={(value: number) => calcMathRound(value, HOT_WATER_BLUR_DIGIT)}
                            significantDigits={DRIVE_INPUT_DIGITS['hot_water_supply_heating_value']}
                          />
                        </td>
                      </>
                    ),
                  )}
                </tr>
                {/* 炉別運転日数 */}
                {Array.from({ length: sessionResource.furnacesCount + 1 }).map((_, runIndex) => (
                  <tr
                    className="table-carryrow"
                    style={{
                      display: isMonthDisplay() && showOperationDaysVisible && planResultUsageDisplay['furnace_operating_days'] ? 'table-row' : 'none',
                    }}
                  >
                    {Array.from({
                      length: isMonthDisplay() ? displayDays.length : 0,
                    }).map((_, colIndex) => (
                      <td className="table-carrycell">{planResultMonitorMonthResource[colIndex].furnace_operating_days[runIndex]}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
              {/* ##### 計画情報 テーブル表示・入力管理 #### */}
              <tbody style={{ display: !(existAutoDraft && isResultVisible()) && isPlanVisible() ? 'table-row-group' : 'none' }}>
                <tr className="table-carryrow" style={{ zIndex: 2 }}>
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td className="table-carrycell-pog" style={{ width: isMonthDisplay() ? MONTH_CELL_WIDTH : '' }}>
                      {'計画'}
                    </td>
                  ))}
                </tr>
                {/* 搬入量[t] */}
                <tr className="table-carryrow" style={{ display: planUsageDisplay['carry_volume'] ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td
                      className={
                        (isMonthDisplay() ? planDisableCheck.month[colIndex]['carry_volume'] : planDisableCheck.day[lastDayOfLastMonth + colIndex]['carry_volume'])
                          ? 'table-carrycell'
                          : 'table-carrycell-green'
                      }
                      style={{
                        pointerEvents: (isMonthDisplay() ? planDisableCheck.month[colIndex]['carry_volume'] : planDisableCheck.day[lastDayOfLastMonth + colIndex]['carry_volume'])
                          ? 'none'
                          : 'auto',
                      }}
                    >
                      <EmptyNumberInput
                        className="table-inputcell"
                        colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                        data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                        data-key={[DriveMap.carry_volume]}
                        formValueState={planEditResourceState}
                        emptyValueState={planEmptyCheckState}
                        onBlur={(event) => {
                          handleCommonBlurChange(
                            event,
                            planEditResourceState,
                            planEmptyCheckState,
                            planInitResource,
                            planUsageCalc,
                            isMonthDisplay() ? DraftMap.month : DraftMap.day,
                            DriveMap.carry_volume,
                            selectMonthIndex,
                            isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex,
                            undefined,
                            updateCarryDependent,
                          );
                        }}
                        mathCalc={(value: number) => calcMathRound(value, CARRY_BLUR_DIGIT)}
                        significantDigits={DRIVE_INPUT_DIGITS['carry_volume']}
                      />
                    </td>
                  ))}
                </tr>
                {/* 可燃ごみ搬入量[t] */}
                <tr className="table-carryrow" style={{ display: planUsageDisplay['combustible_waste_carry_volume'] ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td
                      className={
                        (isMonthDisplay() ? true : planDisableCheck.day[lastDayOfLastMonth + colIndex]['combustible_waste_carry_volume'])
                          ? 'table-carrycell'
                          : 'table-carrycell-green'
                      }
                      style={{
                        pointerEvents: (isMonthDisplay() ? true : planDisableCheck.day[lastDayOfLastMonth + colIndex]['combustible_waste_carry_volume']) ? 'none' : 'auto',
                      }}
                    >
                      <EmptyNumberInput
                        className="table-inputcell"
                        colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                        data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                        data-key={[DriveMap.combustible_waste_carry_volume]}
                        formValueState={planEditResourceState}
                        emptyValueState={planEmptyCheckState}
                        onBlur={(event) =>
                          handleCommonBlurChange(
                            event,
                            planEditResourceState,
                            planEmptyCheckState,
                            planInitResource,
                            planUsageCalc,
                            isMonthDisplay() ? DraftMap.month : DraftMap.day,
                            DriveMap.combustible_waste_carry_volume,
                            selectMonthIndex,
                            isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex,
                            undefined,
                            updateCarryDependent,
                          )
                        }
                        mathCalc={(value: number) => calcMathRound(value, CARRY_BLUR_DIGIT)}
                        significantDigits={DRIVE_INPUT_DIGITS['combustible_waste_carry_volume']}
                      />
                    </td>
                  ))}
                </tr>
                {/* 剪定枝等搬入量[t] */}
                <tr className="table-carryrow" style={{ display: planUsageDisplay['pruning_branches_carry_volume'] ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td
                      className={
                        (isMonthDisplay() ? true : planDisableCheck.day[lastDayOfLastMonth + colIndex]['pruning_branches_carry_volume'])
                          ? 'table-carrycell'
                          : 'table-carrycell-green'
                      }
                      style={{
                        pointerEvents: (isMonthDisplay() ? true : planDisableCheck.day[lastDayOfLastMonth + colIndex]['pruning_branches_carry_volume']) ? 'none' : 'auto',
                      }}
                    >
                      <EmptyNumberInput
                        className="table-inputcell"
                        colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                        data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                        data-key={[DriveMap.pruning_branches_carry_volume]}
                        formValueState={planEditResourceState}
                        emptyValueState={planEmptyCheckState}
                        onBlur={(event) =>
                          handleCommonBlurChange(
                            event,
                            planEditResourceState,
                            planEmptyCheckState,
                            planInitResource,
                            planUsageCalc,
                            isMonthDisplay() ? DraftMap.month : DraftMap.day,
                            DriveMap.pruning_branches_carry_volume,
                            selectMonthIndex,
                            isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex,
                            undefined,
                            updateCarryDependent,
                          )
                        }
                        mathCalc={(value: number) => calcMathRound(value, CARRY_BLUR_DIGIT)}
                        significantDigits={DRIVE_INPUT_DIGITS['pruning_branches_carry_volume']}
                      />
                    </td>
                  ))}
                </tr>
                {/* 小動物搬入量[t] */}
                <tr className="table-carryrow" style={{ display: planUsageDisplay['small_animals_carry_volume'] ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td
                      className={
                        (isMonthDisplay() ? true : planDisableCheck.day[lastDayOfLastMonth + colIndex]['small_animals_carry_volume']) ? 'table-carrycell' : 'table-carrycell-green'
                      }
                      style={{
                        pointerEvents: (isMonthDisplay() ? true : planDisableCheck.day[lastDayOfLastMonth + colIndex]['small_animals_carry_volume']) ? 'none' : 'auto',
                      }}
                    >
                      <EmptyNumberInput
                        className="table-inputcell"
                        colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                        data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                        data-key={[DriveMap.small_animals_carry_volume]}
                        formValueState={planEditResourceState}
                        emptyValueState={planEmptyCheckState}
                        onBlur={(event) =>
                          handleCommonBlurChange(
                            event,
                            planEditResourceState,
                            planEmptyCheckState,
                            planInitResource,
                            planUsageCalc,
                            isMonthDisplay() ? DraftMap.month : DraftMap.day,
                            DriveMap.small_animals_carry_volume,
                            selectMonthIndex,
                            isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex,
                            undefined,
                            updateCarryDependent,
                          )
                        }
                        mathCalc={(value: number) => calcMathRound(value, CARRY_BLUR_DIGIT)}
                        significantDigits={DRIVE_INPUT_DIGITS['small_animals_carry_volume']}
                      />
                    </td>
                  ))}
                </tr>
                {/* 可燃粗大ごみ搬入量[t] */}
                <tr className="table-carryrow" style={{ display: planUsageDisplay['combustible_bulk_waste_carry_volume'] ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td
                      className={
                        (isMonthDisplay() ? true : planDisableCheck.day[lastDayOfLastMonth + colIndex]['combustible_bulk_waste_carry_volume'])
                          ? 'table-carrycell'
                          : 'table-carrycell-green'
                      }
                      style={{
                        pointerEvents: (isMonthDisplay() ? true : planDisableCheck.day[lastDayOfLastMonth + colIndex]['combustible_bulk_waste_carry_volume']) ? 'none' : 'auto',
                      }}
                    >
                      <EmptyNumberInput
                        className="table-inputcell"
                        colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                        data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                        data-key={[DriveMap.combustible_bulk_waste_carry_volume]}
                        formValueState={planEditResourceState}
                        emptyValueState={planEmptyCheckState}
                        onBlur={(event) =>
                          handleCommonBlurChange(
                            event,
                            planEditResourceState,
                            planEmptyCheckState,
                            planInitResource,
                            planUsageCalc,
                            isMonthDisplay() ? DraftMap.month : DraftMap.day,
                            DriveMap.combustible_bulk_waste_carry_volume,
                            selectMonthIndex,
                            isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex,
                            undefined,
                            updateCarryDependent,
                          )
                        }
                        mathCalc={(value: number) => calcMathRound(value, CARRY_BLUR_DIGIT)}
                        significantDigits={DRIVE_INPUT_DIGITS['combustible_bulk_waste_carry_volume']}
                      />
                    </td>
                  ))}
                </tr>
                {/* 破砕ごみ残渣等搬入量[t] */}
                <tr className="table-carryrow" style={{ display: planUsageDisplay['shredded_waste_residue_carry_volume'] ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td
                      className={
                        (isMonthDisplay() ? true : planDisableCheck.day[lastDayOfLastMonth + colIndex]['shredded_waste_residue_carry_volume'])
                          ? 'table-carrycell'
                          : 'table-carrycell-green'
                      }
                      style={{
                        pointerEvents: (isMonthDisplay() ? true : planDisableCheck.day[lastDayOfLastMonth + colIndex]['shredded_waste_residue_carry_volume']) ? 'none' : 'auto',
                      }}
                    >
                      <EmptyNumberInput
                        className="table-inputcell"
                        colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                        data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                        data-key={[DriveMap.shredded_waste_residue_carry_volume]}
                        formValueState={planEditResourceState}
                        emptyValueState={planEmptyCheckState}
                        onBlur={(event) =>
                          handleCommonBlurChange(
                            event,
                            planEditResourceState,
                            planEmptyCheckState,
                            planInitResource,
                            planUsageCalc,
                            isMonthDisplay() ? DraftMap.month : DraftMap.day,
                            DriveMap.shredded_waste_residue_carry_volume,
                            selectMonthIndex,
                            isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex,
                            undefined,
                            updateCarryDependent,
                          )
                        }
                        mathCalc={(value: number) => calcMathRound(value, CARRY_BLUR_DIGIT)}
                        significantDigits={DRIVE_INPUT_DIGITS['shredded_waste_residue_carry_volume']}
                      />
                    </td>
                  ))}
                </tr>
                {/* 災害ごみ組合内搬入量[t] */}
                <tr className="table-carryrow" style={{ display: planUsageDisplay['disaster_waste_internal_carry_volume'] ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td
                      className={
                        (isMonthDisplay() ? true : planDisableCheck.day[lastDayOfLastMonth + colIndex]['disaster_waste_internal_carry_volume'])
                          ? 'table-carrycell'
                          : 'table-carrycell-green'
                      }
                      style={{
                        pointerEvents: (isMonthDisplay() ? true : planDisableCheck.day[lastDayOfLastMonth + colIndex]['disaster_waste_internal_carry_volume']) ? 'none' : 'auto',
                      }}
                    >
                      <EmptyNumberInput
                        className="table-inputcell"
                        colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                        data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                        data-key={[DriveMap.disaster_waste_internal_carry_volume]}
                        formValueState={planEditResourceState}
                        emptyValueState={planEmptyCheckState}
                        onBlur={(event) =>
                          handleCommonBlurChange(
                            event,
                            planEditResourceState,
                            planEmptyCheckState,
                            planInitResource,
                            planUsageCalc,
                            isMonthDisplay() ? DraftMap.month : DraftMap.day,
                            DriveMap.disaster_waste_internal_carry_volume,
                            selectMonthIndex,
                            isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex,
                            undefined,
                            updateCarryDependent,
                          )
                        }
                        mathCalc={(value: number) => calcMathRound(value, CARRY_BLUR_DIGIT)}
                        significantDigits={DRIVE_INPUT_DIGITS['disaster_waste_internal_carry_volume']}
                      />
                    </td>
                  ))}
                </tr>
                {/* 災害ごみ組合外搬入量[t] */}
                <tr className="table-carryrow" style={{ display: planUsageDisplay['disaster_waste_external_carry_volume'] ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td
                      className={
                        (isMonthDisplay() ? true : planDisableCheck.day[lastDayOfLastMonth + colIndex]['disaster_waste_external_carry_volume'])
                          ? 'table-carrycell'
                          : 'table-carrycell-green'
                      }
                      style={{
                        pointerEvents: (isMonthDisplay() ? true : planDisableCheck.day[lastDayOfLastMonth + colIndex]['disaster_waste_external_carry_volume']) ? 'none' : 'auto',
                      }}
                    >
                      <EmptyNumberInput
                        className="table-inputcell"
                        colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                        data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                        data-key={[DriveMap.disaster_waste_external_carry_volume]}
                        formValueState={planEditResourceState}
                        emptyValueState={planEmptyCheckState}
                        onBlur={(event) =>
                          handleCommonBlurChange(
                            event,
                            planEditResourceState,
                            planEmptyCheckState,
                            planInitResource,
                            planUsageCalc,
                            isMonthDisplay() ? DraftMap.month : DraftMap.day,
                            DriveMap.disaster_waste_external_carry_volume,
                            selectMonthIndex,
                            isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex,
                            undefined,
                            updateCarryDependent,
                          )
                        }
                        mathCalc={(value: number) => calcMathRound(value, CARRY_BLUR_DIGIT)}
                        significantDigits={DRIVE_INPUT_DIGITS['disaster_waste_external_carry_volume']}
                      />
                    </td>
                  ))}
                </tr>
                {/* その他搬入量[t] */}
                <tr className="table-carryrow" style={{ display: planUsageDisplay['other_carry_volume'] ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td
                      className={
                        (isMonthDisplay() ? true : planDisableCheck.day[lastDayOfLastMonth + colIndex]['other_carry_volume']) ? 'table-carrycell' : 'table-carrycell-green'
                      }
                      style={{
                        pointerEvents: (isMonthDisplay() ? true : planDisableCheck.day[lastDayOfLastMonth + colIndex]['other_carry_volume']) ? 'none' : 'auto',
                      }}
                    >
                      <EmptyNumberInput
                        className="table-inputcell"
                        colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                        data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                        data-key={[DriveMap.other_carry_volume]}
                        formValueState={planEditResourceState}
                        emptyValueState={planEmptyCheckState}
                        onBlur={(event) =>
                          handleCommonBlurChange(
                            event,
                            planEditResourceState,
                            planEmptyCheckState,
                            planInitResource,
                            planUsageCalc,
                            isMonthDisplay() ? DraftMap.month : DraftMap.day,
                            DriveMap.other_carry_volume,
                            selectMonthIndex,
                            isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex,
                            undefined,
                            updateCarryDependent,
                          )
                        }
                        mathCalc={(value: number) => calcMathRound(value, CARRY_BLUR_DIGIT)}
                        significantDigits={DRIVE_INPUT_DIGITS['other_carry_volume']}
                      />
                    </td>
                  ))}
                </tr>
                {/* 焼却施設底ごみ搬入量[t] */}
                <tr className="table-carryrow" style={{ display: planUsageDisplay['incineration_facility_bottom_ash_carry_volume'] ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td
                      className={
                        (isMonthDisplay() ? true : planDisableCheck.day[lastDayOfLastMonth + colIndex]['incineration_facility_bottom_ash_carry_volume'])
                          ? 'table-carrycell'
                          : 'table-carrycell-green'
                      }
                      style={{
                        pointerEvents: (isMonthDisplay() ? true : planDisableCheck.day[lastDayOfLastMonth + colIndex]['incineration_facility_bottom_ash_carry_volume'])
                          ? 'none'
                          : 'auto',
                      }}
                    >
                      <EmptyNumberInput
                        className="table-inputcell"
                        colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                        data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                        data-key={[DriveMap.incineration_facility_bottom_ash_carry_volume]}
                        formValueState={planEditResourceState}
                        emptyValueState={planEmptyCheckState}
                        onBlur={(event) =>
                          handleCommonBlurChange(
                            event,
                            planEditResourceState,
                            planEmptyCheckState,
                            planInitResource,
                            planUsageCalc,
                            isMonthDisplay() ? DraftMap.month : DraftMap.day,
                            DriveMap.incineration_facility_bottom_ash_carry_volume,
                            selectMonthIndex,
                            isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex,
                            undefined,
                            updateCarryDependent,
                          )
                        }
                        mathCalc={(value: number) => calcMathRound(value, CARRY_BLUR_DIGIT)}
                        significantDigits={DRIVE_INPUT_DIGITS['incineration_facility_bottom_ash_carry_volume']}
                      />
                    </td>
                  ))}
                </tr>
                {/* 資源物焼却処分搬入量[t] */}
                <tr className="table-carryrow" style={{ display: planUsageDisplay['recyclables_carry_volume'] ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td
                      className={
                        (isMonthDisplay() ? true : planDisableCheck.day[lastDayOfLastMonth + colIndex]['recyclables_carry_volume']) ? 'table-carrycell' : 'table-carrycell-green'
                      }
                      style={{
                        pointerEvents: (isMonthDisplay() ? true : planDisableCheck.day[lastDayOfLastMonth + colIndex]['recyclables_carry_volume']) ? 'none' : 'auto',
                      }}
                    >
                      <EmptyNumberInput
                        className="table-inputcell"
                        colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                        data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                        data-key={[DriveMap.recyclables_carry_volume]}
                        formValueState={planEditResourceState}
                        emptyValueState={planEmptyCheckState}
                        onBlur={(event) =>
                          handleCommonBlurChange(
                            event,
                            planEditResourceState,
                            planEmptyCheckState,
                            planInitResource,
                            planUsageCalc,
                            isMonthDisplay() ? DraftMap.month : DraftMap.day,
                            DriveMap.recyclables_carry_volume,
                            selectMonthIndex,
                            isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex,
                            undefined,
                            updateCarryDependent,
                          )
                        }
                        mathCalc={(value: number) => calcMathRound(value, CARRY_BLUR_DIGIT)}
                        significantDigits={DRIVE_INPUT_DIGITS['recyclables_carry_volume']}
                      />
                    </td>
                  ))}
                </tr>
                {/* 搬入量合計[t] */}
                <tr className="table-carryrow" style={{ display: planUsageDisplay['total_carry_volume'] ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td className="table-carrycell">
                      <div className="table-inputcell m-auto">
                        {!CARRY_KEYS.every((key) => (isMonthDisplay() ? planEmptyCheck.month[colIndex][key] : planEmptyCheck.day[lastDayOfLastMonth + colIndex][key]))
                          ? addComma(calcMathRound(planMonitorResource[isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex].total_carry_volume, CARRY_BLUR_DIGIT))
                          : ''}
                      </div>
                    </td>
                  ))}
                </tr>

                {/* 各炉の状態 */}
                {Array.from({ length: sessionResource.furnacesCount }).map((_, furnacesIndex) => (
                  <>
                    {/* 焼却量[t] */}
                    <tr className="table-carryrow">
                      {Array.from({
                        length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                      }).map((_, colIndex) => (
                        <td
                          className={
                            (isMonthDisplay() ? true : planDisableCheck.day[lastDayOfLastMonth + colIndex]['burn_volume'][furnacesIndex])
                              ? 'table-carrycell'
                              : 'table-carrycell-green'
                          }
                          style={{
                            pointerEvents: (isMonthDisplay() ? true : planDisableCheck.day[lastDayOfLastMonth + colIndex]['burn_volume'][furnacesIndex]) ? 'none' : 'auto',
                            background: !isMonthDisplay() && planWarningVals[furnacesIndex][lastDayOfLastMonth + colIndex] ? 'yellow' : undefined,
                          }}
                        >
                          <EmptyNumberInput
                            className={`table-inputcell${
                              !isMonthDisplay() && planEditResource.day[lastDayOfLastMonth + colIndex].burn_volume[furnacesIndex] > conditionResource.rateCapacity
                                ? ' color-red'
                                : ''
                            }`}
                            colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                            rowIndex={furnacesIndex}
                            data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                            data-key={[DriveMap.burn_volume]}
                            formValueState={planEditResourceState}
                            emptyValueState={planEmptyCheckState}
                            onBlur={(event, previousValue) => {
                              handleCommonBlurChange(
                                event,
                                planEditResourceState,
                                planEmptyCheckState,
                                planInitResource,
                                planUsageCalc,
                                isMonthDisplay() ? DraftMap.month : DraftMap.day,
                                DriveMap.burn_volume,
                                selectMonthIndex,
                                colIndex,
                                furnacesIndex,
                                updateBurnDependent,
                                previousValue,
                              );
                              updateWarningVals(planWarningValsState, selectMonthIndex, isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex, furnacesIndex);
                            }}
                            mathCalc={(value: number) => calcMathRound(value, BURNVOLUME_BLUR_DIGIT)}
                            validateChange={validBurnChange}
                            significantDigits={DRIVE_INPUT_DIGITS['burn_volume']}
                          />
                        </td>
                      ))}
                    </tr>
                    {/* 焼却負荷率(停炉日を含まない)[%] */}
                    <tr className="table-carryrow" style={{ display: planUsageDisplay['burn_load'] && isMonthDisplay() ? 'table-row' : 'none' }}>
                      {Array.from({
                        length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                      }).map((_, colIndex) => (
                        <td className="table-carrycell">
                          {isMonthDisplay()
                            ? !planEmptyCheck.month[colIndex].burn_volume[furnacesIndex]
                              ? addComma(calcMathFloor(planMonitorMonthResource[colIndex].burn_load[furnacesIndex], BURNLOAD_DIGIT))
                              : ''
                            : ''}
                        </td>
                      ))}
                    </tr>
                    {/* 焼却負荷率(停炉日を含む)[%] */}
                    <tr className="table-carryrow" style={{ display: planUsageDisplay['burn_load_include_shutdown'] && isMonthDisplay() ? 'table-row' : 'none' }}>
                      {Array.from({
                        length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                      }).map((_, colIndex) => (
                        <td className="table-carrycell">
                          {isMonthDisplay()
                            ? !planEmptyCheck.month[colIndex].burn_volume[furnacesIndex]
                              ? addComma(calcMathFloor(planMonitorMonthResource[colIndex].burn_load_include_shutdown[furnacesIndex], BURNLOAD_DIGIT))
                              : ''
                            : ''}
                        </td>
                      ))}
                    </tr>
                    {/* 運転日数[日] */}
                    <tr className="table-carryrow" style={{ display: isMonthDisplay() ? 'table-row' : 'none' }}>
                      {Array.from({
                        length: isMonthDisplay() ? displayDays.length : 0,
                      }).map((_, colIndex) => (
                        <td className="table-carrycell">
                          {!planEmptyCheck.month[colIndex].burn_volume[furnacesIndex] ? planMonitorMonthResource[colIndex].operating_days[furnacesIndex] : ''}
                        </td>
                      ))}
                    </tr>
                    {/* ごみ低位発熱量 */}
                    <tr className="table-carryrow" style={{ display: planUsageDisplay['waste_lower_heating_value'] ? 'table-row' : 'none' }}>
                      {Array.from({
                        length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                      }).map((_, colIndex) => (
                        <td
                          className={
                            (isMonthDisplay() ? true : planDisableCheck.day[lastDayOfLastMonth + colIndex]['waste_lower_heating_value'][furnacesIndex])
                              ? 'table-carrycell'
                              : 'table-carrycell-green'
                          }
                          style={{
                            pointerEvents: (isMonthDisplay() ? true : planDisableCheck.day[lastDayOfLastMonth + colIndex]['waste_lower_heating_value'][furnacesIndex])
                              ? 'none'
                              : 'auto',
                          }}
                        >
                          <EmptyNumberInput
                            className="table-inputcell"
                            colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                            rowIndex={furnacesIndex}
                            data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                            data-key={[DriveMap.waste_lower_heating_value]}
                            formValueState={planEditResourceState}
                            emptyValueState={planEmptyCheckState}
                            onBlur={(event) =>
                              handleCommonBlurChange(
                                event,
                                planEditResourceState,
                                planEmptyCheckState,
                                planInitResource,
                                planUsageCalc,
                                isMonthDisplay() ? DraftMap.month : DraftMap.day,
                                DriveMap.waste_lower_heating_value,
                                selectMonthIndex,
                                colIndex,
                                furnacesIndex,
                              )
                            }
                            mathCalc={(value: number) => calcMathRound(value, WASTE_BLUR_DIGIT)}
                            significantDigits={DRIVE_INPUT_DIGITS['waste_lower_heating_value']}
                          />
                        </td>
                      ))}
                    </tr>
                  </>
                ))}
                {/* 全炉 */}
                {/* 焼却量[t] */}
                <tr className="table-carryrow">
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) =>
                    isMonthDisplay() ? (
                      <td className="table-carrycell">
                        {!planEmptyCheck.month[colIndex].burn_volume.some((value) => value === true)
                          ? addComma(
                              calcMathRound(
                                Array.from({ length: sessionResource.furnacesCount }, (_, furnacesIndex) => planEditResource.month[colIndex].burn_volume[furnacesIndex]).reduce(
                                  (sum, value) => sum + value,
                                  0,
                                ),
                                BURNVOLUME_BLUR_DIGIT,
                              ),
                            )
                          : ''}
                      </td>
                    ) : (
                      <td className="table-carrycell">
                        {!planEmptyCheck.day[lastDayOfLastMonth + colIndex].burn_volume.some((value) => value === true)
                          ? addComma(
                              calcMathRound(
                                Array.from(
                                  { length: sessionResource.furnacesCount },
                                  (_, furnacesIndex) => planEditResource.day[lastDayOfLastMonth + colIndex].burn_volume[furnacesIndex],
                                ).reduce((sum, value) => sum + value, 0),
                                BURNVOLUME_BLUR_DIGIT,
                              ),
                            )
                          : ''}
                      </td>
                    ),
                  )}
                </tr>
                {/* 焼却負荷率(停炉日を含まない)[%] */}
                <tr className="table-carryrow" style={{ display: planUsageDisplay['burn_load'] && isMonthDisplay() ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: displayDays.length,
                  }).map((_, colIndex) => (
                    <td className="table-carrycell">
                      {!planEmptyCheck.month[colIndex].burn_volume.some((value) => value === true)
                        ? addComma(calcMathFloor(planMonitorMonthResource[colIndex].burn_load_sum, BURNLOAD_DIGIT))
                        : ''}
                    </td>
                  ))}
                </tr>
                {/* 焼却負荷率(停炉日を含む)[%] */}
                <tr className="table-carryrow" style={{ display: planUsageDisplay['burn_load_include_shutdown'] && isMonthDisplay() ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: displayDays.length,
                  }).map((_, colIndex) => (
                    <td className="table-carrycell">
                      {!planEmptyCheck.month[colIndex].burn_volume.some((value) => value === true)
                        ? addComma(calcMathFloor(planMonitorMonthResource[colIndex].burn_load_include_shutdown_sum, BURNLOAD_DIGIT))
                        : ''}
                    </td>
                  ))}
                </tr>
                {/* ピット残量[t] */}
                <tr className="table-carryrow" style={{ display: isMonthDisplay() ? 'none' : 'table-row' }}>
                  {' '}
                  {Array.from({ length: displayDayCountOfMonth }).map((_, colIndex) =>
                    existDayFull(planEditResource.day[lastDayOfLastMonth + colIndex]) ? (
                      !planEmptyCheck.day[lastDayOfLastMonth + colIndex].trash_pit_rest === true ? (
                        <td
                          className={
                            (isMonthDisplay() ? true : planDisableCheck.day[lastDayOfLastMonth + colIndex]['trash_pit_rest']) ? 'table-carrycell' : 'table-carrycell-green'
                          }
                          style={{
                            pointerEvents: (isMonthDisplay() ? true : planDisableCheck.day[lastDayOfLastMonth + colIndex]['trash_pit_rest']) ? 'none' : 'auto',
                          }}
                        >
                          {' '}
                          <EmptyNumberInput
                            className="table-inputcell"
                            colIndex={lastDayOfLastMonth + colIndex}
                            data-sign={[DraftMap.day]}
                            data-key={[DriveMap.trash_pit_rest]}
                            formValueState={planEditResourceState}
                            emptyValueState={planEmptyCheckState}
                            onBlur={(event) =>
                              handleCommonBlurChange(
                                event,
                                planEditResourceState,
                                planEmptyCheckState,
                                planInitResource,
                                planUsageCalc,
                                isMonthDisplay() ? DraftMap.month : DraftMap.day,
                                DriveMap.trash_pit_rest,
                                selectMonthIndex,
                                colIndex,
                                undefined,
                                updatePitRest,
                              )
                            }
                            mathCalc={(value: number) => calcMathRound(value, TRASH_PIT_BLUR_DIGIT)}
                            significantDigits={DRIVE_INPUT_DIGITS['trash_pit_rest']}
                          />{' '}
                        </td>
                      ) : (
                        <td className="table-carrycell" />
                      )
                    ) : (
                      <td className="table-carrycell">
                        {' '}
                        {isDayFull(planEmptyCheck.day[lastDayOfLastMonth + colIndex], planDayUsage)
                          ? addComma(calcMathRound(planEditResource.day[lastDayOfLastMonth + colIndex].main_ash_pit_rest, TRASH_PIT_BLUR_DIGIT))
                          : ''}{' '}
                      </td>
                    ),
                  )}{' '}
                </tr>
                {/* 主灰発生量[t] */}
                <tr className="table-carryrow" style={{ display: planUsageDisplay['main_ash_generate'] && showAshVisible ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td
                      className={(isMonthDisplay() ? true : planDisableCheck.day[lastDayOfLastMonth + colIndex]['main_ash_generate']) ? 'table-carrycell' : 'table-carrycell-green'}
                      style={{
                        pointerEvents: (isMonthDisplay() ? true : planDisableCheck.day[lastDayOfLastMonth + colIndex]['main_ash_generate']) ? 'none' : 'auto',
                      }}
                    >
                      <EmptyNumberInput
                        className="table-inputcell"
                        colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                        data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                        data-key={[DriveMap.main_ash_generate]}
                        formValueState={planEditResourceState}
                        emptyValueState={planEmptyCheckState}
                        onBlur={(event) =>
                          handleCommonBlurChange(
                            event,
                            planEditResourceState,
                            planEmptyCheckState,
                            planInitResource,
                            planUsageCalc,
                            isMonthDisplay() ? DraftMap.month : DraftMap.day,
                            DriveMap.main_ash_generate,
                            selectMonthIndex,
                            colIndex,
                            undefined,
                          )
                        }
                        mathCalc={(value: number) => calcMathRound(value, ASH_BLUR_DIGIT)}
                        significantDigits={DRIVE_INPUT_DIGITS['main_ash_generate']}
                      />
                    </td>
                  ))}
                </tr>
                {/* 主灰搬出台数[t] */}
                <tr className="table-carryrow" style={{ display: planUsageDisplay['main_ash_carry_out_units'] && showAshVisible ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td
                      className={
                        (isMonthDisplay() ? true : planDisableCheck.day[lastDayOfLastMonth + colIndex]['main_ash_carry_out_units']) ? 'table-carrycell' : 'table-carrycell-green'
                      }
                      style={{
                        pointerEvents: (isMonthDisplay() ? true : planDisableCheck.day[lastDayOfLastMonth + colIndex]['main_ash_carry_out_units']) ? 'none' : 'auto',
                      }}
                    >
                      <EmptyNumberInput
                        className="table-inputcell"
                        colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                        data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                        data-key={[DriveMap.main_ash_carry_out_units]}
                        formValueState={planEditResourceState}
                        emptyValueState={planEmptyCheckState}
                        onBlur={(event) =>
                          handleCommonBlurChange(
                            event,
                            planEditResourceState,
                            planEmptyCheckState,
                            planInitResource,
                            planUsageCalc,
                            isMonthDisplay() ? DraftMap.month : DraftMap.day,
                            DriveMap.main_ash_carry_out_units,
                            selectMonthIndex,
                            colIndex,
                            undefined,
                          )
                        }
                        mathCalc={(value: number) => calcMathRound(value, ASH_BLUR_DIGIT)}
                        significantDigits={DRIVE_INPUT_DIGITS['main_ash_carry_out_units']}
                      />
                    </td>
                  ))}
                </tr>
                {/* 主灰搬出量[t] */}
                <tr className="table-carryrow" style={{ display: planUsageDisplay['main_ash_carry_out'] && showAshVisible ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td
                      className={
                        (isMonthDisplay() ? true : planDisableCheck.day[lastDayOfLastMonth + colIndex]['main_ash_carry_out']) ? 'table-carrycell' : 'table-carrycell-green'
                      }
                      style={{
                        pointerEvents: (isMonthDisplay() ? true : planDisableCheck.day[lastDayOfLastMonth + colIndex]['main_ash_carry_out']) ? 'none' : 'auto',
                      }}
                    >
                      <EmptyNumberInput
                        className="table-inputcell"
                        colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                        data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                        data-key={[DriveMap.main_ash_carry_out]}
                        formValueState={planEditResourceState}
                        emptyValueState={planEmptyCheckState}
                        onBlur={(event) =>
                          handleCommonBlurChange(
                            event,
                            planEditResourceState,
                            planEmptyCheckState,
                            planInitResource,
                            planUsageCalc,
                            isMonthDisplay() ? DraftMap.month : DraftMap.day,
                            DriveMap.main_ash_carry_out,
                            selectMonthIndex,
                            colIndex,
                            undefined,
                            updateMainAshCarryOut,
                          )
                        }
                        mathCalc={(value: number) => calcMathRound(value, ASH_BLUR_DIGIT)}
                        significantDigits={DRIVE_INPUT_DIGITS['main_ash_carry_out']}
                      />
                    </td>
                  ))}
                </tr>
                {/* 主灰ピット残量[t] */}
                <tr className="table-carryrow" style={{ display: showAshVisible && !isMonthDisplay() ? 'table-row' : 'none' }}>
                  {' '}
                  {Array.from({ length: displayDayCountOfMonth }).map((_, colIndex) =>
                    existDayFull(planEditResource.day[lastDayOfLastMonth + colIndex]) ? (
                      !planEmptyCheck.day[lastDayOfLastMonth + colIndex].main_ash_generate === true ? (
                        <td
                          className={
                            (isMonthDisplay() ? true : planDisableCheck.day[lastDayOfLastMonth + colIndex]['main_ash_pit_rest']) ? 'table-carrycell' : 'table-carrycell-green'
                          }
                          style={{
                            pointerEvents: (isMonthDisplay() ? true : planDisableCheck.day[lastDayOfLastMonth + colIndex]['main_ash_pit_rest']) ? 'none' : 'auto',
                          }}
                        >
                          {' '}
                          <EmptyNumberInput
                            className="table-inputcell"
                            colIndex={lastDayOfLastMonth + colIndex}
                            data-sign={[DraftMap.day]}
                            data-key={[DriveMap.main_ash_pit_rest]}
                            formValueState={planEditResourceState}
                            emptyValueState={planEmptyCheckState}
                            onBlur={(event) =>
                              handleCommonBlurChange(
                                event,
                                planEditResourceState,
                                planEmptyCheckState,
                                planInitResource,
                                planUsageCalc,
                                isMonthDisplay() ? DraftMap.month : DraftMap.day,
                                DriveMap.main_ash_pit_rest,
                                selectMonthIndex,
                                colIndex,
                                undefined,
                                updateMainAshRest,
                              )
                            }
                            mathCalc={(value: number) => calcMathRound(value, TRASH_PIT_BLUR_DIGIT)}
                            significantDigits={DRIVE_INPUT_DIGITS['main_ash_pit_rest']}
                          />{' '}
                        </td>
                      ) : (
                        <td className="table-carrycell" />
                      )
                    ) : (
                      <td className="table-carrycell">
                        {' '}
                        {isDayFull(planEmptyCheck.day[lastDayOfLastMonth + colIndex], planDayUsage)
                          ? addComma(calcMathRound(planEditResource.day[lastDayOfLastMonth + colIndex].main_ash_pit_rest, TRASH_PIT_BLUR_DIGIT))
                          : ''}{' '}
                      </td>
                    ),
                  )}{' '}
                </tr>
                {/* 飛灰発生量[t] */}
                <tr className="table-carryrow" style={{ display: planUsageDisplay['fly_ash_generate'] && showAshVisible ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td
                      className={(isMonthDisplay() ? true : planDisableCheck.day[lastDayOfLastMonth + colIndex]['fly_ash_generate']) ? 'table-carrycell' : 'table-carrycell-green'}
                      style={{
                        pointerEvents: (isMonthDisplay() ? true : planDisableCheck.day[lastDayOfLastMonth + colIndex]['fly_ash_generate']) ? 'none' : 'auto',
                      }}
                    >
                      <EmptyNumberInput
                        className="table-inputcell"
                        colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                        data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                        data-key={[DriveMap.fly_ash_generate]}
                        formValueState={planEditResourceState}
                        emptyValueState={planEmptyCheckState}
                        onBlur={(event) =>
                          handleCommonBlurChange(
                            event,
                            planEditResourceState,
                            planEmptyCheckState,
                            planInitResource,
                            planUsageCalc,
                            isMonthDisplay() ? DraftMap.month : DraftMap.day,
                            DriveMap.fly_ash_generate,
                            selectMonthIndex,
                            colIndex,
                            undefined,
                          )
                        }
                        mathCalc={(value: number) => calcMathRound(value, ASH_BLUR_DIGIT)}
                        significantDigits={DRIVE_INPUT_DIGITS['fly_ash_generate']}
                      />
                    </td>
                  ))}
                </tr>
                {/* 飛灰搬出台数[台] */}
                <tr className="table-carryrow" style={{ display: planUsageDisplay['fly_ash_carry_out_units'] && showAshVisible ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td
                      className={
                        (isMonthDisplay() ? true : planDisableCheck.day[lastDayOfLastMonth + colIndex]['fly_ash_carry_out_units']) ? 'table-carrycell' : 'table-carrycell-green'
                      }
                      style={{
                        pointerEvents: (isMonthDisplay() ? true : planDisableCheck.day[lastDayOfLastMonth + colIndex]['fly_ash_carry_out_units']) ? 'none' : 'auto',
                      }}
                    >
                      <EmptyNumberInput
                        className="table-inputcell"
                        colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                        data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                        data-key={[DriveMap.fly_ash_carry_out_units]}
                        formValueState={planEditResourceState}
                        emptyValueState={planEmptyCheckState}
                        onBlur={(event) =>
                          handleCommonBlurChange(
                            event,
                            planEditResourceState,
                            planEmptyCheckState,
                            planInitResource,
                            planUsageCalc,
                            isMonthDisplay() ? DraftMap.month : DraftMap.day,
                            DriveMap.fly_ash_carry_out_units,
                            selectMonthIndex,
                            colIndex,
                            undefined,
                          )
                        }
                        mathCalc={(value: number) => calcMathRound(value, ASH_BLUR_DIGIT)}
                        significantDigits={DRIVE_INPUT_DIGITS['fly_ash_carry_out_units']}
                      />
                    </td>
                  ))}
                </tr>
                {/* 飛灰搬出量[t] */}
                <tr className="table-carryrow" style={{ display: planUsageDisplay['fly_ash_carry_out'] && showAshVisible ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td
                      className={(isMonthDisplay() ? true : planDisableCheck.day[lastDayOfLastMonth + colIndex]['fly_ash_carry_out']) ? 'table-carrycell' : 'table-carrycell-green'}
                      style={{
                        pointerEvents: (isMonthDisplay() ? true : planDisableCheck.day[lastDayOfLastMonth + colIndex]['fly_ash_carry_out']) ? 'none' : 'auto',
                      }}
                    >
                      <EmptyNumberInput
                        className="table-inputcell"
                        colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                        data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                        data-key={[DriveMap.fly_ash_carry_out]}
                        formValueState={planEditResourceState}
                        emptyValueState={planEmptyCheckState}
                        onBlur={(event) =>
                          handleCommonBlurChange(
                            event,
                            planEditResourceState,
                            planEmptyCheckState,
                            planInitResource,
                            planUsageCalc,
                            isMonthDisplay() ? DraftMap.month : DraftMap.day,
                            DriveMap.fly_ash_carry_out,
                            selectMonthIndex,
                            colIndex,
                            undefined,
                            updateFlyAshCarryOut,
                          )
                        }
                        mathCalc={(value: number) => calcMathRound(value, ASH_BLUR_DIGIT)}
                        significantDigits={DRIVE_INPUT_DIGITS['fly_ash_carry_out']}
                      />
                    </td>
                  ))}
                </tr>
                {/* 飛灰処理物搬出量 乾灰[t] */}
                <tr className="table-carryrow" style={{ display: planUsageDisplay['dry_fly_ash_carry_out'] && showAshVisible ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td
                      className={
                        (isMonthDisplay() ? true : planDisableCheck.day[lastDayOfLastMonth + colIndex]['dry_fly_ash_carry_out']) ? 'table-carrycell' : 'table-carrycell-green'
                      }
                      style={{
                        pointerEvents: (isMonthDisplay() ? true : planDisableCheck.day[lastDayOfLastMonth + colIndex]['dry_fly_ash_carry_out']) ? 'none' : 'auto',
                      }}
                    >
                      <EmptyNumberInput
                        className="table-inputcell"
                        colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                        data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                        data-key={[DriveMap.dry_fly_ash_carry_out]}
                        formValueState={planEditResourceState}
                        emptyValueState={planEmptyCheckState}
                        onBlur={(event) =>
                          handleCommonBlurChange(
                            event,
                            planEditResourceState,
                            planEmptyCheckState,
                            planInitResource,
                            planUsageCalc,
                            isMonthDisplay() ? DraftMap.month : DraftMap.day,
                            DriveMap.dry_fly_ash_carry_out,
                            selectMonthIndex,
                            colIndex,
                            undefined,
                            updateFlyAshCarryOut,
                          )
                        }
                        mathCalc={(value: number) => calcMathRound(value, ASH_BLUR_DIGIT)}
                        significantDigits={DRIVE_INPUT_DIGITS['dry_fly_ash_carry_out']}
                      />
                    </td>
                  ))}
                </tr>
                {/* 飛灰処理物搬出量 処理灰[t] */}
                <tr className="table-carryrow" style={{ display: planUsageDisplay['processed_fly_ash_carry_out'] && showAshVisible ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td
                      className={
                        (isMonthDisplay() ? true : planDisableCheck.day[lastDayOfLastMonth + colIndex]['processed_fly_ash_carry_out']) ? 'table-carrycell' : 'table-carrycell-green'
                      }
                      style={{
                        pointerEvents: (isMonthDisplay() ? true : planDisableCheck.day[lastDayOfLastMonth + colIndex]['processed_fly_ash_carry_out']) ? 'none' : 'auto',
                      }}
                    >
                      <EmptyNumberInput
                        className="table-inputcell"
                        colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                        data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                        data-key={[DriveMap.processed_fly_ash_carry_out]}
                        formValueState={planEditResourceState}
                        emptyValueState={planEmptyCheckState}
                        onBlur={(event) =>
                          handleCommonBlurChange(
                            event,
                            planEditResourceState,
                            planEmptyCheckState,
                            planInitResource,
                            planUsageCalc,
                            isMonthDisplay() ? DraftMap.month : DraftMap.day,
                            DriveMap.processed_fly_ash_carry_out,
                            selectMonthIndex,
                            colIndex,
                            undefined,
                            updateFlyAshCarryOut,
                          )
                        }
                        mathCalc={(value: number) => calcMathRound(value, ASH_BLUR_DIGIT)}
                        significantDigits={DRIVE_INPUT_DIGITS['processed_fly_ash_carry_out']}
                      />
                    </td>
                  ))}
                </tr>
                {/* 飛灰ピット残量[t] */}
                <tr className="table-carryrow" style={{ display: showAshVisible && !isMonthDisplay() ? 'table-row' : 'none' }}>
                  {' '}
                  {Array.from({ length: displayDayCountOfMonth }).map((_, colIndex) =>
                    existDayFull(planEditResource.day[lastDayOfLastMonth + colIndex]) ? (
                      !planEmptyCheck.day[lastDayOfLastMonth + colIndex].fly_ash_pit_rest === true ? (
                        <td
                          className={
                            (isMonthDisplay() ? true : planDisableCheck.day[lastDayOfLastMonth + colIndex]['fly_ash_pit_rest']) ? 'table-carrycell' : 'table-carrycell-green'
                          }
                          style={{
                            pointerEvents: (isMonthDisplay() ? true : planDisableCheck.day[lastDayOfLastMonth + colIndex]['fly_ash_pit_rest']) ? 'none' : 'auto',
                          }}
                        >
                          {' '}
                          <EmptyNumberInput
                            className="table-inputcell"
                            colIndex={lastDayOfLastMonth + colIndex}
                            data-sign={[DraftMap.day]}
                            data-key={[DriveMap.fly_ash_pit_rest]}
                            formValueState={planEditResourceState}
                            emptyValueState={planEmptyCheckState}
                            onBlur={(event) =>
                              handleCommonBlurChange(
                                event,
                                planEditResourceState,
                                planEmptyCheckState,
                                planInitResource,
                                planUsageCalc,
                                isMonthDisplay() ? DraftMap.month : DraftMap.day,
                                DriveMap.fly_ash_pit_rest,
                                selectMonthIndex,
                                colIndex,
                                undefined,
                                updateFlyAshRest,
                              )
                            }
                            mathCalc={(value: number) => calcMathRound(value, TRASH_PIT_BLUR_DIGIT)}
                            significantDigits={DRIVE_INPUT_DIGITS['fly_ash_pit_rest']}
                          />{' '}
                        </td>
                      ) : (
                        <td className="table-carrycell" />
                      )
                    ) : (
                      <td className="table-carrycell">
                        {' '}
                        {isDayFull(planEmptyCheck.day[lastDayOfLastMonth + colIndex], planDayUsage)
                          ? addComma(calcMathRound(planEditResource.day[lastDayOfLastMonth + colIndex].fly_ash_pit_rest, TRASH_PIT_BLUR_DIGIT))
                          : ''}{' '}
                      </td>
                    ),
                  )}{' '}
                </tr>
                {/* 温水供給設備供給熱量 */}
                <tr className="table-carryrow" style={{ display: planUsageDisplay['hot_water_supply_heating_value'] ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td
                      className={
                        (isMonthDisplay() ? true : planDisableCheck.day[lastDayOfLastMonth + colIndex]['hot_water_supply_heating_value'])
                          ? 'table-carrycell'
                          : 'table-carrycell-green'
                      }
                      style={{
                        pointerEvents: (isMonthDisplay() ? true : planDisableCheck.day[lastDayOfLastMonth + colIndex]['hot_water_supply_heating_value']) ? 'none' : 'auto',
                      }}
                    >
                      <EmptyNumberInput
                        className="table-inputcell"
                        colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                        data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                        data-key={[DriveMap.hot_water_supply_heating_value]}
                        formValueState={planEditResourceState}
                        emptyValueState={planEmptyCheckState}
                        onBlur={(event) =>
                          handleCommonBlurChange(
                            event,
                            planEditResourceState,
                            planEmptyCheckState,
                            planInitResource,
                            planUsageCalc,
                            isMonthDisplay() ? DraftMap.month : DraftMap.day,
                            DriveMap.hot_water_supply_heating_value,
                            selectMonthIndex,
                            colIndex,
                          )
                        }
                        mathCalc={(value: number) => calcMathRound(value, HOT_WATER_BLUR_DIGIT)}
                        significantDigits={DRIVE_INPUT_DIGITS['hot_water_supply_heating_value']}
                      />
                    </td>
                  ))}
                </tr>
                {/* 炉別運転日数 */}
                {Array.from({ length: sessionResource.furnacesCount + 1 }).map((_, runIndex) => (
                  <tr
                    className="table-carryrow"
                    style={{ display: isMonthDisplay() && showOperationDaysVisible && planUsageDisplay['furnace_operating_days'] ? 'table-row' : 'none' }}
                  >
                    {Array.from({
                      length: displayDays.length,
                    }).map((_, colIndex) => (
                      <td className="table-carrycell">
                        {!planEmptyCheck.month[colIndex].burn_volume.some((value) => value === true) ? planMonitorMonthResource[colIndex].furnace_operating_days[runIndex] : ''}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
              {/* ##### 実績情報 テーブル表示・入力管理 #### */}
              <tbody style={{ display: !(existAutoDraft && isPlanVisible()) && isResultVisible() ? 'table-row-group' : 'none' }}>
                <tr className="table-carryrow" style={{ zIndex: 2 }}>
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td className="table-carrycell-lightgreen" style={{ width: isMonthDisplay() ? MONTH_CELL_WIDTH : '' }}>
                      {'実績'}
                    </td>
                  ))}
                </tr>
                {/* 搬入量[t] */}
                <tr className="table-carryrow" style={{ display: resultUsageDisplay['carry_volume'] ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td
                      className={(isMonthDisplay() ? true : resultDisableCheck.day[lastDayOfLastMonth + colIndex]['carry_volume']) ? 'table-carrycell' : 'table-carrycell-green'}
                      style={{ pointerEvents: (isMonthDisplay() ? true : resultDisableCheck.day[lastDayOfLastMonth + colIndex]['carry_volume']) ? 'none' : 'auto' }}
                    >
                      <EmptyNumberInput
                        className="table-inputcell"
                        colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                        data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                        data-key={[DriveMap.carry_volume]}
                        formValueState={resultEditResourceState}
                        emptyValueState={resultEmptyCheckState}
                        onBlur={(event) => {
                          handleCommonBlurChange(
                            event,
                            resultEditResourceState,
                            resultEmptyCheckState,
                            resultInitResource,
                            resultUsageCalc,
                            isMonthDisplay() ? DraftMap.month : DraftMap.day,
                            DriveMap.carry_volume,
                            selectMonthIndex,
                            isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex,
                            undefined,
                            undefined,
                          );
                        }}
                        mathCalc={(value: number) => calcMathRound(value, CARRY_BLUR_DIGIT)}
                        significantDigits={DRIVE_INPUT_DIGITS['carry_volume']}
                      />
                    </td>
                  ))}
                </tr>
                {/* 可燃ごみ搬入量[t] */}
                <tr className="table-carryrow" style={{ display: resultUsageDisplay['combustible_waste_carry_volume'] ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td
                      className={
                        (isMonthDisplay() ? true : resultDisableCheck.day[lastDayOfLastMonth + colIndex]['combustible_waste_carry_volume'])
                          ? 'table-carrycell'
                          : 'table-carrycell-green'
                      }
                      style={{
                        pointerEvents: (isMonthDisplay() ? true : resultDisableCheck.day[lastDayOfLastMonth + colIndex]['combustible_waste_carry_volume']) ? 'none' : 'auto',
                      }}
                    >
                      <EmptyNumberInput
                        className="table-inputcell"
                        colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                        data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                        data-key={[DriveMap.combustible_waste_carry_volume]}
                        formValueState={resultEditResourceState}
                        emptyValueState={resultEmptyCheckState}
                        onBlur={(event) =>
                          handleCommonBlurChange(
                            event,
                            resultEditResourceState,
                            resultEmptyCheckState,
                            resultInitResource,
                            resultUsageCalc,
                            isMonthDisplay() ? DraftMap.month : DraftMap.day,
                            DriveMap.combustible_waste_carry_volume,
                            selectMonthIndex,
                            isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex,
                            undefined,
                            undefined,
                          )
                        }
                        mathCalc={(value: number) => calcMathRound(value, CARRY_BLUR_DIGIT)}
                        significantDigits={DRIVE_INPUT_DIGITS['combustible_waste_carry_volume']}
                      />
                    </td>
                  ))}
                </tr>
                {/* 剪定枝等搬入量[t] */}
                <tr className="table-carryrow" style={{ display: resultUsageDisplay['pruning_branches_carry_volume'] ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td
                      className={
                        (isMonthDisplay() ? true : resultDisableCheck.day[lastDayOfLastMonth + colIndex]['pruning_branches_carry_volume'])
                          ? 'table-carrycell'
                          : 'table-carrycell-green'
                      }
                      style={{
                        pointerEvents: (isMonthDisplay() ? true : resultDisableCheck.day[lastDayOfLastMonth + colIndex]['pruning_branches_carry_volume']) ? 'none' : 'auto',
                      }}
                    >
                      <EmptyNumberInput
                        className="table-inputcell"
                        colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                        data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                        data-key={[DriveMap.pruning_branches_carry_volume]}
                        formValueState={resultEditResourceState}
                        emptyValueState={resultEmptyCheckState}
                        onBlur={(event) =>
                          handleCommonBlurChange(
                            event,
                            resultEditResourceState,
                            resultEmptyCheckState,
                            resultInitResource,
                            resultUsageCalc,
                            isMonthDisplay() ? DraftMap.month : DraftMap.day,
                            DriveMap.pruning_branches_carry_volume,
                            selectMonthIndex,
                            isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex,
                            undefined,
                            undefined,
                          )
                        }
                        mathCalc={(value: number) => calcMathRound(value, CARRY_BLUR_DIGIT)}
                        significantDigits={DRIVE_INPUT_DIGITS['pruning_branches_carry_volume']}
                      />
                    </td>
                  ))}
                </tr>
                {/* 小動物搬入量[t] */}
                <tr className="table-carryrow" style={{ display: resultUsageDisplay['small_animals_carry_volume'] ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td
                      className={
                        (isMonthDisplay() ? true : resultDisableCheck.day[lastDayOfLastMonth + colIndex]['small_animals_carry_volume'])
                          ? 'table-carrycell'
                          : 'table-carrycell-green'
                      }
                      style={{ pointerEvents: (isMonthDisplay() ? true : resultDisableCheck.day[lastDayOfLastMonth + colIndex]['small_animals_carry_volume']) ? 'none' : 'auto' }}
                    >
                      <EmptyNumberInput
                        className="table-inputcell"
                        colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                        data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                        data-key={[DriveMap.small_animals_carry_volume]}
                        formValueState={resultEditResourceState}
                        emptyValueState={resultEmptyCheckState}
                        onBlur={(event) =>
                          handleCommonBlurChange(
                            event,
                            resultEditResourceState,
                            resultEmptyCheckState,
                            resultInitResource,
                            resultUsageCalc,
                            isMonthDisplay() ? DraftMap.month : DraftMap.day,
                            DriveMap.small_animals_carry_volume,
                            selectMonthIndex,
                            isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex,
                            undefined,
                            undefined,
                          )
                        }
                        mathCalc={(value: number) => calcMathRound(value, CARRY_BLUR_DIGIT)}
                        significantDigits={DRIVE_INPUT_DIGITS['small_animals_carry_volume']}
                      />
                    </td>
                  ))}
                </tr>
                {/* 可燃粗大ごみ搬入量[t] */}
                <tr className="table-carryrow" style={{ display: resultUsageDisplay['combustible_bulk_waste_carry_volume'] ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td
                      className={
                        (isMonthDisplay() ? true : resultDisableCheck.day[lastDayOfLastMonth + colIndex]['combustible_bulk_waste_carry_volume'])
                          ? 'table-carrycell'
                          : 'table-carrycell-green'
                      }
                      style={{
                        pointerEvents: (isMonthDisplay() ? true : resultDisableCheck.day[lastDayOfLastMonth + colIndex]['combustible_bulk_waste_carry_volume']) ? 'none' : 'auto',
                      }}
                    >
                      <EmptyNumberInput
                        className="table-inputcell"
                        colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                        data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                        data-key={[DriveMap.combustible_bulk_waste_carry_volume]}
                        formValueState={resultEditResourceState}
                        emptyValueState={resultEmptyCheckState}
                        onBlur={(event) =>
                          handleCommonBlurChange(
                            event,
                            resultEditResourceState,
                            resultEmptyCheckState,
                            resultInitResource,
                            resultUsageCalc,
                            isMonthDisplay() ? DraftMap.month : DraftMap.day,
                            DriveMap.combustible_bulk_waste_carry_volume,
                            selectMonthIndex,
                            isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex,
                            undefined,
                            undefined,
                          )
                        }
                        mathCalc={(value: number) => calcMathRound(value, CARRY_BLUR_DIGIT)}
                        significantDigits={DRIVE_INPUT_DIGITS['combustible_bulk_waste_carry_volume']}
                      />
                    </td>
                  ))}
                </tr>
                {/* 破砕ごみ残渣等搬入量[t] */}
                <tr className="table-carryrow" style={{ display: resultUsageDisplay['shredded_waste_residue_carry_volume'] ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td
                      className={
                        (isMonthDisplay() ? true : resultDisableCheck.day[lastDayOfLastMonth + colIndex]['shredded_waste_residue_carry_volume'])
                          ? 'table-carrycell'
                          : 'table-carrycell-green'
                      }
                      style={{
                        pointerEvents: (isMonthDisplay() ? true : resultDisableCheck.day[lastDayOfLastMonth + colIndex]['shredded_waste_residue_carry_volume']) ? 'none' : 'auto',
                      }}
                    >
                      <EmptyNumberInput
                        className="table-inputcell"
                        colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                        data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                        data-key={[DriveMap.shredded_waste_residue_carry_volume]}
                        formValueState={resultEditResourceState}
                        emptyValueState={resultEmptyCheckState}
                        onBlur={(event) =>
                          handleCommonBlurChange(
                            event,
                            resultEditResourceState,
                            resultEmptyCheckState,
                            resultInitResource,
                            resultUsageCalc,
                            isMonthDisplay() ? DraftMap.month : DraftMap.day,
                            DriveMap.shredded_waste_residue_carry_volume,
                            selectMonthIndex,
                            isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex,
                            undefined,
                            undefined,
                          )
                        }
                        mathCalc={(value: number) => calcMathRound(value, CARRY_BLUR_DIGIT)}
                        significantDigits={DRIVE_INPUT_DIGITS['shredded_waste_residue_carry_volume']}
                      />
                    </td>
                  ))}
                </tr>
                {/* 災害ごみ組合内搬入量[t] */}
                <tr className="table-carryrow" style={{ display: resultUsageDisplay['disaster_waste_internal_carry_volume'] ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td
                      className={
                        (isMonthDisplay() ? true : resultDisableCheck.day[lastDayOfLastMonth + colIndex]['disaster_waste_internal_carry_volume'])
                          ? 'table-carrycell'
                          : 'table-carrycell-green'
                      }
                      style={{
                        pointerEvents: (isMonthDisplay() ? true : resultDisableCheck.day[lastDayOfLastMonth + colIndex]['disaster_waste_internal_carry_volume']) ? 'none' : 'auto',
                      }}
                    >
                      <EmptyNumberInput
                        className="table-inputcell"
                        colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                        data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                        data-key={[DriveMap.disaster_waste_internal_carry_volume]}
                        formValueState={resultEditResourceState}
                        emptyValueState={resultEmptyCheckState}
                        onBlur={(event) =>
                          handleCommonBlurChange(
                            event,
                            resultEditResourceState,
                            resultEmptyCheckState,
                            resultInitResource,
                            resultUsageCalc,
                            isMonthDisplay() ? DraftMap.month : DraftMap.day,
                            DriveMap.disaster_waste_internal_carry_volume,
                            selectMonthIndex,
                            isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex,
                            undefined,
                            undefined,
                          )
                        }
                        mathCalc={(value: number) => calcMathRound(value, CARRY_BLUR_DIGIT)}
                        significantDigits={DRIVE_INPUT_DIGITS['disaster_waste_internal_carry_volume']}
                      />
                    </td>
                  ))}
                </tr>
                {/* 災害ごみ組合外搬入量[t] */}
                <tr className="table-carryrow" style={{ display: resultUsageDisplay['disaster_waste_external_carry_volume'] ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td
                      className={
                        (isMonthDisplay() ? true : resultDisableCheck.day[lastDayOfLastMonth + colIndex]['disaster_waste_external_carry_volume'])
                          ? 'table-carrycell'
                          : 'table-carrycell-green'
                      }
                      style={{
                        pointerEvents: (isMonthDisplay() ? true : resultDisableCheck.day[lastDayOfLastMonth + colIndex]['disaster_waste_external_carry_volume']) ? 'none' : 'auto',
                      }}
                    >
                      <EmptyNumberInput
                        className="table-inputcell"
                        colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                        data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                        data-key={[DriveMap.disaster_waste_external_carry_volume]}
                        formValueState={resultEditResourceState}
                        emptyValueState={resultEmptyCheckState}
                        onBlur={(event) =>
                          handleCommonBlurChange(
                            event,
                            resultEditResourceState,
                            resultEmptyCheckState,
                            resultInitResource,
                            resultUsageCalc,
                            isMonthDisplay() ? DraftMap.month : DraftMap.day,
                            DriveMap.disaster_waste_external_carry_volume,
                            selectMonthIndex,
                            isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex,
                            undefined,
                            undefined,
                          )
                        }
                        mathCalc={(value: number) => calcMathRound(value, CARRY_BLUR_DIGIT)}
                        significantDigits={DRIVE_INPUT_DIGITS['disaster_waste_external_carry_volume']}
                      />
                    </td>
                  ))}
                </tr>
                {/* その他搬入量[t] */}
                <tr className="table-carryrow" style={{ display: resultUsageDisplay['other_carry_volume'] ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td
                      className={
                        (isMonthDisplay() ? true : resultDisableCheck.day[lastDayOfLastMonth + colIndex]['other_carry_volume']) ? 'table-carrycell' : 'table-carrycell-green'
                      }
                      style={{ pointerEvents: (isMonthDisplay() ? true : resultDisableCheck.day[lastDayOfLastMonth + colIndex]['other_carry_volume']) ? 'none' : 'auto' }}
                    >
                      <EmptyNumberInput
                        className="table-inputcell"
                        colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                        data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                        data-key={[DriveMap.other_carry_volume]}
                        formValueState={resultEditResourceState}
                        emptyValueState={resultEmptyCheckState}
                        onBlur={(event) =>
                          handleCommonBlurChange(
                            event,
                            resultEditResourceState,
                            resultEmptyCheckState,
                            resultInitResource,
                            resultUsageCalc,
                            isMonthDisplay() ? DraftMap.month : DraftMap.day,
                            DriveMap.other_carry_volume,
                            selectMonthIndex,
                            isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex,
                            undefined,
                            undefined,
                          )
                        }
                        mathCalc={(value: number) => calcMathRound(value, CARRY_BLUR_DIGIT)}
                        significantDigits={DRIVE_INPUT_DIGITS['other_carry_volume']}
                      />
                    </td>
                  ))}
                </tr>
                {/* 焼却施設底ごみ搬入量[t] */}
                <tr className="table-carryrow" style={{ display: resultUsageDisplay['incineration_facility_bottom_ash_carry_volume'] ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td
                      className={
                        (isMonthDisplay() ? true : resultDisableCheck.day[lastDayOfLastMonth + colIndex]['incineration_facility_bottom_ash_carry_volume'])
                          ? 'table-carrycell'
                          : 'table-carrycell-green'
                      }
                      style={{
                        pointerEvents: (isMonthDisplay() ? true : resultDisableCheck.day[lastDayOfLastMonth + colIndex]['incineration_facility_bottom_ash_carry_volume'])
                          ? 'none'
                          : 'auto',
                      }}
                    >
                      <EmptyNumberInput
                        className="table-inputcell"
                        colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                        data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                        data-key={[DriveMap.incineration_facility_bottom_ash_carry_volume]}
                        formValueState={resultEditResourceState}
                        emptyValueState={resultEmptyCheckState}
                        onBlur={(event) =>
                          handleCommonBlurChange(
                            event,
                            resultEditResourceState,
                            resultEmptyCheckState,
                            resultInitResource,
                            resultUsageCalc,
                            isMonthDisplay() ? DraftMap.month : DraftMap.day,
                            DriveMap.incineration_facility_bottom_ash_carry_volume,
                            selectMonthIndex,
                            isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex,
                            undefined,
                            undefined,
                          )
                        }
                        mathCalc={(value: number) => calcMathRound(value, CARRY_BLUR_DIGIT)}
                        significantDigits={DRIVE_INPUT_DIGITS['incineration_facility_bottom_ash_carry_volume']}
                      />
                    </td>
                  ))}
                </tr>
                {/* 資源物焼却処分搬入量 */}
                <tr className="table-carryrow" style={{ display: resultUsageDisplay['recyclables_carry_volume'] ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td
                      className={
                        (isMonthDisplay() ? true : resultDisableCheck.day[lastDayOfLastMonth + colIndex]['recyclables_carry_volume']) ? 'table-carrycell' : 'table-carrycell-green'
                      }
                      style={{
                        pointerEvents: (isMonthDisplay() ? true : resultDisableCheck.day[lastDayOfLastMonth + colIndex]['recyclables_carry_volume']) ? 'none' : 'auto',
                      }}
                    >
                      <EmptyNumberInput
                        className="table-inputcell"
                        colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                        data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                        data-key={[DriveMap.recyclables_carry_volume]}
                        formValueState={resultEditResourceState}
                        emptyValueState={resultEmptyCheckState}
                        onBlur={(event) =>
                          handleCommonBlurChange(
                            event,
                            resultEditResourceState,
                            resultEmptyCheckState,
                            resultInitResource,
                            resultUsageCalc,
                            isMonthDisplay() ? DraftMap.month : DraftMap.day,
                            DriveMap.recyclables_carry_volume,
                            selectMonthIndex,
                            isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex,
                            undefined,
                            undefined,
                          )
                        }
                        mathCalc={(value: number) => calcMathRound(value, CARRY_BLUR_DIGIT)}
                        significantDigits={DRIVE_INPUT_DIGITS['recyclables_carry_volume']}
                      />
                    </td>
                  ))}
                </tr>
                {/* 搬入量合計[t] */}
                <tr className="table-carryrow" style={{ display: resultUsageDisplay['total_carry_volume'] ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td className="table-carrycell">
                      <div className="table-inputcell m-auto">
                        {!CARRY_KEYS.every((key) => (isMonthDisplay() ? resultEmptyCheck.month[colIndex][key] : resultEmptyCheck.day[lastDayOfLastMonth + colIndex][key]))
                          ? addComma(calcMathRound(resultMonitorResource[isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex].total_carry_volume, CARRY_BLUR_DIGIT))
                          : ''}
                      </div>
                    </td>
                  ))}
                </tr>
                {/* 各炉の状態 */}
                {Array.from({ length: sessionResource.furnacesCount }).map((_, furnacesIndex) => (
                  <>
                    {/* 焼却量[t] */}
                    <tr className="table-carryrow">
                      {Array.from({
                        length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                      }).map((_, colIndex) => (
                        <td className="table-carrycell">
                          <div className="table-inputcell m-auto">
                            {isMonthDisplay()
                              ? !resultEmptyCheck.month[colIndex].burn_volume[furnacesIndex]
                                ? addComma(calcMathRound(resultEditResource.month[colIndex].burn_volume[furnacesIndex], BURNVOLUME_BLUR_DIGIT))
                                : ''
                              : !resultEmptyCheck.day[lastDayOfLastMonth + colIndex].burn_volume[furnacesIndex]
                                ? addComma(calcMathRound(resultEditResource.day[lastDayOfLastMonth + colIndex].burn_volume[furnacesIndex], BURNVOLUME_BLUR_DIGIT))
                                : ''}
                          </div>
                        </td>
                      ))}
                    </tr>
                    {/* 焼却負荷率(停炉日を含まない)[%] */}
                    <tr className="table-carryrow" style={{ display: resultUsageDisplay['burn_load'] && isMonthDisplay() ? 'table-row' : 'none' }}>
                      {Array.from({
                        length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                      }).map((_, colIndex) => (
                        <td className="table-carrycell">
                          {isMonthDisplay()
                            ? !resultEmptyCheck.month[colIndex].burn_volume[furnacesIndex]
                              ? addComma(calcMathFloor(resultMonitorMonthResource[colIndex].burn_load[furnacesIndex], BURNLOAD_DIGIT))
                              : ''
                            : ''}
                        </td>
                      ))}
                    </tr>
                    {/* 焼却負荷率(停炉日を含む)[%] */}
                    <tr className="table-carryrow" style={{ display: resultUsageDisplay['burn_load_include_shutdown'] && isMonthDisplay() ? 'table-row' : 'none' }}>
                      {Array.from({
                        length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                      }).map((_, colIndex) => (
                        <td className="table-carrycell">
                          {isMonthDisplay()
                            ? !resultEmptyCheck.month[colIndex].burn_volume[furnacesIndex]
                              ? addComma(calcMathFloor(resultMonitorMonthResource[colIndex].burn_load_include_shutdown[furnacesIndex], BURNLOAD_DIGIT))
                              : ''
                            : ''}
                        </td>
                      ))}
                    </tr>
                    {/* 運転日数[日] */}
                    <tr className="table-carryrow" style={{ display: isMonthDisplay() ? 'table-row' : 'none' }}>
                      {Array.from({
                        length: isMonthDisplay() ? displayDays.length : 0,
                      }).map((_, colIndex) => (
                        <td className="table-carrycell">
                          {!resultEmptyCheck.month[colIndex].burn_volume[furnacesIndex] ? resultMonitorMonthResource[colIndex].operating_days[furnacesIndex] : ''}
                        </td>
                      ))}
                    </tr>
                    {/* ごみ低位発熱量 */}
                    <tr className="table-carryrow" style={{ display: resultUsageDisplay['waste_lower_heating_value'] ? 'table-row' : 'none' }}>
                      {Array.from({
                        length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                      }).map((_, colIndex) => (
                        <td
                          className={
                            (isMonthDisplay() ? true : resultDisableCheck.day[lastDayOfLastMonth + colIndex]['waste_lower_heating_value'][furnacesIndex])
                              ? 'table-carrycell'
                              : 'table-carrycell-green'
                          }
                          style={{
                            pointerEvents: (isMonthDisplay() ? true : resultDisableCheck.day[lastDayOfLastMonth + colIndex]['waste_lower_heating_value'][furnacesIndex])
                              ? 'none'
                              : 'auto',
                          }}
                        >
                          <EmptyNumberInput
                            className="table-inputcell"
                            colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                            rowIndex={furnacesIndex}
                            data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                            data-key={[DriveMap.waste_lower_heating_value]}
                            formValueState={resultEditResourceState}
                            emptyValueState={resultEmptyCheckState}
                            onBlur={(event) =>
                              handleCommonBlurChange(
                                event,
                                resultEditResourceState,
                                resultEmptyCheckState,
                                resultInitResource,
                                resultUsageCalc,
                                isMonthDisplay() ? DraftMap.month : DraftMap.day,
                                DriveMap.waste_lower_heating_value,
                                selectMonthIndex,
                                colIndex,
                                furnacesIndex,
                              )
                            }
                            mathCalc={(value: number) => calcMathRound(value, WASTE_BLUR_DIGIT)}
                            significantDigits={DRIVE_INPUT_DIGITS['waste_lower_heating_value']}
                          />
                        </td>
                      ))}
                    </tr>
                  </>
                ))}
                {/* 全炉 */}
                {/* 焼却量[t] */}
                <tr className="table-carryrow">
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) =>
                    isMonthDisplay() ? (
                      <td className="table-carrycell">
                        {!resultEmptyCheck.month[colIndex].burn_volume.some((value) => value === true)
                          ? addComma(
                              calcMathRound(
                                Array.from({ length: sessionResource.furnacesCount }, (_, furnacesIndex) => resultEditResource.month[colIndex].burn_volume[furnacesIndex]).reduce(
                                  (sum, value) => sum + value,
                                  0,
                                ),
                                BURNVOLUME_BLUR_DIGIT,
                              ),
                            )
                          : ''}
                      </td>
                    ) : (
                      <td className="table-carrycell">
                        {!resultEmptyCheck.day[lastDayOfLastMonth + colIndex].burn_volume.some((value) => value === true)
                          ? addComma(
                              calcMathRound(
                                Array.from(
                                  { length: sessionResource.furnacesCount },
                                  (_, furnacesIndex) => resultEditResource.day[lastDayOfLastMonth + colIndex].burn_volume[furnacesIndex],
                                ).reduce((sum, value) => sum + value, 0),
                                BURNVOLUME_BLUR_DIGIT,
                              ),
                            )
                          : ''}
                      </td>
                    ),
                  )}
                </tr>
                {/* 焼却負荷率(停炉日を含まない)[%] */}
                <tr className="table-carryrow" style={{ display: resultUsageDisplay['burn_load'] && isMonthDisplay() ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: displayDays.length,
                  }).map((_, colIndex) => (
                    <td className="table-carrycell">
                      {!resultEmptyCheck.month[colIndex].burn_volume.some((value) => value === true)
                        ? addComma(calcMathFloor(resultMonitorMonthResource[colIndex].burn_load_sum, BURNLOAD_DIGIT))
                        : ''}
                    </td>
                  ))}
                </tr>
                {/* 焼却負荷率(停炉日を含む)[%] */}
                <tr className="table-carryrow" style={{ display: resultUsageDisplay['burn_load_include_shutdown'] && isMonthDisplay() ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: displayDays.length,
                  }).map((_, colIndex) => (
                    <td className="table-carrycell">
                      {!resultEmptyCheck.month[colIndex].burn_volume.some((value) => value === true)
                        ? addComma(calcMathFloor(resultMonitorMonthResource[colIndex].burn_load_include_shutdown_sum, BURNLOAD_DIGIT))
                        : ''}
                    </td>
                  ))}
                </tr>
                {/* ピット残量[t] */}
                <tr className="table-carryrow" style={{ display: isMonthDisplay() ? 'none' : 'table-row' }}>
                  {Array.from({
                    length: displayDayCountOfMonth,
                  }).map((_, colIndex) =>
                    !resultEmptyCheck.day[lastDayOfLastMonth + colIndex].burn_volume.some((value) => value === true) ? (
                      <td className="table-carrycell-green">
                        <EmptyNumberInput
                          className="table-inputcell"
                          colIndex={lastDayOfLastMonth + colIndex}
                          data-sign={[DraftMap.day]}
                          data-key={[DriveMap.trash_pit_rest]}
                          formValueState={resultEditResourceState}
                          emptyValueState={resultEmptyCheckState}
                          mathCalc={(value: number) => calcMathRound(value, TRASH_PIT_BLUR_DIGIT)}
                          significantDigits={DRIVE_INPUT_DIGITS['trash_pit_rest']}
                        />
                      </td>
                    ) : (
                      <td className="table-carrycell" />
                    ),
                  )}
                </tr>
                {/* 主灰発生量[t] */}
                <tr className="table-carryrow" style={{ display: resultUsageDisplay['main_ash_generate'] && showAshVisible ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td
                      className={
                        (isMonthDisplay() ? true : resultDisableCheck.day[lastDayOfLastMonth + colIndex]['main_ash_generate']) ? 'table-carrycell' : 'table-carrycell-green'
                      }
                      style={{ pointerEvents: (isMonthDisplay() ? true : resultDisableCheck.day[lastDayOfLastMonth + colIndex]['main_ash_generate']) ? 'none' : 'auto' }}
                    >
                      <EmptyNumberInput
                        className="table-inputcell"
                        colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                        data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                        data-key={[DriveMap.main_ash_generate]}
                        formValueState={resultEditResourceState}
                        emptyValueState={resultEmptyCheckState}
                        onBlur={(event) =>
                          handleCommonBlurChange(
                            event,
                            resultEditResourceState,
                            resultEmptyCheckState,
                            resultInitResource,
                            resultUsageCalc,
                            isMonthDisplay() ? DraftMap.month : DraftMap.day,
                            DriveMap.main_ash_generate,
                            selectMonthIndex,
                            colIndex,
                            undefined,
                          )
                        }
                        mathCalc={(value: number) => calcMathRound(value, ASH_BLUR_DIGIT)}
                        significantDigits={DRIVE_INPUT_DIGITS['main_ash_generate']}
                      />
                    </td>
                  ))}
                </tr>
                {/* 主灰搬出台数[台] */}
                <tr className="table-carryrow" style={{ display: resultUsageDisplay['main_ash_carry_out_units'] && showAshVisible ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td
                      className={
                        (isMonthDisplay() ? true : resultDisableCheck.day[lastDayOfLastMonth + colIndex]['main_ash_carry_out_units']) ? 'table-carrycell' : 'table-carrycell-green'
                      }
                      style={{ pointerEvents: (isMonthDisplay() ? true : resultDisableCheck.day[lastDayOfLastMonth + colIndex]['main_ash_carry_out_units']) ? 'none' : 'auto' }}
                    >
                      <EmptyNumberInput
                        className="table-inputcell"
                        colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                        data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                        data-key={[DriveMap.main_ash_carry_out_units]}
                        formValueState={resultEditResourceState}
                        emptyValueState={resultEmptyCheckState}
                        onBlur={(event) =>
                          handleCommonBlurChange(
                            event,
                            resultEditResourceState,
                            resultEmptyCheckState,
                            resultInitResource,
                            resultUsageCalc,
                            isMonthDisplay() ? DraftMap.month : DraftMap.day,
                            DriveMap.main_ash_carry_out_units,
                            selectMonthIndex,
                            colIndex,
                            undefined,
                          )
                        }
                        mathCalc={(value: number) => calcMathRound(value, ASH_BLUR_DIGIT)}
                        significantDigits={DRIVE_INPUT_DIGITS['main_ash_carry_out_units']}
                      />
                    </td>
                  ))}
                </tr>
                {/* 主灰搬出量[t] */}
                <tr className="table-carryrow" style={{ display: resultUsageDisplay['main_ash_carry_out'] && showAshVisible ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td
                      className={
                        (isMonthDisplay() ? true : resultDisableCheck.day[lastDayOfLastMonth + colIndex]['main_ash_carry_out']) ? 'table-carrycell' : 'table-carrycell-green'
                      }
                      style={{ pointerEvents: (isMonthDisplay() ? true : resultDisableCheck.day[lastDayOfLastMonth + colIndex]['main_ash_carry_out']) ? 'none' : 'auto' }}
                    >
                      <EmptyNumberInput
                        className="table-inputcell"
                        colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                        data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                        data-key={[DriveMap.main_ash_carry_out]}
                        formValueState={resultEditResourceState}
                        emptyValueState={resultEmptyCheckState}
                        onBlur={(event) =>
                          handleCommonBlurChange(
                            event,
                            resultEditResourceState,
                            resultEmptyCheckState,
                            resultInitResource,
                            resultUsageCalc,
                            isMonthDisplay() ? DraftMap.month : DraftMap.day,
                            DriveMap.main_ash_carry_out,
                            selectMonthIndex,
                            colIndex,
                            undefined,
                          )
                        }
                        mathCalc={(value: number) => calcMathRound(value, ASH_BLUR_DIGIT)}
                        significantDigits={DRIVE_INPUT_DIGITS['main_ash_carry_out']}
                      />
                    </td>
                  ))}
                </tr>
                {/* 主灰ピット残量[t] */}
                <tr className="table-carryrow" style={{ display: showAshVisible && !isMonthDisplay() ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: displayDayCountOfMonth,
                  }).map((_, colIndex) =>
                    !resultEmptyCheck.day[lastDayOfLastMonth + colIndex].main_ash_generate === true ? (
                      <td className="table-carrycell-green">
                        <EmptyNumberInput
                          className="table-inputcell"
                          colIndex={lastDayOfLastMonth + colIndex}
                          data-sign={[DraftMap.day]}
                          data-key={[DriveMap.main_ash_pit_rest]}
                          formValueState={resultEditResourceState}
                          emptyValueState={resultEmptyCheckState}
                          mathCalc={(value: number) => calcMathRound(value, TRASH_PIT_BLUR_DIGIT)}
                          significantDigits={DRIVE_INPUT_DIGITS['main_ash_pit_rest']}
                        />
                      </td>
                    ) : (
                      <td className="table-carrycell" />
                    ),
                  )}
                </tr>
                {/* 飛灰生産量[t] */}
                <tr className="table-carryrow" style={{ display: resultUsageDisplay['fly_ash_generate'] && showAshVisible ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td
                      className={
                        (isMonthDisplay() ? true : resultDisableCheck.day[lastDayOfLastMonth + colIndex]['fly_ash_generate']) ? 'table-carrycell' : 'table-carrycell-green'
                      }
                      style={{ pointerEvents: (isMonthDisplay() ? true : resultDisableCheck.day[lastDayOfLastMonth + colIndex]['fly_ash_generate']) ? 'none' : 'auto' }}
                    >
                      <EmptyNumberInput
                        className="table-inputcell"
                        colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                        data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                        data-key={[DriveMap.fly_ash_generate]}
                        formValueState={resultEditResourceState}
                        emptyValueState={resultEmptyCheckState}
                        onBlur={(event) =>
                          handleCommonBlurChange(
                            event,
                            resultEditResourceState,
                            resultEmptyCheckState,
                            resultInitResource,
                            resultUsageCalc,
                            isMonthDisplay() ? DraftMap.month : DraftMap.day,
                            DriveMap.fly_ash_generate,
                            selectMonthIndex,
                            colIndex,
                            undefined,
                          )
                        }
                        mathCalc={(value: number) => calcMathRound(value, ASH_BLUR_DIGIT)}
                        significantDigits={DRIVE_INPUT_DIGITS['fly_ash_generate']}
                      />
                    </td>
                  ))}
                </tr>
                {/* 飛灰搬出台数[台]  */}
                <tr className="table-carryrow" style={{ display: resultUsageDisplay['fly_ash_carry_out_units'] && showAshVisible ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td
                      className={
                        (isMonthDisplay() ? true : resultDisableCheck.day[lastDayOfLastMonth + colIndex]['fly_ash_carry_out_units']) ? 'table-carrycell' : 'table-carrycell-green'
                      }
                      style={{ pointerEvents: (isMonthDisplay() ? true : resultDisableCheck.day[lastDayOfLastMonth + colIndex]['fly_ash_carry_out_units']) ? 'none' : 'auto' }}
                    >
                      <EmptyNumberInput
                        className="table-inputcell"
                        colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                        data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                        data-key={[DriveMap.fly_ash_carry_out_units]}
                        formValueState={resultEditResourceState}
                        emptyValueState={resultEmptyCheckState}
                        onBlur={(event) =>
                          handleCommonBlurChange(
                            event,
                            resultEditResourceState,
                            resultEmptyCheckState,
                            resultInitResource,
                            resultUsageCalc,
                            isMonthDisplay() ? DraftMap.month : DraftMap.day,
                            DriveMap.fly_ash_carry_out_units,
                            selectMonthIndex,
                            colIndex,
                            undefined,
                          )
                        }
                        mathCalc={(value: number) => calcMathRound(value, ASH_BLUR_DIGIT)}
                        significantDigits={DRIVE_INPUT_DIGITS['fly_ash_carry_out_units']}
                      />
                    </td>
                  ))}
                </tr>
                {/* 飛灰搬出量[t] */}
                <tr className="table-carryrow" style={{ display: resultUsageDisplay['fly_ash_carry_out'] && showAshVisible ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td
                      className={
                        (isMonthDisplay() ? true : resultDisableCheck.day[lastDayOfLastMonth + colIndex]['fly_ash_carry_out']) ? 'table-carrycell' : 'table-carrycell-green'
                      }
                      style={{ pointerEvents: (isMonthDisplay() ? true : resultDisableCheck.day[lastDayOfLastMonth + colIndex]['fly_ash_carry_out']) ? 'none' : 'auto' }}
                    >
                      <EmptyNumberInput
                        className="table-inputcell"
                        colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                        data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                        data-key={[DriveMap.fly_ash_carry_out]}
                        formValueState={resultEditResourceState}
                        emptyValueState={resultEmptyCheckState}
                        onBlur={(event) =>
                          handleCommonBlurChange(
                            event,
                            resultEditResourceState,
                            resultEmptyCheckState,
                            resultInitResource,
                            resultUsageCalc,
                            isMonthDisplay() ? DraftMap.month : DraftMap.day,
                            DriveMap.fly_ash_carry_out,
                            selectMonthIndex,
                            colIndex,
                            undefined,
                          )
                        }
                        mathCalc={(value: number) => calcMathRound(value, ASH_BLUR_DIGIT)}
                        significantDigits={DRIVE_INPUT_DIGITS['fly_ash_carry_out']}
                      />
                    </td>
                  ))}
                </tr>
                {/* 飛灰処理物搬出量 乾灰[t] */}
                <tr className="table-carryrow" style={{ display: resultUsageDisplay['dry_fly_ash_carry_out'] && showAshVisible ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td
                      className={
                        (isMonthDisplay() ? true : resultDisableCheck.day[lastDayOfLastMonth + colIndex]['dry_fly_ash_carry_out']) ? 'table-carrycell' : 'table-carrycell-green'
                      }
                      style={{ pointerEvents: (isMonthDisplay() ? true : resultDisableCheck.day[lastDayOfLastMonth + colIndex]['dry_fly_ash_carry_out']) ? 'none' : 'auto' }}
                    >
                      <EmptyNumberInput
                        className="table-inputcell"
                        colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                        data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                        data-key={[DriveMap.dry_fly_ash_carry_out]}
                        formValueState={resultEditResourceState}
                        emptyValueState={resultEmptyCheckState}
                        onBlur={(event) =>
                          handleCommonBlurChange(
                            event,
                            resultEditResourceState,
                            resultEmptyCheckState,
                            resultInitResource,
                            resultUsageCalc,
                            isMonthDisplay() ? DraftMap.month : DraftMap.day,
                            DriveMap.dry_fly_ash_carry_out,
                            selectMonthIndex,
                            colIndex,
                            undefined,
                          )
                        }
                        mathCalc={(value: number) => calcMathRound(value, ASH_BLUR_DIGIT)}
                        significantDigits={DRIVE_INPUT_DIGITS['dry_fly_ash_carry_out']}
                      />
                    </td>
                  ))}
                </tr>
                {/* 飛灰処理物搬出量 処理灰[t] */}
                <tr className="table-carryrow" style={{ display: resultUsageDisplay['processed_fly_ash_carry_out'] && showAshVisible ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td
                      className={
                        (isMonthDisplay() ? true : resultDisableCheck.day[lastDayOfLastMonth + colIndex]['processed_fly_ash_carry_out'])
                          ? 'table-carrycell'
                          : 'table-carrycell-green'
                      }
                      style={{ pointerEvents: (isMonthDisplay() ? true : resultDisableCheck.day[lastDayOfLastMonth + colIndex]['processed_fly_ash_carry_out']) ? 'none' : 'auto' }}
                    >
                      <EmptyNumberInput
                        className="table-inputcell"
                        colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                        data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                        data-key={[DriveMap.processed_fly_ash_carry_out]}
                        formValueState={resultEditResourceState}
                        emptyValueState={resultEmptyCheckState}
                        onBlur={(event) =>
                          handleCommonBlurChange(
                            event,
                            resultEditResourceState,
                            resultEmptyCheckState,
                            resultInitResource,
                            resultUsageCalc,
                            isMonthDisplay() ? DraftMap.month : DraftMap.day,
                            DriveMap.processed_fly_ash_carry_out,
                            selectMonthIndex,
                            colIndex,
                            undefined,
                          )
                        }
                        mathCalc={(value: number) => calcMathRound(value, ASH_BLUR_DIGIT)}
                        significantDigits={DRIVE_INPUT_DIGITS['processed_fly_ash_carry_out']}
                      />
                    </td>
                  ))}
                </tr>
                {/* 飛灰ピット残量[t] */}
                <tr className="table-carryrow" style={{ display: showAshVisible && !isMonthDisplay() ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: displayDayCountOfMonth,
                  }).map((_, colIndex) =>
                    !resultEmptyCheck.day[lastDayOfLastMonth + colIndex].fly_ash_generate === true ? (
                      <td className="table-carrycell-green">
                        <EmptyNumberInput
                          className="table-inputcell"
                          colIndex={lastDayOfLastMonth + colIndex}
                          data-sign={[DraftMap.day]}
                          data-key={[DriveMap.fly_ash_pit_rest]}
                          formValueState={resultEditResourceState}
                          emptyValueState={resultEmptyCheckState}
                          mathCalc={(value: number) => calcMathRound(value, TRASH_PIT_BLUR_DIGIT)}
                          significantDigits={DRIVE_INPUT_DIGITS['fly_ash_pit_rest']}
                        />
                      </td>
                    ) : (
                      <td className="table-carrycell" />
                    ),
                  )}
                </tr>
                {/* 温水供給設備供給熱量 */}
                <tr className="table-carryrow" style={{ display: resultUsageDisplay['hot_water_supply_heating_value'] ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td
                      className={
                        (isMonthDisplay() ? true : resultDisableCheck.day[lastDayOfLastMonth + colIndex]['hot_water_supply_heating_value'])
                          ? 'table-carrycell'
                          : 'table-carrycell-green'
                      }
                      style={{
                        pointerEvents: (isMonthDisplay() ? true : resultDisableCheck.day[lastDayOfLastMonth + colIndex]['hot_water_supply_heating_value']) ? 'none' : 'auto',
                      }}
                    >
                      <EmptyNumberInput
                        className="table-inputcell"
                        colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                        data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                        data-key={[DriveMap.hot_water_supply_heating_value]}
                        formValueState={resultEditResourceState}
                        emptyValueState={resultEmptyCheckState}
                        onBlur={(event) =>
                          handleCommonBlurChange(
                            event,
                            resultEditResourceState,
                            resultEmptyCheckState,
                            resultInitResource,
                            resultUsageCalc,
                            isMonthDisplay() ? DraftMap.month : DraftMap.day,
                            DriveMap.hot_water_supply_heating_value,
                            selectMonthIndex,
                            colIndex,
                          )
                        }
                        mathCalc={(value: number) => calcMathRound(value, HOT_WATER_BLUR_DIGIT)}
                        significantDigits={DRIVE_INPUT_DIGITS['hot_water_supply_heating_value']}
                      />
                    </td>
                  ))}
                </tr>
                {/* 炉別運転日数 */}
                {Array.from({ length: sessionResource.furnacesCount + 1 }).map((_, runIndex) => (
                  <tr
                    className="table-carryrow"
                    style={{ display: isMonthDisplay() && showOperationDaysVisible && resultUsageDisplay['furnace_operating_days'] ? 'table-row' : 'none' }}
                  >
                    {Array.from({
                      length: displayDays.length,
                    }).map((_, colIndex) => (
                      <td className="table-carrycell">
                        {!resultEmptyCheck.month[colIndex].burn_volume.some((value) => value === true) ? resultMonitorMonthResource[colIndex].furnace_operating_days[runIndex] : ''}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
              {/* ##### 計画立案情報 テーブル表示・入力管理 #### */}
              <tbody style={{ display: existAutoDraft ? 'table-row-group' : 'none' }}>
                <tr style={{ display: isSelectDraft.some((value) => value) ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td className="table-carrycell-pog" style={{ width: isMonthDisplay() ? MONTH_CELL_WIDTH : '' }}>
                      {'計画'}
                    </td>
                  ))}
                </tr>
                {/* 搬入量[t] */}
                <tr className="table-carryrow" style={{ display: isSelectDraft.some((value) => value) && draftUsageDisplay['carry_volume'] ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td className={'table-carrycell'} style={{ pointerEvents: 'none' }}>
                      <EmptyNumberInput
                        className="table-inputcell"
                        colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                        data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                        data-key={[DriveMap.carry_volume]}
                        formValueState={draftLength > 0 ? getArrayElementState(draftEditResource, setDraftEditResource, 0) : planEditResourceState}
                        emptyValueState={getArrayElementState(draftEmptyCheck, setDraftEmptyCheck, 0)}
                        onBlur={(event) => {
                          const newValue = Number((event.target as HTMLInputElement).value);
                          const editResources: DraftContext[] = handleDraftBlurChange(
                            event,
                            0,
                            DriveMap.carry_volume,
                            selectMonthIndex,
                            isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex,
                            newValue,
                          );
                          Array.from({ length: editResources.length }).forEach((_, draftIndex) => {
                            handleCommonBlurChange(
                              event,
                              getArrayElementState(editResources, setDraftEditResource, draftIndex),
                              getArrayElementState(draftEmptyCheck, setDraftEmptyCheck, draftIndex),
                              draftInitResource[draftIndex],
                              draftUsageCalc,
                              isMonthDisplay() ? DraftMap.month : DraftMap.day,
                              DriveMap.carry_volume,
                              selectMonthIndex,
                              isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex,
                              undefined,
                              updateCarryDependent,
                            );
                          });
                        }}
                        mathCalc={(value: number) => calcMathRound(value, CARRY_BLUR_DIGIT)}
                        significantDigits={DRIVE_INPUT_DIGITS['carry_volume']}
                      />
                    </td>
                  ))}
                </tr>
                {/* 可燃ごみ搬入量[t] */}
                <tr
                  className="table-carryrow"
                  style={{ display: isSelectDraft.some((value) => value) && draftUsageDisplay['combustible_waste_carry_volume'] ? 'table-row' : 'none' }}
                >
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td
                      className={
                        (isMonthDisplay() ? true : draftDisableCheck[0].day[lastDayOfLastMonth + colIndex]['combustible_waste_carry_volume'])
                          ? 'table-carrycell'
                          : 'table-carrycell-green'
                      }
                      style={{
                        pointerEvents: (isMonthDisplay() ? true : draftDisableCheck[0].day[lastDayOfLastMonth + colIndex]['combustible_waste_carry_volume']) ? 'none' : 'auto',
                      }}
                    >
                      <EmptyNumberInput
                        className="table-inputcell"
                        colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                        data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                        data-key={[DriveMap.combustible_waste_carry_volume]}
                        formValueState={draftLength > 0 ? getArrayElementState(draftEditResource, setDraftEditResource, 0) : planEditResourceState}
                        emptyValueState={getArrayElementState(draftEmptyCheck, setDraftEmptyCheck, 0)}
                        onBlur={(event) => {
                          Array.from({ length: draftLength }).forEach((_, draftIndex) => {
                            handleCommonBlurChange(
                              event,
                              getArrayElementState(draftEditResource, setDraftEditResource, draftIndex),
                              getArrayElementState(draftEmptyCheck, setDraftEmptyCheck, draftIndex),
                              draftInitResource[draftIndex],
                              draftUsageCalc,
                              isMonthDisplay() ? DraftMap.month : DraftMap.day,
                              DriveMap.combustible_waste_carry_volume,
                              selectMonthIndex,
                              isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex,
                              undefined,
                              updateCarryDependent,
                            );
                          });
                        }}
                        mathCalc={(value: number) => calcMathRound(value, CARRY_BLUR_DIGIT)}
                        significantDigits={DRIVE_INPUT_DIGITS['combustible_waste_carry_volume']}
                      />
                    </td>
                  ))}
                </tr>
                {/* 剪定枝等搬入量[t] */}
                <tr
                  className="table-carryrow"
                  style={{ display: isSelectDraft.some((value) => value) && draftUsageDisplay['pruning_branches_carry_volume'] ? 'table-row' : 'none' }}
                >
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td
                      className={
                        (isMonthDisplay() ? true : draftDisableCheck[0].day[lastDayOfLastMonth + colIndex]['pruning_branches_carry_volume'])
                          ? 'table-carrycell'
                          : 'table-carrycell-green'
                      }
                      style={{
                        pointerEvents: (isMonthDisplay() ? true : draftDisableCheck[0].day[lastDayOfLastMonth + colIndex]['pruning_branches_carry_volume']) ? 'none' : 'auto',
                      }}
                    >
                      <EmptyNumberInput
                        className="table-inputcell"
                        colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                        data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                        data-key={[DriveMap.pruning_branches_carry_volume]}
                        formValueState={draftLength > 0 ? getArrayElementState(draftEditResource, setDraftEditResource, 0) : planEditResourceState}
                        emptyValueState={getArrayElementState(draftEmptyCheck, setDraftEmptyCheck, 0)}
                        onBlur={(event) => {
                          Array.from({ length: draftLength }).forEach((_, draftIndex) => {
                            handleCommonBlurChange(
                              event,
                              getArrayElementState(draftEditResource, setDraftEditResource, draftIndex),
                              getArrayElementState(draftEmptyCheck, setDraftEmptyCheck, draftIndex),
                              draftInitResource[draftIndex],
                              draftUsageCalc,
                              isMonthDisplay() ? DraftMap.month : DraftMap.day,
                              DriveMap.pruning_branches_carry_volume,
                              selectMonthIndex,
                              isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex,
                              undefined,
                              updateCarryDependent,
                            );
                          });
                        }}
                        mathCalc={(value: number) => calcMathRound(value, CARRY_BLUR_DIGIT)}
                        significantDigits={DRIVE_INPUT_DIGITS['pruning_branches_carry_volume']}
                      />
                    </td>
                  ))}
                </tr>
                {/* 小動物搬入量[t] */}
                <tr className="table-carryrow" style={{ display: isSelectDraft.some((value) => value) && draftUsageDisplay['small_animals_carry_volume'] ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td
                      className={
                        (isMonthDisplay() ? true : draftDisableCheck[0].day[lastDayOfLastMonth + colIndex]['small_animals_carry_volume'])
                          ? 'table-carrycell'
                          : 'table-carrycell-green'
                      }
                      style={{
                        pointerEvents: (isMonthDisplay() ? true : draftDisableCheck[0].day[lastDayOfLastMonth + colIndex]['small_animals_carry_volume']) ? 'none' : 'auto',
                      }}
                    >
                      <EmptyNumberInput
                        className="table-inputcell"
                        colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                        data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                        data-key={[DriveMap.small_animals_carry_volume]}
                        formValueState={draftLength > 0 ? getArrayElementState(draftEditResource, setDraftEditResource, 0) : planEditResourceState}
                        emptyValueState={getArrayElementState(draftEmptyCheck, setDraftEmptyCheck, 0)}
                        onBlur={(event) => {
                          Array.from({ length: draftLength }).forEach((_, draftIndex) => {
                            handleCommonBlurChange(
                              event,
                              getArrayElementState(draftEditResource, setDraftEditResource, draftIndex),
                              getArrayElementState(draftEmptyCheck, setDraftEmptyCheck, draftIndex),
                              draftInitResource[draftIndex],
                              draftUsageCalc,
                              isMonthDisplay() ? DraftMap.month : DraftMap.day,
                              DriveMap.small_animals_carry_volume,
                              selectMonthIndex,
                              isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex,
                              undefined,
                              updateCarryDependent,
                            );
                          });
                        }}
                        mathCalc={(value: number) => calcMathRound(value, CARRY_BLUR_DIGIT)}
                        significantDigits={DRIVE_INPUT_DIGITS['small_animals_carry_volume']}
                      />
                    </td>
                  ))}
                </tr>
                {/* 可燃粗大ごみ搬入量[t] */}
                <tr
                  className="table-carryrow"
                  style={{ display: isSelectDraft.some((value) => value) && draftUsageDisplay['combustible_bulk_waste_carry_volume'] ? 'table-row' : 'none' }}
                >
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td
                      className={
                        (isMonthDisplay() ? true : draftDisableCheck[0].day[lastDayOfLastMonth + colIndex]['combustible_bulk_waste_carry_volume'])
                          ? 'table-carrycell'
                          : 'table-carrycell-green'
                      }
                      style={{
                        pointerEvents: (isMonthDisplay() ? true : draftDisableCheck[0].day[lastDayOfLastMonth + colIndex]['combustible_bulk_waste_carry_volume']) ? 'none' : 'auto',
                      }}
                    >
                      <EmptyNumberInput
                        className="table-inputcell"
                        colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                        data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                        data-key={[DriveMap.combustible_bulk_waste_carry_volume]}
                        formValueState={draftLength > 0 ? getArrayElementState(draftEditResource, setDraftEditResource, 0) : planEditResourceState}
                        emptyValueState={getArrayElementState(draftEmptyCheck, setDraftEmptyCheck, 0)}
                        onBlur={(event) => {
                          Array.from({ length: draftLength }).forEach((_, draftIndex) => {
                            handleCommonBlurChange(
                              event,
                              getArrayElementState(draftEditResource, setDraftEditResource, draftIndex),
                              getArrayElementState(draftEmptyCheck, setDraftEmptyCheck, draftIndex),
                              draftInitResource[draftIndex],
                              draftUsageCalc,
                              isMonthDisplay() ? DraftMap.month : DraftMap.day,
                              DriveMap.combustible_bulk_waste_carry_volume,
                              selectMonthIndex,
                              isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex,
                              undefined,
                              updateCarryDependent,
                            );
                          });
                        }}
                        mathCalc={(value: number) => calcMathRound(value, CARRY_BLUR_DIGIT)}
                        significantDigits={DRIVE_INPUT_DIGITS['combustible_bulk_waste_carry_volume']}
                      />
                    </td>
                  ))}
                </tr>
                {/* 破砕ごみ残渣等搬入量[t] */}
                <tr
                  className="table-carryrow"
                  style={{ display: isSelectDraft.some((value) => value) && draftUsageDisplay['shredded_waste_residue_carry_volume'] ? 'table-row' : 'none' }}
                >
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td
                      className={
                        (isMonthDisplay() ? true : draftDisableCheck[0].day[lastDayOfLastMonth + colIndex]['shredded_waste_residue_carry_volume'])
                          ? 'table-carrycell'
                          : 'table-carrycell-green'
                      }
                      style={{
                        pointerEvents: (isMonthDisplay() ? true : draftDisableCheck[0].day[lastDayOfLastMonth + colIndex]['shredded_waste_residue_carry_volume']) ? 'none' : 'auto',
                      }}
                    >
                      <EmptyNumberInput
                        className="table-inputcell"
                        colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                        data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                        data-key={[DriveMap.shredded_waste_residue_carry_volume]}
                        formValueState={draftLength > 0 ? getArrayElementState(draftEditResource, setDraftEditResource, 0) : planEditResourceState}
                        emptyValueState={getArrayElementState(draftEmptyCheck, setDraftEmptyCheck, 0)}
                        onBlur={(event) => {
                          Array.from({ length: draftLength }).forEach((_, draftIndex) => {
                            handleCommonBlurChange(
                              event,
                              getArrayElementState(draftEditResource, setDraftEditResource, draftIndex),
                              getArrayElementState(draftEmptyCheck, setDraftEmptyCheck, draftIndex),
                              draftInitResource[draftIndex],
                              draftUsageCalc,
                              isMonthDisplay() ? DraftMap.month : DraftMap.day,
                              DriveMap.shredded_waste_residue_carry_volume,
                              selectMonthIndex,
                              isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex,
                              undefined,
                              updateCarryDependent,
                            );
                          });
                        }}
                        mathCalc={(value: number) => calcMathRound(value, CARRY_BLUR_DIGIT)}
                        significantDigits={DRIVE_INPUT_DIGITS['shredded_waste_residue_carry_volume']}
                      />
                    </td>
                  ))}
                </tr>
                {/* 災害ごみ組合内搬入量[t] */}
                <tr
                  className="table-carryrow"
                  style={{ display: isSelectDraft.some((value) => value) && draftUsageDisplay['disaster_waste_internal_carry_volume'] ? 'table-row' : 'none' }}
                >
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td
                      className={
                        (isMonthDisplay() ? true : draftDisableCheck[0].day[lastDayOfLastMonth + colIndex]['disaster_waste_internal_carry_volume'])
                          ? 'table-carrycell'
                          : 'table-carrycell-green'
                      }
                      style={{
                        pointerEvents: (isMonthDisplay() ? true : draftDisableCheck[0].day[lastDayOfLastMonth + colIndex]['disaster_waste_internal_carry_volume'])
                          ? 'none'
                          : 'auto',
                      }}
                    >
                      <EmptyNumberInput
                        className="table-inputcell"
                        colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                        data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                        data-key={[DriveMap.disaster_waste_internal_carry_volume]}
                        formValueState={draftLength > 0 ? getArrayElementState(draftEditResource, setDraftEditResource, 0) : planEditResourceState}
                        emptyValueState={getArrayElementState(draftEmptyCheck, setDraftEmptyCheck, 0)}
                        onBlur={(event) => {
                          Array.from({ length: draftLength }).forEach((_, draftIndex) => {
                            handleCommonBlurChange(
                              event,
                              getArrayElementState(draftEditResource, setDraftEditResource, draftIndex),
                              getArrayElementState(draftEmptyCheck, setDraftEmptyCheck, draftIndex),
                              draftInitResource[draftIndex],
                              draftUsageCalc,
                              isMonthDisplay() ? DraftMap.month : DraftMap.day,
                              DriveMap.disaster_waste_internal_carry_volume,
                              selectMonthIndex,
                              isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex,
                              undefined,
                              updateCarryDependent,
                            );
                          });
                        }}
                        mathCalc={(value: number) => calcMathRound(value, CARRY_BLUR_DIGIT)}
                        significantDigits={DRIVE_INPUT_DIGITS['disaster_waste_internal_carry_volume']}
                      />
                    </td>
                  ))}
                </tr>
                {/* 災害ごみ組合外搬入量[t] */}
                <tr
                  className="table-carryrow"
                  style={{ display: isSelectDraft.some((value) => value) && draftUsageDisplay['disaster_waste_external_carry_volume'] ? 'table-row' : 'none' }}
                >
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td
                      className={
                        (isMonthDisplay() ? true : draftDisableCheck[0].day[lastDayOfLastMonth + colIndex]['disaster_waste_external_carry_volume'])
                          ? 'table-carrycell'
                          : 'table-carrycell-green'
                      }
                      style={{
                        pointerEvents: (isMonthDisplay() ? true : draftDisableCheck[0].day[lastDayOfLastMonth + colIndex]['disaster_waste_external_carry_volume'])
                          ? 'none'
                          : 'auto',
                      }}
                    >
                      <EmptyNumberInput
                        className="table-inputcell"
                        colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                        data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                        data-key={[DriveMap.disaster_waste_external_carry_volume]}
                        formValueState={draftLength > 0 ? getArrayElementState(draftEditResource, setDraftEditResource, 0) : planEditResourceState}
                        emptyValueState={getArrayElementState(draftEmptyCheck, setDraftEmptyCheck, 0)}
                        onBlur={(event) => {
                          Array.from({ length: draftLength }).forEach((_, draftIndex) => {
                            handleCommonBlurChange(
                              event,
                              getArrayElementState(draftEditResource, setDraftEditResource, draftIndex),
                              getArrayElementState(draftEmptyCheck, setDraftEmptyCheck, draftIndex),
                              draftInitResource[draftIndex],
                              draftUsageCalc,
                              isMonthDisplay() ? DraftMap.month : DraftMap.day,
                              DriveMap.disaster_waste_external_carry_volume,
                              selectMonthIndex,
                              isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex,
                              undefined,
                              updateCarryDependent,
                            );
                          });
                        }}
                        mathCalc={(value: number) => calcMathRound(value, CARRY_BLUR_DIGIT)}
                        significantDigits={DRIVE_INPUT_DIGITS['disaster_waste_external_carry_volume']}
                      />
                    </td>
                  ))}
                </tr>
                {/* その他搬入量[t] */}
                <tr className="table-carryrow" style={{ display: isSelectDraft.some((value) => value) && draftUsageDisplay['other_carry_volume'] ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td
                      className={
                        (isMonthDisplay() ? true : draftDisableCheck[0].day[lastDayOfLastMonth + colIndex]['other_carry_volume']) ? 'table-carrycell' : 'table-carrycell-green'
                      }
                      style={{
                        pointerEvents: (isMonthDisplay() ? true : draftDisableCheck[0].day[lastDayOfLastMonth + colIndex]['other_carry_volume']) ? 'none' : 'auto',
                      }}
                    >
                      <EmptyNumberInput
                        className="table-inputcell"
                        colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                        data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                        data-key={[DriveMap.other_carry_volume]}
                        formValueState={draftLength > 0 ? getArrayElementState(draftEditResource, setDraftEditResource, 0) : planEditResourceState}
                        emptyValueState={getArrayElementState(draftEmptyCheck, setDraftEmptyCheck, 0)}
                        onBlur={(event) => {
                          Array.from({ length: draftLength }).forEach((_, draftIndex) => {
                            handleCommonBlurChange(
                              event,
                              getArrayElementState(draftEditResource, setDraftEditResource, draftIndex),
                              getArrayElementState(draftEmptyCheck, setDraftEmptyCheck, draftIndex),
                              draftInitResource[draftIndex],
                              draftUsageCalc,
                              isMonthDisplay() ? DraftMap.month : DraftMap.day,
                              DriveMap.other_carry_volume,
                              selectMonthIndex,
                              isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex,
                              undefined,
                              updateCarryDependent,
                            );
                          });
                        }}
                        mathCalc={(value: number) => calcMathRound(value, CARRY_BLUR_DIGIT)}
                        significantDigits={DRIVE_INPUT_DIGITS['other_carry_volume']}
                      />
                    </td>
                  ))}
                </tr>
                {/* 焼却施設底ごみ搬入量[t] */}
                <tr
                  className="table-carryrow"
                  style={{
                    display: isSelectDraft.some((value) => value) && draftUsageDisplay['incineration_facility_bottom_ash_carry_volume'] ? 'table-row' : 'none',
                  }}
                >
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td
                      className={
                        (isMonthDisplay() ? true : draftDisableCheck[0].day[lastDayOfLastMonth + colIndex]['incineration_facility_bottom_ash_carry_volume'])
                          ? 'table-carrycell'
                          : 'table-carrycell-green'
                      }
                      style={{
                        pointerEvents: (isMonthDisplay() ? true : draftDisableCheck[0].day[lastDayOfLastMonth + colIndex]['incineration_facility_bottom_ash_carry_volume'])
                          ? 'none'
                          : 'auto',
                      }}
                    >
                      <EmptyNumberInput
                        className="table-inputcell"
                        colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                        data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                        data-key={[DriveMap.incineration_facility_bottom_ash_carry_volume]}
                        formValueState={draftLength > 0 ? getArrayElementState(draftEditResource, setDraftEditResource, 0) : planEditResourceState}
                        emptyValueState={getArrayElementState(draftEmptyCheck, setDraftEmptyCheck, 0)}
                        onBlur={(event) => {
                          Array.from({ length: draftLength }).forEach((_, draftIndex) => {
                            handleCommonBlurChange(
                              event,
                              getArrayElementState(draftEditResource, setDraftEditResource, draftIndex),
                              getArrayElementState(draftEmptyCheck, setDraftEmptyCheck, draftIndex),
                              draftInitResource[draftIndex],
                              draftUsageCalc,
                              isMonthDisplay() ? DraftMap.month : DraftMap.day,
                              DriveMap.incineration_facility_bottom_ash_carry_volume,
                              selectMonthIndex,
                              isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex,
                              undefined,
                              updateCarryDependent,
                            );
                          });
                        }}
                        mathCalc={(value: number) => calcMathRound(value, CARRY_BLUR_DIGIT)}
                        significantDigits={DRIVE_INPUT_DIGITS['incineration_facility_bottom_ash_carry_volume']}
                      />
                    </td>
                  ))}
                </tr>
                {/* 資源物焼却処分搬入量[t] */}
                <tr
                  className="table-carryrow"
                  style={{
                    display: isSelectDraft.some((value) => value) && draftUsageDisplay['recyclables_carry_volume'] ? 'table-row' : 'none',
                  }}
                >
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td
                      className={
                        (isMonthDisplay() ? true : draftDisableCheck[0].day[lastDayOfLastMonth + colIndex]['recyclables_carry_volume'])
                          ? 'table-carrycell'
                          : 'table-carrycell-green'
                      }
                      style={{
                        pointerEvents: (isMonthDisplay() ? true : draftDisableCheck[0].day[lastDayOfLastMonth + colIndex]['recyclables_carry_volume']) ? 'none' : 'auto',
                      }}
                    >
                      <EmptyNumberInput
                        className="table-inputcell"
                        colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                        data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                        data-key={[DriveMap.recyclables_carry_volume]}
                        formValueState={draftLength > 0 ? getArrayElementState(draftEditResource, setDraftEditResource, 0) : planEditResourceState}
                        emptyValueState={getArrayElementState(draftEmptyCheck, setDraftEmptyCheck, 0)}
                        onBlur={(event) => {
                          Array.from({ length: draftLength }).forEach((_, draftIndex) => {
                            handleCommonBlurChange(
                              event,
                              getArrayElementState(draftEditResource, setDraftEditResource, draftIndex),
                              getArrayElementState(draftEmptyCheck, setDraftEmptyCheck, draftIndex),
                              draftInitResource[draftIndex],
                              draftUsageCalc,
                              isMonthDisplay() ? DraftMap.month : DraftMap.day,
                              DriveMap.recyclables_carry_volume,
                              selectMonthIndex,
                              isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex,
                              undefined,
                              updateCarryDependent,
                            );
                          });
                        }}
                        mathCalc={(value: number) => calcMathRound(value, CARRY_BLUR_DIGIT)}
                        significantDigits={DRIVE_INPUT_DIGITS['recyclables_carry_volume']}
                      />
                    </td>
                  ))}
                </tr>
                {/* 搬入量合計[t] */}
                <tr className="table-carryrow" style={{ display: isSelectDraft.some((value) => value) && draftUsageDisplay['total_carry_volume'] ? 'table-row' : 'none' }}>
                  {Array.from({
                    length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                  }).map((_, colIndex) => (
                    <td className="table-carrycell">
                      <div className="table-inputcell">
                        {draftLength > 0 &&
                        !CARRY_KEYS.every((key) => (isMonthDisplay() ? draftEmptyCheck[0].month[colIndex][key] : draftEmptyCheck[0].day[lastDayOfLastMonth + colIndex][key]))
                          ? addComma(calcMathRound(draftMonitorResource[0][isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex].total_carry_volume, CARRY_BLUR_DIGIT))
                          : ''}
                      </div>
                    </td>
                  ))}
                </tr>
                {/* 各案 */}
                {Array.from({ length: draftLength }).map((_, draftIndex) => (
                  <>
                    <tr style={{ display: isSelectDraft[draftIndex] ? 'table-row' : 'none' }}>
                      {Array.from({
                        length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                      }).map((_, colIndex) => (
                        <td className={'table-carrycell-' + draftIndex}>{'計画'}</td>
                      ))}
                    </tr>
                    {/* 各炉の状態 */}
                    {Array.from({ length: sessionResource.furnacesCount }).map((_, furnacesIndex) => (
                      <>
                        {/* 焼却量[t] */}
                        <tr className="table-carryrow" style={{ display: isSelectDraft[draftIndex] ? 'table-row' : 'none' }}>
                          {Array.from({
                            length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                          }).map((_, colIndex) => (
                            <td
                              className={
                                (isMonthDisplay() ? true : draftDisableCheck[draftIndex].day[lastDayOfLastMonth + colIndex]['burn_volume'][furnacesIndex])
                                  ? 'table-carrycell'
                                  : 'table-carrycell-green'
                              }
                              style={{
                                pointerEvents: (isMonthDisplay() ? true : draftDisableCheck[draftIndex].day[lastDayOfLastMonth + colIndex]['burn_volume'][furnacesIndex])
                                  ? 'none'
                                  : 'auto',
                                background: !isMonthDisplay() && draftWarningVals[draftIndex][furnacesIndex][lastDayOfLastMonth + colIndex] ? 'yellow' : undefined,
                              }}
                            >
                              <EmptyNumberInput
                                className={`table-inputcell${
                                  !isMonthDisplay() && draftEditResource[draftIndex].day[lastDayOfLastMonth + colIndex].burn_volume[furnacesIndex] > conditionResource.rateCapacity
                                    ? ' color-red'
                                    : ''
                                }`}
                                colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                                rowIndex={furnacesIndex}
                                data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                                data-key={[DriveMap.burn_volume]}
                                formValueState={draftLength > 0 ? getArrayElementState(draftEditResource, setDraftEditResource, draftIndex) : planEditResourceState}
                                emptyValueState={getArrayElementState(draftEmptyCheck, setDraftEmptyCheck, draftIndex)}
                                onBlur={(event, previousValue) => {
                                  handleCommonBlurChange(
                                    event,
                                    getArrayElementState(draftEditResource, setDraftEditResource, draftIndex),
                                    getArrayElementState(draftEmptyCheck, setDraftEmptyCheck, draftIndex),
                                    draftInitResource[draftIndex],
                                    draftUsageCalc,
                                    isMonthDisplay() ? DraftMap.month : DraftMap.day,
                                    DriveMap.burn_volume,
                                    selectMonthIndex,
                                    colIndex,
                                    furnacesIndex,
                                    updateBurnDependent,
                                    previousValue,
                                  );
                                  updateWarningVals(
                                    getArrayElementState(draftWarningVals, setDraftWarningVals, draftIndex),
                                    selectMonthIndex,
                                    isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex,
                                    furnacesIndex,
                                  );
                                }}
                                mathCalc={(value: number) => calcMathRound(value, BURNVOLUME_BLUR_DIGIT)}
                                validateChange={validBurnChange}
                                significantDigits={DRIVE_INPUT_DIGITS['burn_volume']}
                              />
                            </td>
                          ))}
                        </tr>
                        {/* 焼却負荷率(停炉日を含まない)[%] */}
                        <tr className="table-carryrow" style={{ display: draftUsageDisplay['burn_load'] && isSelectDraft[draftIndex] && isMonthDisplay() ? 'table-row' : 'none' }}>
                          {Array.from({
                            length: displayDays.length,
                          }).map((_, colIndex) => (
                            <td className="table-carrycell">
                              {!draftEmptyCheck[draftIndex].month[colIndex].burn_volume[furnacesIndex]
                                ? addComma(calcMathFloor(draftMonitorMonthResource[draftIndex][colIndex].burn_load[furnacesIndex], BURNLOAD_DIGIT))
                                : ''}
                            </td>
                          ))}
                        </tr>
                        {/* 焼却負荷率(停炉日を含む)[%] */}
                        <tr
                          className="table-carryrow"
                          style={{ display: draftUsageDisplay['burn_load_include_shutdown'] && isSelectDraft[draftIndex] && isMonthDisplay() ? 'table-row' : 'none' }}
                        >
                          {Array.from({
                            length: displayDays.length,
                          }).map((_, colIndex) => (
                            <td className="table-carrycell">
                              {!draftEmptyCheck[draftIndex].month[colIndex].burn_volume[furnacesIndex]
                                ? addComma(calcMathFloor(draftMonitorMonthResource[draftIndex][colIndex].burn_load_include_shutdown[furnacesIndex], BURNLOAD_DIGIT))
                                : ''}
                            </td>
                          ))}
                        </tr>
                        {/* 運転日数[日] */}
                        <tr className="table-carryrow" style={{ display: isSelectDraft[draftIndex] && isMonthDisplay() ? 'table-row' : 'none' }}>
                          {Array.from({
                            length: isMonthDisplay() ? displayDays.length : 0,
                          }).map((_, colIndex) => (
                            <td className="table-carrycell">
                              {!draftEmptyCheck[draftIndex].month[colIndex].burn_volume[furnacesIndex]
                                ? draftMonitorMonthResource[draftIndex][colIndex].operating_days[furnacesIndex]
                                : ''}
                            </td>
                          ))}
                        </tr>
                        {/* ごみ低位発熱量 */}
                        <tr className="table-carryrow" style={{ display: isSelectDraft[draftIndex] && draftUsageDisplay['waste_lower_heating_value'] ? 'table-row' : 'none' }}>
                          {Array.from({
                            length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                          }).map((_, colIndex) => (
                            <td
                              className={
                                (isMonthDisplay() ? true : draftDisableCheck[draftIndex].day[lastDayOfLastMonth + colIndex]['waste_lower_heating_value'][furnacesIndex])
                                  ? 'table-carrycell'
                                  : 'table-carrycell-green'
                              }
                              style={{
                                pointerEvents: (
                                  isMonthDisplay() ? true : draftDisableCheck[draftIndex].day[lastDayOfLastMonth + colIndex]['waste_lower_heating_value'][furnacesIndex]
                                )
                                  ? 'none'
                                  : 'auto',
                              }}
                            >
                              <EmptyNumberInput
                                className="table-inputcell"
                                colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                                rowIndex={furnacesIndex}
                                data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                                data-key={[DriveMap.waste_lower_heating_value]}
                                formValueState={draftLength > 0 ? getArrayElementState(draftEditResource, setDraftEditResource, draftIndex) : planEditResourceState}
                                emptyValueState={getArrayElementState(draftEmptyCheck, setDraftEmptyCheck, draftIndex)}
                                onBlur={(event) => {
                                  Array.from({ length: draftLength }).forEach((_, draftIndex) => {
                                    handleCommonBlurChange(
                                      event,
                                      getArrayElementState(draftEditResource, setDraftEditResource, draftIndex),
                                      getArrayElementState(draftEmptyCheck, setDraftEmptyCheck, draftIndex),
                                      draftInitResource[draftIndex],
                                      draftUsageCalc,
                                      isMonthDisplay() ? DraftMap.month : DraftMap.day,
                                      DriveMap.waste_lower_heating_value,
                                      selectMonthIndex,
                                      isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex,
                                      furnacesIndex,
                                    );
                                  });
                                }}
                                mathCalc={(value: number) => calcMathRound(value, WASTE_BLUR_DIGIT)}
                                significantDigits={DRIVE_INPUT_DIGITS['waste_lower_heating_value']}
                              />
                            </td>
                          ))}
                        </tr>
                      </>
                    ))}
                    {/* 全炉 */}
                    {/* 焼却量[t] */}
                    <tr className="table-carryrow" style={{ display: isSelectDraft[draftIndex] ? 'table-row' : 'none' }}>
                      {Array.from({
                        length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                      }).map((_, colIndex) => (
                        <td className="table-carrycell">
                          {isMonthDisplay()
                            ? !draftEmptyCheck[draftIndex].month[colIndex].burn_volume.some((value) => value === true)
                              ? addComma(
                                  calcMathRound(
                                    Array.from(
                                      { length: sessionResource.furnacesCount },
                                      (_, furnacesIndex) =>
                                        burnDraftVolumes(draftEditResource[draftIndex].month, draftEditResource[draftIndex].day, 'burn_volume', furnacesIndex)[colIndex],
                                    ).reduce((sum, value) => sum + value, 0),
                                    BURNVOLUME_BLUR_DIGIT,
                                  ),
                                )
                              : ''
                            : !draftEmptyCheck[draftIndex].day[lastDayOfLastMonth + colIndex].burn_volume.some((value) => value === true)
                              ? addComma(
                                  calcMathRound(
                                    Array.from(
                                      { length: sessionResource.furnacesCount },
                                      (_, furnacesIndex) =>
                                        burnDraftVolumes(draftEditResource[draftIndex].month, draftEditResource[draftIndex].day, 'burn_volume', furnacesIndex)[colIndex],
                                    ).reduce((sum, value) => sum + value, 0),
                                    BURNVOLUME_BLUR_DIGIT,
                                  ),
                                )
                              : ''}
                        </td>
                      ))}
                    </tr>
                    {/* 焼却負荷率(停炉日を含まない)[%] */}
                    <tr className="table-carryrow" style={{ display: draftUsageDisplay['burn_load'] && isSelectDraft[draftIndex] && isMonthDisplay() ? 'table-row' : 'none' }}>
                      {Array.from({
                        length: displayDays.length,
                      }).map((_, colIndex) => (
                        <td className="table-carrycell">
                          {!draftEmptyCheck[draftIndex].month[colIndex].burn_volume.some((value) => value === true)
                            ? addComma(calcMathFloor(draftMonitorMonthResource[draftIndex][colIndex].burn_load_sum, BURNLOAD_DIGIT))
                            : ''}
                        </td>
                      ))}
                    </tr>
                    {/* 焼却負荷率(停炉日を含む)[%] */}
                    <tr
                      className="table-carryrow"
                      style={{ display: draftUsageDisplay['burn_load_include_shutdown'] && isSelectDraft[draftIndex] && isMonthDisplay() ? 'table-row' : 'none' }}
                    >
                      {Array.from({
                        length: displayDays.length,
                      }).map((_, colIndex) => (
                        <td className="table-carrycell">
                          {!draftEmptyCheck[draftIndex].month[colIndex].burn_volume.some((value) => value === true)
                            ? addComma(calcMathFloor(draftMonitorMonthResource[draftIndex][colIndex].burn_load_include_shutdown_sum, BURNLOAD_DIGIT))
                            : ''}
                        </td>
                      ))}
                    </tr>
                    {/* ピット残量[t] */}
                    <tr className="table-carryrow" style={{ display: isSelectDraft[draftIndex] && !isMonthDisplay() ? 'table-row' : 'none' }}>
                      {' '}
                      {Array.from({ length: displayDayCountOfMonth }).map((_, colIndex) =>
                        existDayFull(draftEditResource[draftIndex].day[lastDayOfLastMonth + colIndex]) ? (
                          !draftEmptyCheck[draftIndex].day[lastDayOfLastMonth + colIndex].trash_pit_rest === true ? (
                            <td
                              className={
                                (isMonthDisplay() ? true : draftDisableCheck[draftIndex].day[lastDayOfLastMonth + colIndex]['trash_pit_rest'])
                                  ? 'table-carrycell'
                                  : 'table-carrycell-green'
                              }
                              style={{
                                pointerEvents: (isMonthDisplay() ? true : draftDisableCheck[draftIndex].day[lastDayOfLastMonth + colIndex]['trash_pit_rest']) ? 'none' : 'auto',
                              }}
                            >
                              {' '}
                              <EmptyNumberInput
                                className="table-inputcell"
                                colIndex={lastDayOfLastMonth + colIndex}
                                data-sign={[DraftMap.day]}
                                data-key={[DriveMap.trash_pit_rest]}
                                formValueState={draftLength > 0 ? getArrayElementState(draftEditResource, setDraftEditResource, draftIndex) : planEditResourceState}
                                emptyValueState={getArrayElementState(draftEmptyCheck, setDraftEmptyCheck, draftIndex)}
                                onBlur={(event) =>
                                  handleCommonBlurChange(
                                    event,
                                    getArrayElementState(draftEditResource, setDraftEditResource, draftIndex),
                                    getArrayElementState(draftEmptyCheck, setDraftEmptyCheck, draftIndex),
                                    draftInitResource[draftIndex],
                                    draftUsageCalc,
                                    isMonthDisplay() ? DraftMap.month : DraftMap.day,
                                    DriveMap.trash_pit_rest,
                                    selectMonthIndex,
                                    colIndex,
                                    undefined,
                                    updatePitRest,
                                  )
                                }
                                mathCalc={(value: number) => calcMathRound(value, TRASH_PIT_BLUR_DIGIT)}
                                significantDigits={DRIVE_INPUT_DIGITS['trash_pit_rest']}
                              />{' '}
                            </td>
                          ) : (
                            <td className="table-carrycell" />
                          )
                        ) : (
                          <td className="table-carrycell">
                            {' '}
                            {isDayFull(draftEmptyCheck[draftIndex].day[lastDayOfLastMonth + colIndex], draftDayUsage)
                              ? addComma(calcMathRound(draftEditResource[draftIndex].day[lastDayOfLastMonth + colIndex].main_ash_pit_rest, TRASH_PIT_BLUR_DIGIT))
                              : ''}{' '}
                          </td>
                        ),
                      )}{' '}
                    </tr>
                    {/* 主灰発生量[t] */}
                    <tr
                      className="table-carryrow"
                      style={{ display: isSelectDraft[draftIndex] && draftUsageDisplay['main_ash_generate'] && showAshVisible ? 'table-row' : 'none' }}
                    >
                      {Array.from({
                        length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                      }).map((_, colIndex) => (
                        <td
                          className={
                            (isMonthDisplay() ? true : draftDisableCheck[draftIndex].day[lastDayOfLastMonth + colIndex]['main_ash_generate'])
                              ? 'table-carrycell'
                              : 'table-carrycell-green'
                          }
                          style={{
                            pointerEvents: (isMonthDisplay() ? true : draftDisableCheck[draftIndex].day[lastDayOfLastMonth + colIndex]['main_ash_generate']) ? 'none' : 'auto',
                          }}
                        >
                          <EmptyNumberInput
                            className="table-inputcell"
                            colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                            data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                            data-key={[DriveMap.main_ash_generate]}
                            formValueState={getArrayElementState(draftEditResource, setDraftEditResource, draftIndex)}
                            emptyValueState={getArrayElementState(draftEmptyCheck, setDraftEmptyCheck, draftIndex)}
                            onBlur={(event) => {
                              Array.from({ length: draftLength }).forEach((_, draftIndex) => {
                                handleCommonBlurChange(
                                  event,
                                  getArrayElementState(draftEditResource, setDraftEditResource, draftIndex),
                                  getArrayElementState(draftEmptyCheck, setDraftEmptyCheck, draftIndex),
                                  draftInitResource[draftIndex],
                                  draftUsageCalc,
                                  isMonthDisplay() ? DraftMap.month : DraftMap.day,
                                  DriveMap.main_ash_generate,
                                  selectMonthIndex,
                                  colIndex,
                                  undefined,
                                );
                              });
                            }}
                            mathCalc={(value: number) => calcMathRound(value, ASH_BLUR_DIGIT)}
                            significantDigits={DRIVE_INPUT_DIGITS['main_ash_generate']}
                          />
                        </td>
                      ))}
                    </tr>
                    {/* 主灰搬出台数[台] */}
                    <tr
                      className="table-carryrow"
                      style={{ display: isSelectDraft[draftIndex] && draftUsageDisplay['main_ash_carry_out_units'] && showAshVisible ? 'table-row' : 'none' }}
                    >
                      {Array.from({
                        length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                      }).map((_, colIndex) => (
                        <td
                          className={
                            (isMonthDisplay() ? true : draftDisableCheck[draftIndex].day[lastDayOfLastMonth + colIndex]['main_ash_carry_out_units'])
                              ? 'table-carrycell'
                              : 'table-carrycell-green'
                          }
                          style={{
                            pointerEvents: (isMonthDisplay() ? true : draftDisableCheck[draftIndex].day[lastDayOfLastMonth + colIndex]['main_ash_carry_out_units'])
                              ? 'none'
                              : 'auto',
                          }}
                        >
                          <EmptyNumberInput
                            className="table-inputcell"
                            colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                            data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                            data-key={[DriveMap.main_ash_carry_out_units]}
                            formValueState={draftLength > 0 ? getArrayElementState(draftEditResource, setDraftEditResource, draftIndex) : planEditResourceState}
                            emptyValueState={getArrayElementState(draftEmptyCheck, setDraftEmptyCheck, draftIndex)}
                            onBlur={(event) => {
                              Array.from({ length: draftLength }).forEach((_, draftIndex) => {
                                handleCommonBlurChange(
                                  event,
                                  getArrayElementState(draftEditResource, setDraftEditResource, draftIndex),
                                  getArrayElementState(draftEmptyCheck, setDraftEmptyCheck, draftIndex),
                                  draftInitResource[draftIndex],
                                  draftUsageCalc,
                                  isMonthDisplay() ? DraftMap.month : DraftMap.day,
                                  DriveMap.main_ash_carry_out_units,
                                  selectMonthIndex,
                                  colIndex,
                                  undefined,
                                );
                              });
                            }}
                            mathCalc={(value: number) => calcMathRound(value, ASH_BLUR_DIGIT)}
                            significantDigits={DRIVE_INPUT_DIGITS['main_ash_carry_out_units']}
                          />
                        </td>
                      ))}
                    </tr>
                    {/* 主灰搬出量[t] */}
                    <tr
                      className="table-carryrow"
                      style={{ display: isSelectDraft[draftIndex] && draftUsageDisplay['main_ash_carry_out'] && showAshVisible ? 'table-row' : 'none' }}
                    >
                      {Array.from({
                        length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                      }).map((_, colIndex) => (
                        <td
                          className={
                            (isMonthDisplay() ? true : draftDisableCheck[draftIndex].day[lastDayOfLastMonth + colIndex]['main_ash_carry_out'])
                              ? 'table-carrycell'
                              : 'table-carrycell-green'
                          }
                          style={{
                            pointerEvents: (isMonthDisplay() ? true : draftDisableCheck[draftIndex].day[lastDayOfLastMonth + colIndex]['main_ash_carry_out']) ? 'none' : 'auto',
                          }}
                        >
                          <EmptyNumberInput
                            className="table-inputcell"
                            colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                            data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                            data-key={[DriveMap.main_ash_carry_out]}
                            formValueState={draftLength > 0 ? getArrayElementState(draftEditResource, setDraftEditResource, draftIndex) : planEditResourceState}
                            emptyValueState={getArrayElementState(draftEmptyCheck, setDraftEmptyCheck, draftIndex)}
                            onBlur={(event) => {
                              Array.from({ length: draftLength }).forEach((_, draftIndex) => {
                                handleCommonBlurChange(
                                  event,
                                  getArrayElementState(draftEditResource, setDraftEditResource, draftIndex),
                                  getArrayElementState(draftEmptyCheck, setDraftEmptyCheck, draftIndex),
                                  draftInitResource[draftIndex],
                                  draftUsageCalc,
                                  isMonthDisplay() ? DraftMap.month : DraftMap.day,
                                  DriveMap.main_ash_carry_out,
                                  selectMonthIndex,
                                  colIndex,
                                  undefined,
                                  updateMainAshCarryOut,
                                );
                              });
                            }}
                            mathCalc={(value: number) => calcMathRound(value, ASH_BLUR_DIGIT)}
                            significantDigits={DRIVE_INPUT_DIGITS['main_ash_carry_out']}
                          />
                        </td>
                      ))}
                    </tr>
                    {/* 主灰ピット残量[t] */}
                    <tr className="table-carryrow" style={{ display: isSelectDraft[draftIndex] && showAshVisible && !isMonthDisplay() ? 'table-row' : 'none' }}>
                      {' '}
                      {Array.from({ length: displayDayCountOfMonth }).map((_, colIndex) =>
                        existDayFull(draftEditResource[draftIndex].day[lastDayOfLastMonth + colIndex]) ? (
                          !draftEmptyCheck[draftIndex].day[lastDayOfLastMonth + colIndex].main_ash_generate === true ? (
                            <td
                              className={
                                (isMonthDisplay() ? true : draftDisableCheck[draftIndex].day[lastDayOfLastMonth + colIndex]['main_ash_pit_rest'])
                                  ? 'table-carrycell'
                                  : 'table-carrycell-green'
                              }
                              style={{
                                pointerEvents: (isMonthDisplay() ? true : draftDisableCheck[draftIndex].day[lastDayOfLastMonth + colIndex]['main_ash_pit_rest']) ? 'none' : 'auto',
                              }}
                            >
                              <EmptyNumberInput
                                className="table-inputcell"
                                colIndex={lastDayOfLastMonth + colIndex}
                                data-sign={[DraftMap.day]}
                                data-key={[DriveMap.main_ash_pit_rest]}
                                formValueState={draftLength > 0 ? getArrayElementState(draftEditResource, setDraftEditResource, draftIndex) : planEditResourceState}
                                emptyValueState={getArrayElementState(draftEmptyCheck, setDraftEmptyCheck, draftIndex)}
                                onBlur={(event) => {
                                  Array.from({ length: draftLength }).forEach((_, draftIndex) => {
                                    handleCommonBlurChange(
                                      event,
                                      getArrayElementState(draftEditResource, setDraftEditResource, draftIndex),
                                      getArrayElementState(draftEmptyCheck, setDraftEmptyCheck, draftIndex),
                                      draftInitResource[draftIndex],
                                      draftUsageCalc,
                                      isMonthDisplay() ? DraftMap.month : DraftMap.day,
                                      DriveMap.main_ash_pit_rest,
                                      selectMonthIndex,
                                      colIndex,
                                      undefined,
                                      updateMainAshRest,
                                    );
                                  });
                                }}
                                mathCalc={(value: number) => calcMathRound(value, ASH_BLUR_DIGIT)}
                                significantDigits={DRIVE_INPUT_DIGITS['main_ash_pit_rest']}
                              />
                            </td>
                          ) : (
                            <td className="table-carrycell" />
                          )
                        ) : (
                          <td className="table-carrycell">
                            {' '}
                            {!draftEmptyCheck[draftIndex].day[lastDayOfLastMonth + colIndex].burn_volume.some((value) => value === true)
                              ? addComma(calcMathRound(draftEditResource[draftIndex].day[lastDayOfLastMonth + colIndex].main_ash_pit_rest, TRASH_PIT_BLUR_DIGIT))
                              : ''}
                          </td>
                        ),
                      )}{' '}
                    </tr>
                    {/* 飛灰発生量 */}
                    <tr className="table-carryrow" style={{ display: isSelectDraft[draftIndex] && draftUsageDisplay['fly_ash_generate'] && showAshVisible ? 'table-row' : 'none' }}>
                      {Array.from({
                        length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                      }).map((_, colIndex) => (
                        <td
                          className={
                            (isMonthDisplay() ? true : draftDisableCheck[draftIndex].day[lastDayOfLastMonth + colIndex]['fly_ash_generate'])
                              ? 'table-carrycell'
                              : 'table-carrycell-green'
                          }
                          style={{
                            pointerEvents: (isMonthDisplay() ? true : draftDisableCheck[draftIndex].day[lastDayOfLastMonth + colIndex]['fly_ash_generate']) ? 'none' : 'auto',
                          }}
                        >
                          <EmptyNumberInput
                            className="table-inputcell"
                            colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                            data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                            data-key={[DriveMap.fly_ash_generate]}
                            formValueState={draftLength > 0 ? getArrayElementState(draftEditResource, setDraftEditResource, draftIndex) : planEditResourceState}
                            emptyValueState={getArrayElementState(draftEmptyCheck, setDraftEmptyCheck, draftIndex)}
                            onBlur={(event) => {
                              Array.from({ length: draftLength }).forEach((_, draftIndex) => {
                                handleCommonBlurChange(
                                  event,
                                  getArrayElementState(draftEditResource, setDraftEditResource, draftIndex),
                                  getArrayElementState(draftEmptyCheck, setDraftEmptyCheck, draftIndex),
                                  draftInitResource[draftIndex],
                                  draftUsageCalc,
                                  isMonthDisplay() ? DraftMap.month : DraftMap.day,
                                  DriveMap.fly_ash_generate,
                                  selectMonthIndex,
                                  colIndex,
                                  undefined,
                                );
                              });
                            }}
                            mathCalc={(value: number) => calcMathRound(value, ASH_BLUR_DIGIT)}
                            significantDigits={DRIVE_INPUT_DIGITS['fly_ash_generate']}
                          />
                        </td>
                      ))}
                    </tr>
                    {/* 飛灰搬出台数[台] */}
                    <tr
                      className="table-carryrow"
                      style={{ display: isSelectDraft[draftIndex] && draftUsageDisplay['fly_ash_carry_out_units'] && showAshVisible ? 'table-row' : 'none' }}
                    >
                      {Array.from({
                        length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                      }).map((_, colIndex) => (
                        <td
                          className={
                            (isMonthDisplay() ? true : draftDisableCheck[draftIndex].day[lastDayOfLastMonth + colIndex]['fly_ash_carry_out_units'])
                              ? 'table-carrycell'
                              : 'table-carrycell-green'
                          }
                          style={{
                            pointerEvents: (isMonthDisplay() ? true : draftDisableCheck[draftIndex].day[lastDayOfLastMonth + colIndex]['fly_ash_carry_out_units'])
                              ? 'none'
                              : 'auto',
                          }}
                        >
                          <EmptyNumberInput
                            className="table-inputcell"
                            colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                            data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                            data-key={[DriveMap.fly_ash_carry_out_units]}
                            formValueState={draftLength > 0 ? getArrayElementState(draftEditResource, setDraftEditResource, draftIndex) : planEditResourceState}
                            emptyValueState={getArrayElementState(draftEmptyCheck, setDraftEmptyCheck, draftIndex)}
                            onBlur={(event) => {
                              Array.from({ length: draftLength }).forEach((_, draftIndex) => {
                                handleCommonBlurChange(
                                  event,
                                  getArrayElementState(draftEditResource, setDraftEditResource, draftIndex),
                                  getArrayElementState(draftEmptyCheck, setDraftEmptyCheck, draftIndex),
                                  draftInitResource[draftIndex],
                                  draftUsageCalc,
                                  isMonthDisplay() ? DraftMap.month : DraftMap.day,
                                  DriveMap.fly_ash_carry_out_units,
                                  selectMonthIndex,
                                  colIndex,
                                  undefined,
                                );
                              });
                            }}
                            mathCalc={(value: number) => calcMathRound(value, ASH_BLUR_DIGIT)}
                            significantDigits={DRIVE_INPUT_DIGITS['fly_ash_carry_out_units']}
                          />
                        </td>
                      ))}
                    </tr>
                    {/* 飛灰搬出量 */}
                    <tr
                      className="table-carryrow"
                      style={{ display: isSelectDraft[draftIndex] && draftUsageDisplay['fly_ash_carry_out'] && showAshVisible ? 'table-row' : 'none' }}
                    >
                      {Array.from({
                        length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                      }).map((_, colIndex) => (
                        <td
                          className={
                            (isMonthDisplay() ? true : draftDisableCheck[draftIndex].day[lastDayOfLastMonth + colIndex]['fly_ash_carry_out'])
                              ? 'table-carrycell'
                              : 'table-carrycell-green'
                          }
                          style={{
                            pointerEvents: (isMonthDisplay() ? true : draftDisableCheck[draftIndex].day[lastDayOfLastMonth + colIndex]['fly_ash_carry_out']) ? 'none' : 'auto',
                          }}
                        >
                          <EmptyNumberInput
                            className="table-inputcell"
                            colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                            data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                            data-key={[DriveMap.fly_ash_carry_out]}
                            formValueState={draftLength > 0 ? getArrayElementState(draftEditResource, setDraftEditResource, draftIndex) : planEditResourceState}
                            emptyValueState={getArrayElementState(draftEmptyCheck, setDraftEmptyCheck, draftIndex)}
                            onBlur={(event) => {
                              Array.from({ length: draftLength }).forEach((_, draftIndex) => {
                                handleCommonBlurChange(
                                  event,
                                  getArrayElementState(draftEditResource, setDraftEditResource, draftIndex),
                                  getArrayElementState(draftEmptyCheck, setDraftEmptyCheck, draftIndex),
                                  draftInitResource[draftIndex],
                                  draftUsageCalc,
                                  isMonthDisplay() ? DraftMap.month : DraftMap.day,
                                  DriveMap.fly_ash_carry_out,
                                  selectMonthIndex,
                                  colIndex,
                                  undefined,
                                  updateFlyAshCarryOut,
                                );
                              });
                            }}
                            mathCalc={(value: number) => calcMathRound(value, ASH_BLUR_DIGIT)}
                            significantDigits={DRIVE_INPUT_DIGITS['fly_ash_carry_out']}
                          />
                        </td>
                      ))}
                    </tr>
                    {/* 飛灰処理物搬出量 乾灰[t] */}
                    <tr
                      className="table-carryrow"
                      style={{ display: isSelectDraft[draftIndex] && draftUsageDisplay['dry_fly_ash_carry_out'] && showAshVisible ? 'table-row' : 'none' }}
                    >
                      {Array.from({
                        length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                      }).map((_, colIndex) => (
                        <td
                          className={
                            (isMonthDisplay() ? true : draftDisableCheck[draftIndex].day[lastDayOfLastMonth + colIndex]['dry_fly_ash_carry_out'])
                              ? 'table-carrycell'
                              : 'table-carrycell-green'
                          }
                          style={{
                            pointerEvents: (isMonthDisplay() ? true : draftDisableCheck[draftIndex].day[lastDayOfLastMonth + colIndex]['dry_fly_ash_carry_out']) ? 'none' : 'auto',
                          }}
                        >
                          <EmptyNumberInput
                            className="table-inputcell"
                            colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                            data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                            data-key={[DriveMap.dry_fly_ash_carry_out]}
                            formValueState={draftLength > 0 ? getArrayElementState(draftEditResource, setDraftEditResource, draftIndex) : planEditResourceState}
                            emptyValueState={getArrayElementState(draftEmptyCheck, setDraftEmptyCheck, draftIndex)}
                            onBlur={(event) => {
                              Array.from({ length: draftLength }).forEach((_, draftIndex) => {
                                handleCommonBlurChange(
                                  event,
                                  getArrayElementState(draftEditResource, setDraftEditResource, draftIndex),
                                  getArrayElementState(draftEmptyCheck, setDraftEmptyCheck, draftIndex),
                                  draftInitResource[draftIndex],
                                  draftUsageCalc,
                                  isMonthDisplay() ? DraftMap.month : DraftMap.day,
                                  DriveMap.dry_fly_ash_carry_out,
                                  selectMonthIndex,
                                  colIndex,
                                  undefined,
                                  updateFlyAshCarryOut,
                                );
                              });
                            }}
                            mathCalc={(value: number) => calcMathRound(value, ASH_BLUR_DIGIT)}
                            significantDigits={DRIVE_INPUT_DIGITS['dry_fly_ash_carry_out']}
                          />
                        </td>
                      ))}
                    </tr>
                    {/* 飛灰処理物搬出量 処理灰[t] */}
                    <tr
                      className="table-carryrow"
                      style={{ display: isSelectDraft[draftIndex] && draftUsageDisplay['processed_fly_ash_carry_out'] && showAshVisible ? 'table-row' : 'none' }}
                    >
                      {Array.from({
                        length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                      }).map((_, colIndex) => (
                        <td
                          className={
                            (isMonthDisplay() ? true : draftDisableCheck[draftIndex].day[lastDayOfLastMonth + colIndex]['processed_fly_ash_carry_out'])
                              ? 'table-carrycell'
                              : 'table-carrycell-green'
                          }
                          style={{
                            pointerEvents: (isMonthDisplay() ? true : draftDisableCheck[draftIndex].day[lastDayOfLastMonth + colIndex]['processed_fly_ash_carry_out'])
                              ? 'none'
                              : 'auto',
                          }}
                        >
                          <EmptyNumberInput
                            className="table-inputcell"
                            colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                            data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                            data-key={[DriveMap.processed_fly_ash_carry_out]}
                            formValueState={draftLength > 0 ? getArrayElementState(draftEditResource, setDraftEditResource, draftIndex) : planEditResourceState}
                            emptyValueState={getArrayElementState(draftEmptyCheck, setDraftEmptyCheck, draftIndex)}
                            onBlur={(event) => {
                              Array.from({ length: draftLength }).forEach((_, draftIndex) => {
                                handleCommonBlurChange(
                                  event,
                                  getArrayElementState(draftEditResource, setDraftEditResource, draftIndex),
                                  getArrayElementState(draftEmptyCheck, setDraftEmptyCheck, draftIndex),
                                  draftInitResource[draftIndex],
                                  draftUsageCalc,
                                  isMonthDisplay() ? DraftMap.month : DraftMap.day,
                                  DriveMap.processed_fly_ash_carry_out,
                                  selectMonthIndex,
                                  colIndex,
                                  undefined,
                                  updateFlyAshCarryOut,
                                );
                              });
                            }}
                            mathCalc={(value: number) => calcMathRound(value, ASH_BLUR_DIGIT)}
                            significantDigits={DRIVE_INPUT_DIGITS['processed_fly_ash_carry_out']}
                          />
                        </td>
                      ))}
                    </tr>
                    {/* 飛灰ピット残量[t] */}
                    <tr className="table-carryrow" style={{ display: isSelectDraft[draftIndex] && showAshVisible && !isMonthDisplay() ? 'table-row' : 'none' }}>
                      {' '}
                      {Array.from({ length: displayDayCountOfMonth }).map((_, colIndex) =>
                        existDayFull(draftEditResource[draftIndex].day[lastDayOfLastMonth + colIndex]) ? (
                          !draftEmptyCheck[draftIndex].day[lastDayOfLastMonth + colIndex].main_ash_generate === true ? (
                            <td
                              className={
                                (isMonthDisplay() ? true : draftDisableCheck[draftIndex].day[lastDayOfLastMonth + colIndex]['fly_ash_pit_rest'])
                                  ? 'table-carrycell'
                                  : 'table-carrycell-green'
                              }
                              style={{
                                pointerEvents: (isMonthDisplay() ? true : draftDisableCheck[draftIndex].day[lastDayOfLastMonth + colIndex]['fly_ash_pit_rest']) ? 'none' : 'auto',
                              }}
                            >
                              <EmptyNumberInput
                                className="table-inputcell"
                                colIndex={lastDayOfLastMonth + colIndex}
                                data-sign={[DraftMap.day]}
                                data-key={[DriveMap.fly_ash_pit_rest]}
                                formValueState={draftLength > 0 ? getArrayElementState(draftEditResource, setDraftEditResource, draftIndex) : planEditResourceState}
                                emptyValueState={getArrayElementState(draftEmptyCheck, setDraftEmptyCheck, draftIndex)}
                                onBlur={(event) => {
                                  Array.from({ length: draftLength }).forEach((_, draftIndex) => {
                                    handleCommonBlurChange(
                                      event,
                                      getArrayElementState(draftEditResource, setDraftEditResource, draftIndex),
                                      getArrayElementState(draftEmptyCheck, setDraftEmptyCheck, draftIndex),
                                      draftInitResource[draftIndex],
                                      draftUsageCalc,
                                      isMonthDisplay() ? DraftMap.month : DraftMap.day,
                                      DriveMap.fly_ash_pit_rest,
                                      selectMonthIndex,
                                      colIndex,
                                      undefined,
                                      updateFlyAshRest,
                                    );
                                  });
                                }}
                                mathCalc={(value: number) => calcMathRound(value, ASH_BLUR_DIGIT)}
                                significantDigits={DRIVE_INPUT_DIGITS['fly_ash_pit_rest']}
                              />
                            </td>
                          ) : (
                            <td className="table-carrycell" />
                          )
                        ) : (
                          <td className="table-carrycell">
                            {' '}
                            {!draftEmptyCheck[draftIndex].day[lastDayOfLastMonth + colIndex].burn_volume.some((value) => value === true)
                              ? addComma(calcMathRound(draftEditResource[draftIndex].day[lastDayOfLastMonth + colIndex].fly_ash_pit_rest, TRASH_PIT_BLUR_DIGIT))
                              : ''}
                          </td>
                        ),
                      )}{' '}
                    </tr>
                    {/* 温水供給設備供給熱量 */}
                    <tr className="table-carryrow" style={{ display: isSelectDraft[draftIndex] && draftUsageDisplay['hot_water_supply_heating_value'] ? 'table-row' : 'none' }}>
                      {Array.from({
                        length: isMonthDisplay() ? displayDays.length : displayDayCountOfMonth,
                      }).map((_, colIndex) => (
                        <td
                          className={
                            (isMonthDisplay() ? true : draftDisableCheck[draftIndex].day[lastDayOfLastMonth + colIndex]['hot_water_supply_heating_value'])
                              ? 'table-carrycell'
                              : 'table-carrycell-green'
                          }
                          style={{
                            pointerEvents: (isMonthDisplay() ? true : draftDisableCheck[draftIndex].day[lastDayOfLastMonth + colIndex]['hot_water_supply_heating_value'])
                              ? 'none'
                              : 'auto',
                          }}
                        >
                          <EmptyNumberInput
                            className="table-inputcell"
                            colIndex={isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex}
                            data-sign={isMonthDisplay() ? [DraftMap.month] : [DraftMap.day]}
                            data-key={[DriveMap.hot_water_supply_heating_value]}
                            formValueState={draftLength > 0 ? getArrayElementState(draftEditResource, setDraftEditResource, draftIndex) : planEditResourceState}
                            emptyValueState={getArrayElementState(draftEmptyCheck, setDraftEmptyCheck, draftIndex)}
                            onBlur={(event) => {
                              Array.from({ length: draftLength }).forEach((_, draftIndex) => {
                                handleCommonBlurChange(
                                  event,
                                  getArrayElementState(draftEditResource, setDraftEditResource, draftIndex),
                                  getArrayElementState(draftEmptyCheck, setDraftEmptyCheck, draftIndex),
                                  draftInitResource[draftIndex],
                                  draftUsageCalc,
                                  isMonthDisplay() ? DraftMap.month : DraftMap.day,
                                  DriveMap.hot_water_supply_heating_value,
                                  selectMonthIndex,
                                  isMonthDisplay() ? colIndex : lastDayOfLastMonth + colIndex,
                                );
                              });
                            }}
                            mathCalc={(value: number) => calcMathRound(value, HOT_WATER_BLUR_DIGIT)}
                            significantDigits={DRIVE_INPUT_DIGITS['hot_water_supply_heating_value']}
                          />
                        </td>
                      ))}
                    </tr>
                    {/* 炉別運転日数 */}
                    {Array.from({ length: sessionResource.furnacesCount + 1 }).map((_, furnacesIndex) => (
                      <tr className="table-carryrow" style={{ display: isSelectDraft[draftIndex] && isMonthDisplay() && showOperationDaysVisible ? 'table-row' : 'none' }}>
                        {Array.from({
                          length: displayDays.length,
                        }).map((_, colIndex) => (
                          <td className="table-carrycell">
                            {!draftEmptyCheck[draftIndex].month[colIndex].burn_volume.some((value) => value === true)
                              ? draftMonitorMonthResource[draftIndex][colIndex].furnace_operating_days[furnacesIndex]
                              : ''}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </>
                ))}
              </tbody>
            </table>
            {/* ##### 合計情報 #### */}
            <table style={{ borderCollapse: 'collapse', minWidth: '70px', width: '70px' }}>
              <thead className="table-sticky-header">
                <tr className="table-carryrow" style={{ textAlign: 'center' }}>
                  <td className="table-carrydoublecell-14-blue m-auto">合計値</td>
                </tr>
              </thead>
              {/* ##### 合計情報（期初計画） テーブル表示管理 #### */}
              <tbody style={{ display: showInitialPlanVisible ? 'table-row-group' : 'none' }}>
                <tr className="table-carryrow">
                  <td className="table-carrydoublecell-14 p-0">
                    <div
                      style={{
                        height: '100%',
                        width: '100%',
                        backgroundImage: 'linear-gradient(35deg, var(--color-initial-plan) 49%, black 50%, var(--color-initial-plan) 51%)',
                      }}
                    ></div>
                  </td>
                </tr>
                <>
                  {/* 搬入量[t] */}
                  <tr className="table-carryrow" style={{ display: initialPlanUsageDisplay['carry_volume'] ? 'table-row' : 'none' }}>
                    <td className="table-carrydoublecell-14">
                      {addComma(calcMathRound(sumArray(itemDraftVolumes(initialPlanEditResource.month, initialPlanEditResource.day, 'carry_volume')), CARRY_BLUR_DIGIT))}
                    </td>
                  </tr>
                  {/* 可燃ごみ搬入量[t]～資源物焼却処分搬入量[t] */}
                  {Array.from({
                    length: CARRY_DETAILS.length,
                  }).map((_, mapIndex) => (
                    <tr
                      className="table-carryrow"
                      style={{
                        display: initialPlanUsageDisplay[CARRY_KEYS[mapIndex] as keyof DriveUsage] ? 'table-row' : 'none',
                      }}
                    >
                      <td className="table-carrydoublecell-14">
                        {addComma(
                          calcMathRound(
                            sumArray(
                              itemDraftVolumes(
                                initialPlanEditResource.month,
                                initialPlanEditResource.day,
                                Object.keys(new DriveContextObject(0, sessionResource.furnacesCount))[CARRY_DETAILS[mapIndex]] as keyof DriveContext,
                              ),
                            ),
                            CARRY_BLUR_DIGIT,
                          ),
                        )}
                      </td>
                    </tr>
                  ))}
                  {/* 搬入量合計[t] */}
                  <tr className="table-carryrow" style={{ display: initialPlanUsageDisplay['total_carry_volume'] ? 'table-row' : 'none' }}>
                    <td className="table-carrydoublecell-14">{addComma(calcMathRound(planMonitorSumResource.total_carry_volume, CARRY_BLUR_DIGIT))}</td>
                  </tr>
                </>
                {/* 各炉の状態 */}
                {Array.from({ length: sessionResource.furnacesCount }).map((_, furnacesIndex) => (
                  <>
                    {/* 焼却量[t] */}
                    <tr className="table-carryrow">
                      <td className="table-carrydoublecell-14">
                        {addComma(
                          calcMathRound(
                            sumArray(burnDraftVolumes(initialPlanEditResource.month, initialPlanEditResource.day, 'burn_volume', furnacesIndex)),
                            BURNVOLUME_BLUR_DIGIT,
                          ),
                        )}
                      </td>
                    </tr>
                    {/* 焼却負荷率(停炉日を含まない)[%] */}
                    <tr className="table-carryrow" style={{ display: initialPlanUsageDisplay['burn_load'] && isMonthDisplay() ? 'table-row' : 'none' }}>
                      <td className="table-carrydoublecell-14">{addComma(calcMathFloor(initialPlanMonitorMonthSumResource.burn_load[furnacesIndex], BURNLOAD_DIGIT))}</td>
                    </tr>
                    {/* 焼却負荷率(停炉日を含む)[%] */}
                    <tr className="table-carryrow" style={{ display: initialPlanUsageDisplay['burn_load_include_shutdown'] && isMonthDisplay() ? 'table-row' : 'none' }}>
                      <td className="table-carrydoublecell-14">
                        {addComma(calcMathFloor(initialPlanMonitorMonthSumResource.burn_load_include_shutdown[furnacesIndex], BURNLOAD_DIGIT))}
                      </td>
                    </tr>
                    {/* 運転日数[日] */}
                    <tr className="table-carryrow" style={{ display: isMonthDisplay() ? 'table-row' : 'none' }}>
                      <td className="table-carrydoublecell-14">{initialPlanMonitorMonthSumResource.operating_days[furnacesIndex]}</td>
                    </tr>
                    {/* ごみ低位発熱量 */}
                    <tr className="table-carryrow" style={{ display: initialPlanUsageDisplay['waste_lower_heating_value'] ? 'table-row' : 'none' }}>
                      <td className="table-carrydoublecell-14">
                        {addComma(
                          calcMathRound(
                            sumArray(burnDraftVolumes(initialPlanEditResource.month, initialPlanEditResource.day, 'waste_lower_heating_value', furnacesIndex)),
                            WASTE_BLUR_DIGIT,
                          ),
                        )}
                      </td>
                    </tr>
                  </>
                ))}
                {/* 全炉 */}
                {/* 焼却量[t] */}
                <tr className="table-carryrow">
                  <td className="table-carrydoublecell-14">
                    {addComma(
                      calcMathRound(
                        Array.from({ length: sessionResource.furnacesCount }, (_, furnacesIndex) =>
                          sumArray(burnDraftVolumes(initialPlanEditResource.month, initialPlanEditResource.day, 'burn_volume', furnacesIndex)),
                        ).reduce((sum, value) => sum + value, 0),
                        BURNVOLUME_BLUR_DIGIT,
                      ),
                    )}
                  </td>
                </tr>
                {/* 焼却負荷率(停炉日を含まない)[%] */}
                <tr className="table-carryrow" style={{ display: initialPlanUsageDisplay['burn_load'] && isMonthDisplay() ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14">{addComma(calcMathFloor(initialPlanMonitorMonthSumResource.burn_load_sum, BURNLOAD_DIGIT))}</td>
                </tr>
                {/* 焼却負荷率(停炉日を含む)[%] */}
                <tr className="table-carryrow" style={{ display: initialPlanUsageDisplay['burn_load_include_shutdown'] && isMonthDisplay() ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14">{addComma(calcMathFloor(initialPlanMonitorMonthSumResource.burn_load_include_shutdown_sum, BURNLOAD_DIGIT))}</td>
                </tr>
                {/* ピット残量[t] */}
                <tr className="table-carryrow" style={{ display: isMonthDisplay() ? 'none' : 'table-row' }}>
                  <td className="table-carrydoublecell-14 p-0">
                    <div
                      style={{
                        height: '100%',
                        width: '100%',
                        backgroundImage: 'linear-gradient(35deg, var(--color-initial-plan) 49%, black 50%, var(--color-initial-plan) 51%)',
                      }}
                    ></div>
                  </td>
                </tr>
                {/* 主灰発生量[t] */}
                <tr className="table-carryrow" style={{ display: initialPlanUsageDisplay['main_ash_generate'] && showAshVisible ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14">
                    {addComma(calcMathRound(sumArray(itemDraftVolumes(initialPlanEditResource.month, initialPlanEditResource.day, 'main_ash_generate')), ASH_BLUR_DIGIT))}
                  </td>
                </tr>
                {/* 主灰搬出台数[台] */}
                <tr className="table-carryrow" style={{ display: initialPlanUsageDisplay['main_ash_carry_out_units'] && showAshVisible ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14">
                    {addComma(calcMathRound(sumArray(itemDraftVolumes(initialPlanEditResource.month, initialPlanEditResource.day, 'main_ash_carry_out_units')), ASH_BLUR_DIGIT))}
                  </td>
                </tr>
                {/* 主灰搬出量[t] */}
                <tr className="table-carryrow" style={{ display: initialPlanUsageDisplay['main_ash_carry_out'] && showAshVisible ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14">
                    {addComma(calcMathRound(sumArray(itemDraftVolumes(initialPlanEditResource.month, initialPlanEditResource.day, 'main_ash_carry_out')), ASH_BLUR_DIGIT))}
                  </td>
                </tr>
                {/* 主灰ピット残量[t] */}
                <tr className="table-carryrow" style={{ display: showAshVisible && !isMonthDisplay() ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14 p-0">
                    <div
                      style={{
                        height: '100%',
                        width: '100%',
                        backgroundImage: 'linear-gradient(35deg, var(--color-initial-plan) 49%, black 50%, var(--color-initial-plan) 51%)',
                      }}
                    ></div>
                  </td>
                </tr>
                {/* 飛灰発生量[t] */}
                <tr className="table-carryrow" style={{ display: initialPlanUsageDisplay['fly_ash_generate'] && showAshVisible ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14">
                    {addComma(calcMathRound(sumArray(itemDraftVolumes(initialPlanEditResource.month, initialPlanEditResource.day, 'fly_ash_generate')), ASH_BLUR_DIGIT))}
                  </td>
                </tr>
                {/* 飛灰搬出台数[台] */}
                <tr className="table-carryrow" style={{ display: initialPlanUsageDisplay['fly_ash_carry_out_units'] && showAshVisible ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14">
                    {addComma(calcMathRound(sumArray(itemDraftVolumes(initialPlanEditResource.month, initialPlanEditResource.day, 'fly_ash_carry_out_units')), ASH_BLUR_DIGIT))}
                  </td>
                </tr>
                {/* 飛灰搬出量[t] */}
                <tr className="table-carryrow" style={{ display: initialPlanUsageDisplay['fly_ash_carry_out'] && showAshVisible ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14">
                    {addComma(calcMathRound(sumArray(itemDraftVolumes(initialPlanEditResource.month, initialPlanEditResource.day, 'fly_ash_carry_out')), ASH_BLUR_DIGIT))}
                  </td>
                </tr>
                {/* 飛灰処理物搬出量 乾灰[t] */}
                <tr className="table-carryrow" style={{ display: initialPlanUsageDisplay['dry_fly_ash_carry_out'] && showAshVisible ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14">
                    {addComma(calcMathRound(sumArray(itemDraftVolumes(initialPlanEditResource.month, initialPlanEditResource.day, 'dry_fly_ash_carry_out')), ASH_BLUR_DIGIT))}
                  </td>
                </tr>
                {/* 飛灰処理物搬出量 処理灰[t] */}
                <tr className="table-carryrow" style={{ display: initialPlanUsageDisplay['processed_fly_ash_carry_out'] && showAshVisible ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14">
                    {addComma(calcMathRound(sumArray(itemDraftVolumes(initialPlanEditResource.month, initialPlanEditResource.day, 'processed_fly_ash_carry_out')), ASH_BLUR_DIGIT))}
                  </td>
                </tr>
                {/* 飛灰ピット残量[t] */}
                <tr className="table-carryrow" style={{ display: showAshVisible && !isMonthDisplay() ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14 p-0">
                    <div
                      style={{
                        height: '100%',
                        width: '100%',
                        backgroundImage: 'linear-gradient(35deg, var(--color-initial-plan) 49%, black 50%, var(--color-initial-plan) 51%)',
                      }}
                    ></div>
                  </td>
                </tr>
                {/* 温水供給設備供給熱量 */}
                <tr className="table-carryrow" style={{ display: initialPlanUsageDisplay['hot_water_supply_heating_value'] ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14">
                    {addComma(
                      calcMathRound(sumArray(itemDraftVolumes(initialPlanEditResource.month, initialPlanEditResource.day, 'hot_water_supply_heating_value')), HOT_WATER_BLUR_DIGIT),
                    )}
                  </td>
                </tr>
                {/* 炉別運転日数 */}
                {Array.from({ length: sessionResource.furnacesCount + 1 }).map((_, runIndex) => (
                  <tr
                    className="table-carryrow"
                    style={{ display: isMonthDisplay() && initialPlanUsageDisplay['furnace_operating_days'] && showOperationDaysVisible ? 'table-row' : 'none' }}
                  >
                    <td className="table-carrydoublecell-14">{initialPlanMonitorMonthSumResource.furnace_operating_days[runIndex]}</td>
                  </tr>
                ))}
              </tbody>
              {/* ##### 合計情報（計画＋実績） テーブル表示管理 #### */}
              <tbody style={{ display: existAutoDraft && isResultVisible() && isPlanVisible() ? 'table-row-group' : 'none' }}>
                <tr className="table-carryrow">
                  <td className="table-carrydoublecell-14 p-0">
                    <div
                      style={{
                        height: '100%',
                        width: '100%',
                        backgroundImage: `linear-gradient(35deg, var(--color-triad-1) 49%, black 50%, var(--color-triad-2) 51%)`,
                      }}
                    ></div>
                  </td>
                </tr>
                {/* 搬入量[t] */}
                <tr className="table-carryrow" style={{ display: planResultUsageDisplay['carry_volume'] ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14">
                    {addComma(
                      calcMathRound(
                        sumArray(
                          itemDraftVolumes(
                            concatResultPlanMonthDraft(resultEditResource.month, planEditResource.month, resultEditResource.day),
                            concatResultPlanDayDraft(resultEditResource.day, planEditResource.day),
                            'carry_volume',
                          ),
                        ),
                        CARRY_BLUR_DIGIT,
                      ),
                    )}
                  </td>
                </tr>
                {/* 可燃ごみ搬入量[t]～資源物焼却処分搬入量[t] */}
                {Array.from({
                  length: CARRY_DETAILS.length,
                }).map((_, mapIndex) => (
                  <tr
                    className="table-carryrow"
                    style={{
                      display: planResultUsageDisplay[CARRY_KEYS[mapIndex] as keyof DriveUsage] ? 'table-row' : 'none',
                    }}
                  >
                    <td className="table-carrydoublecell-14">
                      {addComma(
                        calcMathRound(
                          sumArray(
                            itemDraftVolumes(
                              concatResultPlanMonthDraft(resultEditResource.month, planEditResource.month, resultEditResource.day),
                              concatResultPlanDayDraft(resultEditResource.day, planEditResource.day),
                              Object.keys(new DriveContextObject(0, sessionResource.furnacesCount))[CARRY_DETAILS[mapIndex]] as keyof DriveContext,
                            ),
                          ),
                          CARRY_BLUR_DIGIT,
                        ),
                      )}
                    </td>
                  </tr>
                ))}
                {/* 搬入量合計[t] */}
                <tr className="table-carryrow" style={{ display: planResultUsageDisplay['total_carry_volume'] ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14">{addComma(calcMathRound(planResultMonitorSumResource.total_carry_volume, CARRY_BLUR_DIGIT))}</td>
                </tr>
                {/* 各炉の状態 */}
                {Array.from({ length: sessionResource.furnacesCount }).map((_, furnacesIndex) => (
                  <>
                    {/* 焼却量[t] */}
                    <tr className="table-carryrow">
                      <td className="table-carrydoublecell-14">
                        {addComma(
                          calcMathRound(
                            sumArray(
                              burnDraftVolumes(
                                concatResultPlanMonthDraft(resultEditResource.month, planEditResource.month, resultEditResource.day),
                                concatResultPlanDayDraft(resultEditResource.day, planEditResource.day),
                                'burn_volume',
                                furnacesIndex,
                              ),
                            ),
                            BURNVOLUME_BLUR_DIGIT,
                          ),
                        )}
                      </td>
                    </tr>
                    {/* 焼却負荷率(停炉日を含まない)[%] */}
                    <tr className="table-carryrow" style={{ display: planResultUsageDisplay['burn_load'] && isMonthDisplay() ? 'table-row' : 'none' }}>
                      <td className="table-carrydoublecell-14">{addComma(calcMathFloor(planResultMonitorMonthSumResource.burn_load[furnacesIndex], BURNLOAD_DIGIT))}</td>
                    </tr>
                    {/* 焼却負荷率(停炉日を含む)[%] */}
                    <tr className="table-carryrow" style={{ display: planResultUsageDisplay['burn_load_include_shutdown'] && isMonthDisplay() ? 'table-row' : 'none' }}>
                      <td className="table-carrydoublecell-14">
                        {addComma(calcMathFloor(planResultMonitorMonthSumResource.burn_load_include_shutdown[furnacesIndex], BURNLOAD_DIGIT))}
                      </td>
                    </tr>
                    {/* 運転日数[日] */}
                    <tr className="table-carryrow" style={{ display: isMonthDisplay() ? 'table-row' : 'none' }}>
                      <td className="table-carrydoublecell-14">{planResultMonitorMonthSumResource.operating_days[furnacesIndex]}</td>
                    </tr>
                    {/* ごみ低位発熱量 */}
                    <tr className="table-carryrow" style={{ display: planResultUsageDisplay['waste_lower_heating_value'] ? 'table-row' : 'none' }}>
                      <td className="table-carrydoublecell-14">
                        {addComma(
                          calcMathRound(
                            sumArray(
                              burnDraftVolumes(
                                concatResultPlanMonthDraft(resultEditResource.month, planEditResource.month, resultEditResource.day),
                                concatResultPlanDayDraft(resultEditResource.day, planEditResource.day),
                                'waste_lower_heating_value',
                                furnacesIndex,
                              ),
                            ),
                            WASTE_BLUR_DIGIT,
                          ),
                        )}
                      </td>
                    </tr>
                  </>
                ))}
                {/* 全炉 */}
                {/* 焼却量[t] */}
                <tr className="table-carryrow">
                  <td className="table-carrydoublecell-14">
                    {addComma(
                      calcMathRound(
                        Array.from({ length: sessionResource.furnacesCount }, (_, furnacesIndex) =>
                          sumArray(
                            burnDraftVolumes(
                              concatResultPlanMonthDraft(resultEditResource.month, planEditResource.month, resultEditResource.day),
                              concatResultPlanDayDraft(resultEditResource.day, planEditResource.day),
                              'burn_volume',
                              furnacesIndex,
                            ),
                          ),
                        ).reduce((sum, value) => sum + value, 0),
                        BURNVOLUME_BLUR_DIGIT,
                      ),
                    )}
                  </td>
                </tr>
                {/* 焼却負荷率(停炉日を含まない)[%] */}
                <tr className="table-carryrow" style={{ display: planResultUsageDisplay['burn_load'] && isMonthDisplay() ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14">{isMonthDisplay() ? addComma(calcMathFloor(planResultMonitorMonthSumResource.burn_load_sum, BURNLOAD_DIGIT)) : ''}</td>
                </tr>
                {/* 焼却負荷率(停炉日を含む)[%] */}
                <tr className="table-carryrow" style={{ display: planResultUsageDisplay['burn_load_include_shutdown'] && isMonthDisplay() ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14">
                    {isMonthDisplay() ? addComma(calcMathFloor(planResultMonitorMonthSumResource.burn_load_include_shutdown_sum, BURNLOAD_DIGIT)) : ''}
                  </td>
                </tr>
                {/* ピット残量[t] */}
                <tr className="table-carryrow" style={{ display: isMonthDisplay() ? 'none' : 'table-row' }}>
                  <td className="table-carrydoublecell-14 p-0">
                    <div
                      style={{
                        height: '100%',
                        width: '100%',
                        backgroundImage: `linear-gradient(35deg, var(--color-triad-1) 49%, black 50%, var(--color-triad-2) 51%)`,
                      }}
                    ></div>
                  </td>
                </tr>
                {/* 主灰発生量[t] */}
                <tr className="table-carryrow" style={{ display: planResultUsageDisplay['main_ash_generate'] && showAshVisible ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14">
                    {addComma(
                      calcMathRound(
                        sumArray(
                          itemDraftVolumes(
                            concatResultPlanMonthDraft(resultEditResource.month, planEditResource.month, resultEditResource.day),
                            concatResultPlanDayDraft(resultEditResource.day, planEditResource.day),
                            'main_ash_generate',
                          ),
                        ),
                        ASH_BLUR_DIGIT,
                      ),
                    )}
                  </td>
                </tr>
                {/* 主灰搬出台数[台] */}
                <tr className="table-carryrow" style={{ display: planResultUsageDisplay['main_ash_carry_out_units'] && showAshVisible ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14">
                    {addComma(
                      calcMathRound(
                        sumArray(
                          itemDraftVolumes(
                            concatResultPlanMonthDraft(resultEditResource.month, planEditResource.month, resultEditResource.day),
                            concatResultPlanDayDraft(resultEditResource.day, planEditResource.day),
                            'main_ash_carry_out_units',
                          ),
                        ),
                        ASH_BLUR_DIGIT,
                      ),
                    )}
                  </td>
                </tr>
                {/* 主灰搬出量[t] */}
                <tr className="table-carryrow" style={{ display: planResultUsageDisplay['main_ash_carry_out'] && showAshVisible ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14">
                    {addComma(
                      calcMathRound(
                        sumArray(
                          itemDraftVolumes(
                            concatResultPlanMonthDraft(resultEditResource.month, planEditResource.month, resultEditResource.day),
                            concatResultPlanDayDraft(resultEditResource.day, planEditResource.day),
                            'main_ash_carry_out',
                          ),
                        ),
                        ASH_BLUR_DIGIT,
                      ),
                    )}
                  </td>
                </tr>
                {/* 主灰ピット残量[t] */}
                <tr className="table-carryrow" style={{ display: showAshVisible && !isMonthDisplay() ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14 p-0">
                    <div
                      style={{
                        height: '100%',
                        width: '100%',
                        backgroundImage: `linear-gradient(35deg, var(--color-triad-1) 49%, black 50%, var(--color-triad-2) 51%)`,
                      }}
                    ></div>
                  </td>
                </tr>
                {/* 飛灰発生量[t] */}
                <tr className="table-carryrow" style={{ display: planResultUsageDisplay['fly_ash_generate'] && showAshVisible ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14">
                    {addComma(
                      calcMathRound(
                        sumArray(
                          itemDraftVolumes(
                            concatResultPlanMonthDraft(resultEditResource.month, planEditResource.month, resultEditResource.day),
                            concatResultPlanDayDraft(resultEditResource.day, planEditResource.day),
                            'fly_ash_generate',
                          ),
                        ),
                        ASH_BLUR_DIGIT,
                      ),
                    )}
                  </td>
                </tr>
                {/* 飛灰搬出台数[台] */}
                <tr className="table-carryrow" style={{ display: planResultUsageDisplay['fly_ash_carry_out_units'] && showAshVisible ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14">
                    {addComma(
                      calcMathRound(
                        sumArray(
                          itemDraftVolumes(
                            concatResultPlanMonthDraft(resultEditResource.month, planEditResource.month, resultEditResource.day),
                            concatResultPlanDayDraft(resultEditResource.day, planEditResource.day),
                            'fly_ash_carry_out_units',
                          ),
                        ),
                        ASH_BLUR_DIGIT,
                      ),
                    )}
                  </td>
                </tr>
                {/* 飛灰搬出量[t] */}
                <tr className="table-carryrow" style={{ display: planResultUsageDisplay['fly_ash_carry_out'] && showAshVisible ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14">
                    {addComma(
                      calcMathRound(
                        sumArray(
                          itemDraftVolumes(
                            concatResultPlanMonthDraft(resultEditResource.month, planEditResource.month, resultEditResource.day),
                            concatResultPlanDayDraft(resultEditResource.day, planEditResource.day),
                            'fly_ash_carry_out',
                          ),
                        ),
                        ASH_BLUR_DIGIT,
                      ),
                    )}
                  </td>
                </tr>
                {/* 飛灰処理物搬出量 乾灰[t] */}
                <tr className="table-carryrow" style={{ display: planResultUsageDisplay['dry_fly_ash_carry_out'] && showAshVisible ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14">
                    {addComma(
                      calcMathRound(
                        sumArray(
                          itemDraftVolumes(
                            concatResultPlanMonthDraft(resultEditResource.month, planEditResource.month, resultEditResource.day),
                            concatResultPlanDayDraft(resultEditResource.day, planEditResource.day),
                            'dry_fly_ash_carry_out',
                          ),
                        ),
                        ASH_BLUR_DIGIT,
                      ),
                    )}
                  </td>
                </tr>
                {/* 飛灰処理物搬出量 処理灰[t] */}
                <tr className="table-carryrow" style={{ display: planResultUsageDisplay['processed_fly_ash_carry_out'] && showAshVisible ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14">
                    {addComma(
                      calcMathRound(
                        sumArray(
                          itemDraftVolumes(
                            concatResultPlanMonthDraft(resultEditResource.month, planEditResource.month, resultEditResource.day),
                            concatResultPlanDayDraft(resultEditResource.day, planEditResource.day),
                            'processed_fly_ash_carry_out',
                          ),
                        ),
                        ASH_BLUR_DIGIT,
                      ),
                    )}
                  </td>
                </tr>
                {/* 飛灰ピット残量[t] */}
                <tr className="table-carryrow" style={{ display: showAshVisible && !isMonthDisplay() ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14 p-0">
                    <div
                      style={{
                        height: '100%',
                        width: '100%',
                        backgroundImage: `linear-gradient(35deg, var(--color-triad-1) 49%, black 50%, var(--color-triad-2) 51%)`,
                      }}
                    ></div>
                  </td>
                </tr>
                {/* 温水供給設備供給熱量 */}
                <tr className="table-carryrow" style={{ display: planResultUsageDisplay['hot_water_supply_heating_value'] ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14">
                    {addComma(
                      calcMathRound(
                        sumArray(
                          itemDraftVolumes(
                            concatResultPlanMonthDraft(resultEditResource.month, planEditResource.month, resultEditResource.day),
                            concatResultPlanDayDraft(resultEditResource.day, planEditResource.day),
                            'hot_water_supply_heating_value',
                          ),
                        ),
                        HOT_WATER_BLUR_DIGIT,
                      ),
                    )}
                  </td>
                </tr>
                {/* 炉別運転日数 */}
                {Array.from({ length: sessionResource.furnacesCount + 1 }).map((_, runIndex) => (
                  <tr className="table-carryrow" style={{ display: isMonthDisplay() && showOperationDaysVisible ? 'table-row' : 'none' }}>
                    <td className="table-carrydoublecell-14">{initialPlanMonitorMonthSumResource.furnace_operating_days[runIndex]}</td>
                  </tr>
                ))}
              </tbody>
              {/* ##### 合計情報（計画） テーブル表示管理 #### */}
              <tbody style={{ display: !(existAutoDraft && isResultVisible()) && isPlanVisible() ? 'table-row-group' : 'none' }}>
                <tr className="table-carryrow">
                  <td className="table-carrydoublecell-14 p-0">
                    <div
                      style={{
                        height: '100%',
                        width: '100%',
                        backgroundImage: 'linear-gradient(35deg, var(--color-triad-2) 49%, black 50%, var(--color-triad-2) 51%)',
                      }}
                    ></div>
                  </td>
                </tr>
                <>
                  {/* 搬入量[t] */}
                  <tr className="table-carryrow" style={{ display: planUsageDisplay['carry_volume'] ? 'table-row' : 'none' }}>
                    <td className="table-carrydoublecell-14">
                      {addComma(calcMathRound(sumArray(itemDraftVolumes(planEditResource.month, planEditResource.day, 'carry_volume')), CARRY_BLUR_DIGIT))}
                    </td>
                  </tr>
                  {/* 可燃ごみ搬入量[t]～資源物焼却処分搬入量[t] */}
                  {Array.from({
                    length: CARRY_DETAILS.length,
                  }).map((_, mapIndex) => (
                    <tr
                      className="table-carryrow"
                      style={{
                        display: planUsageDisplay[CARRY_KEYS[mapIndex] as keyof DriveUsage] ? 'table-row' : 'none',
                      }}
                    >
                      <td className="table-carrydoublecell-14">
                        {addComma(
                          calcMathRound(
                            sumArray(
                              itemDraftVolumes(
                                planEditResource.month,
                                planEditResource.day,
                                Object.keys(new DriveContextObject(0, sessionResource.furnacesCount))[CARRY_DETAILS[mapIndex]] as keyof DriveContext,
                              ),
                            ),
                            CARRY_BLUR_DIGIT,
                          ),
                        )}
                      </td>
                    </tr>
                  ))}
                  {/* 搬入量合計[t] */}
                  <tr className="table-carryrow" style={{ display: planUsageDisplay['total_carry_volume'] ? 'table-row' : 'none' }}>
                    <td className="table-carrydoublecell-14">{addComma(calcMathRound(planMonitorSumResource.total_carry_volume, CARRY_BLUR_DIGIT))}</td>
                  </tr>
                </>
                {/* 各炉の状態 */}
                {Array.from({ length: sessionResource.furnacesCount }).map((_, furnacesIndex) => (
                  <>
                    {/* 焼却量[t] */}
                    <tr className="table-carryrow">
                      <td className="table-carrydoublecell-14">
                        {addComma(calcMathRound(sumArray(burnDraftVolumes(planEditResource.month, planEditResource.day, 'burn_volume', furnacesIndex)), BURNVOLUME_BLUR_DIGIT))}
                      </td>
                    </tr>
                    {/* 焼却負荷率(停炉日を含まない)[%] */}
                    <tr className="table-carryrow" style={{ display: planUsageDisplay['burn_load'] && isMonthDisplay() ? 'table-row' : 'none' }}>
                      <td className="table-carrydoublecell-14">{addComma(calcMathFloor(planMonitorMonthSumResource.burn_load[furnacesIndex], BURNLOAD_DIGIT))}</td>
                    </tr>
                    {/* 焼却負荷率(停炉日を含む)[%] */}
                    <tr className="table-carryrow" style={{ display: planUsageDisplay['burn_load_include_shutdown'] && isMonthDisplay() ? 'table-row' : 'none' }}>
                      <td className="table-carrydoublecell-14">{addComma(calcMathFloor(planMonitorMonthSumResource.burn_load_include_shutdown[furnacesIndex], BURNLOAD_DIGIT))}</td>
                    </tr>
                    {/* 運転日数[日] */}
                    <tr className="table-carryrow" style={{ display: isMonthDisplay() ? 'table-row' : 'none' }}>
                      <td className="table-carrydoublecell-14">{planMonitorMonthSumResource.operating_days[furnacesIndex]}</td>
                    </tr>
                    {/* ごみ低位発熱量 */}
                    <tr className="table-carryrow" style={{ display: planUsageDisplay['waste_lower_heating_value'] ? 'table-row' : 'none' }}>
                      <td className="table-carrydoublecell-14">
                        {addComma(
                          calcMathRound(sumArray(burnDraftVolumes(planEditResource.month, planEditResource.day, 'waste_lower_heating_value', furnacesIndex)), WASTE_BLUR_DIGIT),
                        )}
                      </td>
                    </tr>
                  </>
                ))}
                {/* 全炉 */}
                {/* 焼却量[t] */}
                <tr className="table-carryrow">
                  <td className="table-carrydoublecell-14">
                    {addComma(
                      calcMathRound(
                        Array.from({ length: sessionResource.furnacesCount }, (_, furnacesIndex) =>
                          sumArray(burnDraftVolumes(planEditResource.month, planEditResource.day, 'burn_volume', furnacesIndex)),
                        ).reduce((sum, value) => sum + value, 0),
                        BURNVOLUME_BLUR_DIGIT,
                      ),
                    )}
                  </td>
                </tr>
                {/* 焼却負荷率(停炉日を含まない)[%] */}
                <tr className="table-carryrow" style={{ display: planUsageDisplay['burn_load'] && isMonthDisplay() ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14">{addComma(calcMathFloor(planMonitorMonthSumResource.burn_load_sum, BURNLOAD_DIGIT))}</td>
                </tr>
                {/* 焼却負荷率(停炉日を含む)[%] */}
                <tr className="table-carryrow" style={{ display: planUsageDisplay['burn_load_include_shutdown'] && isMonthDisplay() ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14">{addComma(calcMathFloor(planMonitorMonthSumResource.burn_load_include_shutdown_sum, BURNLOAD_DIGIT))}</td>
                </tr>
                {/* ピット残量[t] */}
                <tr className="table-carryrow" style={{ display: isMonthDisplay() ? 'none' : 'table-row' }}>
                  <td className="table-carrydoublecell-14 p-0">
                    <div
                      style={{
                        height: '100%',
                        width: '100%',
                        backgroundImage: 'linear-gradient(35deg, var(--color-triad-2) 49%, black 50%, var(--color-triad-2) 51%)',
                      }}
                    ></div>
                  </td>
                </tr>
                {/* 主灰発生量[t] */}
                <tr className="table-carryrow" style={{ display: planUsageDisplay['main_ash_generate'] && showAshVisible ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14">
                    {addComma(calcMathRound(sumArray(itemDraftVolumes(planEditResource.month, planEditResource.day, 'main_ash_generate')), ASH_BLUR_DIGIT))}
                  </td>
                </tr>
                {/* 主灰搬出台数[台] */}
                <tr className="table-carryrow" style={{ display: planUsageDisplay['main_ash_carry_out_units'] && showAshVisible ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14">
                    {addComma(calcMathRound(sumArray(itemDraftVolumes(planEditResource.month, planEditResource.day, 'main_ash_carry_out_units')), ASH_BLUR_DIGIT))}
                  </td>
                </tr>
                {/* 主灰搬出量[t] */}
                <tr className="table-carryrow" style={{ display: planUsageDisplay['main_ash_carry_out'] && showAshVisible ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14">
                    {addComma(calcMathRound(sumArray(itemDraftVolumes(planEditResource.month, planEditResource.day, 'main_ash_carry_out')), ASH_BLUR_DIGIT))}
                  </td>
                </tr>
                {/* 主灰ピット残量[t] */}
                <tr className="table-carryrow" style={{ display: showAshVisible && !isMonthDisplay() ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14 p-0">
                    <div
                      style={{
                        height: '100%',
                        width: '100%',
                        backgroundImage: 'linear-gradient(35deg, var(--color-triad-2) 49%, black 50%, var(--color-triad-2) 51%)',
                      }}
                    ></div>
                  </td>
                </tr>
                {/* 飛灰発生量[t] */}
                <tr className="table-carryrow" style={{ display: planUsageDisplay['fly_ash_generate'] && showAshVisible ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14">
                    {addComma(calcMathRound(sumArray(itemDraftVolumes(planEditResource.month, planEditResource.day, 'fly_ash_generate')), ASH_BLUR_DIGIT))}
                  </td>
                </tr>
                {/* 飛灰搬出台数[台] */}
                <tr className="table-carryrow" style={{ display: planUsageDisplay['fly_ash_carry_out_units'] && showAshVisible ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14">
                    {addComma(calcMathRound(sumArray(itemDraftVolumes(planEditResource.month, planEditResource.day, 'fly_ash_carry_out_units')), ASH_BLUR_DIGIT))}
                  </td>
                </tr>
                {/* 飛灰搬出量[t] */}
                <tr className="table-carryrow" style={{ display: planUsageDisplay['fly_ash_carry_out'] && showAshVisible ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14">
                    {addComma(calcMathRound(sumArray(itemDraftVolumes(planEditResource.month, planEditResource.day, 'fly_ash_carry_out')), ASH_BLUR_DIGIT))}
                  </td>
                </tr>
                {/* 飛灰処理物搬出量 乾灰[t] */}
                <tr className="table-carryrow" style={{ display: planUsageDisplay['dry_fly_ash_carry_out'] && showAshVisible ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14">
                    {addComma(calcMathRound(sumArray(itemDraftVolumes(planEditResource.month, planEditResource.day, 'dry_fly_ash_carry_out')), ASH_BLUR_DIGIT))}
                  </td>
                </tr>
                {/* 飛灰処理物搬出量 処理灰[t] */}
                <tr className="table-carryrow" style={{ display: planUsageDisplay['processed_fly_ash_carry_out'] && showAshVisible ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14">
                    {addComma(calcMathRound(sumArray(itemDraftVolumes(planEditResource.month, planEditResource.day, 'processed_fly_ash_carry_out')), ASH_BLUR_DIGIT))}
                  </td>
                </tr>
                {/* 飛灰ピット残量[t] */}
                <tr className="table-carryrow" style={{ display: showAshVisible && !isMonthDisplay() ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14 p-0">
                    <div
                      style={{
                        height: '100%',
                        width: '100%',
                        backgroundImage: 'linear-gradient(35deg, var(--color-triad-2) 49%, black 50%, var(--color-triad-2) 51%)',
                      }}
                    ></div>
                  </td>
                </tr>
                {/* 温水供給設備供給熱量 */}
                <tr className="table-carryrow" style={{ display: planUsageDisplay['hot_water_supply_heating_value'] ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14">
                    {addComma(calcMathRound(sumArray(itemDraftVolumes(planEditResource.month, planEditResource.day, 'hot_water_supply_heating_value')), HOT_WATER_BLUR_DIGIT))}
                  </td>
                </tr>
                {/* 炉別運転日数 */}
                {Array.from({ length: sessionResource.furnacesCount + 1 }).map((_, runIndex) => (
                  <tr
                    className="table-carryrow"
                    style={{ display: isMonthDisplay() && planUsageDisplay['furnace_operating_days'] && showOperationDaysVisible ? 'table-row' : 'none' }}
                  >
                    <td className="table-carrydoublecell-14">{planMonitorMonthSumResource.furnace_operating_days[runIndex]}</td>
                  </tr>
                ))}
              </tbody>
              {/* ##### 合計情報（実績） テーブル表示管理 #### */}
              <tbody style={{ display: !(existAutoDraft && isPlanVisible()) && isResultVisible() ? 'table-row-group' : 'none' }}>
                <tr className="table-carryrow">
                  <td className="table-carrycell p-0">
                    <div
                      style={{
                        height: '100%',
                        width: '100%',
                        backgroundImage: 'linear-gradient(35deg, var(--color-triad-1) 49%, black 50%, var(--color-triad-1) 51%)',
                      }}
                    ></div>
                  </td>
                </tr>
                <>
                  {/* 搬入量[t] */}
                  <tr className="table-carryrow" style={{ display: resultUsageDisplay['carry_volume'] ? 'table-row' : 'none' }}>
                    <td className="table-carrycell">
                      {addComma(calcMathRound(sumArray(itemDraftVolumes(resultEditResource.month, resultEditResource.day, 'carry_volume')), CARRY_BLUR_DIGIT))}
                    </td>
                  </tr>
                  {/* 可燃ごみ搬入量[t]～資源物焼却処分搬入量[t] */}
                  {Array.from({
                    length: CARRY_DETAILS.length,
                  }).map((_, mapIndex) => (
                    <tr
                      className="table-carryrow"
                      style={{
                        display: resultUsageDisplay[CARRY_KEYS[mapIndex] as keyof DriveUsage] ? 'table-row' : 'none',
                      }}
                    >
                      <td className="table-carrydoublecell-14">
                        {addComma(
                          calcMathRound(
                            sumArray(
                              itemDraftVolumes(
                                resultEditResource.month,
                                resultEditResource.day,
                                Object.keys(new DriveContextObject(0, sessionResource.furnacesCount))[CARRY_DETAILS[mapIndex]] as keyof DriveContext,
                              ),
                            ),
                            CARRY_BLUR_DIGIT,
                          ),
                        )}
                      </td>
                    </tr>
                  ))}
                  {/* 搬入量合計[t] */}
                  <tr className="table-carryrow" style={{ display: resultUsageDisplay['total_carry_volume'] ? 'table-row' : 'none' }}>
                    <td className="table-carrydoublecell-14">{addComma(calcMathRound(resultMonitorSumResource.total_carry_volume, CARRY_BLUR_DIGIT))}</td>
                  </tr>
                </>
                {/* 各炉の状態 */}
                {Array.from({ length: sessionResource.furnacesCount }).map((_, furnacesIndex) => (
                  <>
                    {/* 焼却量[t] */}
                    <tr className="table-carryrow">
                      <td className="table-carrycell">
                        {addComma(calcMathRound(sumArray(burnDraftVolumes(resultEditResource.month, resultEditResource.day, 'burn_volume', furnacesIndex)), BURNVOLUME_BLUR_DIGIT))}
                      </td>
                    </tr>
                    {/* 焼却負荷率(停炉日を含まない)[%] */}
                    <tr className="table-carryrow" style={{ display: resultUsageDisplay['burn_load'] && isMonthDisplay() ? 'table-row' : 'none' }}>
                      <td className="table-carrycell">{addComma(calcMathFloor(resultMonitorMonthSumResource.burn_load[furnacesIndex], BURNLOAD_DIGIT))}</td>
                    </tr>
                    {/* 焼却負荷率(停炉日を含む)[%] */}
                    <tr className="table-carryrow" style={{ display: resultUsageDisplay['burn_load_include_shutdown'] && isMonthDisplay() ? 'table-row' : 'none' }}>
                      <td className="table-carrycell">{addComma(calcMathFloor(resultMonitorMonthSumResource.burn_load_include_shutdown[furnacesIndex], BURNLOAD_DIGIT))}</td>
                    </tr>
                    {/* 運転日数[日] */}
                    <tr className="table-carryrow" style={{ display: isMonthDisplay() ? 'table-row' : 'none' }}>
                      <td className="table-carrydoublecell-14">{resultMonitorMonthSumResource.operating_days[furnacesIndex]}</td>
                    </tr>
                    {/* ごみ低位発熱量 */}
                    <tr className="table-carryrow" style={{ display: resultUsageDisplay['waste_lower_heating_value'] ? 'table-row' : 'none' }}>
                      <td className="table-carrydoublecell-14">
                        {addComma(
                          calcMathRound(sumArray(burnDraftVolumes(resultEditResource.month, resultEditResource.day, 'waste_lower_heating_value', furnacesIndex)), WASTE_BLUR_DIGIT),
                        )}
                      </td>
                    </tr>
                  </>
                ))}
                {/* 全炉 */}
                {/* 焼却量[t] */}
                <tr className="table-carryrow">
                  <td className="table-carrydoublecell-14">
                    {addComma(
                      calcMathRound(
                        Array.from({ length: sessionResource.furnacesCount }, (_, furnacesIndex) =>
                          sumArray(burnDraftVolumes(resultEditResource.month, resultEditResource.day, 'burn_volume', furnacesIndex)),
                        ).reduce((sum, value) => sum + value, 0),
                        BURNVOLUME_BLUR_DIGIT,
                      ),
                    )}
                  </td>
                </tr>
                {/* 焼却負荷率(停炉日を含まない)[%] */}
                <tr className="table-carryrow" style={{ display: resultUsageDisplay['burn_load'] && isMonthDisplay() ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14">{addComma(calcMathFloor(resultMonitorMonthSumResource.burn_load_sum, BURNLOAD_DIGIT))}</td>
                </tr>
                {/* 焼却負荷率(停炉日を含む)[%] */}
                <tr className="table-carryrow" style={{ display: resultUsageDisplay['burn_load_include_shutdown'] && isMonthDisplay() ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14">{addComma(calcMathFloor(resultMonitorMonthSumResource.burn_load_include_shutdown_sum, BURNLOAD_DIGIT))}</td>
                </tr>
                {/* ピット残量[t] */}
                <tr className="table-carryrow" style={{ display: isMonthDisplay() ? 'none' : 'table-row' }}>
                  <td className="table-carrydoublecell-14 p-0">
                    <div
                      style={{
                        height: '100%',
                        width: '100%',
                        backgroundImage: 'linear-gradient(35deg, var(--color-triad-1) 49%, black 50%, var(--color-triad-1) 51%)',
                      }}
                    ></div>
                  </td>
                </tr>
                {/* 主灰生産量[t] */}
                <tr className="table-carryrow" style={{ display: resultUsageDisplay['main_ash_generate'] && showAshVisible ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14">
                    {addComma(calcMathRound(sumArray(itemDraftVolumes(resultEditResource.month, resultEditResource.day, 'main_ash_generate')), ASH_BLUR_DIGIT))}
                  </td>
                </tr>
                {/* 主灰搬出台数[台] */}
                <tr className="table-carryrow" style={{ display: resultUsageDisplay['main_ash_carry_out_units'] && showAshVisible ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14">
                    {addComma(calcMathRound(sumArray(itemDraftVolumes(resultEditResource.month, resultEditResource.day, 'main_ash_carry_out_units')), ASH_BLUR_DIGIT))}
                  </td>
                </tr>
                {/* 主灰搬出量[t] */}
                <tr className="table-carryrow" style={{ display: resultUsageDisplay['main_ash_carry_out'] && showAshVisible ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14">
                    {addComma(calcMathRound(sumArray(itemDraftVolumes(resultEditResource.month, resultEditResource.day, 'main_ash_carry_out')), ASH_BLUR_DIGIT))}
                  </td>
                </tr>
                {/* 主灰ピット残量[t] */}
                <tr className="table-carryrow" style={{ display: showAshVisible && !isMonthDisplay() ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14 p-0">
                    <div
                      style={{
                        height: '100%',
                        width: '100%',
                        backgroundImage: 'linear-gradient(35deg, var(--color-triad-1) 49%, black 50%, var(--color-triad-1) 51%)',
                      }}
                    ></div>
                  </td>
                </tr>
                {/* 飛灰生産量[t] */}
                <tr className="table-carryrow" style={{ display: resultUsageDisplay['fly_ash_generate'] && showAshVisible ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14">
                    {addComma(calcMathRound(sumArray(itemDraftVolumes(resultEditResource.month, resultEditResource.day, 'fly_ash_generate')), ASH_BLUR_DIGIT))}
                  </td>
                </tr>
                {/* 飛灰搬出台数[台] */}
                <tr className="table-carryrow" style={{ display: resultUsageDisplay['fly_ash_carry_out_units'] && showAshVisible ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14">
                    {addComma(calcMathRound(sumArray(itemDraftVolumes(resultEditResource.month, resultEditResource.day, 'fly_ash_carry_out_units')), ASH_BLUR_DIGIT))}
                  </td>
                </tr>
                {/* 飛灰搬出量[t] */}
                <tr className="table-carryrow" style={{ display: resultUsageDisplay['fly_ash_carry_out'] && showAshVisible ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14">
                    {addComma(calcMathRound(sumArray(itemDraftVolumes(resultEditResource.month, resultEditResource.day, 'fly_ash_carry_out')), ASH_BLUR_DIGIT))}
                  </td>
                </tr>
                {/* 飛灰処理物搬出量 乾灰[t] */}
                <tr className="table-carryrow" style={{ display: resultUsageDisplay['dry_fly_ash_carry_out'] && showAshVisible ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14">
                    {addComma(calcMathRound(sumArray(itemDraftVolumes(resultEditResource.month, resultEditResource.day, 'dry_fly_ash_carry_out')), ASH_BLUR_DIGIT))}
                  </td>
                </tr>
                {/* 飛灰処理物搬出量 処理灰[t] */}
                <tr className="table-carryrow" style={{ display: resultUsageDisplay['processed_fly_ash_carry_out'] && showAshVisible ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14">
                    {addComma(calcMathRound(sumArray(itemDraftVolumes(resultEditResource.month, resultEditResource.day, 'processed_fly_ash_carry_out')), ASH_BLUR_DIGIT))}
                  </td>
                </tr>
                {/* 飛灰ピット残量[t] */}
                <tr className="table-carryrow" style={{ display: showAshVisible && !isMonthDisplay() ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14 p-0">
                    <div
                      style={{
                        height: '100%',
                        width: '100%',
                        backgroundImage: 'linear-gradient(35deg, var(--color-triad-1) 49%, black 50%, var(--color-triad-1) 51%)',
                      }}
                    ></div>
                  </td>
                </tr>
                {/* 温水供給設備供給熱量 */}
                <tr className="table-carryrow" style={{ display: resultUsageDisplay['hot_water_supply_heating_value'] ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14">
                    {addComma(calcMathRound(sumArray(itemDraftVolumes(resultEditResource.month, resultEditResource.day, 'hot_water_supply_heating_value')), HOT_WATER_BLUR_DIGIT))}
                  </td>
                </tr>
                {/* 炉別運転日数 */}
                {Array.from({ length: sessionResource.furnacesCount + 1 }).map((_, runIndex) => (
                  <tr
                    className="table-carryrow"
                    style={{ display: isMonthDisplay() && resultUsageDisplay['furnace_operating_days'] && showOperationDaysVisible ? 'table-row' : 'none' }}
                  >
                    <td className="table-carrydoublecell-14">{resultMonitorMonthSumResource.furnace_operating_days[runIndex]}</td>
                  </tr>
                ))}
              </tbody>
              {/* ##### 合計情報（計画立案） テーブル表示・入力管理 #### */}
              <tbody style={{ display: existAutoDraft ? 'table-row-group' : 'none' }}>
                <tr className="table-carryrow" style={{ display: isSelectDraft.some((value) => value) ? 'table-row' : 'none' }}>
                  <td className="table-carrycell p-0">
                    <div
                      style={{
                        width: '100%',
                        height: '100%',
                        backgroundImage: 'linear-gradient(35deg, var(--color-triad-2) 49%, black 50%, var(--color-triad-2) 51%)',
                      }}
                    ></div>
                  </td>
                </tr>
                {/* 搬入量[t] */}
                <tr className="table-carryrow" style={{ display: isSelectDraft.some((value) => value) && draftUsageDisplay['carry_volume'] ? 'table-row' : 'none' }}>
                  <td className="table-carrycell">
                    {draftLength > 0
                      ? isMonthDisplay()
                        ? draftEditResource[0]
                          ? addComma(calcMathRound(calcItemSum(draftEditResource[0].month, 'carry_volume'), CARRY_BLUR_DIGIT))
                          : ''
                        : addComma(calcMathRound(calcItemSum(getMonthData(draftEditResource[0].day), 'carry_volume'), CARRY_BLUR_DIGIT))
                      : ''}
                  </td>
                </tr>
                {/* 可燃ごみ搬入量[t] */}
                <tr
                  className="table-carryrow"
                  style={{ display: isSelectDraft.some((value) => value) && draftUsageDisplay['combustible_waste_carry_volume'] ? 'table-row' : 'none' }}
                >
                  <td className="table-carrydoublecell-14">
                    {draftLength > 0
                      ? addComma(
                          calcMathRound(sumArray(itemDraftVolumes(draftEditResource[0].month, draftEditResource[0].day, 'combustible_waste_carry_volume')), CARRY_BLUR_DIGIT),
                        )
                      : ''}
                  </td>
                </tr>
                {/* 剪定枝等搬入量[t] */}
                <tr
                  className="table-carryrow"
                  style={{ display: isSelectDraft.some((value) => value) && draftUsageDisplay['pruning_branches_carry_volume'] ? 'table-row' : 'none' }}
                >
                  <td className="table-carrydoublecell-14">
                    {draftLength > 0
                      ? addComma(calcMathRound(sumArray(itemDraftVolumes(draftEditResource[0].month, draftEditResource[0].day, 'pruning_branches_carry_volume')), CARRY_BLUR_DIGIT))
                      : ''}
                  </td>
                </tr>
                {/* 小動物搬入量[t] */}
                <tr className="table-carryrow" style={{ display: isSelectDraft.some((value) => value) && draftUsageDisplay['small_animals_carry_volume'] ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14">
                    {draftLength > 0
                      ? addComma(calcMathRound(sumArray(itemDraftVolumes(draftEditResource[0].month, draftEditResource[0].day, 'small_animals_carry_volume')), CARRY_BLUR_DIGIT))
                      : ''}
                  </td>
                </tr>
                {/* 可燃粗大ごみ搬入量[t] */}
                <tr
                  className="table-carryrow"
                  style={{ display: isSelectDraft.some((value) => value) && draftUsageDisplay['combustible_bulk_waste_carry_volume'] ? 'table-row' : 'none' }}
                >
                  <td className="table-carrydoublecell-14">
                    {draftLength > 0
                      ? addComma(
                          calcMathRound(sumArray(itemDraftVolumes(draftEditResource[0].month, draftEditResource[0].day, 'combustible_bulk_waste_carry_volume')), CARRY_BLUR_DIGIT),
                        )
                      : ''}
                  </td>
                </tr>
                {/* 破砕ごみ残渣等搬入量[t] */}
                <tr
                  className="table-carryrow"
                  style={{ display: isSelectDraft.some((value) => value) && draftUsageDisplay['shredded_waste_residue_carry_volume'] ? 'table-row' : 'none' }}
                >
                  <td className="table-carrydoublecell-14">
                    {draftLength > 0
                      ? addComma(
                          calcMathRound(sumArray(itemDraftVolumes(draftEditResource[0].month, draftEditResource[0].day, 'shredded_waste_residue_carry_volume')), CARRY_BLUR_DIGIT),
                        )
                      : ''}
                  </td>
                </tr>
                {/* 災害ごみ組合内搬入量[t] */}
                <tr
                  className="table-carryrow"
                  style={{ display: isSelectDraft.some((value) => value) && draftUsageDisplay['disaster_waste_internal_carry_volume'] ? 'table-row' : 'none' }}
                >
                  <td className="table-carrydoublecell-14">
                    {draftLength > 0
                      ? addComma(
                          calcMathRound(sumArray(itemDraftVolumes(draftEditResource[0].month, draftEditResource[0].day, 'disaster_waste_internal_carry_volume')), CARRY_BLUR_DIGIT),
                        )
                      : ''}
                  </td>
                </tr>
                {/* 災害ごみ組合外搬入量[t] */}
                <tr
                  className="table-carryrow"
                  style={{ display: isSelectDraft.some((value) => value) && draftUsageDisplay['disaster_waste_external_carry_volume'] ? 'table-row' : 'none' }}
                >
                  <td className="table-carrydoublecell-14">
                    {draftLength > 0
                      ? addComma(
                          calcMathRound(sumArray(itemDraftVolumes(draftEditResource[0].month, draftEditResource[0].day, 'disaster_waste_external_carry_volume')), CARRY_BLUR_DIGIT),
                        )
                      : ''}
                  </td>
                </tr>
                {/* その他搬入量[t] */}
                <tr className="table-carryrow" style={{ display: isSelectDraft.some((value) => value) && draftUsageDisplay['other_carry_volume'] ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14">
                    {draftLength > 0
                      ? addComma(calcMathRound(sumArray(itemDraftVolumes(draftEditResource[0].month, draftEditResource[0].day, 'other_carry_volume')), CARRY_BLUR_DIGIT))
                      : ''}
                  </td>
                </tr>
                {/* 焼却施設底ごみ搬入量[t] */}
                <tr
                  className="table-carryrow"
                  style={{ display: isSelectDraft.some((value) => value) && draftUsageDisplay['incineration_facility_bottom_ash_carry_volume'] ? 'table-row' : 'none' }}
                >
                  <td className="table-carrydoublecell-14">
                    {draftLength > 0
                      ? addComma(
                          calcMathRound(
                            sumArray(itemDraftVolumes(draftEditResource[0].month, draftEditResource[0].day, 'incineration_facility_bottom_ash_carry_volume')),
                            CARRY_BLUR_DIGIT,
                          ),
                        )
                      : ''}
                  </td>
                </tr>
                {/* 資源物焼却処分搬入量[t] */}
                <tr className="table-carryrow" style={{ display: isSelectDraft.some((value) => value) && draftUsageDisplay['recyclables_carry_volume'] ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14">
                    {draftLength > 0
                      ? addComma(calcMathRound(sumArray(itemDraftVolumes(draftEditResource[0].month, draftEditResource[0].day, 'recyclables_carry_volume')), CARRY_BLUR_DIGIT))
                      : ''}
                  </td>
                </tr>
                {/* 搬入量合計[t] */}
                <tr className="table-carryrow" style={{ display: isSelectDraft.some((value) => value) && draftUsageDisplay['total_carry_volume'] ? 'table-row' : 'none' }}>
                  <td className="table-carrydoublecell-14">{draftLength > 0 ? addComma(calcMathRound(draftMonitorSumResource[0].total_carry_volume, CARRY_BLUR_DIGIT)) : ''}</td>
                </tr>
                {/* 各案 */}
                {Array.from({ length: draftLength }).map((_, draftIndex) => (
                  <>
                    <tr className="table-carryrow" style={{ display: isSelectDraft[draftIndex] ? 'table-row' : 'none' }}>
                      <td className="table-carrycell p-0">
                        <div
                          style={{
                            width: '100%',
                            height: '100%',
                            backgroundImage: `linear-gradient(35deg, var(--color-plan-${draftIndex + 1}) 49%, black 50%, var(--color-plan-${draftIndex + 1}) 51%)`,
                          }}
                        ></div>
                      </td>
                    </tr>
                    {/* 各炉の状態 */}
                    {Array.from({ length: sessionResource.furnacesCount }).map((_, furnacesIndex) => (
                      <>
                        {/* 焼却量[t] */}
                        <tr className="table-carryrow" style={{ display: isSelectDraft[draftIndex] ? 'table-row' : 'none' }}>
                          <td className="table-carrycell">
                            {isMonthDisplay()
                              ? addComma(calcMathRound(calcItemBurnSum(draftEditResource[draftIndex].month, furnacesIndex), BURNVOLUME_BLUR_DIGIT))
                              : addComma(calcMathRound(calcItemBurnSum(getMonthData(draftEditResource[draftIndex].day), furnacesIndex), BURNVOLUME_BLUR_DIGIT))}
                          </td>
                        </tr>
                        {/* 焼却負荷率(停炉日を含まない)[%] */}
                        <tr className="table-carryrow" style={{ display: draftUsageDisplay['burn_load'] && isSelectDraft[draftIndex] && isMonthDisplay() ? 'table-row' : 'none' }}>
                          <td className="table-carrycell">
                            {isMonthDisplay() ? addComma(calcMathFloor(draftMonitorMonthSumResource[draftIndex].burn_load[furnacesIndex], BURNLOAD_DIGIT)) : ''}
                          </td>
                        </tr>
                        {/* 焼却負荷率(停炉日を含む)[%] */}
                        <tr
                          className="table-carryrow"
                          style={{ display: draftUsageDisplay['burn_load_include_shutdown'] && isSelectDraft[draftIndex] && isMonthDisplay() ? 'table-row' : 'none' }}
                        >
                          <td className="table-carrycell">
                            {isMonthDisplay() ? addComma(calcMathFloor(draftMonitorMonthSumResource[draftIndex].burn_load_include_shutdown[furnacesIndex], BURNLOAD_DIGIT)) : ''}
                          </td>
                        </tr>
                        {/* 運転日数[日] */}
                        <tr className="table-carryrow" style={{ display: isSelectDraft[draftIndex] && isMonthDisplay() ? 'table-row' : 'none' }}>
                          <td className="table-carrydoublecell-14">{draftMonitorMonthSumResource[draftIndex].operating_days[furnacesIndex]}</td>
                        </tr>
                        {/* ごみ低位発熱量 */}
                        <tr className="table-carryrow" style={{ display: isSelectDraft[draftIndex] && draftUsageDisplay['waste_lower_heating_value'] ? 'table-row' : 'none' }}>
                          <td className="table-carrydoublecell-14">
                            {addComma(
                              calcMathRound(
                                sumArray(burnDraftVolumes(draftEditResource[draftIndex].month, draftEditResource[draftIndex].day, 'waste_lower_heating_value', furnacesIndex)),
                                WASTE_BLUR_DIGIT,
                              ),
                            )}
                          </td>
                        </tr>
                      </>
                    ))}
                    {/* 全炉 */}
                    {/* 焼却量[t] */}
                    <tr className="table-carryrow" style={{ display: isSelectDraft[draftIndex] ? 'table-row' : 'none' }}>
                      <td className="table-carrycell">
                        {isMonthDisplay()
                          ? addComma(
                              calcMathRound(
                                Array.from({ length: sessionResource.furnacesCount }, (_, furnacesIndex) =>
                                  calcItemBurnSum(draftEditResource[draftIndex].month, furnacesIndex),
                                ).reduce((sum, value) => sum + value, 0),
                                BURNVOLUME_BLUR_DIGIT,
                              ),
                            )
                          : addComma(
                              calcMathRound(
                                Array.from({ length: sessionResource.furnacesCount }, (_, furnacesIndex) =>
                                  calcItemBurnSum(getMonthData(draftEditResource[draftIndex].day), furnacesIndex),
                                ).reduce((sum, value) => sum + value, 0),
                                BURNVOLUME_BLUR_DIGIT,
                              ),
                            )}
                      </td>
                    </tr>
                    {/* 焼却負荷率(停炉日を含まない)[%] */}
                    <tr className="table-carryrow" style={{ display: draftUsageDisplay['burn_load'] && isSelectDraft[draftIndex] && isMonthDisplay() ? 'table-row' : 'none' }}>
                      <td className="table-carrycell">{isMonthDisplay() ? addComma(calcMathFloor(draftMonitorMonthSumResource[draftIndex].burn_load_sum, BURNLOAD_DIGIT)) : ''}</td>
                    </tr>
                    {/* 焼却負荷率(停炉日を含む)[%] */}
                    <tr
                      className="table-carryrow"
                      style={{ display: draftUsageDisplay['burn_load_include_shutdown'] && isSelectDraft[draftIndex] && isMonthDisplay() ? 'table-row' : 'none' }}
                    >
                      <td className="table-carrycell">
                        {isMonthDisplay() ? addComma(calcMathFloor(draftMonitorMonthSumResource[draftIndex].burn_load_include_shutdown_sum, BURNLOAD_DIGIT)) : ''}
                      </td>
                    </tr>
                    {/* ピット残量[t] */}
                    <tr className="table-carryrow" style={{ display: isSelectDraft[draftIndex] && !isMonthDisplay() ? 'table-row' : 'none' }}>
                      <td className="table-carrydoublecell-14 p-0">
                        <div
                          style={{
                            height: '100%',
                            width: '100%',
                            backgroundImage: `linear-gradient(35deg, var(--color-plan-${draftIndex + 1}) 49%, black 50%, var(--color-plan-${draftIndex + 1}) 51%)`,
                          }}
                        ></div>
                      </td>
                    </tr>
                    {/* 主灰発生量[t] */}
                    <tr
                      className="table-carryrow"
                      style={{ display: isSelectDraft[draftIndex] && draftUsageDisplay['main_ash_generate'] && showAshVisible ? 'table-row' : 'none' }}
                    >
                      <td className="table-carrydoublecell-14">
                        {addComma(
                          calcMathRound(sumArray(itemDraftVolumes(draftEditResource[draftIndex].month, draftEditResource[draftIndex].day, 'main_ash_generate')), ASH_BLUR_DIGIT),
                        )}
                      </td>
                    </tr>
                    {/* 主灰搬出台数[台] */}
                    <tr className="table-carryrow" style={{ display: draftUsageDisplay['main_ash_carry_out_units'] && showAshVisible ? 'table-row' : 'none' }}>
                      <td className="table-carrydoublecell-14">
                        {addComma(
                          calcMathRound(
                            sumArray(itemDraftVolumes(draftEditResource[draftIndex].month, draftEditResource[draftIndex].day, 'main_ash_carry_out_units')),
                            ASH_BLUR_DIGIT,
                          ),
                        )}
                      </td>
                    </tr>
                    {/* 主灰搬出量[t] */}
                    <tr
                      className="table-carryrow"
                      style={{ display: isSelectDraft[draftIndex] && draftUsageDisplay['main_ash_carry_out'] && showAshVisible ? 'table-row' : 'none' }}
                    >
                      <td className="table-carrydoublecell-14">
                        {addComma(
                          calcMathRound(sumArray(itemDraftVolumes(draftEditResource[draftIndex].month, draftEditResource[draftIndex].day, 'main_ash_carry_out')), ASH_BLUR_DIGIT),
                        )}
                      </td>
                    </tr>
                    {/* 主灰ピット残量[t] */}
                    <tr className="table-carryrow" style={{ display: showAshVisible && !isMonthDisplay() ? 'table-row' : 'none' }}>
                      <td className="table-carrydoublecell-14 p-0">
                        <div
                          style={{
                            height: '100%',
                            width: '100%',
                            backgroundImage: `linear-gradient(35deg, var(--color-plan-${draftIndex + 1}) 49%, black 50%, var(--color-plan-${draftIndex + 1}) 51%)`,
                          }}
                        ></div>
                      </td>
                    </tr>
                    {/* 飛灰発生量[t] */}
                    <tr className="table-carryrow" style={{ display: isSelectDraft[draftIndex] && draftUsageDisplay['fly_ash_generate'] && showAshVisible ? 'table-row' : 'none' }}>
                      <td className="table-carrydoublecell-14">
                        {addComma(
                          calcMathRound(sumArray(itemDraftVolumes(draftEditResource[draftIndex].month, draftEditResource[draftIndex].day, 'fly_ash_generate')), ASH_BLUR_DIGIT),
                        )}
                      </td>
                    </tr>
                    {/* 飛灰搬出台数[台] */}
                    <tr className="table-carryrow" style={{ display: draftUsageDisplay['fly_ash_carry_out_units'] && showAshVisible ? 'table-row' : 'none' }}>
                      <td className="table-carrydoublecell-14">
                        {addComma(
                          calcMathRound(
                            sumArray(itemDraftVolumes(draftEditResource[draftIndex].month, draftEditResource[draftIndex].day, 'fly_ash_carry_out_units')),
                            ASH_BLUR_DIGIT,
                          ),
                        )}
                      </td>
                    </tr>
                    {/* 飛灰搬出量[t] */}
                    <tr
                      className="table-carryrow"
                      style={{ display: isSelectDraft[draftIndex] && draftUsageDisplay['fly_ash_carry_out'] && showAshVisible ? 'table-row' : 'none' }}
                    >
                      <td className="table-carrydoublecell-14">
                        {addComma(
                          calcMathRound(sumArray(itemDraftVolumes(draftEditResource[draftIndex].month, draftEditResource[draftIndex].day, 'fly_ash_carry_out')), ASH_BLUR_DIGIT),
                        )}
                      </td>
                    </tr>
                    {/* 飛灰処理物搬出量 乾灰[t] */}
                    <tr
                      className="table-carryrow"
                      style={{ display: isSelectDraft[draftIndex] && draftUsageDisplay['dry_fly_ash_carry_out'] && showAshVisible ? 'table-row' : 'none' }}
                    >
                      <td className="table-carrydoublecell-14">
                        {addComma(
                          calcMathRound(
                            sumArray(itemDraftVolumes(draftEditResource[draftIndex].month, draftEditResource[draftIndex].day, 'dry_fly_ash_carry_out')),
                            ASH_BLUR_DIGIT,
                          ),
                        )}
                      </td>
                    </tr>
                    {/* 飛灰処理物搬出量 処理灰[t] */}
                    <tr
                      className="table-carryrow"
                      style={{ display: isSelectDraft[draftIndex] && draftUsageDisplay['processed_fly_ash_carry_out'] && showAshVisible ? 'table-row' : 'none' }}
                    >
                      <td className="table-carrydoublecell-14">
                        {addComma(
                          calcMathRound(
                            sumArray(itemDraftVolumes(draftEditResource[draftIndex].month, draftEditResource[draftIndex].day, 'processed_fly_ash_carry_out')),
                            ASH_BLUR_DIGIT,
                          ),
                        )}
                      </td>
                    </tr>
                    {/* 飛灰ピット残量[t] */}
                    <tr className="table-carryrow" style={{ display: showAshVisible && !isMonthDisplay() ? 'table-row' : 'none' }}>
                      <td className="table-carrydoublecell-14 p-0">
                        <div
                          style={{
                            height: '100%',
                            width: '100%',
                            backgroundImage: `linear-gradient(35deg, var(--color-plan-${draftIndex + 1}) 49%, black 50%, var(--color-plan-${draftIndex + 1}) 51%)`,
                          }}
                        ></div>
                      </td>
                    </tr>
                    {/* 温水供給設備供給熱量 */}
                    <tr className="table-carryrow" style={{ display: isSelectDraft[draftIndex] && draftUsageDisplay['hot_water_supply_heating_value'] ? 'table-row' : 'none' }}>
                      <td className="table-carrydoublecell-14">
                        {addComma(
                          calcMathRound(
                            sumArray(itemDraftVolumes(draftEditResource[draftIndex].month, draftEditResource[draftIndex].day, 'hot_water_supply_heating_value')),
                            HOT_WATER_BLUR_DIGIT,
                          ),
                        )}
                      </td>
                    </tr>
                    {/* 炉別運転日数 */}
                    {Array.from({ length: sessionResource.furnacesCount + 1 }).map((_, runIndex) => (
                      <tr
                        className="table-carryrow"
                        style={{
                          display: isSelectDraft[draftIndex] && draftUsageDisplay['furnace_operating_days'] && isMonthDisplay() && showOperationDaysVisible ? 'table-row' : 'none',
                        }}
                      >
                        <td className="table-carrydoublecell-14">{draftMonitorMonthSumResource[draftIndex].furnace_operating_days[runIndex]}</td>
                      </tr>
                    ))}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="indent-planresult" style={{ marginTop: '5px' }}>
        <div className="titlerow-planresult" style={{ display: existPlanDraft || existResultDraft || existAutoDraft ? 'inline-block' : 'none' }}>
          <div className="itemname-planresult">
            <div className="itemname-back-planresult"></div>
            ごみピット残量
          </div>
          <div
            style={{
              display: displayTrashAcceptableAmountDate !== '' ? 'flex' : 'none',
              marginLeft: '570px',
              fontSize: '24px',
              fontWeight: 'bold',
            }}
          >{`${displayTrashAcceptableAmountDate.length === 1 ? ' ' : ''}${displayTrashAcceptableAmountDate}:00 時点 ごみ受け入れ可能量：${calcMathRound(
            trashAcceptableAmount.percent,
            0,
          )}% (${calcMathRound(trashAcceptableAmount.value, 0).toLocaleString()}t)`}</div>
        </div>
        <div className="row-planresult" style={{ display: existPlanDraft || existResultDraft || existAutoDraft ? 'inline-block' : 'none' }}>
          {Object.keys(pitGraphProps.trash_pit_rest).length > 0 ? (
            <LineChart canvas={pitGraphProps.trash_pit_rest.canvas} lines={pitGraphProps.trash_pit_rest.lines} grid={pitGraphProps.trash_pit_rest.grid} />
          ) : (
            <></>
          )}
        </div>
      </div>

      <div
        className="indent-planresult"
        style={{ display: showInitialPlanVisible || isPlanVisible() || isResultVisible() || isSelectDraft.some((value) => value) ? 'inline-block' : 'none', marginTop: '5px' }}
      >
        <div className="titlerow-planresult" style={{ display: showInitialPlanVisible ? 'inline-block' : 'none' }}>
          <div className="itemname-planresult">
            <div className="itemname-back-planresult" style={{ background: COLOR_CODE.INITIAL }}></div>
            立上下日
          </div>
        </div>
        <div className="row-planresult" style={{ display: showInitialPlanVisible ? 'flex' : 'none' }}>
          <div className="flex-column">
            {Array.from({ length: sessionResource.furnacesCount }).map((_, furnacesIndex) => (
              <>
                <div className="graph-planresult" style={{ cursor: 'inherit' }}>
                  <div className="column-title-initial-plan">{String(furnacesIndex + 1).concat('号炉')}</div>
                  <div className="column-title-initial-plan">{`運転・停止\n期初計画`}</div>
                  <div className="column-item-planresult">
                    <div className="column-value-initial-plan">立上日</div>
                    <div className="column-value-initial-plan">立下日</div>
                  </div>
                  <div
                    style={{
                      position: 'relative',
                      maxHeight: '100px',
                      width: '938px',
                      border: 'solid 1px black',
                    }}
                  >
                    <BurnGraphBackground dayCount={displayDays} monthIndex={selectMonthIndex} highlightDays={initialPlanEditResource.shutdown} displayYear={displayYear} />
                    <div style={{ overflow: 'hidden', maxHeight: '50px' }}>
                      <MarkDateGraph
                        dayLength={isMonthDisplay() ? sumArray(displayDays) : displayDays[selectMonthIndex - 1]}
                        dayStr={shutDownSums(item_launch_date, initialPlanEditResource.shutdown[furnacesIndex])}
                        directionY="Bottom"
                        dayCount={displayDays}
                        monthIndex={selectMonthIndex}
                      />
                    </div>
                    <div style={{ overflow: 'hidden', maxHeight: '50px', width: '875px', border: '1px black solid' }}>
                      <BurnGraph2
                        dayCount={isMonthDisplay() ? sumArray(displayDays) : displayDayCountOfMonth}
                        planArray={burnDraftDaysVolumes(initialPlanEditResource.day, 'burn_volume', furnacesIndex)}
                        rateCapacity={conditionResource.rateCapacity}
                        warningDays={
                          isMonthDisplay() ? initialPlanWarningVals[furnacesIndex] : initialPlanWarningVals[furnacesIndex].slice(lastDayOfLastMonth, daySum(selectMonthIndex))
                        }
                      ></BurnGraph2>
                    </div>
                    <div style={{ overflow: 'hidden', maxHeight: '50px' }}>
                      <MarkDateGraph
                        dayLength={isMonthDisplay() ? sumArray(displayDays) : displayDays[selectMonthIndex - 1]}
                        dayStr={shutDownSums(item_shutdown_date, initialPlanEditResource.shutdown[furnacesIndex])}
                        directionY="Top"
                        dayCount={displayDays}
                        monthIndex={selectMonthIndex}
                      />
                    </div>
                  </div>
                </div>
              </>
            ))}
          </div>
        </div>

        <div className="titlerow-planresult" style={{ display: isPlanVisible() ? 'inline-block' : 'none' }}>
          <div className="itemname-planresult">
            <div className="itemname-back-planresult" style={{ background: COLOR_CODE.PLAN }}></div>
            立上下日変更
          </div>
        </div>
        <div className="row-planresult" style={{ display: isPlanVisible() ? 'flex' : 'none' }}>
          <div className="flex-column">
            {Array.from({ length: sessionResource.furnacesCount }).map((_, furnacesIndex) => (
              <>
                <div style={{ display: 'flex', alignItems: 'end' }}>
                  <Button
                    className="button-2-planresult width-17 ml-auto mt-1 mr-0 mb-1"
                    variant="outline-secondary"
                    size="sm"
                    value={'前に戻る'}
                    onClick={(event) =>
                      handleInputUndoDateClick(planEditResourceState, planShutdownRecordState, planWarningValsState, planInitResource, planEmptyCheck, planUsageCalc, furnacesIndex)
                    }
                    style={{ display: isMonthDisplay() && planShutdownVisible[furnacesIndex] ? 'inline-block' : 'none' }}
                    disabled={planHistory[furnacesIndex].length === 0}
                  >
                    前に戻る
                  </Button>
                  <Button
                    className="button-2-planresult width-17 ml-0 mt-1 mr-0 mb-1"
                    variant="outline-secondary"
                    size="sm"
                    value={'追加'}
                    onClick={() => handleInputAddDateClick(planShutdownInputVisibleState, furnacesIndex)}
                    style={{
                      justifyContent: 'end',
                      display: isMonthDisplay() && planShutdownVisible[furnacesIndex] ? 'inline-block' : 'none',
                    }}
                    disabled={planShutdownInputVisible[furnacesIndex]}
                  >
                    追加
                  </Button>
                </div>
                <div className="graph-planresult" onClick={() => handleShutdownClick(planShutdownVisibleState, furnacesIndex)} style={{ marginBottom: '10px' }}>
                  <div className="column-title-planresult">{String(furnacesIndex + 1).concat('号炉')}</div>
                  <div className="column-title-planresult">{`運転・停止\n計画`}</div>
                  <div className="column-item-planresult">
                    <div className="column-value-planresult">立上日</div>
                    <div className="column-value-planresult">立下日</div>
                  </div>
                  <div
                    style={{
                      position: 'relative',
                      maxHeight: '100px',
                      width: '938px',
                      border: 'solid 1px black',
                    }}
                  >
                    <BurnGraphBackground dayCount={displayDays} monthIndex={selectMonthIndex} highlightDays={planEditResource.shutdown} displayYear={displayYear} />
                    <div style={{ overflow: 'hidden', maxHeight: '50px' }}>
                      <MarkDateGraph
                        dayLength={isMonthDisplay() ? sumArray(displayDays) : displayDays[selectMonthIndex - 1]}
                        dayStr={shutDownSums(item_launch_date, planEditResource.shutdown[furnacesIndex])}
                        directionY="Bottom"
                        dayCount={displayDays}
                        monthIndex={selectMonthIndex}
                      />
                    </div>
                    <div style={{ overflow: 'hidden', maxHeight: '50px', width: '875px', border: '1px black solid' }}>
                      <BurnGraph2
                        dayCount={isMonthDisplay() ? sumArray(displayDays) : displayDayCountOfMonth}
                        planArray={burnDraftDaysVolumes(planEditResource.day, 'burn_volume', furnacesIndex)}
                        rateCapacity={conditionResource.rateCapacity}
                        warningDays={isMonthDisplay() ? planWarningVals[furnacesIndex] : planWarningVals[furnacesIndex].slice(lastDayOfLastMonth, daySum(selectMonthIndex))}
                      ></BurnGraph2>
                    </div>
                    <div style={{ overflow: 'hidden', maxHeight: '50px' }}>
                      <MarkDateGraph
                        dayLength={isMonthDisplay() ? sumArray(displayDays) : displayDays[selectMonthIndex - 1]}
                        dayStr={shutDownSums(item_shutdown_date, planEditResource.shutdown[furnacesIndex])}
                        directionY="Top"
                        dayCount={displayDays}
                        monthIndex={selectMonthIndex}
                      />
                    </div>
                  </div>
                </div>
                <div className="mh-1" style={{ display: planShutdownVisible[furnacesIndex] ? 'flex' : 'none', overflowX: 'auto', width: '1100px', marginBottom: '10px' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', width: '60px', margin: '8px 70px 0px 0px' }}>
                        {/* 期間行 */}
                        <div style={{ marginBottom: '19px' }}>　</div>
                        <div className="itemname-planresult" style={{ flexBasis: '0rem' }}>
                          <div className="itemname-back-planresult"></div>
                          立下日
                        </div>
                        <div style={{ marginBottom: '5px' }}></div>
                        <div className="itemname-planresult" style={{ flexBasis: '0rem' }}>
                          <div className="itemname-back-planresult"></div>
                          立上日
                        </div>
                        {/* エラー行 */}
                        <div></div>
                      </div>
                      {/* 追加ボタン押下後の要素 */}
                      {isMonthDisplay() && planShutdownInputVisible[furnacesIndex] && (
                        <div className="date-frame">
                          <div className="date-border">
                            {/* 期間 */}
                            <div className="date-period">
                              ●期間1
                              {/* 削除ボタン */}
                              <Button
                                className="button-2-planresult"
                                variant="outline-secondary"
                                size="sm"
                                style={{ marginLeft: '19px' }}
                                value={'削除'}
                                onClick={() => handleInputDeleteDateClick(planInputShutdownState, planShutdownInputVisibleState, furnacesIndex)}
                              >
                                削除
                              </Button>
                            </div>
                            {/* 立下日 */}
                            <CustomDatePicker
                              className="dateinput-planresult"
                              minDate={new Date(getInputableMinDate(displayYear, displayDays, planEmptyCheck.day, systemDateIndex))}
                              maxDate={new Date(getMaxDate(displayYear))}
                              selected={planInputShutdown[furnacesIndex].shutdown_date ? new Date(planInputShutdown[furnacesIndex].shutdown_date) : null}
                              onChange={(date: Date | null) =>
                                handleInputDateChange(
                                  planEditResourceState,
                                  planShutdownRecordState,
                                  planInputShutdownState,
                                  planShutdownInputVisibleState,
                                  planWarningValsState,
                                  planInitResource,
                                  planEmptyCheck,
                                  planUsageCalc,
                                  item_shutdown_date,
                                  date,
                                  furnacesIndex,
                                )
                              }
                              dateFormat={`${DATE_FORMAT_MMDD_SLASH}(${WEEK_DAYS[new Date(planInputShutdown[furnacesIndex].shutdown_date).getDay()]})`}
                              portalId="datepicker-portal"
                              locale={ja}
                              // キーボード入力の際は、年を含めて打たないと今年と解釈されるため、プレースホルダーは年を残す
                              placeholderText="年/月/日(曜日)"
                            />
                            <div style={{ marginBottom: '5px' }}></div>

                            {/* 立上日 */}
                            <CustomDatePicker
                              className="dateinput-planresult-launch-style"
                              minDate={new Date(getInputableMinDate(displayYear, displayDays, planEmptyCheck.day, systemDateIndex))}
                              maxDate={new Date(getMaxDate(displayYear))}
                              selected={planInputShutdown[furnacesIndex].launch_date ? new Date(planInputShutdown[furnacesIndex].launch_date) : null}
                              onChange={(date: Date | null) =>
                                handleInputDateChange(
                                  planEditResourceState,
                                  planShutdownRecordState,
                                  planInputShutdownState,
                                  planShutdownInputVisibleState,
                                  planWarningValsState,
                                  planInitResource,
                                  planEmptyCheck,
                                  planUsageCalc,
                                  item_launch_date,
                                  date,
                                  furnacesIndex,
                                )
                              }
                              dateFormat={`${DATE_FORMAT_MMDD_SLASH}(${WEEK_DAYS[new Date(planInputShutdown[furnacesIndex].launch_date).getDay()]})`}
                              portalId="datepicker-portal"
                              disabled={planInputShutdown[furnacesIndex].shutdown_date === ''}
                              locale={ja}
                              // キーボード入力の際は、年を含めて打たないと今年と解釈されるため、プレースホルダーは年を残す
                              placeholderText="年/月/日(曜日)"
                            />
                          </div>
                          {/* エラー文 */}
                          <div style={{ display: isMonthDisplay() ? 'flex' : 'none', color: 'red' }}>
                            {planEditResource.shutdown && planEditResource.shutdown.length > 0 && planEditResource.shutdown[furnacesIndex]
                              ? getErrMassage(planInputShutdown[furnacesIndex], [...planEditResource.shutdown[furnacesIndex], planInputShutdown[furnacesIndex]]) !== ''
                                ? getErrMassage(planInputShutdown[furnacesIndex], [...planEditResource.shutdown[furnacesIndex], planInputShutdown[furnacesIndex]])
                                : ''
                              : ''}
                          </div>
                        </div>
                      )}

                      {/* 追加ボタン押下前の要素 */}
                      {Array.from({ length: planEditResource.shutdown[furnacesIndex] ? planEditResource.shutdown[furnacesIndex].length : 0 }).map((_, dataIndex) => (
                        <div key={dataIndex} className="date-frame">
                          <div
                            className="date-border"
                            style={{ backgroundColor: planEditResource.shutdown[furnacesIndex][dataIndex].not_change_flag ? 'rgba(255, 0, 47, 0.2)' : 'transparent' }}
                          >
                            {/* 期間 */}
                            <div className="date-period">
                              ●期間{!(isMonthDisplay() && planShutdownInputVisible[furnacesIndex]) ? dataIndex + 1 : dataIndex + 2}
                              {/* 削除ボタン */}
                              <Button
                                className="button-2-planresult white-bg-button"
                                variant="outline-secondary"
                                size="sm"
                                onClick={() =>
                                  handleDeleteDateClick(
                                    planEditResourceState,
                                    planShutdownRecordState,
                                    planWarningValsState,
                                    planInitResource,
                                    planEmptyCheck,
                                    planUsageCalc,
                                    furnacesIndex,
                                    dataIndex,
                                  )
                                }
                                onMouseDown={(e) => e.preventDefault()}
                                style={{ display: isMonthDisplay() ? 'inline-block' : 'none', marginLeft: '19px' }}
                              >
                                削除
                              </Button>
                            </div>
                            {/* 立下日 */}
                            <CustomDatePicker
                              className="dateinput-planresult"
                              disabled={
                                (inShutdownPeriod(planEditResource.shutdown[furnacesIndex][dataIndex], item_shutdown_date) ||
                                  planEditResource.shutdown[furnacesIndex][dataIndex].shutdown_date === '') &&
                                systemDateIndex <= getDateIndex(planEditResource.shutdown[furnacesIndex][dataIndex].shutdown_date, displayYear, displayDays) &&
                                isMonthDisplay()
                                  ? false
                                  : true
                              }
                              minDate={new Date(getInputableMinDate(displayYear, displayDays, planEmptyCheck.day, systemDateIndex))}
                              maxDate={new Date(getMaxDate(displayYear))}
                              selected={
                                planEditResource.shutdown[furnacesIndex][dataIndex].shutdown_date
                                  ? new Date(planEditResource.shutdown[furnacesIndex][dataIndex].shutdown_date)
                                  : null
                              }
                              onChange={(date: Date | null) => {
                                handleDateChange(
                                  planEditResourceState,
                                  planShutdownRecordState,
                                  planWarningValsState,
                                  planInitResource,
                                  planEmptyCheck,
                                  planUsageCalc,
                                  item_shutdown_date,
                                  date,
                                  furnacesIndex,
                                  dataIndex,
                                );
                              }}
                              dateFormat={`${DATE_FORMAT_MMDD_SLASH}(${WEEK_DAYS[new Date(planEditResource.shutdown[furnacesIndex][dataIndex].shutdown_date).getDay()]})`}
                              portalId="datepicker-portal"
                              locale={ja}
                              // キーボード入力の際は、年を含めて打たないと今年と解釈されるため、プレースホルダーは年を残す
                              placeholderText="年/月/日(曜日)"
                            />

                            <div style={{ marginBottom: '5px' }}></div>

                            {/* 立上日 */}
                            <CustomDatePicker
                              className="dateinput-planresult-launch-style"
                              disabled={
                                ((inShutdownPeriod(planEditResource.shutdown[furnacesIndex][dataIndex], item_launch_date) &&
                                  systemDateIndex <= getDateIndex(planEditResource.shutdown[furnacesIndex][dataIndex].launch_date, displayYear, displayDays)) ||
                                  planEditResource.shutdown[furnacesIndex][dataIndex].launch_date === '') &&
                                isMonthDisplay()
                                  ? false
                                  : true
                              }
                              minDate={new Date(getInputableMinDate(displayYear, displayDays, planEmptyCheck.day, systemDateIndex))}
                              maxDate={new Date(getMaxDate(displayYear))}
                              selected={
                                planEditResource.shutdown[furnacesIndex][dataIndex].launch_date ? new Date(planEditResource.shutdown[furnacesIndex][dataIndex].launch_date) : null
                              }
                              onChange={(date: Date | null) => {
                                handleDateChange(
                                  planEditResourceState,
                                  planShutdownRecordState,
                                  planWarningValsState,
                                  planInitResource,
                                  planEmptyCheck,
                                  planUsageCalc,
                                  item_launch_date,
                                  date,
                                  furnacesIndex,
                                  dataIndex,
                                );
                              }}
                              dateFormat={`${DATE_FORMAT_MMDD_SLASH}(${WEEK_DAYS[new Date(planEditResource.shutdown[furnacesIndex][dataIndex].launch_date).getDay()]})`}
                              portalId="datepicker-portal"
                              locale={ja}
                              placeholderText="年/月/日(曜日)"
                            />
                          </div>

                          {/* エラー文 */}
                          <div style={{ color: 'red' }}>
                            {getErrMassage(
                              planEditResource.shutdown[furnacesIndex][dataIndex],
                              planEditResource.shutdown[furnacesIndex],
                              planEditResource.shutdown[furnacesIndex].length === dataIndex + 1,
                            ) !== ''
                              ? getErrMassage(
                                  planEditResource.shutdown[furnacesIndex][dataIndex],
                                  planEditResource.shutdown[furnacesIndex],
                                  planEditResource.shutdown[furnacesIndex].length === dataIndex + 1,
                                )
                              : getErrMassageCommonPeriodNotOverlap(planEditResource, furnacesIndex, dataIndex)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            ))}
          </div>
        </div>
        <div className="titlerow-planresult" style={{ display: isResultVisible() ? 'inline-block' : 'none' }}>
          <div className="itemname-planresult">
            <div className="itemname-back-planresult" style={{ background: COLOR_CODE.RESULT }}></div>
            立上下日
          </div>
        </div>
        <div className="row-planresult" style={{ display: isResultVisible() ? 'flex' : 'none' }}>
          <div className="flex-column">
            {Array.from({ length: sessionResource.furnacesCount }).map((_, furnacesIndex) => (
              <>
                <div className="graph-planresult" style={{ cursor: 'inherit' }}>
                  <div className="column-title-achievement">{String(furnacesIndex + 1).concat('号炉')}</div>
                  <div className="column-title-achievement">{`運転・停止\n実績`}</div>
                  <div className="column-item-planresult">
                    <div className="column-value-achievement">立上日</div>
                    <div className="column-value-achievement">立下日</div>
                  </div>
                  <div
                    style={{
                      position: 'relative',
                      maxHeight: '100px',
                      width: '938px',
                      border: 'solid 1px black',
                    }}
                  >
                    <BurnGraphBackground dayCount={displayDays} monthIndex={selectMonthIndex} highlightDays={resultEditResource.shutdown} displayYear={displayYear} />
                    <div style={{ overflow: 'hidden', maxHeight: '30px', maxWidth: '875px' }}>
                      <MarkDateGraph
                        dayLength={isMonthDisplay() ? sumArray(displayDays) : displayDays[selectMonthIndex - 1]}
                        dayStr={shutDownSums(item_launch_date, resultEditResource.shutdown[furnacesIndex])}
                        directionY="Bottom"
                        dayCount={displayDays}
                        monthIndex={selectMonthIndex}
                      />
                    </div>
                    <div style={{ overflow: 'hidden', maxHeight: '50px', width: '875px', border: '1px black solid' }}>
                      <BurnGraph2
                        dayCount={isMonthDisplay() ? sumArray(displayDays) : displayDayCountOfMonth}
                        planArray={burnDraftDaysVolumes(resultEditResource.day, 'burn_volume', furnacesIndex)}
                        rateCapacity={conditionResource.rateCapacity}
                      ></BurnGraph2>
                    </div>
                    <div style={{ overflow: 'hidden', maxHeight: '30px', maxWidth: '875px' }}>
                      <MarkDateGraph
                        dayLength={isMonthDisplay() ? sumArray(displayDays) : displayDays[selectMonthIndex - 1]}
                        dayStr={shutDownSums(item_shutdown_date, resultEditResource.shutdown[furnacesIndex])}
                        directionY="Top"
                        dayCount={displayDays}
                        monthIndex={selectMonthIndex}
                      />
                    </div>
                  </div>
                </div>
              </>
            ))}
          </div>
        </div>
        {Array.from({ length: draftLength }).map((_, draftIndex) => (
          <>
            <div className="titlerow-planresult" style={{ display: isSelectDraft[draftIndex] ? 'inline-block' : 'none' }}>
              <div className="itemname-planresult">
                <div
                  className="itemname-back-planresult"
                  style={{ background: [COLOR_CODE.DRAFT1, COLOR_CODE.DRAFT2, COLOR_CODE.DRAFT3, COLOR_CODE.DRAFT4, COLOR_CODE.DRAFT5][draftIndex] }}
                ></div>
                立上下日変更
              </div>
            </div>
            {isSelectDraft[draftIndex] && (
              <div className="row-planresult">
                <div className="flex-column ">
                  <div className="mb-2">
                    {Array.from({ length: sessionResource.furnacesCount }).map((_, furnacesIndex) => (
                      <>
                        <div style={{ display: 'flex', alignItems: 'end' }}>
                          <Button
                            className="button-2-planresult width-17 ml-auto mt-1 mr-0 mb-1"
                            variant="outline-secondary"
                            size="sm"
                            value={'前に戻る'}
                            onClick={(event) =>
                              handleInputUndoDateClick(
                                getArrayElementState(draftEditResource, setDraftEditResource, draftIndex),
                                getArrayElementState(draftShutdownRecord, setDraftShutdownRecord, draftIndex),
                                getArrayElementState(draftWarningVals, setDraftWarningVals, draftIndex),
                                draftInitResource[draftIndex],
                                draftEmptyCheck[draftIndex],
                                draftUsageCalc,
                                furnacesIndex,
                                draftIndex,
                              )
                            }
                            style={{ display: isMonthDisplay() && draftShutdownVisible[draftIndex][furnacesIndex] ? 'inline-block' : 'none' }}
                            disabled={draftHistory[draftIndex][furnacesIndex].length === 0}
                          >
                            前に戻る
                          </Button>
                          <Button
                            className="button-2-planresult width-17 ml-0 mt-1 mr-0 mb-1"
                            variant="outline-secondary"
                            size="sm"
                            value={'追加'}
                            onClick={() => handleInputAddDateClick(getArrayElementState(draftShutdownInputVisible, setDraftShutdownInputVisible, draftIndex), furnacesIndex)}
                            style={{
                              justifyContent: 'end',
                              display: isMonthDisplay() && draftShutdownVisible[draftIndex][furnacesIndex] ? 'inline-block' : 'none',
                            }}
                            disabled={draftShutdownInputVisible[draftIndex][furnacesIndex]}
                          >
                            追加
                          </Button>
                        </div>
                        <div
                          className="graph-planresult"
                          style={{
                            display: isSelectDraft[draftIndex] ? 'flex' : 'none',
                            flexDirection: 'row',
                            marginBottom: '10px',
                          }}
                          onClick={() => handleShutdownClick(getArrayElementState(draftShutdownVisible, setDraftShutdownVisible, draftIndex), furnacesIndex)}
                        >
                          <div className={'column-title-plan' + draftIndex}>{String(furnacesIndex + 1).concat('号炉')}</div>
                          <div className={'column-title-plan' + draftIndex}>
                            {`運転・停止`}
                            <br />
                            {draftPattern[draftIndex].priority_name}
                          </div>
                          <div className="column-item-planresult">
                            <div className={'column-value-plan' + draftIndex}>立上日</div>
                            <div className={'column-value-plan' + draftIndex}>立下日</div>
                          </div>
                          <div
                            style={{
                              position: 'relative',
                              maxHeight: '100px',
                              width: '938px',
                              border: 'solid 1px black',
                            }}
                          >
                            <BurnGraphBackground
                              dayCount={displayDays}
                              monthIndex={selectMonthIndex}
                              highlightDays={draftEditResource[draftIndex].shutdown}
                              displayYear={displayYear}
                            />
                            <div style={{ overflow: 'hidden', maxHeight: '30px' }}>
                              <MarkDateGraph
                                dayLength={isMonthDisplay() ? sumArray(displayDays) : displayDays[selectMonthIndex - 1]}
                                dayStr={shutDownSums(item_launch_date, draftEditResource[draftIndex].shutdown[furnacesIndex])}
                                directionY="Bottom"
                                dayCount={displayDays}
                                monthIndex={selectMonthIndex}
                              />
                            </div>
                            <div style={{ overflow: 'hidden', maxHeight: '50px', width: '875px', border: '1px black solid' }}>
                              <BurnGraph2
                                dayCount={isMonthDisplay() ? sumArray(displayDays) : displayDayCountOfMonth}
                                planArray={burnDraftDaysVolumes(draftEditResource[draftIndex].day, 'burn_volume', furnacesIndex)}
                                rateCapacity={conditionResource.rateCapacity}
                                warningDays={
                                  isMonthDisplay()
                                    ? draftWarningVals[draftIndex][furnacesIndex]
                                    : draftWarningVals[draftIndex][furnacesIndex].slice(lastDayOfLastMonth, daySum(selectMonthIndex))
                                }
                              ></BurnGraph2>
                            </div>
                            <div style={{ overflow: 'hidden', maxHeight: '30px' }}>
                              <MarkDateGraph
                                dayLength={isMonthDisplay() ? sumArray(displayDays) : displayDays[selectMonthIndex - 1]}
                                dayStr={shutDownSums(item_shutdown_date, draftEditResource[draftIndex].shutdown[furnacesIndex])}
                                directionY="Top"
                                dayCount={displayDays}
                                monthIndex={selectMonthIndex}
                              />
                            </div>
                          </div>
                        </div>
                        <div
                          className="mh-1"
                          style={{ display: draftShutdownVisible[draftIndex][furnacesIndex] ? 'flex' : 'none', overflowX: 'auto', width: '1100px', marginBottom: '10px' }}
                        >
                          <div>
                            <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                              <div style={{ display: 'flex', flexDirection: 'column', width: '60px', margin: '8px 70px 0px 0px' }}>
                                {/* 期間行 */}
                                <div style={{ marginBottom: '19px' }}>　</div>
                                <div className="itemname-planresult" style={{ flexBasis: '0rem' }}>
                                  <div className="itemname-back-planresult"></div>
                                  立下日
                                </div>
                                <div style={{ marginBottom: '5px' }}></div>
                                <div className="itemname-planresult" style={{ flexBasis: '0rem' }}>
                                  <div className="itemname-back-planresult"></div>
                                  立上日
                                </div>
                                {/* エラー行 */}
                                <div></div>
                              </div>
                              {/* 追加ボタン押下後の要素 */}
                              {isMonthDisplay() && draftShutdownInputVisible[draftIndex][furnacesIndex] && (
                                <div className="date-frame">
                                  <div className="date-border">
                                    {/* 期間 */}
                                    <div className="date-period">
                                      ●期間1
                                      {/* 削除ボタン */}
                                      <Button
                                        className="button-2-planresult"
                                        variant="outline-secondary"
                                        size="sm"
                                        style={{ marginLeft: '19px' }}
                                        value={'削除'}
                                        onClick={() =>
                                          handleInputDeleteDateClick(
                                            getArrayElementState(draftInputShutdown, setDraftInputShutdown, draftIndex),
                                            getArrayElementState(draftShutdownInputVisible, setDraftShutdownInputVisible, draftIndex),
                                            furnacesIndex,
                                          )
                                        }
                                      >
                                        削除
                                      </Button>
                                    </div>
                                    {/* 立下日 */}
                                    <CustomDatePicker
                                      className="dateinput-planresult"
                                      minDate={new Date(getInputableMinDate(displayYear, displayDays, draftEmptyCheck[draftIndex].day, systemDateIndex))}
                                      maxDate={new Date(getMaxDate(displayYear))}
                                      selected={
                                        draftInputShutdown[draftIndex][furnacesIndex].shutdown_date ? new Date(draftInputShutdown[draftIndex][furnacesIndex].shutdown_date) : null
                                      }
                                      onChange={(date: Date | null) =>
                                        handleInputDateChange(
                                          getArrayElementState(draftEditResource, setDraftEditResource, draftIndex),
                                          getArrayElementState(draftShutdownRecord, setDraftShutdownRecord, draftIndex),
                                          getArrayElementState(draftInputShutdown, setDraftInputShutdown, draftIndex),
                                          getArrayElementState(draftShutdownInputVisible, setDraftShutdownInputVisible, draftIndex),
                                          getArrayElementState(draftWarningVals, setDraftWarningVals, draftIndex),
                                          draftInitResource[draftIndex],
                                          draftEmptyCheck[draftIndex],
                                          draftUsageCalc,
                                          item_shutdown_date,
                                          date,
                                          furnacesIndex,
                                          draftIndex,
                                        )
                                      }
                                      dateFormat={`${DATE_FORMAT_MMDD_SLASH}(${WEEK_DAYS[new Date(draftInputShutdown[draftIndex][furnacesIndex].shutdown_date).getDay()]})`}
                                      portalId="datepicker-portal"
                                      locale={ja}
                                      placeholderText="年/月/日(曜日)"
                                    />
                                    <div style={{ marginBottom: '5px' }}></div>

                                    {/* 立上日 */}
                                    <CustomDatePicker
                                      className="dateinput-planresult-launch-style"
                                      minDate={new Date(getInputableMinDate(displayYear, displayDays, draftEmptyCheck[draftIndex].day, systemDateIndex))}
                                      maxDate={new Date(getMaxDate(displayYear))}
                                      selected={
                                        draftInputShutdown[draftIndex][furnacesIndex].launch_date ? new Date(draftInputShutdown[draftIndex][furnacesIndex].launch_date) : null
                                      }
                                      onChange={(date: Date | null) =>
                                        handleInputDateChange(
                                          getArrayElementState(draftEditResource, setDraftEditResource, draftIndex),
                                          getArrayElementState(draftShutdownRecord, setDraftShutdownRecord, draftIndex),
                                          getArrayElementState(draftInputShutdown, setDraftInputShutdown, draftIndex),
                                          getArrayElementState(draftShutdownInputVisible, setDraftShutdownInputVisible, draftIndex),
                                          getArrayElementState(draftWarningVals, setDraftWarningVals, draftIndex),
                                          draftInitResource[draftIndex],
                                          draftEmptyCheck[draftIndex],
                                          draftUsageCalc,
                                          item_launch_date,
                                          date,
                                          furnacesIndex,
                                          draftIndex,
                                        )
                                      }
                                      dateFormat={`${DATE_FORMAT_MMDD_SLASH}(${WEEK_DAYS[new Date(draftInputShutdown[draftIndex][furnacesIndex].launch_date).getDay()]})`}
                                      portalId="datepicker-portal"
                                      disabled={draftInputShutdown[draftIndex][furnacesIndex].shutdown_date === ''}
                                      locale={ja}
                                      // キーボード入力の際は、年を含めて打たないと今年と解釈されるため、プレースホルダーは年を残す
                                      placeholderText="年/月/日(曜日)"
                                    />
                                  </div>

                                  {/* エラー文 */}
                                  <div style={{ display: isMonthDisplay() ? 'flex' : 'none', color: 'red', marginLeft: '20px' }}>
                                    {draftEditResource[draftIndex].shutdown &&
                                    draftEditResource[draftIndex].shutdown.length > 0 &&
                                    draftEditResource[draftIndex].shutdown[furnacesIndex]
                                      ? getErrMassage(draftInputShutdown[draftIndex][furnacesIndex], [
                                          ...draftEditResource[draftIndex].shutdown[furnacesIndex],
                                          draftInputShutdown[draftIndex][furnacesIndex],
                                        ]) !== ''
                                        ? getErrMassage(draftInputShutdown[draftIndex][furnacesIndex], [
                                            ...draftEditResource[draftIndex].shutdown[furnacesIndex],
                                            draftInputShutdown[draftIndex][furnacesIndex],
                                          ])
                                        : ''
                                      : ''}
                                  </div>
                                </div>
                              )}

                              {/* 追加ボタン押下前の要素 */}
                              {Array.from({
                                length: draftEditResource[draftIndex].shutdown[furnacesIndex] ? draftEditResource[draftIndex].shutdown[furnacesIndex].length : 0,
                              }).map((_, dataIndex) => (
                                <div key={dataIndex} className="date-frame">
                                  <div
                                    className="date-border"
                                    style={{
                                      backgroundColor: draftEditResource[draftIndex].shutdown[furnacesIndex][dataIndex].not_change_flag ? 'rgba(255, 0, 47, 0.2)' : 'transparent',
                                    }}
                                  >
                                    {/* 期間 */}
                                    <div className="date-period">
                                      ●期間{!(isMonthDisplay() && draftShutdownInputVisible[draftIndex][furnacesIndex]) ? dataIndex + 1 : dataIndex + 2}
                                      {/* 削除ボタン */}
                                      <Button
                                        className="button-2-planresult white-bg-button"
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={() =>
                                          handleDeleteDateClick(
                                            getArrayElementState(draftEditResource, setDraftEditResource, draftIndex),
                                            getArrayElementState(draftShutdownRecord, setDraftShutdownRecord, draftIndex),
                                            getArrayElementState(draftWarningVals, setDraftWarningVals, draftIndex),
                                            draftInitResource[draftIndex],
                                            draftEmptyCheck[draftIndex],
                                            planUsageCalc,
                                            furnacesIndex,
                                            dataIndex,
                                            draftIndex,
                                          )
                                        }
                                        onMouseDown={(e) => e.preventDefault()}
                                        style={{ display: isMonthDisplay() ? 'inline-block' : 'none', marginLeft: '19px' }}
                                      >
                                        削除
                                      </Button>
                                    </div>
                                    {/* 立下日 */}
                                    <CustomDatePicker
                                      className="dateinput-planresult"
                                      disabled={
                                        (inShutdownPeriod(draftEditResource[draftIndex].shutdown[furnacesIndex][dataIndex], item_shutdown_date) ||
                                          draftEditResource[draftIndex].shutdown[furnacesIndex][dataIndex].shutdown_date === '') &&
                                        systemDateIndex <= getDateIndex(draftEditResource[draftIndex].shutdown[furnacesIndex][dataIndex].shutdown_date, displayYear, displayDays) &&
                                        isMonthDisplay()
                                          ? false
                                          : true
                                      }
                                      minDate={new Date(getInputableMinDate(displayYear, displayDays, draftEmptyCheck[draftIndex].day, systemDateIndex))}
                                      maxDate={new Date(getMaxDate(displayYear))}
                                      selected={
                                        draftEditResource[draftIndex].shutdown[furnacesIndex][dataIndex].shutdown_date
                                          ? new Date(draftEditResource[draftIndex].shutdown[furnacesIndex][dataIndex].shutdown_date)
                                          : null
                                      }
                                      onChange={(date: Date | null) => {
                                        handleDateChange(
                                          getArrayElementState(draftEditResource, setDraftEditResource, draftIndex),
                                          getArrayElementState(draftShutdownRecord, setDraftShutdownRecord, draftIndex),
                                          getArrayElementState(draftWarningVals, setDraftWarningVals, draftIndex),
                                          draftInitResource[draftIndex],
                                          draftEmptyCheck[draftIndex],
                                          draftUsageCalc,
                                          item_shutdown_date,
                                          date,
                                          furnacesIndex,
                                          dataIndex,
                                          draftIndex,
                                        );
                                      }}
                                      dateFormat={`${DATE_FORMAT_MMDD_SLASH}(${
                                        WEEK_DAYS[new Date(draftEditResource[draftIndex].shutdown[furnacesIndex][dataIndex].shutdown_date).getDay()]
                                      })`}
                                      portalId="datepicker-portal"
                                      locale={ja}
                                      // キーボード入力の際は、年を含めて打たないと今年と解釈されるため、プレースホルダーは年を残す
                                      placeholderText="年/月/日(曜日)"
                                    />

                                    <div style={{ marginBottom: '5px' }}></div>

                                    {/* 立上日 */}
                                    <CustomDatePicker
                                      className="dateinput-planresult-launch-style"
                                      disabled={
                                        ((inShutdownPeriod(draftEditResource[draftIndex].shutdown[furnacesIndex][dataIndex], item_launch_date) &&
                                          systemDateIndex <=
                                            getDateIndex(draftEditResource[draftIndex].shutdown[furnacesIndex][dataIndex].launch_date, displayYear, displayDays)) ||
                                          draftEditResource[draftIndex].shutdown[furnacesIndex][dataIndex].launch_date === '') &&
                                        isMonthDisplay()
                                          ? false
                                          : true
                                      }
                                      minDate={new Date(getInputableMinDate(displayYear, displayDays, draftEmptyCheck[draftIndex].day, systemDateIndex))}
                                      maxDate={new Date(getMaxDate(displayYear))}
                                      selected={
                                        draftEditResource[draftIndex].shutdown[furnacesIndex][dataIndex].launch_date
                                          ? new Date(draftEditResource[draftIndex].shutdown[furnacesIndex][dataIndex].launch_date)
                                          : null
                                      }
                                      onChange={(date: Date | null) => {
                                        handleDateChange(
                                          getArrayElementState(draftEditResource, setDraftEditResource, draftIndex),
                                          getArrayElementState(draftShutdownRecord, setDraftShutdownRecord, draftIndex),
                                          getArrayElementState(draftWarningVals, setDraftWarningVals, draftIndex),
                                          draftInitResource[draftIndex],
                                          draftEmptyCheck[draftIndex],
                                          draftUsageCalc,
                                          item_launch_date,
                                          date,
                                          furnacesIndex,
                                          dataIndex,
                                          draftIndex,
                                        );
                                      }}
                                      dateFormat={`${DATE_FORMAT_MMDD_SLASH}(${
                                        WEEK_DAYS[new Date(draftEditResource[draftIndex].shutdown[furnacesIndex][dataIndex].launch_date).getDay()]
                                      })`}
                                      portalId="datepicker-portal"
                                      locale={ja}
                                      // キーボード入力の際は、年を含めて打たないと今年と解釈されるため、プレースホルダーは年を残す
                                      placeholderText="年/月/日(曜日)"
                                    />
                                  </div>

                                  {/* エラー文 */}
                                  <div style={{ color: 'red', marginLeft: '20px' }}>
                                    {getErrMassage(
                                      draftEditResource[draftIndex].shutdown[furnacesIndex][dataIndex],
                                      draftEditResource[draftIndex].shutdown[furnacesIndex],
                                      draftEditResource[draftIndex].shutdown[furnacesIndex].length === dataIndex + 1,
                                    ) !== ''
                                      ? getErrMassage(
                                          draftEditResource[draftIndex].shutdown[furnacesIndex][dataIndex],
                                          draftEditResource[draftIndex].shutdown[furnacesIndex],
                                          draftEditResource[draftIndex].shutdown[furnacesIndex].length === dataIndex + 1,
                                        )
                                      : getErrMassageCommonPeriodNotOverlap(draftEditResource[draftIndex], furnacesIndex, dataIndex)}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </>
        ))}
      </div>

      <div className="indent-planresult">
        <div className="titlerow-planresult" style={{ display: existSomeDraft && planResultUsageDisplay['electric_create'] && showPowerVisible ? 'flex' : 'none' }}>
          <div className="itemname-planresult">
            <div className="itemname-back-planresult"></div>
            発電電力量
          </div>
        </div>
        <div className="row-planresult flex-row" style={{ display: existSomeDraft && planResultUsageDisplay['electric_create'] && showPowerVisible ? 'flex' : 'none' }}>
          <div style={{ display: 'flex', width: 'calc(100% - 14em)' }}>
            {Object.keys(graphProps.electric_create).length > 0 ? (
              <LineChart canvas={graphProps.electric_create.canvas} lines={graphProps.electric_create.lines} grid={graphProps.electric_create.grid} />
            ) : (
              <></>
            )}
          </div>
          <SumTable
            unit="MWh"
            labels={['期初計画', '計画', '実績', ...draftPattern.map((item) => item.priority_name)]}
            values={[
              convertMWhArray(itemDraftVolumes(initialPlanEditResource.month, initialPlanEditResource.day, 'electric_create')),
              convertMWhArray(itemDraftVolumes(planEditResource.month, planEditResource.day, 'electric_create')),
              convertMWhArray(itemDraftVolumes(resultEditResource.month, resultEditResource.day, 'electric_create')),
              ...Array.from({ length: draftLength }, (_, index) =>
                convertMWhArray(itemDraftVolumes(draftEditResource[index].month, draftEditResource[index].day, 'electric_create')),
              ),
            ]}
            backgrounds={COLOR_ARRAY.slice(0, draftLength + 3)}
            isSelect={[showInitialPlanVisible, showPlanVisible, showResultVisible, ...isSelectDraft]}
            exist={[
              existInitialPlanDraft && initialPlanUsageDisplay['electric_create'],
              existPlanDraft && planUsageDisplay['electric_create'],
              existResultDraft && resultUsageDisplay['electric_create'],
              ...Array.from({ length: draftLength }, () => existAutoDraft && draftUsageDisplay['electric_create']),
            ]}
          />
        </div>
        <div className="titlerow-planresult" style={{ display: existSomeDraft && planResultUsageDisplay['electric_sale'] && showPowerVisible ? 'flex' : 'none' }}>
          <div className="itemname-planresult">
            <div className="itemname-back-planresult"></div>
            売電電力量
          </div>
        </div>
        <div className="row-planresult flex-row" style={{ display: existSomeDraft && planResultUsageDisplay['electric_sale'] && showPowerVisible ? 'flex' : 'none' }}>
          <div style={{ display: 'flex', width: 'calc(100% - 14em)' }}>
            {Object.keys(graphProps.electric_sale).length > 0 ? (
              <LineChart canvas={graphProps.electric_sale.canvas} lines={graphProps.electric_sale.lines} grid={graphProps.electric_sale.grid} />
            ) : (
              <></>
            )}
          </div>
          <SumTable
            unit="MWh"
            labels={['期初計画', '計画', '実績', ...draftPattern.map((item) => item.priority_name)]}
            values={[
              convertMWhArray(itemDraftVolumes(initialPlanEditResource.month, initialPlanEditResource.day, 'electric_sale')),
              convertMWhArray(itemDraftVolumes(planEditResource.month, planEditResource.day, 'electric_sale')),
              convertMWhArray(itemDraftVolumes(resultEditResource.month, resultEditResource.day, 'electric_sale')),
              ...Array.from({ length: draftLength }, (_, index) =>
                convertMWhArray(itemDraftVolumes(draftEditResource[index].month, draftEditResource[index].day, 'electric_sale')),
              ),
            ]}
            backgrounds={COLOR_ARRAY.slice(0, draftLength + 3)}
            isSelect={[showInitialPlanVisible, showPlanVisible, showResultVisible, ...isSelectDraft]}
            exist={[
              existInitialPlanDraft && initialPlanUsageDisplay['electric_sale'],
              existPlanDraft && planUsageDisplay['electric_sale'],
              existResultDraft && resultUsageDisplay['electric_sale'],
              ...Array.from({ length: draftLength }, () => existAutoDraft && draftUsageDisplay['electric_sale']),
            ]}
          />
        </div>
        <div className="titlerow-planresult" style={{ display: existSomeDraft && planResultUsageDisplay['electric_buy'] && showPowerVisible ? 'flex' : 'none' }}>
          <div className="itemname-planresult">
            <div className="itemname-back-planresult"></div>
            買電電力量
          </div>
        </div>
        <div className="row-planresult flex-row" style={{ display: existSomeDraft && planResultUsageDisplay['electric_buy'] && showPowerVisible ? 'flex' : 'none' }}>
          <div style={{ display: 'flex', width: 'calc(100% - 14em)' }}>
            {Object.keys(graphProps.electric_buy).length > 0 ? (
              <LineChart canvas={graphProps.electric_buy.canvas} lines={graphProps.electric_buy.lines} grid={graphProps.electric_buy.grid} />
            ) : (
              <></>
            )}
          </div>
          <SumTable
            unit="MWh"
            labels={['期初計画', '計画', '実績', ...draftPattern.map((item) => item.priority_name)]}
            values={[
              convertMWhArray(itemDraftVolumes(initialPlanEditResource.month, initialPlanEditResource.day, 'electric_buy')),
              convertMWhArray(itemDraftVolumes(planEditResource.month, planEditResource.day, 'electric_buy')),
              convertMWhArray(itemDraftVolumes(resultEditResource.month, resultEditResource.day, 'electric_buy')),
              ...Array.from({ length: draftLength }, (_, index) => convertMWhArray(itemDraftVolumes(draftEditResource[index].month, draftEditResource[index].day, 'electric_buy'))),
            ]}
            backgrounds={COLOR_ARRAY.slice(0, draftLength + 3)}
            isSelect={[showInitialPlanVisible, showPlanVisible, showResultVisible, ...isSelectDraft]}
            exist={[
              existInitialPlanDraft && initialPlanUsageDisplay['electric_buy'],
              existPlanDraft && planUsageDisplay['electric_buy'],
              existResultDraft && resultUsageDisplay['electric_buy'],
              ...Array.from({ length: draftLength }, () => existAutoDraft && draftUsageDisplay['electric_buy']),
            ]}
          />
        </div>
        <div className="titlerow-planresult" style={{ display: existSomeDraft && planResultUsageDisplay['main_ash_generate'] && showAshVisible ? 'flex' : 'none' }}>
          <div className="itemname-planresult">
            <div className="itemname-back-planresult"></div>
            主灰発生量
          </div>
        </div>
        <div className="row-planresult flex-row" style={{ display: existSomeDraft && planResultUsageDisplay['main_ash_generate'] && showAshVisible ? 'flex' : 'none' }}>
          <div style={{ display: 'flex', width: 'calc(100% - 14em)' }}>
            {Object.keys(graphProps.main_ash_generate).length > 0 ? (
              <LineChart canvas={graphProps.main_ash_generate.canvas} lines={graphProps.main_ash_generate.lines} grid={graphProps.main_ash_generate.grid} />
            ) : (
              <></>
            )}
          </div>
          <SumTable
            unit="t"
            labels={['期初計画', '計画', '実績', ...draftPattern.map((item) => item.priority_name)]}
            values={[
              itemDraftVolumes(initialPlanEditResource.month, initialPlanEditResource.day, 'main_ash_generate'),
              itemDraftVolumes(planEditResource.month, planEditResource.day, 'main_ash_generate'),
              itemDraftVolumes(resultEditResource.month, resultEditResource.day, 'main_ash_generate'),
              ...Array.from({ length: draftLength }, (_, index) => itemDraftVolumes(draftEditResource[index].month, draftEditResource[index].day, 'main_ash_generate')),
            ]}
            backgrounds={COLOR_ARRAY.slice(0, draftLength + 3)}
            isSelect={[showInitialPlanVisible, showPlanVisible, showResultVisible, ...isSelectDraft]}
            exist={[
              existInitialPlanDraft && initialPlanUsageDisplay['main_ash_generate'],
              existPlanDraft && planUsageDisplay['main_ash_generate'],
              existResultDraft && resultUsageDisplay['main_ash_generate'],
              ...Array.from({ length: draftLength }, () => existAutoDraft && draftUsageDisplay['main_ash_generate']),
            ]}
          />
        </div>
        <div
          className="titlerow-planresult"
          style={{
            display: existSomeDraft && (planResultUsageDisplay['main_ash_carry_out'] || planResultUsageDisplay['main_ash_carry_out_units']) && showAshVisible ? 'flex' : 'none',
          }}
        >
          <div className="itemname-planresult">
            <div className="itemname-back-planresult"></div>
            主灰搬出量
          </div>
        </div>
        <div
          className="row-planresult flex-row"
          style={{
            display:
              existSomeDraft &&
              (planResultUsageDisplay['main_ash_carry_out'] ||
                planResultUsageDisplay['main_ash_carry_out_units'] ||
                initialPlanUsageDisplay['main_ash_carry_out'] ||
                initialPlanUsageDisplay['main_ash_carry_out_units']) &&
              showAshVisible
                ? 'flex'
                : 'none',
          }}
        >
          <div style={{ display: 'flex', width: 'calc(100% - 14em)' }}>
            <AshGraph
              lineTitles={tableStr}
              barTitles={tableStr}
              dayCount={isMonthDisplay() ? months.length : displayDayCountOfMonth}
              lineDatas={[
                getMonitorValues(
                  isMonthDisplay() ? initialPlanMonitorResource : initialPlanMonitorResource.slice(lastDayOfLastMonth, daySum(selectMonthIndex)),
                  'main_ash_carry_out_sum',
                ),
                getMonitorValues(isMonthDisplay() ? planMonitorResource : planMonitorResource.slice(lastDayOfLastMonth, daySum(selectMonthIndex)), 'main_ash_carry_out_sum'),
                getMonitorValues(isMonthDisplay() ? resultMonitorResource : resultMonitorResource.slice(lastDayOfLastMonth, daySum(selectMonthIndex)), 'main_ash_carry_out_sum'),
                ...Array.from({ length: draftLength }, (_, index) =>
                  getMonitorValues(
                    isMonthDisplay() ? draftMonitorResource[index] : draftMonitorResource[index].slice(lastDayOfLastMonth, daySum(selectMonthIndex)),
                    'main_ash_carry_out_sum',
                  ),
                ),
              ]}
              barDatas={[
                itemDraftVolumes(initialPlanEditResource.month, initialPlanEditResource.day, 'main_ash_carry_out_units'),
                itemDraftVolumes(planEditResource.month, planEditResource.day, 'main_ash_carry_out_units'),
                itemDraftVolumes(resultEditResource.month, resultEditResource.day, 'main_ash_carry_out_units'),
                ...Array.from({ length: draftLength }, (_, index) => itemDraftVolumes(draftEditResource[index].month, draftEditResource[index].day, 'main_ash_carry_out_units')),
              ]}
              isSelect={[showInitialPlanVisible, showPlanVisible, showResultVisible, ...isSelectDraft]}
              exist={[
                existInitialPlanDraft && initialPlanUsageDisplay['main_ash_carry_out'],
                existPlanDraft && planUsageDisplay['main_ash_carry_out'],
                existResultDraft && resultUsageDisplay['main_ash_carry_out'],
                ...Array.from({ length: draftLength }, () => existAutoDraft && draftUsageDisplay['main_ash_carry_out']),
              ]}
            />
          </div>
          <SumTable
            unit="t"
            labels={['期初計画', '計画', '実績', ...draftPattern.map((item) => item.priority_name)]}
            values={[
              getMonitorValues(
                isMonthDisplay() ? initialPlanMonitorResource : initialPlanMonitorResource.slice(lastDayOfLastMonth, daySum(selectMonthIndex)),
                'main_ash_carry_out_sum',
              ),
              getMonitorValues(isMonthDisplay() ? planMonitorResource : planMonitorResource.slice(lastDayOfLastMonth, daySum(selectMonthIndex)), 'main_ash_carry_out_sum'),
              getMonitorValues(isMonthDisplay() ? resultMonitorResource : resultMonitorResource.slice(lastDayOfLastMonth, daySum(selectMonthIndex)), 'main_ash_carry_out_sum'),
              ...Array.from({ length: draftLength }, (_, index) =>
                getMonitorValues(
                  isMonthDisplay() ? draftMonitorResource[index] : draftMonitorResource[index].slice(lastDayOfLastMonth, daySum(selectMonthIndex)),
                  'main_ash_carry_out_sum',
                ),
              ),
            ]}
            backgrounds={COLOR_ARRAY.slice(0, draftLength + 3)}
            isSelect={[showInitialPlanVisible, showPlanVisible, showResultVisible, ...isSelectDraft]}
            exist={[
              existInitialPlanDraft && initialPlanUsageDisplay['main_ash_carry_out'],
              existPlanDraft && planUsageDisplay['main_ash_carry_out'],
              existResultDraft && resultUsageDisplay['main_ash_carry_out'],
              ...Array.from({ length: draftLength }, () => existAutoDraft && draftUsageDisplay['main_ash_carry_out']),
            ]}
          />
        </div>
        <div className="titlerow-planresult" style={{ display: existSomeDraft && planResultUsageDisplay['main_ash_pit_rest'] && showAshVisible ? 'flex' : 'none' }}>
          <div className="itemname-planresult">
            <div className="itemname-back-planresult"></div>
            主灰ピット残量
          </div>
        </div>
        <div className="row-planresult flex-row" style={{ display: existSomeDraft && planResultUsageDisplay['main_ash_pit_rest'] && showAshVisible ? 'flex' : 'none' }}>
          <div style={{ display: 'flex', width: '100%' }}>
            {Object.keys(pitGraphProps.main_ash_pit_rest).length > 0 ? (
              <LineChart canvas={pitGraphProps.main_ash_pit_rest.canvas} lines={pitGraphProps.main_ash_pit_rest.lines} grid={pitGraphProps.main_ash_pit_rest.grid} />
            ) : (
              <></>
            )}
          </div>
        </div>
        <div className="titlerow-planresult" style={{ display: existSomeDraft && planResultUsageDisplay['fly_ash_generate'] && showAshVisible ? 'flex' : 'none' }}>
          <div className="itemname-planresult">
            <div className="itemname-back-planresult"></div>
            飛灰発生量
          </div>
        </div>
        <div className="row-planresult flex-row" style={{ display: existSomeDraft && planResultUsageDisplay['fly_ash_generate'] && showAshVisible ? 'flex' : 'none' }}>
          <div style={{ display: 'flex', width: 'calc(100% - 14em)' }}>
            {Object.keys(graphProps.fly_ash_generate).length > 0 ? (
              <LineChart canvas={graphProps.fly_ash_generate.canvas} lines={graphProps.fly_ash_generate.lines} grid={graphProps.fly_ash_generate.grid} />
            ) : (
              <></>
            )}
          </div>
          <SumTable
            unit="t"
            labels={['期初計画', '計画', '実績', ...draftPattern.map((item) => item.priority_name)]}
            values={[
              itemDraftVolumes(initialPlanEditResource.month, initialPlanEditResource.day, 'fly_ash_generate'),
              itemDraftVolumes(planEditResource.month, planEditResource.day, 'fly_ash_generate'),
              itemDraftVolumes(resultEditResource.month, resultEditResource.day, 'fly_ash_generate'),
              ...Array.from({ length: draftLength }, (_, index) => itemDraftVolumes(draftEditResource[index].month, draftEditResource[index].day, 'fly_ash_generate')),
            ]}
            backgrounds={COLOR_ARRAY.slice(0, draftLength + 3)}
            isSelect={[showInitialPlanVisible, showPlanVisible, showResultVisible, ...isSelectDraft]}
            exist={[
              existInitialPlanDraft && initialPlanUsageDisplay['fly_ash_generate'],
              existPlanDraft && planUsageDisplay['fly_ash_generate'],
              existResultDraft && resultUsageDisplay['fly_ash_generate'],
              ...Array.from({ length: draftLength }, () => existAutoDraft && draftUsageDisplay['fly_ash_generate']),
            ]}
          />
        </div>
        <div
          className="titlerow-planresult"
          style={{
            display:
              existSomeDraft &&
              (planResultUsageDisplay['fly_ash_carry_out'] ||
                planResultUsageDisplay['dry_fly_ash_carry_out'] ||
                planResultUsageDisplay['processed_fly_ash_carry_out'] ||
                planResultUsageDisplay['fly_ash_carry_out_units']) &&
              showAshVisible
                ? 'flex'
                : 'none',
          }}
        >
          <div className="itemname-planresult">
            <div className="itemname-back-planresult"></div>
            飛灰搬出量
          </div>
        </div>
        <div
          className="row-planresult flex-row"
          style={{
            display:
              existSomeDraft &&
              (planResultUsageDisplay['fly_ash_carry_out'] ||
                planResultUsageDisplay['dry_fly_ash_carry_out'] ||
                planResultUsageDisplay['processed_fly_ash_carry_out'] ||
                initialPlanUsageDisplay['fly_ash_carry_out'] ||
                initialPlanUsageDisplay['dry_fly_ash_carry_out'] ||
                initialPlanUsageDisplay['processed_fly_ash_carry_out'] ||
                planResultUsageDisplay['fly_ash_carry_out_units']) &&
              showAshVisible
                ? 'flex'
                : 'none',
          }}
        >
          <div style={{ display: 'flex', width: 'calc(100% - 14em)' }}>
            <AshGraph
              lineTitles={tableStr}
              barTitles={tableStr}
              dayCount={isMonthDisplay() ? months.length : displayDayCountOfMonth}
              lineDatas={[
                getMonitorValues(
                  isMonthDisplay() ? initialPlanMonitorResource : initialPlanMonitorResource.slice(lastDayOfLastMonth, daySum(selectMonthIndex)),
                  'fly_ash_carry_out_sum',
                ),
                getMonitorValues(isMonthDisplay() ? planMonitorResource : planMonitorResource.slice(lastDayOfLastMonth, daySum(selectMonthIndex)), 'fly_ash_carry_out_sum'),
                getMonitorValues(isMonthDisplay() ? resultMonitorResource : resultMonitorResource.slice(lastDayOfLastMonth, daySum(selectMonthIndex)), 'fly_ash_carry_out_sum'),
                ...Array.from({ length: draftLength }, (_, index) =>
                  getMonitorValues(
                    isMonthDisplay() ? draftMonitorResource[index] : draftMonitorResource[index].slice(lastDayOfLastMonth, daySum(selectMonthIndex)),
                    'fly_ash_carry_out_sum',
                  ),
                ),
              ]}
              barDatas={[
                itemDraftVolumes(initialPlanEditResource.month, initialPlanEditResource.day, 'fly_ash_carry_out_units'),
                itemDraftVolumes(planEditResource.month, planEditResource.day, 'fly_ash_carry_out_units'),
                itemDraftVolumes(resultEditResource.month, resultEditResource.day, 'fly_ash_carry_out_units'),
                ...Array.from({ length: draftLength }, (_, index) => itemDraftVolumes(draftEditResource[index].month, draftEditResource[index].day, 'fly_ash_carry_out_units')),
              ]}
              isSelect={[showInitialPlanVisible, showPlanVisible, showResultVisible, ...isSelectDraft]}
              exist={[
                existInitialPlanDraft &&
                  (initialPlanUsageDisplay['fly_ash_carry_out'] || initialPlanUsageDisplay['dry_fly_ash_carry_out'] || initialPlanUsageDisplay['processed_fly_ash_carry_out']),
                existPlanDraft && (planUsageDisplay['fly_ash_carry_out'] || planUsageDisplay['dry_fly_ash_carry_out'] || planUsageDisplay['processed_fly_ash_carry_out']),
                existResultDraft && (resultUsageDisplay['fly_ash_carry_out'] || resultUsageDisplay['dry_fly_ash_carry_out'] || resultUsageDisplay['processed_fly_ash_carry_out']),
                existAutoDraft && (draftUsageDisplay['fly_ash_carry_out'] || draftUsageDisplay['dry_fly_ash_carry_out'] || draftUsageDisplay['processed_fly_ash_carry_out']),
              ]}
            />
          </div>
          <SumTable
            unit="t"
            labels={['期初計画', '計画', '実績', ...draftPattern.map((item) => item.priority_name)]}
            values={[
              getMonitorValues(
                isMonthDisplay() ? initialPlanMonitorResource : initialPlanMonitorResource.slice(lastDayOfLastMonth, daySum(selectMonthIndex)),
                'fly_ash_carry_out_sum',
              ),
              getMonitorValues(isMonthDisplay() ? planMonitorResource : planMonitorResource.slice(lastDayOfLastMonth, daySum(selectMonthIndex)), 'fly_ash_carry_out_sum'),
              getMonitorValues(isMonthDisplay() ? resultMonitorResource : resultMonitorResource.slice(lastDayOfLastMonth, daySum(selectMonthIndex)), 'fly_ash_carry_out_sum'),
              ...Array.from({ length: draftLength }, (_, index) =>
                getMonitorValues(
                  isMonthDisplay() ? draftMonitorResource[index] : draftMonitorResource[index].slice(lastDayOfLastMonth, daySum(selectMonthIndex)),
                  'fly_ash_carry_out_sum',
                ),
              ),
            ]}
            backgrounds={COLOR_ARRAY.slice(0, draftLength + 3)}
            isSelect={[showInitialPlanVisible, showPlanVisible, showResultVisible, ...isSelectDraft]}
            exist={[
              existInitialPlanDraft &&
                (initialPlanUsageDisplay['fly_ash_carry_out'] || initialPlanUsageDisplay['dry_fly_ash_carry_out'] || initialPlanUsageDisplay['processed_fly_ash_carry_out']),
              existPlanDraft && (planUsageDisplay['fly_ash_carry_out'] || planUsageDisplay['dry_fly_ash_carry_out'] || planUsageDisplay['processed_fly_ash_carry_out']),
              existResultDraft && (resultUsageDisplay['fly_ash_carry_out'] || resultUsageDisplay['dry_fly_ash_carry_out'] || resultUsageDisplay['processed_fly_ash_carry_out']),
              ...Array.from(
                { length: draftLength },
                () => existAutoDraft && (draftUsageDisplay['fly_ash_carry_out'] || draftUsageDisplay['dry_fly_ash_carry_out'] || draftUsageDisplay['processed_fly_ash_carry_out']),
              ),
            ]}
          />
        </div>
        <div
          className="titlerow-planresult"
          style={{
            display:
              ((existSomeDraft && planResultUsageDisplay['fly_ash_pit_rest']) || (existInitialPlanDraft && initialPlanUsageDisplay['fly_ash_pit_rest'])) && showAshVisible
                ? 'flex'
                : 'none',
          }}
        >
          <div className="itemname-planresult">
            <div className="itemname-back-planresult"></div>
            飛灰ピット残量
          </div>
        </div>
        <div
          className="row-planresult flex-row"
          style={{
            display:
              ((existSomeDraft && planResultUsageDisplay['fly_ash_pit_rest']) || (existInitialPlanDraft && initialPlanUsageDisplay['fly_ash_pit_rest'])) && showAshVisible
                ? 'flex'
                : 'none',
          }}
        >
          <div style={{ display: 'flex', width: '100%' }}>
            {Object.keys(pitGraphProps.fly_ash_pit_rest).length > 0 ? (
              <LineChart canvas={pitGraphProps.fly_ash_pit_rest.canvas} lines={pitGraphProps.fly_ash_pit_rest.lines} grid={pitGraphProps.fly_ash_pit_rest.grid} />
            ) : (
              <></>
            )}
          </div>
        </div>
      </div>

      <br></br>

      <div className="indent-planresult">
        <div className="row-planresult d-flex align-items-center" style={{ marginTop: '5px' }}>
          <div className="itemname-planresult mx-3" style={{ flexBasis: 'auto' }}>
            <div className="itemname-back-planresult" style={{ width: '7.5em' }}></div>
            過去実績登録
          </div>
          {permissions & (PERMISSIONS.PLAN_RESULT_MAIN_EDIT | PERMISSIONS.PLAN_RESULT_SUB_EDIT | PERMISSIONS.PLAN_RESULT_OTHER_EDIT) ? (
            <>
              <Form.Group controlId="formFileSm">
                <Form.Control type="file" size="sm" accept=".csv" onChange={handleResultRegistChange} />
              </Form.Group>

              <Button className="button-1-planresult mx-1" variant="outline-success" size="sm" onClick={handleHistoryRegist}>
                過去実績登録
              </Button>
            </>
          ) : (
            ''
          )}
          <div className="spacer mx-2" />
          <div className="itemname-planresult mx-3" style={{ flexBasis: 'auto' }}>
            <div className="itemname-back-planresult" style={{ width: '5.5em' }}></div>
            更新履歴
          </div>
          <select className="mx-1" style={{ display: planResultHistory.length > 0 ? 'inline' : 'none' }} value={selectedCreatedAt} onChange={handlePlanResultHistoryChange}>
            {planResultHistory.map((option, index) => {
              const isInitialPlan = option.initial_plan_flag ?? false;
              return (
                <option key={index} value={option.created_at} style={isInitialPlan ? { backgroundColor: '#fff3cd', fontWeight: 'bold' } : undefined}>
                  {option.show_created_at}
                </option>
              );
            })}
          </select>
          <Button
            className="button-1-planresult mx-1"
            variant="outline-success"
            size="sm"
            onClick={handleHistoryExcelOutput}
            style={{ display: planResultHistory.length > 0 ? 'inline-block' : 'none' }}
          >
            Excel出力
          </Button>
          <div className="d-flex mx-5 flex-grow-1 align-items-center justify-center">
            <div className="itemname-planresult initial-plan-label mx-3">
              <div className="itemname-back-planresult"></div>
              期初計画
            </div>
            {existInitialPlanDraft ? (
              <span className="mx-2">{planResultHistory.find((history) => history.initial_plan_flag)?.show_created_at || ''}</span>
            ) : (
              <select
                className="mx-2"
                style={{ display: planResultHistory.length > 0 ? 'inline' : 'none' }}
                value={selectedInitialPlanTargetCreatedAt}
                onChange={handleInitialPlanTargetChange}
              >
                {planResultHistory.map((option, index) => {
                  return (
                    <option key={index} value={option.created_at}>
                      {option.show_created_at}
                    </option>
                  );
                })}
              </select>
            )}
            {(permissions & (PERMISSIONS.PLAN_RESULT_MAIN_EDIT | PERMISSIONS.PLAN_RESULT_SUB_EDIT | PERMISSIONS.PLAN_RESULT_OTHER_EDIT)) !== 0 ? (
              <>
                {existInitialPlanDraft ? (
                  <Button className="button-1-planresult mx-1" variant="outline-success" size="sm" onClick={handleInitialPlanCancel}>
                    撤回
                  </Button>
                ) : (
                  <Button className="button-1-planresult mx-1" variant="outline-success" size="sm" onClick={handleInitialPlanSet}>
                    設定
                  </Button>
                )}
              </>
            ) : (
              ''
            )}
          </div>
        </div>
      </div>

      <br></br>
    </div>
  );
};

export default Page;
